/* tslint:disable */
/* eslint-disable */
/**
 * Dray Dog
 * # Overview  This is the documentation for the Dray Dog API. Dray Dog helps intermodal trucking companies achieve **Data Driven Drayage**, with a primary focus on appointment management.  ## About this API  ### 🤔 Philosophy  Dray Dog is an **API first company**. This means:  - You can do almost anything you can do via our user interface via our API - All APIs have accurate documentation thanks to auto-generation from internal typing   information - We strive to maintain backwards compatibility for all APIs   - **NOTE:** As we use this API internally, we do update it frequently, but we almost     always stick to backwards compatible changes, particularly for the APIs that are     likely to be used by external companies.  We do exclude a few API\'s which are unlikely to be of use from this documentation.  ### 🔄 Requests and Responses  Most APIs return and accept JSON unless otherwise noted.  ### 💣 Error messages  Our API\'s give useful error messages for most mis-use as relatively human readable JSON. For example, if you omit a required field, the response will indicate which field you are missing, or if you provide an invalid enum value, the response will indicate the valid enum values.  ### 🪄 Generating a Client  It can be useful to generate a client for this API in your language of choice. This can make coding against our API easier and help prevent errors. Here are some [useful docs on generating clients](https://fastapi.tiangolo.com/advanced/generate-clients/)  ## About Dray Dog  Dray Dog\'s mission is to make port drayage more efficient by enabling **Data Driven Drayage**. Data Driven Drayage means:  - Connecting to the key data streams in the drayage ecosystem - Validating and interpreting data streams in a way that enable high accuracy and _trust_ - Automating and standardizing processes based on this high accuracy data  Our core service is focused on port appointment scheduling.  Here are some of the ways we are bringing efficiency to port drayage:  - We have tools to align appointments booked with available truck capacity - We allow users to book appointments automatically - We provide a single, ergonomic interface for viewing/booking appointments,   rescheduling, cancelling and other appointment actions  Carriers have seen the following improvements from using Dray Dog:  - Increased moves per day due to better appointment scheduling - Reduced container cycle time and reduced demurrage - 20%+ reduction in missed appointment rate  # Getting Started  The first step is to make sure you can get [authenticated](#section/Getting-Started/Authentication) using your language of choice. Once you\'ve done that, check out our [guides](#section/Guides) to get started with some common use cases.  ## 🔌 Configuring Data Sources  Dray Dog must be connected to external data sources to act on your companies behalf. You can do this via the [settings](https://app.draydog.com/settings) in the UI  ## 🔑 Authentication  Dray Dog uses [OAuth2](https://oauth.net/2/) authentication. You need to use an email and password for a user registered on with your Dray Dog organization to obtain an API token. Tokens do not expire as long is the user is not removed from your organization.  Here is an example of how to get and use a token using Python:  ```python import requests  base_url = \"https://api.draydog.com\" session = requests.Session() resp = requests.post(     base_url + \"/users/token/\",     json={\"email\":\"<user email address>\", \"password\": \"<password>\"} ) resp.raise_for_status() auth_info = resp.json() session.headers.update({     \"Authorization\": f\"{auth_info[\'token_type\']} {auth_info[\'access_token\']}\" })  # Example API request - get data on your containers resp = session.get(base_url + \"/containers/\") print(resp.json()) ```  <SecurityDefinitions />  # Guides  These guides are designed to help you get started with common use cases.  As a reminder, we provide _all_ APIs we\'ve created to power [app.draydog.com](https://app.draydog.com) for your use. However, that does not mean all of the APIs are necessarily _useful_ to _you_ or are even worth perusing. Stick to these guides and you\'ll be on a good track.  ## 📤 Upload Containers  To start tracking containers prior to appointment booking, you need to upload containers to Dray Dog. You can either do this manually or you can use the following API:  - [POST /containers/watch/](#operation/watch_containers_containers_watch__post)   - Includes options to assign users and set customers     - To manage customers, see the [Customers APIs](#tag/Customers)  **NOTE**: Containers with appointments under your SCAC will be automatically added to Dray Dog when we \"sync\" appointments from the various appointment systems, even if you do not upload those containers  ## 📦 Read Containers Data  Our `GET /containers/` API is our most used/most powerful API. It packages together all the _current_ data we have on each container and has many filter options.  - See [GET /containers/](#operation/get_containers_containers__get). - The API returns:   - Computed container status based on all available data   - Import status (availability). Includes things like:     - Last Free Day     - Location     - Holds     - Demurrage due     - Available for appointment     - Ready for pickup   - Latest appointment     - Intelligently selects the \"most relevant\" appointment   - In-gate and out-gate receipts  ## 🗓️ Read Appointments Data  Getting appointment times and numbers into your TMS might be the most useful thing you can do with this API. Having appointments synced into your TMS helps:  **NOTE:** We sync all appointments of all types (loaded/empty, inbound/outbound) scheduled under your SCAC regardless of whether you\'ve added the appointment\'s container number to Dray Dog or not. So we always have an up-to-date list of appointments for your SCAC 🙌  - Reduce tedious data entry - Prevent dry-runs due to:   - Appointments cancelled by the terminal   - Incorrect data entry (example: AM/PM mixup) - Trouble at terminal gates due to incorrect PIN numbers - Missed appointments due to appointment not entered in the TMS or entered incorrectly  For reading appointments, you have two options:  ### Option 1: Use Containers API  You can use the [GET /containers/](#operation/get_containers_containers__get) API described above and access `containers[x].latest_appointment`. If you want to get only _scheduled_ appointments, you can use the `container_cycle_state=import_appt_booked` query parameter. If you want to make sure you pull in cancellations, you want to use `container_cycle_state=import_appt_booked&container_cycle_state=import_appt_booking` or even include `container_cycle_state=import_tracking` as well in case a container goes from booked to unavailable.  ### Option 2: Use Appointments API  We also have the [GET /appointments/](#operation/list_appointments_appointments__get) API which just returns appointments. It\'s faster than the containers API and returns all appointment data, but you have to do the work of deciding which is the most relevant/current appointment for each container.  ## 📝 Edit License Plates  Many terminals offer a faster in-gate experience if you have a correctly set license plate on the appointment, so automatically setting license plates on your appointments when a truck is dispatched from your TMS can be a big win.  - See [POST /appointments/booking/edit_license_plate/](#operation/edit_license_plate_appointments_booking_edit_license_plate__post) for updating license plate - You will need an appointment ID to use this API. See   [Read Appointments Data section](#section/Read-Appointments-Data) for how to get   appointments which have the `id` field you need  ## 📅 Book Appointments  Generally, we recommend taking advantage of our user interface and the Auto Book feature to increase your team\'s appointment booking efficiency instead of using the API for booking. We are always iterating on these features with a dedicated team focused on making the best possible appointment booking experience. It might not be the best use of resources to try to replicate this.  If you do want to book via API, here\'re the APIs you need:  - [GET /appointments/availability/{container_number}/](#operation/get_available_slots_for_container_appointments_availability__container_number___get) for getting available times - [POST /appointments/booking/book/](#operation/book_appointment_appointments_booking_book__post) for booking a specific time - (optional) [GET /appointments/booking/license_plate_options/](#operation/get_license_plate_options_appointments_booking_license_plate_options__get) can help get valid   license plate options for a given terminal  ### 🤖 Auto Book  You can enable Auto Book using this API, but the interface is a bit complex and we update it often. It might be best to have your users manage their Auto Book settings in our UI instead. If you do want to manage this programmatically, here\'s the API:  - [POST /appointments/booking/auto/](#operation/save_auto_book_settings_appointments_booking_auto__post) for \"Auto Booking\"  ## 🧪 Test Mode  ### Overview  Dray Dog\'s test mode lets you exercise the full booking and tracking workflows without touching real terminal systems. Test mode is enabled per organization — contact Dray Dog support to have your organization set up as a test org.  ### 🔑 Terminal Credential Setup (Required — Any Value Works)  Booking workflows require at least one terminal credential to be configured before they can run. This gate exists to ensure that the account setup path is validated end-to-end.  **In test mode, you do not need real credentials.** The login step that checks whether your username and password are valid is mocked — any value you enter will succeed. You still need to add *something* in the credentials form, but the values do not matter.  **Steps to unblock:** 1. Go to **Settings → Connections** in the Dray Dog UI (or call    `POST /connections/save_and_validate/` via the API) 2. Select the terminal you want to test (e.g., APM) 3. Enter any username and password (e.g., `test` / `test`) 4. Save — the validation will succeed immediately  You only need to configure credentials for the specific terminals you intend to test. You do not need to configure every terminal.  ### What Is Mocked in Test Mode  | Operation                         | Behavior in test mode                                                  | | --------------------------------- | ---------------------------------------------------------------------- | | **Credential login check**        | **Mocked** — any credentials pass; no real terminal login is attempted | | **Appointment book/cancel/edit**  | **Mocked** — workflows run end-to-end but do not contact real terminals | | **Import status (TESU containers)** | **Mocked** — returns deterministic data from the catalog below       | | **Mutation rejection/failure**    | **Configurable** — specific TESU containers trigger rejection/failure  | | **Post-action appointment refresh** | **Mocked** — loads directly from the database, skips portal lookup  |  ### What Is Not Mocked  - **The credential configuration step itself** — you must add credentials, but   any value works (see above) - **Import status for non-TESU containers** — real container numbers may still   attempt portal lookups; use TESU-prefix containers for predictable results  ### Troubleshooting  **Symptom:** Booking APIs return a `409` with `\"code\": \"connection_not_configured\"` and a message like `\"Booking at APM needs a working APM Termpoint login. Add APM Termpoint credentials in Settings → Terminal + Line Credentials.\"`, or the UAS `GET /terminals/configuration_status/` returns `\"not_configured\"` for a terminal.  **Cause:** No terminal connection has been configured for that terminal.  **Fix:** Add a connection with any credential values for the affected terminal (see [Terminal Credential Setup](#terminal-credential-setup-required--any-value-works) above).  ---  ### 🧪 Test Containers  Dray Dog provides a set of **test containers** with the `TESU` prefix that return predictable, deterministic import status data. You can use these containers from **any organization** — no special test org is required.  This makes it easy to verify your integration end-to-end: upload a TESU container, check its import status, and confirm your system handles the response correctly — all without waiting on real terminal data.  #### How it works  TESU containers behave like real containers in every way except import status lookups, which return mock data from the catalog below instead of calling external terminal systems.  | Area                         | Behavior for TESU containers                                            | | ---------------------------- | ----------------------------------------------------------------------- | | **Container upload**         | Real — containers are saved to the database                             | | **Import status**            | **Mocked** — returns deterministic data from the catalog below          | | **Appointment booking**      | See test mode behavior above                                            | | **Webhooks / notifications** | Real — events fire as normal                                            |  #### Test Container Catalog  Use these `TESU`-prefix container numbers to get predictable, deterministic import status responses. Each container simulates a specific lifecycle state.  | Container | Terminal | Location | Ready | Holds | Mutation | Scenario | | --------- | -------- | -------- | ----- | ----- | -------- | -------- | | `TESU1000018` | apm | `in_terminal` | Yes | (none) | Success - for all actions | Happy path — ready for pickup | | `TESU1000023` | apm | `in_terminal` | No | Customs Hold | Success - for all actions | Container held by customs | | `TESU1000039` | trapac | `in_terminal` | No | Freight Hold | Success - for all actions | Freight not released | | `TESU1000044` | wbct | `in_terminal` | No | Customs Hold, Line Hold | Success - for all actions | Multiple holds | | `TESU1000050` | tti | `on_vessel` | No | (none) | Success - for all actions | Container still on vessel | | `TESU1000065` | fms | `in_terminal` | Yes | (none) | Success - for all actions | Ready but not yet available | | `TESU1000070` | everport | `out_gated` | No | (none) | Success - for all actions | Already picked up | | `TESU1000086` | pct | `in_terminal` | Yes | (none) | Success - for all actions | Ready, ASB-only terminal | | `TESU1000091` | its | `in_terminal` | Yes | (none) | Success - for all actions | Ready, ASB-only terminal | | `TESU1000105` | lbct | `in_terminal` | No | CTF Hold | Success - for all actions | Terminal-specific hold | | `TESU1000110` | pier_a | `in_terminal` | Yes | (none) | Success - for all actions | Ready, ASB-only terminal | | `TESU1000126` | yti | `in_terminal` | Yes | (none) | Success - for all actions | Ready, PO-only terminal | | `TESU2000012` | apm | `in_terminal` | Yes | (none) | Book Appointment: rejection — `AppointmentBookingRejectedException` | Book → rejection | | `TESU2000028` | apm | `in_terminal` | Yes | (none) | Book Appointment: failure — `Exception` | Book → generic failure | | `TESU2000033` | apm | `in_terminal` | Yes | (none) | Reschedule Appointment: rejection — `AppointmentRescheduleRejectedException` | Reschedule → rejection | | `TESU2000049` | apm | `in_terminal` | Yes | (none) | Cancel Appointment: rejection — `AppointmentCancellationRejectedException` | Cancel → rejection | | `TESU2000054` | apm | `in_terminal` | Yes | (none) | Book Appointment: rejection — `AppointmentSlotNotAvailableException` | Slot Not Available → rejection |  #### Mutation Rejection and Failure Testing  Some TESU containers are configured to test how your system handles **booking rejection** and **generic failure** scenarios — situations where the terminal system rejects or fails to process a booking request.  The **Mutation** column in the catalog above indicates behavior for each action:  - **`<action>: rejection`** — that specific action triggers a rejection exception - **`<action>: failure`** — that specific action triggers a generic failure exception - **Success - for all actions** — the container returns success for all actions (normal behavior)  **Testing workflow:** Use a rejection or failure container to book/reschedule/cancel an appointment in test mode. The mutation will fail with the configured exception, allowing you to verify that your integration correctly handles rejection responses. The booking request will be recorded in the database with the error code and message populated.  #### Known limitations  - TESU containers only affect **import status** lookups. Other data (gate   transactions, vessel ETAs) is not mocked. - Adding a TESU container number that is **not** in the catalog above will   be rejected at upload time with a validation error listing the valid   test container numbers.  ---  ### 🛠️ Test Mode PATCH API (appointments)  Test-mode organizations can directly **mutate existing** appointment records to simulate lifecycle transitions without going through real terminal systems. Appointments are read via the standard appointment APIs; creation happens through the normal booking flows.  **Import status** is not stored as mutable per-org records for portals like Port Optimizer in test mode — use **[Test Containers](#test-containers)** (TESU catalog) and real containers to exercise import-status scenarios instead of a PATCH API.  Typical workflow:  1. Upload a container and book an appointment via the normal APIs 2. [PATCH /test_mode/appointments/{appointment_id}/](#operation/update_test_mode_appointment_test_mode_appointments__appointment_id___patch) to advance the    appointment status (or exercise import status via TESU / production data as    described above) 3. Verify that your downstream integration handles the state change correctly  The endpoint requires the caller\'s organization to have test mode enabled (`is_test=true`). Non-test-mode organizations receive a **403 Forbidden** response. An appointment owned by a different organization returns **404 Not Found**.  Only fields included in the request body are changed; omitted fields retain their current values. The endpoint returns the updated appointment.  
 *
 * The version of the OpenAPI document: 0.1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * 
 * @export
 * @interface APIKeyConfig
 */
export interface APIKeyConfig {
    /**
     * 
     * @type {string}
     * @memberof APIKeyConfig
     */
    'config_type'?: APIKeyConfigConfigTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof APIKeyConfig
     */
    'api_key': string;
}

export const APIKeyConfigConfigTypeEnum = {
    ApiKey: 'api_key'
} as const;

export type APIKeyConfigConfigTypeEnum = typeof APIKeyConfigConfigTypeEnum[keyof typeof APIKeyConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface ActionRequest
 */
export interface ActionRequest {
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'window_start': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'reschedule_cancellation_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'reschedule_request_id'?: any;
    /**
     * 
     * @type {ActionStatus}
     * @memberof ActionRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'id': any;
    /**
     * 
     * @type {TerminalName}
     * @memberof ActionRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'container_number': any;
    /**
     * 
     * @type {SimpleUser}
     * @memberof ActionRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof ActionRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {any}
     * @memberof ActionRequest
     */
    'error_message'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'created': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'appointment_verification_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'organization_id': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'connection_id': any;
    /**
     * 
     * @type {PortalName}
     * @memberof ActionRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'requests_log_link'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'action_type'?: ActionRequestActionTypeEnum;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'auto_book_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'appointment_id': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'terminal_block'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'truck_license_plate': any;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof ActionRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'master_bill_of_lading': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'updated': any;
    /**
     * 
     * @type {SavedEmptyInAppointmentSlot}
     * @memberof ActionRequest
     */
    'appointment_slot': SavedEmptyInAppointmentSlot;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'own_chassis'?: any;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ActionRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof ActionRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'chassis_number': any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'auto_book_empty_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'new_appointment_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'booking_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ActionRequest
     */
    'cancellation_request_id'?: any;
}

export const ActionRequestActionTypeEnum = {
    RescheduleEmpty: 'reschedule_empty'
} as const;

export type ActionRequestActionTypeEnum = typeof ActionRequestActionTypeEnum[keyof typeof ActionRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface ActionRequestIDWithAppointmentID
 */
export interface ActionRequestIDWithAppointmentID {
    /**
     * 
     * @type {number}
     * @memberof ActionRequestIDWithAppointmentID
     */
    'request_id': number;
    /**
     * 
     * @type {ActionType}
     * @memberof ActionRequestIDWithAppointmentID
     */
    'type': ActionType;
    /**
     * 
     * @type {number}
     * @memberof ActionRequestIDWithAppointmentID
     */
    'appointment_id': number;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const ActionStatus = {
    InProgress: 'in_progress',
    Completed: 'completed',
    Failed: 'failed',
    Rejected: 'rejected'
} as const;

export type ActionStatus = typeof ActionStatus[keyof typeof ActionStatus];


/**
 * 
 * @export
 * @enum {string}
 */

export const ActionType = {
    Book: 'book',
    BookEmpty: 'book_empty',
    BookEmptyPickup: 'book_empty_pickup',
    Cancel: 'cancel',
    CheckIn: 'check_in',
    Reschedule: 'reschedule',
    RescheduleEmpty: 'reschedule_empty',
    AddEmpty: 'add_empty',
    SwapEmpty: 'swap_empty',
    EditLicensePlate: 'edit_license_plate',
    EditChassis: 'edit_chassis'
} as const;

export type ActionType = typeof ActionType[keyof typeof ActionType];


/**
 * 
 * @export
 * @interface AddEmptyRequest
 */
export interface AddEmptyRequest {
    /**
     * 
     * @type {number}
     * @memberof AddEmptyRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof AddEmptyRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof AddEmptyRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof AddEmptyRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof AddEmptyRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof AddEmptyRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof AddEmptyRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof AddEmptyRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof AddEmptyRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof AddEmptyRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'empty_container_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof AddEmptyRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof AddEmptyRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'truck_license_plate'?: string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof AddEmptyRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof AddEmptyRequest
     */
    'action_type'?: AddEmptyRequestActionTypeEnum;
}

export const AddEmptyRequestActionTypeEnum = {
    AddEmpty: 'add_empty'
} as const;

export type AddEmptyRequestActionTypeEnum = typeof AddEmptyRequestActionTypeEnum[keyof typeof AddEmptyRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface ApplyAddItem
 */
export interface ApplyAddItem {
    /**
     * 
     * @type {string}
     * @memberof ApplyAddItem
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ApplyAddItem
     */
    'status': ContainerStatus;
    /**
     * 
     * @type {string}
     * @memberof ApplyAddItem
     */
    'location'?: string;
    /**
     * 
     * @type {string}
     * @memberof ApplyAddItem
     */
    'customer'?: string;
    /**
     * 
     * @type {string}
     * @memberof ApplyAddItem
     */
    'master_bl'?: string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ApplyAddItem
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof ApplyAddItem
     */
    'container_type'?: ContainerType;
}


/**
 * 
 * @export
 * @interface ApplyReconciliationError
 */
export interface ApplyReconciliationError {
    /**
     * 
     * @type {string}
     * @memberof ApplyReconciliationError
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof ApplyReconciliationError
     */
    'message': string;
}
/**
 * 
 * @export
 * @interface ApplyReconciliationRequest
 */
export interface ApplyReconciliationRequest {
    /**
     * 
     * @type {Array<ApplyAddItem>}
     * @memberof ApplyReconciliationRequest
     */
    'to_add'?: Array<ApplyAddItem>;
    /**
     * 
     * @type {Array<ApplyUpdateItem>}
     * @memberof ApplyReconciliationRequest
     */
    'to_update'?: Array<ApplyUpdateItem>;
    /**
     * 
     * @type {Array<string>}
     * @memberof ApplyReconciliationRequest
     */
    'to_remove'?: Array<string>;
    /**
     * 
     * @type {PortComplex}
     * @memberof ApplyReconciliationRequest
     */
    'port_complex'?: PortComplex;
}


/**
 * 
 * @export
 * @interface ApplyReconciliationResponse
 */
export interface ApplyReconciliationResponse {
    /**
     * 
     * @type {number}
     * @memberof ApplyReconciliationResponse
     */
    'added'?: number;
    /**
     * 
     * @type {number}
     * @memberof ApplyReconciliationResponse
     */
    'updated'?: number;
    /**
     * 
     * @type {number}
     * @memberof ApplyReconciliationResponse
     */
    'removed'?: number;
    /**
     * 
     * @type {Array<ApplyReconciliationError>}
     * @memberof ApplyReconciliationResponse
     */
    'errors'?: Array<ApplyReconciliationError>;
}
/**
 * 
 * @export
 * @interface ApplyUpdateItem
 */
export interface ApplyUpdateItem {
    /**
     * 
     * @type {string}
     * @memberof ApplyUpdateItem
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ApplyUpdateItem
     */
    'new_status': ContainerStatus;
}


/**
 * 
 * @export
 * @interface AppointmentActionResponse
 */
export interface AppointmentActionResponse {
    /**
     * 
     * @type {string}
     * @memberof AppointmentActionResponse
     */
    'message': string;
    /**
     * 
     * @type {number}
     * @memberof AppointmentActionResponse
     */
    'request_id': number;
    /**
     * 
     * @type {ActionType}
     * @memberof AppointmentActionResponse
     */
    'action': ActionType;
}


/**
 * 
 * @export
 * @interface AppointmentSlot
 */
export interface AppointmentSlot {
    /**
     * The earliest time the driver can arrive for their appointment
     * @type {string}
     * @memberof AppointmentSlot
     */
    'window_start': string;
    /**
     * The latest time the driver can arrive for their appointment
     * @type {string}
     * @memberof AppointmentSlot
     */
    'window_end'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof AppointmentSlot
     */
    'terminal': TerminalName;
    /**
     * If `false` then this is just a placeholder to indicate noappointments are available
     * @type {boolean}
     * @memberof AppointmentSlot
     */
    'has_availability'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof AppointmentSlot
     */
    'num_appointments_available'?: number;
    /**
     * The best unique identifier for the appointment slot time
     * @type {string}
     * @memberof AppointmentSlot
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof AppointmentSlot
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof AppointmentSlot
     */
    'observed': string;
    /**
     * 
     * @type {string}
     * @memberof AppointmentSlot
     */
    'terminal_block'?: string;
    /**
     * 
     * @type {string}
     * @memberof AppointmentSlot
     */
    'cleaned_terminal_block_from_import_status'?: string;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof AppointmentSlot
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof AppointmentSlot
     */
    'organization_id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof AppointmentSlot
     */
    'org_specific'?: boolean;
    /**
     * 
     * @type {SlotTrigger}
     * @memberof AppointmentSlot
     */
    'triggered_by'?: SlotTrigger;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const AppointmentStatus = {
    SchedulingError: 'scheduling_error',
    Scheduled: 'scheduled',
    Cancelled: 'cancelled',
    InProgress: 'in_progress',
    Missed: 'missed',
    Aborted: 'aborted',
    Completed: 'completed'
} as const;

export type AppointmentStatus = typeof AppointmentStatus[keyof typeof AppointmentStatus];


/**
 * \"Checked\" means checked and not observed changed.
 * @export
 * @interface AppointmentsCheckedNotification
 */
export interface AppointmentsCheckedNotification {
    /**
     * Keys are appointments.id, values are new last observed/checked time
     * @type {{ [key: string]: string; }}
     * @memberof AppointmentsCheckedNotification
     */
    'last_checked_by_appointment_ids': { [key: string]: string; };
}
/**
 * 
 * @export
 * @interface AutoBookEmptyRequestPreset
 */
export interface AutoBookEmptyRequestPreset {
    /**
     * 
     * @type {AutoBookEmptySettings}
     * @memberof AutoBookEmptyRequestPreset
     */
    'settings': AutoBookEmptySettings;
    /**
     * 
     * @type {string}
     * @memberof AutoBookEmptyRequestPreset
     */
    'name': string;
}
/**
 * Per-container preferences for auto-booking an empty return.  Deliberately not inherited from the import settings: `holds_to_block_booking` (holds come from an import status, which an empty return does not have) and `master_bill_of_lading`.
 * @export
 * @interface AutoBookEmptySettings
 */
export interface AutoBookEmptySettings {
    /**
     * 
     * @type {Array<DayPreference>}
     * @memberof AutoBookEmptySettings
     */
    'custom_day_preferences'?: Array<DayPreference>;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof AutoBookEmptySettings
     */
    'after_custom_days_preference'?: PreferenceOption;
    /**
     * 
     * @type {Array<DayOfWeekPreference>}
     * @memberof AutoBookEmptySettings
     */
    'custom_day_of_week_preferences'?: Array<DayOfWeekPreference>;
    /**
     * 
     * @type {Array<TimePreference>}
     * @memberof AutoBookEmptySettings
     */
    'time_preferences': Array<TimePreference>;
    /**
     * 
     * @type {string}
     * @memberof AutoBookEmptySettings
     */
    'timezone': string;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'allow_saturdays'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'allow_sundays'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'allow_friday_second_shift'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'allow_last_minute_mondays'?: boolean;
    /**
     * Required if terminal requires license plate
     * @type {string}
     * @memberof AutoBookEmptySettings
     */
    'license_plate'?: string;
    /**
     * When `license_plate` is empty, controls AutoBook fallback behavior. True (default) picks a random plate from recent plate history. False books with no plate at terminals where a plate is optional; at terminals that require a plate, AutoBook still falls back to a random plate for that booking attempt (the saved setting is preserved).
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'set_random_license_plate'?: boolean;
    /**
     * Appointment slots that are less than this number of hours in the future will be considered
     * @type {number}
     * @memberof AutoBookEmptySettings
     */
    'min_advance_notice_hours': number;
    /**
     * 
     * @type {EmptyDayPreferenceType}
     * @memberof AutoBookEmptySettings
     */
    'day_preference': EmptyDayPreferenceType;
    /**
     * Terminals this container may be returned to. None means any terminal that publishes a matching empty slot.
     * @type {Array<TerminalName>}
     * @memberof AutoBookEmptySettings
     */
    'terminals'?: Array<TerminalName>;
    /**
     * Book only while the container is in one of these states. IMPORT_EMPTY alone waits until the container is confirmed empty; adding IMPORT_PICKED_UP books as soon as it is out-gated.
     * @type {Array<ContainerWatchState>}
     * @memberof AutoBookEmptySettings
     */
    'eligible_container_states': Array<ContainerWatchState>;
    /**
     * Required at terminals that ask for a chassis on an empty
     * @type {string}
     * @memberof AutoBookEmptySettings
     */
    'chassis_number'?: string;
    /**
     * Whether the driver brings their own chassis, at terminals that offer the choice
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'own_chassis'?: boolean;
    /**
     * Keep looking after an empty return is booked, and move it when a better slot appears
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'reschedule_existing_appointment'?: boolean;
    /**
     * Book a slot even when the terminal has not yet published a return rule for its date. Does not override an explicit DUALS_ONLY or CLOSED rule for that date.
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'allow_booking_when_no_rules_apply'?: boolean;
    /**
     * If true, this container will disregard any capacity management settings
     * @type {boolean}
     * @memberof AutoBookEmptySettings
     */
    'ignore_capacity_settings'?: boolean;
}


/**
 * 
 * @export
 * @interface AutoBookRequestPreset
 */
export interface AutoBookRequestPreset {
    /**
     * 
     * @type {AutoBookingSettings}
     * @memberof AutoBookRequestPreset
     */
    'settings': AutoBookingSettings;
    /**
     * 
     * @type {string}
     * @memberof AutoBookRequestPreset
     */
    'name': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const AutoBookRequestStatus = {
    Requested: 'requested',
    InProgress: 'in_progress',
    Failed: 'failed',
    Complete: 'complete',
    Cancelled: 'cancelled'
} as const;

export type AutoBookRequestStatus = typeof AutoBookRequestStatus[keyof typeof AutoBookRequestStatus];


/**
 * 
 * @export
 * @interface AutoBookingSettings
 */
export interface AutoBookingSettings {
    /**
     * 
     * @type {Array<DayPreference>}
     * @memberof AutoBookingSettings
     */
    'custom_day_preferences'?: Array<DayPreference>;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof AutoBookingSettings
     */
    'after_custom_days_preference'?: PreferenceOption;
    /**
     * 
     * @type {Array<DayOfWeekPreference>}
     * @memberof AutoBookingSettings
     */
    'custom_day_of_week_preferences'?: Array<DayOfWeekPreference>;
    /**
     * 
     * @type {Array<TimePreference>}
     * @memberof AutoBookingSettings
     */
    'time_preferences': Array<TimePreference>;
    /**
     * 
     * @type {string}
     * @memberof AutoBookingSettings
     */
    'timezone': string;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'allow_saturdays'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'allow_sundays'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'allow_friday_second_shift'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'allow_last_minute_mondays'?: boolean;
    /**
     * Required if terminal requires license plate
     * @type {string}
     * @memberof AutoBookingSettings
     */
    'license_plate'?: string;
    /**
     * When `license_plate` is empty, controls AutoBook fallback behavior. True (default) picks a random plate from recent plate history. False books with no plate at terminals where a plate is optional; at terminals that require a plate, AutoBook still falls back to a random plate for that booking attempt (the saved setting is preserved).
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'set_random_license_plate'?: boolean;
    /**
     * Appointment slots that are less than this number of hours in the future will be considered
     * @type {number}
     * @memberof AutoBookingSettings
     */
    'min_advance_notice_hours': number;
    /**
     * 
     * @type {DayPreferenceType}
     * @memberof AutoBookingSettings
     */
    'day_preference': DayPreferenceType;
    /**
     * 
     * @type {Array<RelativeToLFDPreference>}
     * @memberof AutoBookingSettings
     */
    'relative_to_lfd_preferences'?: Array<RelativeToLFDPreference>;
    /**
     * 
     * @type {Array<HoldType>}
     * @memberof AutoBookingSettings
     */
    'holds_to_block_booking'?: Array<HoldType>;
    /**
     * Required if terminal requires Master BL \"PIN\"
     * @type {string}
     * @memberof AutoBookingSettings
     */
    'master_bill_of_lading'?: string;
    /**
     * If true, this container will disregard any capacity management settings
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'ignore_capacity_settings'?: boolean;
    /**
     * Allows users to specify chassis designation if implemented by the terminal
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AutoBookingSettings
     */
    'reschedule_existing_appointment'?: boolean;
}


/**
 * 
 * @export
 * @interface BodyCancelAppointmentsAppointmentsBookingDelete
 */
export interface BodyCancelAppointmentsAppointmentsBookingDelete {
    /**
     * 
     * @type {Array<number>}
     * @memberof BodyCancelAppointmentsAppointmentsBookingDelete
     */
    'appointment_ids': Array<number>;
}
/**
 * 
 * @export
 * @interface BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost
 */
export interface BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyCancelAutoBooksAppointmentsBookingAutoCancelPost
 */
export interface BodyCancelAutoBooksAppointmentsBookingAutoCancelPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyCancelAutoBooksAppointmentsBookingAutoCancelPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
 */
export interface BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete {
    /**
     * 
     * @type {number}
     * @memberof BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
     */
    'customer_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
     */
    'container_tag'?: string;
    /**
     * 
     * @type {PortComplex}
     * @memberof BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
     */
    'port_complex'?: PortComplex;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
     */
    'direction'?: TransactionDirection;
    /**
     * 
     * @type {boolean}
     * @memberof BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete
     */
    'loaded'?: boolean;
}


/**
 * 
 * @export
 * @interface BodyDeleteCustomersCustomersDelete
 */
export interface BodyDeleteCustomersCustomersDelete {
    /**
     * 
     * @type {Array<number>}
     * @memberof BodyDeleteCustomersCustomersDelete
     */
    'ids': Array<number>;
}
/**
 * 
 * @export
 * @interface BodyGetTokenUsersTokenPost
 */
export interface BodyGetTokenUsersTokenPost {
    /**
     * 
     * @type {string}
     * @memberof BodyGetTokenUsersTokenPost
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof BodyGetTokenUsersTokenPost
     */
    'password': string;
    /**
     * 
     * @type {number}
     * @memberof BodyGetTokenUsersTokenPost
     */
    'organization_id'?: number;
}
/**
 * 
 * @export
 * @interface BodyGetTokenViaTokenUsersTokenViaTokenPost
 */
export interface BodyGetTokenViaTokenUsersTokenViaTokenPost {
    /**
     * 
     * @type {number}
     * @memberof BodyGetTokenViaTokenUsersTokenViaTokenPost
     */
    'organization_id': number;
}
/**
 * 
 * @export
 * @interface BodyGetUserSetupInfoUsersSetupInfoPost
 */
export interface BodyGetUserSetupInfoUsersSetupInfoPost {
    /**
     * 
     * @type {string}
     * @memberof BodyGetUserSetupInfoUsersSetupInfoPost
     */
    'token': string;
}
/**
 * 
 * @export
 * @interface BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost
 */
export interface BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost
 */
export interface BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost
 */
export interface BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost
 */
export interface BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost {
    /**
     * 
     * @type {Array<string>}
     * @memberof BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface BodyMergeCustomersCustomersMergePost
 */
export interface BodyMergeCustomersCustomersMergePost {
    /**
     * 
     * @type {Array<SavedCustomer>}
     * @memberof BodyMergeCustomersCustomersMergePost
     */
    'customers': Array<SavedCustomer>;
    /**
     * 
     * @type {string}
     * @memberof BodyMergeCustomersCustomersMergePost
     */
    'new_name': string;
}
/**
 * 
 * @export
 * @interface BodyResendUserSignupEmailUsersResendSignupEmailPost
 */
export interface BodyResendUserSignupEmailUsersResendSignupEmailPost {
    /**
     * 
     * @type {string}
     * @memberof BodyResendUserSignupEmailUsersResendSignupEmailPost
     */
    'email': string;
}
/**
 * 
 * @export
 * @interface BodyResetPasswordUsersResetPasswordPost
 */
export interface BodyResetPasswordUsersResetPasswordPost {
    /**
     * 
     * @type {string}
     * @memberof BodyResetPasswordUsersResetPasswordPost
     */
    'token': string;
    /**
     * 
     * @type {string}
     * @memberof BodyResetPasswordUsersResetPasswordPost
     */
    'password': string;
}
/**
 * 
 * @export
 * @interface BodySendResetPasswordEmailUsersSendResetPasswordEmailPost
 */
export interface BodySendResetPasswordEmailUsersSendResetPasswordEmailPost {
    /**
     * 
     * @type {string}
     * @memberof BodySendResetPasswordEmailUsersSendResetPasswordEmailPost
     */
    'email': string;
}
/**
 * 
 * @export
 * @interface BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch
 */
export interface BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch {
    /**
     * 
     * @type {string}
     * @memberof BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch
     */
    'last_name': string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch
     */
    'role'?: UserRoleType;
}


/**
 * 
 * @export
 * @interface BookingRequest
 */
export interface BookingRequest {
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {ActionStatus}
     * @memberof BookingRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof BookingRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof BookingRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof BookingRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof BookingRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof BookingRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'action_type'?: BookingRequestActionTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'auto_book_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof BookingRequest
     */
    'appointment_id'?: number;
    /**
     * Set when we are booking based on a shared availability block
     * @type {string}
     * @memberof BookingRequest
     */
    'terminal_block'?: string;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'truck_license_plate': string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof BookingRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'master_bill_of_lading': string;
    /**
     * 
     * @type {string}
     * @memberof BookingRequest
     */
    'updated': string;
    /**
     * 
     * @type {AppointmentSlot}
     * @memberof BookingRequest
     */
    'appointment_slot'?: AppointmentSlot;
    /**
     * 
     * @type {boolean}
     * @memberof BookingRequest
     */
    'own_chassis'?: boolean;
}

export const BookingRequestActionTypeEnum = {
    Book: 'book'
} as const;

export type BookingRequestActionTypeEnum = typeof BookingRequestActionTypeEnum[keyof typeof BookingRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface BookingVerificationNotification
 */
export interface BookingVerificationNotification {
    /**
     * 
     * @type {ActionRequest}
     * @memberof BookingVerificationNotification
     */
    'action_request': ActionRequest;
    /**
     * 
     * @type {number}
     * @memberof BookingVerificationNotification
     */
    'appointment_id'?: number;
    /**
     * 
     * @type {Array<string>}
     * @memberof BookingVerificationNotification
     */
    'appointment_issues'?: Array<string>;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const BotActionName = {
    Login: 'login',
    RefreshSession: 'refresh_session',
    BoostReputation: 'boost_reputation',
    ListAppointments: 'list_appointments',
    LookupAppointments: 'lookup_appointments',
    ParseAppointments: 'parse_appointments',
    ParseBookingResponse: 'parse_booking_response',
    LookupAppointmentAvailabilityForImports: 'lookup_appointment_availability_for_imports',
    LookupAppointmentAvailabilityForEmpty: 'lookup_appointment_availability_for_empty',
    LookupEmptyLineTypeOptions: 'lookup_empty_line_type_options',
    BookAppointment: 'book_appointment',
    CancelAppointment: 'cancel_appointment',
    RescheduleAppointment: 'reschedule_appointment',
    RescheduleEmptyAppointment: 'reschedule_empty_appointment',
    EditAppointmentAddEmpty: 'edit_appointment_add_empty',
    EditAppointmentSwapEmpty: 'edit_appointment_swap_empty',
    EditAppointmentUpdateLicensePlate: 'edit_appointment_update_license_plate',
    EditAppointmentUpdateChassis: 'edit_appointment_update_chassis',
    BookEmptyAppointment: 'book_empty_appointment',
    CheckInAppointment: 'check_in_appointment',
    LookupImportStatuses: 'lookup_import_statuses',
    ParseImportStatuses: 'parse_import_statuses',
    LookupExportBookingStatuses: 'lookup_export_booking_statuses',
    ParseExportBookingStatuses: 'parse_export_booking_statuses',
    LookupExportEmptyPickupSlots: 'lookup_export_empty_pickup_slots',
    BookExportEmptyPickup: 'book_export_empty_pickup',
    ListGateTransactions: 'list_gate_transactions',
    LookupGateTransactions: 'lookup_gate_transactions',
    ParseGateTransactions: 'parse_gate_transactions',
    LoadEmptyReturnRules: 'load_empty_return_rules',
    ParseEmptyReturnRules: 'parse_empty_return_rules',
    LoadGateSchedule: 'load_gate_schedule',
    ParseGateSchedule: 'parse_gate_schedule',
    LookupVesselEtas: 'lookup_vessel_etas',
    LoadVesselSchedule: 'load_vessel_schedule',
    ParseVesselSchedule: 'parse_vessel_schedule',
    LoadTrucks: 'load_trucks',
    DrainUpdates: 'drain_updates',
    DrainServiceOrderUpdates: 'drain_service_order_updates',
    ParseServiceOrderUpdates: 'parse_service_order_updates',
    RegisterTracking: 'register_tracking',
    DeleteMessage: 'delete_message',
    ApmCancelDualEmptyPlaceholder: 'apm_cancel_dual_empty_placeholder'
} as const;

export type BotActionName = typeof BotActionName[keyof typeof BotActionName];


/**
 * 
 * @export
 * @interface BulkBookEmptyPickupResponse
 */
export interface BulkBookEmptyPickupResponse {
    /**
     * 
     * @type {string}
     * @memberof BulkBookEmptyPickupResponse
     */
    'message': string;
    /**
     * 
     * @type {Array<StartedEmptyPickupBooking>}
     * @memberof BulkBookEmptyPickupResponse
     */
    'started_requests': Array<StartedEmptyPickupBooking>;
    /**
     * 
     * @type {Array<FailedEmptyPickupBooking>}
     * @memberof BulkBookEmptyPickupResponse
     */
    'failed_to_start': Array<FailedEmptyPickupBooking>;
}
/**
 * Response for bulk empty appointment booking
 * @export
 * @interface BulkBookEmptyResponse
 */
export interface BulkBookEmptyResponse {
    /**
     * 
     * @type {string}
     * @memberof BulkBookEmptyResponse
     */
    'message': string;
    /**
     * List of successfully started empty booking requests
     * @type {Array<StartedEmptyBookingRequest>}
     * @memberof BulkBookEmptyResponse
     */
    'started_requests': Array<StartedEmptyBookingRequest>;
    /**
     * List of empty booking requests that failed to start
     * @type {Array<FailedEmptyBookingRequest>}
     * @memberof BulkBookEmptyResponse
     */
    'failed_to_start': Array<FailedEmptyBookingRequest>;
}
/**
 * 
 * @export
 * @interface CameraImageUpdatedEvent
 */
export interface CameraImageUpdatedEvent {
    /**
     * 
     * @type {string}
     * @memberof CameraImageUpdatedEvent
     */
    'event_name'?: CameraImageUpdatedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof CameraImageUpdatedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof CameraImageUpdatedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {GETCameraImage}
     * @memberof CameraImageUpdatedEvent
     */
    'image': GETCameraImage;
}

export const CameraImageUpdatedEventEventNameEnum = {
    CameraImageUpdated: 'camera_image_updated'
} as const;

export type CameraImageUpdatedEventEventNameEnum = typeof CameraImageUpdatedEventEventNameEnum[keyof typeof CameraImageUpdatedEventEventNameEnum];

/**
 * Response for cancel appointments
 * @export
 * @interface CancelAppointmentsResponse
 */
export interface CancelAppointmentsResponse {
    /**
     * 
     * @type {string}
     * @memberof CancelAppointmentsResponse
     */
    'message': string;
    /**
     * List of successfully started cancellation request. If a request cancellation fails to start there will be no request in this list
     * @type {Array<ActionRequestIDWithAppointmentID>}
     * @memberof CancelAppointmentsResponse
     */
    'started_requests': Array<ActionRequestIDWithAppointmentID>;
    /**
     * List of cancellation requests that failed to start
     * @type {Array<FailedToStartCancellationRequests>}
     * @memberof CancelAppointmentsResponse
     */
    'failed_to_start': Array<FailedToStartCancellationRequests>;
}
/**
 * 
 * @export
 * @interface CancellationRequest
 */
export interface CancellationRequest {
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof CancellationRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof CancellationRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof CancellationRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof CancellationRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof CancellationRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof CancellationRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof CancellationRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof CancellationRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof CancellationRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof CancellationRequest
     */
    'empty_reschedule_request_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof CancellationRequest
     */
    'action_type'?: CancellationRequestActionTypeEnum;
}

export const CancellationRequestActionTypeEnum = {
    Cancel: 'cancel'
} as const;

export type CancellationRequestActionTypeEnum = typeof CancellationRequestActionTypeEnum[keyof typeof CancellationRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface CandidateConnection
 */
export interface CandidateConnection {
    /**
     * A credential that can satisfy this requirement
     * @type {ConnectionType}
     * @memberof CandidateConnection
     */
    'connection_type': ConnectionType;
    /**
     * Status of this credential alone
     * @type {CredentialStatus}
     * @memberof CandidateConnection
     */
    'status': CredentialStatus;
    /**
     * Raw validation status, or null when the org has no such credential
     * @type {ConnectionValidationStatus}
     * @memberof CandidateConnection
     */
    'validation_status': ConnectionValidationStatus;
    /**
     * Whether the current user can add or repair this credential
     * @type {boolean}
     * @memberof CandidateConnection
     */
    'is_fixable': boolean;
}


/**
 * 
 * @export
 * @interface CapacitySetRemainingCapacities
 */
export interface CapacitySetRemainingCapacities {
    [key: string]: any;

    /**
     * 
     * @type {GETCapacitySetAttributes}
     * @memberof CapacitySetRemainingCapacities
     */
    'capacity_set': GETCapacitySetAttributes;
    /**
     * 
     * @type {Array<RemainingCapacityPeriod>}
     * @memberof CapacitySetRemainingCapacities
     */
    'remaining_capacity_periods': Array<RemainingCapacityPeriod>;
}
/**
 * 
 * @export
 * @interface Change
 */
export interface Change {
    /**
     * Human readable description of what changed
     * @type {string}
     * @memberof Change
     */
    'desc': string;
    /**
     * 
     * @type {boolean}
     * @memberof Change
     */
    'from_raw_data': boolean;
}
/**
 * 
 * @export
 * @interface ChannelInfo
 */
export interface ChannelInfo {
    /**
     * 
     * @type {ChannelName}
     * @memberof ChannelInfo
     */
    'channel_name': ChannelName;
    /**
     * 
     * @type {Array<EventName>}
     * @memberof ChannelInfo
     */
    'event_names'?: Array<EventName>;
}
/**
 * 
 * @export
 * @interface ChannelName
 */
export interface ChannelName {
}
/**
 * 
 * @export
 * @enum {string}
 */

export const ChassisType = {
    Own: 'own',
    Pool: 'pool'
} as const;

export type ChassisType = typeof ChassisType[keyof typeof ChassisType];


/**
 * 
 * @export
 * @interface CheckInAppointmentRequest
 */
export interface CheckInAppointmentRequest {
    /**
     * 
     * @type {number}
     * @memberof CheckInAppointmentRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof CheckInAppointmentRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof CheckInAppointmentRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof CheckInAppointmentRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof CheckInAppointmentRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof CheckInAppointmentRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof CheckInAppointmentRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof CheckInAppointmentRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof CheckInAppointmentRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof CheckInAppointmentRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'truck_license_plate'?: string;
    /**
     * 
     * @type {string}
     * @memberof CheckInAppointmentRequest
     */
    'action_type'?: CheckInAppointmentRequestActionTypeEnum;
}

export const CheckInAppointmentRequestActionTypeEnum = {
    CheckIn: 'check_in'
} as const;

export type CheckInAppointmentRequestActionTypeEnum = typeof CheckInAppointmentRequestActionTypeEnum[keyof typeof CheckInAppointmentRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface CompcareCoreAPIConfig
 */
export interface CompcareCoreAPIConfig {
    /**
     * 
     * @type {string}
     * @memberof CompcareCoreAPIConfig
     */
    'config_type'?: CompcareCoreAPIConfigConfigTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'auto_enter_appointments_from_extension'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'auto_enter_appointments_in_tms'?: boolean;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'enter_appointments_when_added_to_tms_late'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'auto_enter_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'dont_enter_dual_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'send_pickup_number'?: boolean;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'add_new_containers_from_tms_to_dray_dog'?: boolean;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'sync_empty_containers'?: boolean;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'sync_terminated_containers'?: boolean;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: boolean;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'add_dual_note'?: boolean;
    /**
     * Unique identifier for the TMS we are connecting to
     * @type {string}
     * @memberof CompcareCoreAPIConfig
     */
    'tms_id': string;
    /**
     * If True, set the appointment time on the start of the leg. Example: set on mount-empty instead of the dismount-empty event
     * @type {boolean}
     * @memberof CompcareCoreAPIConfig
     */
    'set_empty_appointment_time_on_leg_start'?: boolean;
    /**
     * 
     * @type {{ [key: string]: string; }}
     * @memberof CompcareCoreAPIConfig
     */
    'tms_terminal_mapping'?: { [key: string]: string; };
    /**
     * AI-suggested terminal mappings. Keys are DrayDog terminals, values are lists of TMS terminal names that matched.
     * @type {{ [key: string]: any; }}
     * @memberof CompcareCoreAPIConfig
     */
    'tms_terminal_mapping_suggestions'?: { [key: string]: any; };
    /**
     * Timezone of the TMS
     * @type {string}
     * @memberof CompcareCoreAPIConfig
     */
    'timezone'?: string;
    /**
     * If provided, only loads from these divisions will be imported
     * @type {Array<string>}
     * @memberof CompcareCoreAPIConfig
     */
    'limit_to_division_ids'?: Array<string>;
}

export const CompcareCoreAPIConfigConfigTypeEnum = {
    CompcareCoreApi: 'compcare_core_api'
} as const;

export type CompcareCoreAPIConfigConfigTypeEnum = typeof CompcareCoreAPIConfigConfigTypeEnum[keyof typeof CompcareCoreAPIConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface Config
 */
export interface Config {
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'config_type'?: ConfigConfigTypeEnum;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'user': any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'password': any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'auto_enter_appointments_from_extension'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'auto_enter_appointments_in_tms'?: any;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {any}
     * @memberof Config
     */
    'enter_appointments_when_added_to_tms_late'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'auto_enter_empty_appointments_in_tms'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'dont_enter_dual_empty_appointments_in_tms'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'send_pickup_number'?: any;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {any}
     * @memberof Config
     */
    'add_new_containers_from_tms_to_dray_dog'?: any;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {any}
     * @memberof Config
     */
    'sync_empty_containers'?: any;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {any}
     * @memberof Config
     */
    'sync_terminated_containers'?: any;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {any}
     * @memberof Config
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: any;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {any}
     * @memberof Config
     */
    'add_dual_note'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'client_id': any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'client_secret': any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'refresh_token'?: any;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof Config
     */
    'tms_terminal_mapping'?: { [key: string]: any; };
    /**
     * AI-suggested terminal mappings. Keys are DrayDog terminals, values are lists of TMS terminal names that matched.
     * @type {{ [key: string]: any; }}
     * @memberof Config
     */
    'tms_terminal_mapping_suggestions'?: { [key: string]: any; };
    /**
     * If provided, only loads from this branch will be imported. This is useful for customers who have multiple branches and want to import loads from a specific branch. Only use if you are confident
     * @type {any}
     * @memberof Config
     */
    'branch_name'?: any;
    /**
     * Override for the Port Pro API base URL (e.g. for customers on a self-hosted instance). Defaults to the standard hosted endpoint when unset.
     * @type {any}
     * @memberof Config
     */
    'base_url'?: any;
    /**
     * If true, send pickupFromTime/pickupToTime/returnFromTime/returnToTime as naive local datetime strings (no UTC offset) instead of offset-aware ISO8601. Some self-hosted Port Pro instances don\'t apply the offset when rendering appointment times in their UI, they display the UTC-shifted clock value verbatim instead of converting back to local time. Sending a naive string gives their (apparently non-timezone-aware) display nothing to misinterpret. Only enable for a connection confirmed to need this workaround.
     * @type {any}
     * @memberof Config
     */
    'send_naive_appointment_times'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'api_key': any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'time_zone'?: any;
    /**
     * Unique identifier for the TMS we are connecting to
     * @type {any}
     * @memberof Config
     */
    'tms_id': any;
    /**
     * If True, set the appointment time on the start of the leg. Example: set on mount-empty instead of the dismount-empty event
     * @type {any}
     * @memberof Config
     */
    'set_empty_appointment_time_on_leg_start'?: any;
    /**
     * Timezone of the TMS
     * @type {any}
     * @memberof Config
     */
    'timezone'?: any;
    /**
     * If provided, only loads from these divisions will be imported
     * @type {any}
     * @memberof Config
     */
    'limit_to_division_ids'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'host'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'port'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'adjust_over_midnight_to_previous_day'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'adjust_over_midnight_to_previous_day_before_hour'?: any;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'load_new_containers'?: any;
    /**
     * 
     * @type {FileFormat}
     * @memberof Config
     */
    'file_format'?: FileFormat;
    /**
     * 
     * @type {any}
     * @memberof Config
     */
    'database'?: any;
}

export const ConfigConfigTypeEnum = {
    Null: 'null'
} as const;

export type ConfigConfigTypeEnum = typeof ConfigConfigTypeEnum[keyof typeof ConfigConfigTypeEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const ConnectionCategory = {
    Gps: 'gps',
    TerminalPortal: 'terminal_portal',
    AppointmentSystem: 'appointment_system',
    ShippingLinePortal: 'shipping_line_portal',
    Tms: 'tms',
    Other: 'other'
} as const;

export type ConnectionCategory = typeof ConnectionCategory[keyof typeof ConnectionCategory];


/**
 * 
 * @export
 * @enum {string}
 */

export const ConnectionType = {
    Geotab: 'geotab',
    VoyageControls: 'voyage_controls',
    FmsCommunityPortal: 'fms_community_portal',
    FmsApi: 'fms_api',
    YtiGateLink: 'yti_gate_link',
    YtiVoyageControls: 'yti_voyage_controls',
    YtiLynx: 'yti_lynx',
    Emodal: 'emodal',
    LbctApi: 'lbct_api',
    LbctTrucker: 'lbct_trucker',
    Lbct: 'lbct',
    Tti: 'tti',
    TtiNoLogin: 'tti_no_login',
    Wbct: 'wbct',
    WbctApi: 'wbct_api',
    ApmTermpoint: 'apm_termpoint',
    ApmTermpointApi: 'apm_termpoint_api',
    Apm: 'apm',
    ApmTops: 'apm_tops',
    ApmEir: 'apm_eir',
    Trapac: 'trapac',
    TrapacApi: 'trapac_api',
    PierA: 'pier_a',
    Pct: 'pct',
    Its: 'its',
    ItsApi: 'its_api',
    ShippersExpressTideworks: 'shippers_express_tideworks',
    ShippersLongbeachTideworks: 'shippers_longbeach_tideworks',
    PierPass: 'pier_pass',
    PortOptimizer: 'port_optimizer',
    PortOptimizerApi: 'port_optimizer_api',
    One: 'one',
    Oocl: 'oocl',
    Cosco: 'cosco',
    Hyundai: 'hyundai',
    PortProApi: 'port_pro_api',
    BlackflyConnect: 'blackfly_connect',
    ProfitToolsCoreApi: 'profit_tools_core_api',
    CompcareCoreApi: 'compcare_core_api',
    Everport: 'everport',
    TriniumSftp: 'trinium_sftp',
    TranDataSftp: 'tran_data_sftp',
    EmodalAsb: 'emodal_asb',
    OictTideworks: 'oict_tideworks'
} as const;

export type ConnectionType = typeof ConnectionType[keyof typeof ConnectionType];


/**
 * 
 * @export
 * @enum {string}
 */

export const ConnectionValidationStatus = {
    Unvalidated: 'unvalidated',
    Validated: 'validated',
    Invalid: 'invalid',
    Disabled: 'disabled',
    Deleted: 'deleted',
    NotUsed: 'not_used'
} as const;

export type ConnectionValidationStatus = typeof ConnectionValidationStatus[keyof typeof ConnectionValidationStatus];


/**
 * 
 * @export
 * @enum {string}
 */

export const ContainerEventType = {
    AppointmentCreated: 'appointment_created',
    AppointmentRequested: 'appointment_requested',
    EmptyAppointmentRequested: 'empty_appointment_requested',
    AppointmentCancellationRequested: 'appointment_cancellation_requested',
    AppointmentRescheduleRequested: 'appointment_reschedule_requested',
    EmptyAppointmentRescheduleRequested: 'empty_appointment_reschedule_requested',
    AppointmentAddEmptyRequested: 'appointment_add_empty_requested',
    AppointmentSwapEmptyRequested: 'appointment_swap_empty_requested',
    AppointmentEditLicensePlateRequested: 'appointment_edit_license_plate_requested',
    AppointmentEditChassisRequested: 'appointment_edit_chassis_requested',
    AppointmentCheckInRequested: 'appointment_check_in_requested',
    AppointmentChanged: 'appointment_changed',
    AppointmentChangedViaBrowserExtension: 'appointment_changed_via_browser_extension',
    ImportStatusFound: 'import_status_found',
    ImportStatusChanged: 'import_status_changed',
    InGated: 'in_gated',
    OutGated: 'out_gated',
    AutoBookTurnedOn: 'auto_book_turned_on',
    AutoBookCancelled: 'auto_book_cancelled',
    AutoBookUpdated: 'auto_book_updated',
    EmptyAutoBookTurnedOn: 'empty_auto_book_turned_on',
    EmptyAutoBookCancelled: 'empty_auto_book_cancelled',
    EmptyAutoBookUpdated: 'empty_auto_book_updated',
    TerminalChanged: 'terminal_changed',
    Grounded: 'grounded',
    BecameAvailable: 'became_available',
    BecameUnavailable: 'became_unavailable',
    MarkedEmpty: 'marked_empty',
    MarkedNotEmpty: 'marked_not_empty',
    MarkedTerminated: 'marked_terminated',
    ContainerTagged: 'container_tagged',
    ContainerUntagged: 'container_untagged',
    ShippingLineSet: 'shipping_line_set',
    ContainerTypeSet: 'container_type_set',
    ContainerStateChanged: 'container_state_changed',
    ContainerArchived: 'container_archived',
    WatchCreated: 'watch_created',
    PortComplexChanged: 'port_complex_changed',
    VesselEtaChanged: 'vessel_eta_changed',
    NoteAdded: 'note_added'
} as const;

export type ContainerEventType = typeof ContainerEventType[keyof typeof ContainerEventType];


/**
 * 
 * @export
 * @enum {string}
 */

export const ContainerNotBookableReason = {
    HasHolds: 'has_holds',
    LastHourBookingRateLimited: 'last_hour_booking_rate_limited',
    LastDayContainerNotAvailableRejectionsExceeded: 'last_day_container_not_available_rejections_exceeded',
    LastDaySlotNotAvailableRejectionsExceeded: 'last_day_slot_not_available_rejections_exceeded',
    NoRelevantImportStatus: 'no_relevant_import_status',
    NoRelevantAppointmentForReschedule: 'no_relevant_appointment_for_reschedule',
    ContainerAlreadyInBookedState: 'container_already_in_booked_state',
    ExistingAppointmentWithinMinAdvanceNoticeHours: 'existing_appointment_within_min_advance_notice_hours',
    ExistingAppointmentIsNotAnImportAppointment: 'existing_appointment_is_not_an_import_appointment',
    NoLicensePlatesAvailable: 'no_license_plates_available',
    SettingsChangedSinceEvaluation: 'settings_changed_since_evaluation'
} as const;

export type ContainerNotBookableReason = typeof ContainerNotBookableReason[keyof typeof ContainerNotBookableReason];


/**
 * 
 * @export
 * @enum {string}
 */

export const ContainerStatus = {
    Empty: 'empty',
    Loaded: 'loaded',
    Unspecified: 'unspecified'
} as const;

export type ContainerStatus = typeof ContainerStatus[keyof typeof ContainerStatus];


/**
 * 
 * @export
 * @enum {string}
 */

export const ContainerType = {
    _53Hc: '53 HC',
    _45Hc: '45 HC',
    _45HcReefer: '45 HC Reefer',
    _45Ot: '45 OT',
    _45HcFlatRack: '45 HC Flat Rack',
    _40FlatRack: '40 Flat Rack',
    _40HcFlatRack: '40 HC Flat Rack',
    _40Hc: '40 HC',
    _40HcOt: '40 HC OT',
    _40Ot: '40 OT',
    _40HcReefer: '40 HC Reefer',
    _40SdReefer: '40 SD Reefer',
    _20HcReefer: '20 HC Reefer',
    _40Sd: '40 SD',
    _20FlatRack: '20 Flat Rack',
    _20Sd: '20 SD',
    _20Hc: '20 HC',
    _20Ot: '20 OT',
    _20Tank: '20 Tank',
    _40Tank: '40 Tank',
    _40Hanger: '40 Hanger',
    _20Hanger: '20 Hanger'
} as const;

export type ContainerType = typeof ContainerType[keyof typeof ContainerType];


/**
 * 
 * @export
 * @interface ContainerUploadError
 */
export interface ContainerUploadError {
    /**
     * 
     * @type {string}
     * @memberof ContainerUploadError
     */
    'message': string;
    /**
     * 
     * @type {string}
     * @memberof ContainerUploadError
     */
    'container_number': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const ContainerWatchState = {
    Unknown: 'unknown',
    ImportTrackingOnVessel: 'import_tracking_on_vessel',
    ImportTracking: 'import_tracking',
    ImportApptBooking: 'import_appt_booking',
    ImportApptBooked: 'import_appt_booked',
    ImportPickedUp: 'import_picked_up',
    ImportEmpty: 'import_empty',
    ImportEmptyApptBooked: 'import_empty_appt_booked',
    ImportEmptyReturned: 'import_empty_returned',
    ExportApptBooked: 'export_appt_booked',
    ExportEmptyApptBooked: 'export_empty_appt_booked',
    ExportDroppedOff: 'export_dropped_off',
    ExportEmptyPickedUp: 'export_empty_picked_up'
} as const;

export type ContainerWatchState = typeof ContainerWatchState[keyof typeof ContainerWatchState];


/**
 * 
 * @export
 * @interface CredentialRequirement
 */
export interface CredentialRequirement {
    /**
     * Rolled-up status; prompt only when failing or not_set_up
     * @type {CredentialStatus}
     * @memberof CredentialRequirement
     */
    'status': CredentialStatus;
    /**
     * Credentials that satisfy this requirement, in preference order
     * @type {Array<CandidateConnection>}
     * @memberof CredentialRequirement
     */
    'candidates': Array<CandidateConnection>;
    /**
     * Credential the prompt\'s call to action should open, if any
     * @type {ConnectionType}
     * @memberof CredentialRequirement
     */
    'connection_type_to_fix': ConnectionType;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const CredentialStatus = {
    Connected: 'connected',
    Failing: 'failing',
    Paused: 'paused',
    NotUsed: 'not_used',
    NotSetUp: 'not_set_up',
    NotRequired: 'not_required'
} as const;

export type CredentialStatus = typeof CredentialStatus[keyof typeof CredentialStatus];


/**
 * 
 * @export
 * @interface Customer
 */
export interface Customer {
    /**
     * 
     * @type {string}
     * @memberof Customer
     */
    'name': string;
}
/**
 * 
 * @export
 * @interface DELETECurrentLocation
 */
export interface DELETECurrentLocation {
    /**
     * 
     * @type {Array<string>}
     * @memberof DELETECurrentLocation
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const DayOfWeek = {
    Monday: 'Monday',
    Tuesday: 'Tuesday',
    Wednesday: 'Wednesday',
    Thursday: 'Thursday',
    Friday: 'Friday',
    Saturday: 'Saturday',
    Sunday: 'Sunday'
} as const;

export type DayOfWeek = typeof DayOfWeek[keyof typeof DayOfWeek];


/**
 * 
 * @export
 * @interface DayOfWeekPreference
 */
export interface DayOfWeekPreference {
    /**
     * 
     * @type {DayOfWeek}
     * @memberof DayOfWeekPreference
     */
    'day_of_week': DayOfWeek;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof DayOfWeekPreference
     */
    'preference': PreferenceOption;
}


/**
 * 
 * @export
 * @interface DayPreference
 */
export interface DayPreference {
    /**
     * 
     * @type {string}
     * @memberof DayPreference
     */
    'day': string;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof DayPreference
     */
    'preference': PreferenceOption;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const DayPreferenceType = {
    Earliest: 'earliest',
    Anytime: 'anytime',
    Custom: 'custom',
    CustomByDayOfWeek: 'custom_by_day_of_week',
    RelativeToLfd: 'relative_to_lfd'
} as const;

export type DayPreferenceType = typeof DayPreferenceType[keyof typeof DayPreferenceType];


/**
 * 
 * @export
 * @interface EditChassisRequest
 */
export interface EditChassisRequest {
    /**
     * 
     * @type {number}
     * @memberof EditChassisRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof EditChassisRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EditChassisRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof EditChassisRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof EditChassisRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof EditChassisRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof EditChassisRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof EditChassisRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof EditChassisRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EditChassisRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof EditChassisRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof EditChassisRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof EditChassisRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof EditChassisRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {boolean}
     * @memberof EditChassisRequest
     */
    'own_chassis': boolean;
    /**
     * 
     * @type {string}
     * @memberof EditChassisRequest
     */
    'action_type'?: EditChassisRequestActionTypeEnum;
}

export const EditChassisRequestActionTypeEnum = {
    EditChassis: 'edit_chassis'
} as const;

export type EditChassisRequestActionTypeEnum = typeof EditChassisRequestActionTypeEnum[keyof typeof EditChassisRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface EditLicensePlateEvent
 */
export interface EditLicensePlateEvent {
    /**
     * 
     * @type {SimpleUser}
     * @memberof EditLicensePlateEvent
     */
    'requested_by'?: SimpleUser;
    /**
     * The id of the action request. Can be used with patch /appointments/booking/ to mark as entered in TMS.
     * @type {number}
     * @memberof EditLicensePlateEvent
     */
    'action_request_id': number;
    /**
     * The appointment found after completing the action. Will be None if the appointment was not found.
     * @type {WebhookAppointment}
     * @memberof EditLicensePlateEvent
     */
    'appointment'?: WebhookAppointment;
    /**
     * The url to send a patch request to mark the action as entered TMS. Send with entered_in_tms = True in the body.
     * @type {string}
     * @memberof EditLicensePlateEvent
     */
    'mark_entered_in_tms_url': string;
    /**
     * The container number
     * @type {string}
     * @memberof EditLicensePlateEvent
     */
    'container_number': string;
    /**
     * The terminal the appointment is at
     * @type {TerminalName}
     * @memberof EditLicensePlateEvent
     */
    'terminal': TerminalName;
    /**
     * Identifies the event type. Also available in the `event_type` field
     * @type {string}
     * @memberof EditLicensePlateEvent
     */
    'type'?: EditLicensePlateEventTypeEnum;
}

export const EditLicensePlateEventTypeEnum = {
    EditAppointmentLicensePlate: 'edit_appointment_license_plate'
} as const;

export type EditLicensePlateEventTypeEnum = typeof EditLicensePlateEventTypeEnum[keyof typeof EditLicensePlateEventTypeEnum];

/**
 * 
 * @export
 * @interface EditLicensePlateRequest
 */
export interface EditLicensePlateRequest {
    /**
     * 
     * @type {number}
     * @memberof EditLicensePlateRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof EditLicensePlateRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof EditLicensePlateRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof EditLicensePlateRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof EditLicensePlateRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof EditLicensePlateRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EditLicensePlateRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof EditLicensePlateRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof EditLicensePlateRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof EditLicensePlateRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'truck_license_plate': string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof EditLicensePlateRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof EditLicensePlateRequest
     */
    'action_type'?: EditLicensePlateRequestActionTypeEnum;
}

export const EditLicensePlateRequestActionTypeEnum = {
    EditLicensePlate: 'edit_license_plate'
} as const;

export type EditLicensePlateRequestActionTypeEnum = typeof EditLicensePlateRequestActionTypeEnum[keyof typeof EditLicensePlateRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface EmptiesReconciliationResponse
 */
export interface EmptiesReconciliationResponse {
    /**
     * 
     * @type {Array<ReconciliationAddItem>}
     * @memberof EmptiesReconciliationResponse
     */
    'to_add'?: Array<ReconciliationAddItem>;
    /**
     * 
     * @type {Array<ReconciliationUpdateItem>}
     * @memberof EmptiesReconciliationResponse
     */
    'to_update'?: Array<ReconciliationUpdateItem>;
    /**
     * 
     * @type {Array<ReconciliationNoChangeItem>}
     * @memberof EmptiesReconciliationResponse
     */
    'no_change'?: Array<ReconciliationNoChangeItem>;
    /**
     * 
     * @type {Array<ReconciliationRemoveItem>}
     * @memberof EmptiesReconciliationResponse
     */
    'to_remove'?: Array<ReconciliationRemoveItem>;
    /**
     * 
     * @type {Array<ReconciliationInvalidItem>}
     * @memberof EmptiesReconciliationResponse
     */
    'invalid'?: Array<ReconciliationInvalidItem>;
    /**
     * 
     * @type {number}
     * @memberof EmptiesReconciliationResponse
     */
    'duplicates_removed'?: number;
}
/**
 * 
 * @export
 * @interface EmptyAppointmentBookedEvent
 */
export interface EmptyAppointmentBookedEvent {
    /**
     * 
     * @type {SimpleUser}
     * @memberof EmptyAppointmentBookedEvent
     */
    'requested_by'?: SimpleUser;
    /**
     * The id of the action request. Can be used with patch /appointments/booking/ to mark as entered in TMS.
     * @type {number}
     * @memberof EmptyAppointmentBookedEvent
     */
    'action_request_id': number;
    /**
     * The appointment found after completing the action. Will be None if the appointment was not found.
     * @type {WebhookAppointment}
     * @memberof EmptyAppointmentBookedEvent
     */
    'appointment'?: WebhookAppointment;
    /**
     * The url to send a patch request to mark the action as entered TMS. Send with entered_in_tms = True in the body.
     * @type {string}
     * @memberof EmptyAppointmentBookedEvent
     */
    'mark_entered_in_tms_url': string;
    /**
     * The container number
     * @type {string}
     * @memberof EmptyAppointmentBookedEvent
     */
    'container_number': string;
    /**
     * The terminal the appointment is at
     * @type {TerminalName}
     * @memberof EmptyAppointmentBookedEvent
     */
    'terminal': TerminalName;
    /**
     * Identifies the event type. Also available in the `event_type` field
     * @type {string}
     * @memberof EmptyAppointmentBookedEvent
     */
    'type'?: EmptyAppointmentBookedEventTypeEnum;
}

export const EmptyAppointmentBookedEventTypeEnum = {
    EmptyAppointmentBooked: 'empty_appointment_booked'
} as const;

export type EmptyAppointmentBookedEventTypeEnum = typeof EmptyAppointmentBookedEventTypeEnum[keyof typeof EmptyAppointmentBookedEventTypeEnum];

/**
 * 
 * @export
 * @interface EmptyAppointmentSlotsLoadedEvent
 */
export interface EmptyAppointmentSlotsLoadedEvent {
    /**
     * 
     * @type {string}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'event_name'?: EmptyAppointmentSlotsLoadedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'channel': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'has_availability': boolean;
    /**
     * 
     * @type {Array<GETEmptyInAppointmentSlot>}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'slots': Array<GETEmptyInAppointmentSlot>;
    /**
     * 
     * @type {string}
     * @memberof EmptyAppointmentSlotsLoadedEvent
     */
    'observed': string;
}

export const EmptyAppointmentSlotsLoadedEventEventNameEnum = {
    EmptyAppointmentSlotsLoaded: 'empty_appointment_slots_loaded'
} as const;

export type EmptyAppointmentSlotsLoadedEventEventNameEnum = typeof EmptyAppointmentSlotsLoadedEventEventNameEnum[keyof typeof EmptyAppointmentSlotsLoadedEventEventNameEnum];

/**
 * 
 * @export
 * @interface EmptyBookingRequest
 */
export interface EmptyBookingRequest {
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {ActionStatus}
     * @memberof EmptyBookingRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyBookingRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof EmptyBookingRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof EmptyBookingRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof EmptyBookingRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof EmptyBookingRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof EmptyBookingRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'action_type'?: EmptyBookingRequestActionTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyBookingRequest
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'appointment_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'truck_license_plate': string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof EmptyBookingRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'chassis_number': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyBookingRequest
     */
    'updated': string;
    /**
     * 
     * @type {SavedEmptyInAppointmentSlot}
     * @memberof EmptyBookingRequest
     */
    'appointment_slot': SavedEmptyInAppointmentSlot;
    /**
     * 
     * @type {number}
     * @memberof EmptyBookingRequest
     */
    'auto_book_empty_request_id'?: number;
}

export const EmptyBookingRequestActionTypeEnum = {
    BookEmpty: 'book_empty'
} as const;

export type EmptyBookingRequestActionTypeEnum = typeof EmptyBookingRequestActionTypeEnum[keyof typeof EmptyBookingRequestActionTypeEnum];

/**
 * Why a container could not be booked at all, whatever the slots looked like.
 * @export
 * @enum {string}
 */

export const EmptyContainerNotBookableReason = {
    ContainerNotInEligibleEmptyState: 'container_not_in_eligible_empty_state',
    ContainerAlreadyInBookedState: 'container_already_in_booked_state',
    NoRelevantAppointmentForReschedule: 'no_relevant_appointment_for_reschedule',
    ExistingAppointmentWithinMinAdvanceNoticeHours: 'existing_appointment_within_min_advance_notice_hours',
    ExistingAppointmentIsNotAnEmptyAppointment: 'existing_appointment_is_not_an_empty_appointment',
    PerOrgDailyCapReached: 'per_org_daily_cap_reached',
    LastHourBookingRateLimited: 'last_hour_booking_rate_limited',
    NoLicensePlatesAvailable: 'no_license_plates_available',
    SettingsChangedSinceEvaluation: 'settings_changed_since_evaluation',
    EmptyReturnRuleClosedMidFlight: 'empty_return_rule_closed_mid_flight',
    LineNotListedAtTerminal: 'line_not_listed_at_terminal',
    OutOfCapacity: 'out_of_capacity',
    PortComplexMismatch: 'port_complex_mismatch'
} as const;

export type EmptyContainerNotBookableReason = typeof EmptyContainerNotBookableReason[keyof typeof EmptyContainerNotBookableReason];


/**
 * Empties have no last free date. Per-diem accrues from the outgate instead.
 * @export
 * @enum {string}
 */

export const EmptyDayPreferenceType = {
    Earliest: 'earliest',
    Anytime: 'anytime',
    Custom: 'custom',
    CustomByDayOfWeek: 'custom_by_day_of_week'
} as const;

export type EmptyDayPreferenceType = typeof EmptyDayPreferenceType[keyof typeof EmptyDayPreferenceType];


/**
 * 
 * @export
 * @interface EmptyPickupBookingRequest
 */
export interface EmptyPickupBookingRequest {
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {ActionStatus}
     * @memberof EmptyPickupBookingRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyPickupBookingRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'container_number'?: string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof EmptyPickupBookingRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof EmptyPickupBookingRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof EmptyPickupBookingRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'booking_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof EmptyPickupBookingRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof EmptyPickupBookingRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'action_type'?: EmptyPickupBookingRequestActionTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyPickupBookingRequest
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'appointment_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'truck_license_plate': string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof EmptyPickupBookingRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingRequest
     */
    'updated': string;
    /**
     * 
     * @type {ExportEmptyPickupAppointmentSlot}
     * @memberof EmptyPickupBookingRequest
     */
    'appointment_slot': ExportEmptyPickupAppointmentSlot;
    /**
     * 
     * @type {number}
     * @memberof EmptyPickupBookingRequest
     */
    'auto_book_request_id'?: number;
}

export const EmptyPickupBookingRequestActionTypeEnum = {
    BookEmptyPickup: 'book_empty_pickup'
} as const;

export type EmptyPickupBookingRequestActionTypeEnum = typeof EmptyPickupBookingRequestActionTypeEnum[keyof typeof EmptyPickupBookingRequestActionTypeEnum];

/**
 * Per-unit booking config for one export empty pickup. Container-less — the unit is identified only by its position in the request, and carries its own appointment window.
 * @export
 * @interface EmptyPickupBookingUnit
 */
export interface EmptyPickupBookingUnit {
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingUnit
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingUnit
     */
    'truck_license_plate'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyPickupBookingUnit
     */
    'set_random_license_plate'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof EmptyPickupBookingUnit
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyPickupBookingUnit
     */
    'own_chassis'?: boolean;
}
/**
 * 
 * @export
 * @interface EmptyRescheduleRequest
 */
export interface EmptyRescheduleRequest {
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'window_start': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof EmptyRescheduleRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyRescheduleRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof EmptyRescheduleRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof EmptyRescheduleRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof EmptyRescheduleRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof EmptyRescheduleRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof EmptyRescheduleRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'truck_license_plate'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof EmptyRescheduleRequest
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'new_appointment_id'?: number;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof EmptyRescheduleRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'booking_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'cancellation_request_id'?: number;
    /**
     * 
     * @type {SavedEmptyInAppointmentSlot}
     * @memberof EmptyRescheduleRequest
     */
    'appointment_slot'?: SavedEmptyInAppointmentSlot;
    /**
     * 
     * @type {number}
     * @memberof EmptyRescheduleRequest
     */
    'auto_book_empty_request_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof EmptyRescheduleRequest
     */
    'action_type'?: EmptyRescheduleRequestActionTypeEnum;
}

export const EmptyRescheduleRequestActionTypeEnum = {
    RescheduleEmpty: 'reschedule_empty'
} as const;

export type EmptyRescheduleRequestActionTypeEnum = typeof EmptyRescheduleRequestActionTypeEnum[keyof typeof EmptyRescheduleRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface EmptyReturnRulesCheck
 */
export interface EmptyReturnRulesCheck {
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyReturnRulesCheck
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheck
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheck
     */
    'last_observed': string;
}


/**
 * 
 * @export
 * @interface EmptyReturnRulesCheckedEvent
 */
export interface EmptyReturnRulesCheckedEvent {
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'event_name'?: EmptyReturnRulesCheckedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'check_time': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'first_observed': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyReturnRulesCheckedEvent
     */
    'terminal': TerminalName;
}

export const EmptyReturnRulesCheckedEventEventNameEnum = {
    EmptyReturnRulesChecked: 'empty_return_rules_checked'
} as const;

export type EmptyReturnRulesCheckedEventEventNameEnum = typeof EmptyReturnRulesCheckedEventEventNameEnum[keyof typeof EmptyReturnRulesCheckedEventEventNameEnum];

/**
 * \"Checked\" means checked and not observed changed.
 * @export
 * @interface EmptyReturnRulesCheckedNotification
 */
export interface EmptyReturnRulesCheckedNotification {
    /**
     * 
     * @type {Array<EmptyReturnRulesCheck>}
     * @memberof EmptyReturnRulesCheckedNotification
     */
    'checks': Array<EmptyReturnRulesCheck>;
}
/**
 * 
 * @export
 * @interface EmptyReturnRulesReadingEvent
 */
export interface EmptyReturnRulesReadingEvent {
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesReadingEvent
     */
    'event_name'?: EmptyReturnRulesReadingEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesReadingEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesReadingEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {GETEmptyReturnRulesReading}
     * @memberof EmptyReturnRulesReadingEvent
     */
    'reading': GETEmptyReturnRulesReading;
}

export const EmptyReturnRulesReadingEventEventNameEnum = {
    EmptyReturnRulesReadingUpdated: 'empty_return_rules_reading_updated'
} as const;

export type EmptyReturnRulesReadingEventEventNameEnum = typeof EmptyReturnRulesReadingEventEventNameEnum[keyof typeof EmptyReturnRulesReadingEventEventNameEnum];

/**
 * 
 * @export
 * @interface EmptyReturnRulesUpdatedEvent
 */
export interface EmptyReturnRulesUpdatedEvent {
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'event_name'?: EmptyReturnRulesUpdatedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Array<GETEmptyReturnRule>}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'rules': Array<GETEmptyReturnRule>;
    /**
     * 
     * @type {TerminalName}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'reading_first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof EmptyReturnRulesUpdatedEvent
     */
    'reading_last_parsed': string;
}

export const EmptyReturnRulesUpdatedEventEventNameEnum = {
    EmptyReturnRulesUpdated: 'empty_return_rules_updated'
} as const;

export type EmptyReturnRulesUpdatedEventEventNameEnum = typeof EmptyReturnRulesUpdatedEventEventNameEnum[keyof typeof EmptyReturnRulesUpdatedEventEventNameEnum];

/**
 * Why one slot in a reading could not be booked for a given request.
 * @export
 * @enum {string}
 */

export const EmptySlotNotBookableReason = {
    SaturdayNotAllowed: 'saturday_not_allowed',
    SundayNotAllowed: 'sunday_not_allowed',
    FridaySecondShiftNotAllowed: 'friday_second_shift_not_allowed',
    MondayFirstShiftNotAllowed: 'monday_first_shift_not_allowed',
    TimePreferenceNotAllowed: 'time_preference_not_allowed',
    WithinMinAdvancedNotice: 'within_min_advanced_notice',
    CustomDayPreferenceIsNotAllowed: 'custom_day_preference_is_not_allowed',
    DayOfWeekPreferenceNotAllowed: 'day_of_week_preference_not_allowed',
    TerminalNotInPreferences: 'terminal_not_in_preferences',
    SlotFullyConsumed: 'slot_fully_consumed',
    SlotOutsideOpenReturnRule: 'slot_outside_open_return_rule',
    LineNotListedAtTerminal: 'line_not_listed_at_terminal',
    ReturnRuleDualsOnly: 'return_rule_duals_only',
    SlotInShiftGap: 'slot_in_shift_gap',
    TerminalHasNoShiftWindows: 'terminal_has_no_shift_windows',
    MaxAutoBookAttempts: 'max_auto_book_attempts',
    OutOfCapacity: 'out_of_capacity',
    SameOrLowerPriorityThanExistingAppointment: 'same_or_lower_priority_than_existing_appointment'
} as const;

export type EmptySlotNotBookableReason = typeof EmptySlotNotBookableReason[keyof typeof EmptySlotNotBookableReason];


/**
 * Python exception class name for the error category. Known values are listed in RejectionClassNameEnum (e.g. AppointmentAlreadyBookedException). Unknown values (Python built-ins like TimeoutError or Exception) may also appear — hence the field accepts any string.
 * @export
 * @interface ErrorClassName
 */
export interface ErrorClassName {
}
/**
 * @type Event
 * Identifies the event type. In the docs, select different values for the `type` field to see different event models.
 * @export
 */
export type Event = { type: 'appointment_rescheduled' } & RescheduleEvent | { type: 'edit_appointment_license_plate' } & EditLicensePlateEvent | { type: 'empty_appointment_booked' } & EmptyAppointmentBookedEvent | { type: 'import_appointment_booked' } & ImportAppointmentBookedEvent;

/**
 * 
 * @export
 * @enum {string}
 */

export const EventName = {
    EmptyReturnRulesUpdated: 'empty_return_rules_updated',
    EmptyReturnRulesReadingUpdated: 'empty_return_rules_reading_updated',
    GateSchedulesUpdated: 'gate_schedules_updated',
    GateScheduleChecked: 'gate_schedule_checked',
    GlobalNotification: 'global_notification',
    EmptyReturnRulesChecked: 'empty_return_rules_checked',
    EmptyAppointmentSlotsLoaded: 'empty_appointment_slots_loaded',
    ExportEmptyPickupSlotsLoaded: 'export_empty_pickup_slots_loaded',
    VesselEtaUpdated: 'vessel_eta_updated',
    OrganizationNotification: 'organization_notification',
    UserNotification: 'user_notification',
    CameraImageUpdated: 'camera_image_updated',
    ImportSlotsUpdated: 'import_slots_updated'
} as const;

export type EventName = typeof EventName[keyof typeof EventName];


/**
 * 
 * @export
 * @enum {string}
 */

export const ExportBookingCheckStatus = {
    Found: 'found',
    NotFound: 'not_found',
    Invalid: 'invalid',
    Pending: 'pending',
    Failing: 'failing'
} as const;

export type ExportBookingCheckStatus = typeof ExportBookingCheckStatus[keyof typeof ExportBookingCheckStatus];


/**
 * A hold on an export booking (e.g. a gate-fee hold) reported by a terminal.
 * @export
 * @interface ExportBookingHold
 */
export interface ExportBookingHold {
    /**
     * 
     * @type {string}
     * @memberof ExportBookingHold
     */
    'reason_code': string;
    /**
     * 
     * @type {string}
     * @memberof ExportBookingHold
     */
    'reason_description'?: string;
    /**
     * e.g. HOLD, RELEASED
     * @type {string}
     * @memberof ExportBookingHold
     */
    'status': string;
    /**
     * Terminal facility code the hold applies to
     * @type {string}
     * @memberof ExportBookingHold
     */
    'facility_code'?: string;
}
/**
 * Fine-grained lifecycle of an ACTIVE export booking watch, derived server-side (DEV-8121). Separate from ExportWatchState, which is only liveness. One state per booking so per-state counts sum to the total, like ContainerWatchState.
 * @export
 * @enum {string}
 */

export const ExportBookingState = {
    Unknown: 'unknown',
    NotReceiving: 'not_receiving',
    Unavailable: 'unavailable',
    Bookable: 'bookable',
    Appointed: 'appointed',
    InGated: 'in_gated',
    Closed: 'closed'
} as const;

export type ExportBookingState = typeof ExportBookingState[keyof typeof ExportBookingState];


/**
 * 
 * @export
 * @interface ExportBookingUploadError
 */
export interface ExportBookingUploadError {
    /**
     * 
     * @type {string}
     * @memberof ExportBookingUploadError
     */
    'message': string;
    /**
     * 
     * @type {string}
     * @memberof ExportBookingUploadError
     */
    'booking_number': string;
}
/**
 * Cargo a list row carries. Same words as the Exports list filter. `general` is a line with none of the other flags.
 * @export
 * @enum {string}
 */

export const ExportCargoType = {
    General: 'general',
    Hazmat: 'hazmat',
    Reefer: 'reefer',
    OverDimensional: 'over_dimensional'
} as const;

export type ExportCargoType = typeof ExportCargoType[keyof typeof ExportCargoType];


/**
 * A watched vessel\'s ERD or cutoff moved. One per (vessel, terminal) per recipient, so `changes` carries every field that moved in that save.
 * @export
 * @interface ExportDatesChangedNotification
 */
export interface ExportDatesChangedNotification {
    /**
     * vessel_schedules.id of the changed row. The frontend builds `?selected_schedule=<id>` from it to open the vessel\'s change history
     * @type {number}
     * @memberof ExportDatesChangedNotification
     */
    'schedule_id': number;
    /**
     * 
     * @type {string}
     * @memberof ExportDatesChangedNotification
     */
    'vessel_name': string;
    /**
     * 
     * @type {number}
     * @memberof ExportDatesChangedNotification
     */
    'imo_number': number;
    /**
     * Voyage number on the schedule, when the terminal sends one
     * @type {string}
     * @memberof ExportDatesChangedNotification
     */
    'voyage'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof ExportDatesChangedNotification
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof ExportDatesChangedNotification
     */
    'changed_at': string;
    /**
     * The recipient\'s bookings sailing on this vessel and voyage. Recipients are found through their bookings (DEV-8311), so an empty list means a caller bug, not a recipient to alert
     * @type {Array<string>}
     * @memberof ExportDatesChangedNotification
     */
    'booking_numbers': Array<string>;
    /**
     * Each changed field with its old and new value, in the same shape as the vessel change-history endpoint
     * @type {Array<VesselScheduleChange>}
     * @memberof ExportDatesChangedNotification
     */
    'changes': Array<VesselScheduleChange>;
}


/**
 * A bookable window for collecting an empty against an export booking (eModal `MP`).  A sibling of `EmptyInAppointmentSlot` (the empty *return*) rather than a subclass: these must never satisfy `isinstance(slot, EmptyInAppointmentSlot)`, which the auto-book-empties scoring, checker and repos dispatch on.
 * @export
 * @interface ExportEmptyPickupAppointmentSlot
 */
export interface ExportEmptyPickupAppointmentSlot {
    /**
     * The earliest time the driver can arrive for their appointment
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'window_start': string;
    /**
     * The latest time the driver can arrive for their appointment
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'window_end'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'terminal': TerminalName;
    /**
     * If `false` then this is just a placeholder to indicate noappointments are available
     * @type {boolean}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'has_availability'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'num_appointments_available'?: number;
    /**
     * The best unique identifier for the appointment slot time
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'observed': string;
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'booking_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'container_type'?: ContainerType;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof ExportEmptyPickupAppointmentSlot
     */
    'container_number'?: string;
}


/**
 * Export empty-pickup (eModal `MP`) windows for one (terminal, line, type) pool.  Deliberately carries no `booking_number`. eModal\'s `MP` pool is gate capacity, not booking allocation, so one reading serves every booking on the line — the booking that happened to trigger the lookup is meaningless to the other subscribers, and a client that stamped it onto its own booking would later send it as `CargoRef_Num`.
 * @export
 * @interface ExportEmptyPickupSlotsLoadedEvent
 */
export interface ExportEmptyPickupSlotsLoadedEvent {
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'event_name'?: ExportEmptyPickupSlotsLoadedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'channel': string;
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {boolean}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'has_availability': boolean;
    /**
     * 
     * @type {Array<GETEmptyInAppointmentSlot>}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'slots': Array<GETEmptyInAppointmentSlot>;
    /**
     * 
     * @type {string}
     * @memberof ExportEmptyPickupSlotsLoadedEvent
     */
    'observed': string;
}

export const ExportEmptyPickupSlotsLoadedEventEventNameEnum = {
    ExportEmptyPickupSlotsLoaded: 'export_empty_pickup_slots_loaded'
} as const;

export type ExportEmptyPickupSlotsLoadedEventEventNameEnum = typeof ExportEmptyPickupSlotsLoadedEventEventNameEnum[keyof typeof ExportEmptyPickupSlotsLoadedEventEventNameEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const ExportSortOptions = {
    BookingNumber: 'booking_number',
    Created: 'created',
    Erd: 'erd',
    CargoCutoff: 'cargo_cutoff',
    Appointment: 'appointment'
} as const;

export type ExportSortOptions = typeof ExportSortOptions[keyof typeof ExportSortOptions];


/**
 * Lifecycle state of an export booking watch (booking-number-centric, mirroring ContainerWatchState for the container-centric import side).
 * @export
 * @enum {string}
 */

export const ExportWatchState = {
    Active: 'active',
    Archived: 'archived'
} as const;

export type ExportWatchState = typeof ExportWatchState[keyof typeof ExportWatchState];


/**
 * 
 * @export
 * @interface FailedEmptyBookingRequest
 */
export interface FailedEmptyBookingRequest {
    /**
     * 
     * @type {string}
     * @memberof FailedEmptyBookingRequest
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof FailedEmptyBookingRequest
     */
    'error': string;
}
/**
 * 
 * @export
 * @interface FailedEmptyPickupBooking
 */
export interface FailedEmptyPickupBooking {
    /**
     * 
     * @type {number}
     * @memberof FailedEmptyPickupBooking
     */
    'unit_index': number;
    /**
     * 
     * @type {string}
     * @memberof FailedEmptyPickupBooking
     */
    'error': string;
}
/**
 * 
 * @export
 * @interface FailedToStartCancellationRequests
 */
export interface FailedToStartCancellationRequests {
    /**
     * 
     * @type {number}
     * @memberof FailedToStartCancellationRequests
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof FailedToStartCancellationRequests
     */
    'error': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const FieldChangeValueType = {
    Date: 'date',
    Datetime: 'datetime'
} as const;

export type FieldChangeValueType = typeof FieldChangeValueType[keyof typeof FieldChangeValueType];


/**
 * 
 * @export
 * @enum {string}
 */

export const FileFormat = {
    Standard: 'standard',
    Trinium: 'trinium',
    TriniumV2: 'trinium_v_2'
} as const;

export type FileFormat = typeof FileFormat[keyof typeof FileFormat];


/**
 * 
 * @export
 * @interface FunctionArgumentsInfo
 */
export interface FunctionArgumentsInfo {
    /**
     * The arguments that must be passed to this action
     * @type {Array<string>}
     * @memberof FunctionArgumentsInfo
     */
    'required'?: Array<string>;
    /**
     * The arguments that can optionally be passed to this action
     * @type {Array<string>}
     * @memberof FunctionArgumentsInfo
     */
    'optional'?: Array<string>;
    /**
     * Arguments that should ALWAYS be None. This is useful for arguments when some implementations do not use a certain argument
     * @type {Array<string>}
     * @memberof FunctionArgumentsInfo
     */
    'none'?: Array<string>;
}
/**
 * 
 * @export
 * @interface GETActionMetadata
 */
export interface GETActionMetadata {
    /**
     * 
     * @type {BotActionName}
     * @memberof GETActionMetadata
     */
    'name': BotActionName;
    /**
     * The terminals that this action works for
     * @type {Array<TerminalName>}
     * @memberof GETActionMetadata
     */
    'terminals': Array<TerminalName>;
    /**
     * 
     * @type {FunctionArgumentsInfo}
     * @memberof GETActionMetadata
     */
    'arguments': FunctionArgumentsInfo;
    /**
     * Terminal specific overrides for this action. Keys are the terminal names as strings from the `TerminalName` enum
     * @type {{ [key: string]: FunctionArgumentsInfo; }}
     * @memberof GETActionMetadata
     */
    'terminal_specific_arguments'?: { [key: string]: FunctionArgumentsInfo; };
}


/**
 * 
 * @export
 * @interface GETActionRequest
 */
export interface GETActionRequest {
    /**
     * 
     * @type {number}
     * @memberof GETActionRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETActionRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETActionRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof GETActionRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETActionRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GETActionRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof GETActionRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETActionRequest
     */
    'created': string;
    /**
     * 
     * @type {ActionType}
     * @memberof GETActionRequest
     */
    'action_type': ActionType;
}


/**
 * 
 * @export
 * @interface GETActiveVesselSchedules
 */
export interface GETActiveVesselSchedules {
    /**
     * Whether this org has any active booking (watched, not Closed), even one that matches no schedule. Picks the board\'s default view and its empty state
     * @type {boolean}
     * @memberof GETActiveVesselSchedules
     */
    'has_active_bookings': boolean;
    /**
     * `GETVesselSchedule.id` of each schedule this org has an active booking on: same vessel (name or alias), a terminal the booking uses, and a matching voyage (or either has none). Not limited to the board\'s time window; filter the board\'s rows by these ids
     * @type {Array<number>}
     * @memberof GETActiveVesselSchedules
     */
    'vessel_schedule_ids': Array<number>;
}
/**
 * 
 * @export
 * @interface GETAppointment
 */
export interface GETAppointment {
    /**
     * Dray Dog\'s internal identifier for the appointment
     * @type {number}
     * @memberof GETAppointment
     */
    'id': number;
    /**
     * `null` for containerless appointments (export empty pickups where the terminal has not yet assigned a container); see `booking_number`
     * @type {string}
     * @memberof GETAppointment
     */
    'container_number'?: string;
    /**
     * Export booking number this appointment draws against. Set for export empty pickups; the only stable identifier while the appointment is containerless
     * @type {string}
     * @memberof GETAppointment
     */
    'booking_number'?: string;
    /**
     * The parsed container type for this appointment. Present even for containerless empty pickups before a container number is assigned
     * @type {ContainerType}
     * @memberof GETAppointment
     */
    'container_type'?: ContainerType;
    /**
     * Whether the appointment is for a loaded or empty container
     * @type {boolean}
     * @memberof GETAppointment
     */
    'loaded': boolean;
    /**
     * 
     * @type {AppointmentStatus}
     * @memberof GETAppointment
     */
    'status': AppointmentStatus;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof GETAppointment
     */
    'direction': TransactionDirection;
    /**
     * The start of the appointment window
     * @type {string}
     * @memberof GETAppointment
     */
    'window_start': string;
    /**
     * The end of the appointment window. Not all appointment systems provide this
     * @type {string}
     * @memberof GETAppointment
     */
    'window_end'?: string;
    /**
     * 
     * @type {Array<LinkedAppointment>}
     * @memberof GETAppointment
     */
    'linked_appointments': Array<LinkedAppointment>;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETAppointment
     */
    'terminal': TerminalName;
    /**
     * The terminal\'s unique identifier for the appointment. In cases where the terminal provides multiple identifiers, we may include both here and separate with an underscore (`_`)
     * @type {string}
     * @memberof GETAppointment
     */
    'terminal_reference': string;
    /**
     * The terminal\'s appointment number displayed to the end user. Can be different than the unique identifier used as `terminal_reference` 
     * @type {string}
     * @memberof GETAppointment
     */
    'display_terminal_reference'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETAppointment
     */
    'truck_license_plate_number'?: string;
    /**
     * The chassis number associated with this appointment, if provided
     * @type {string}
     * @memberof GETAppointment
     */
    'chassis_number'?: string;
    /**
     * Own-vs-pool chassis choice reported for this appointment, if captured. `None` for appointments observed before this field existed
     * @type {ChassisType}
     * @memberof GETAppointment
     */
    'chassis_type'?: ChassisType;
    /**
     * Unique identifier for the truck associated with this appointment
     * @type {number}
     * @memberof GETAppointment
     */
    'truck_id'?: number;
    /**
     * The specific terminal area this container is in, if provided with the appointment
     * @type {string}
     * @memberof GETAppointment
     */
    'terminal_block'?: string;
    /**
     * The gate code for the appointment
     * @type {string}
     * @memberof GETAppointment
     */
    'gate_code'?: string;
    /**
     * If this appointment was booked via Dray Dog, this is the user who requested the appointment. If the appointment was booked via Auto Book, this is the user who last updated the Auto Book settings for the appointment\'s container
     * @type {SimpleUser}
     * @memberof GETAppointment
     */
    'requested_by'?: SimpleUser;
    /**
     * If this appointment was rescheduled via Dray Dog, this is the user who rescheduled the appointment.
     * @type {SimpleUser}
     * @memberof GETAppointment
     */
    'rescheduled_by'?: SimpleUser;
    /**
     * If this empty appointment was rescheduled via Dray Dog, this is the user who rescheduled it.
     * @type {SimpleUser}
     * @memberof GETAppointment
     */
    'empty_rescheduled_by'?: SimpleUser;
    /**
     * Users currently assigned to this appointment
     * @type {Array<SimpleUser>}
     * @memberof GETAppointment
     */
    'assigned_users'?: Array<SimpleUser>;
    /**
     * User tags on this appointment, sorted. Same org vocabulary as container tags. Empty when none. For containerless export empty pickups (per-row tags). Containered rows may overlay container_watches.tags in export responses.
     * @type {Array<string>}
     * @memberof GETAppointment
     */
    'tags'?: Array<string>;
    /**
     * When first received *this specific version* of the appointment
     * @type {string}
     * @memberof GETAppointment
     */
    'observed': string;
    /**
     * When we last received data for this appointment
     * @type {string}
     * @memberof GETAppointment
     */
    'last_observed'?: string;
    /**
     * The time when this appointment was created as reported by the data source
     * @type {string}
     * @memberof GETAppointment
     */
    'created_at_terminal'?: string;
    /**
     * 
     * @type {PortalName}
     * @memberof GETAppointment
     */
    'source': PortalName;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof GETAppointment
     */
    'extra_data': { [key: string]: any; };
    /**
     * Raw data from the terminal for this appointment. Only populated when include_raw_data is requested
     * @type {{ [key: string]: any; }}
     * @memberof GETAppointment
     */
    'raw_data'?: { [key: string]: any; };
    /**
     * Where the current license plate came from (`user_manual`, `auto_book_user_specified`, `auto_book_random`, `auto_set_workflow`, or `unknown` for legacy/ambiguous). Use this to distinguish plates that were explicitly chosen by a user from placeholder/dummy plates assigned by the system.
     * @type {LicensePlateOrigin}
     * @memberof GETAppointment
     */
    'license_plate_origin'?: LicensePlateOrigin;
}


/**
 * 
 * @export
 * @interface GETAppointmentStatusCount
 */
export interface GETAppointmentStatusCount {
    /**
     * 
     * @type {AppointmentStatus}
     * @memberof GETAppointmentStatusCount
     */
    'status': AppointmentStatus;
    /**
     * 
     * @type {number}
     * @memberof GETAppointmentStatusCount
     */
    'count': number;
}


/**
 * 
 * @export
 * @interface GETAppointmentWithContainer
 */
export interface GETAppointmentWithContainer {
    /**
     * 
     * @type {GETAppointment}
     * @memberof GETAppointmentWithContainer
     */
    'appointment': GETAppointment;
    /**
     * 
     * @type {GETWatch}
     * @memberof GETAppointmentWithContainer
     */
    'watch': GETWatch;
    /**
     * Will always be `null` if `include_import_statuses=false`
     * @type {GETImportStatus}
     * @memberof GETAppointmentWithContainer
     */
    'import_status'?: GETImportStatus;
    /**
     * Will always be `null` if `include_gate_transactions=false`
     * @type {GateTransaction}
     * @memberof GETAppointmentWithContainer
     */
    'inbound_gate_transaction'?: GateTransaction;
    /**
     * Will always be `null` if `include_gate_transactions=false`
     * @type {GateTransaction}
     * @memberof GETAppointmentWithContainer
     */
    'outbound_gate_transaction'?: GateTransaction;
}
/**
 * 
 * @export
 * @interface GETAutoBookEmptyEvaluation
 */
export interface GETAutoBookEmptyEvaluation {
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'auto_book_empty_request_id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'evaluation_time': string;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'slots_reading_observed': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'allocation_class': string;
    /**
     * 
     * @type {Array<EmptyContainerNotBookableReason>}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'container_not_bookable_reasons': Array<EmptyContainerNotBookableReason>;
    /**
     * 
     * @type {Array<GETEmptyAutoBookSlotEvaluation>}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'slots': Array<GETEmptyAutoBookSlotEvaluation>;
    /**
     * 
     * @type {GETBookingStyleRequest}
     * @memberof GETAutoBookEmptyEvaluation
     */
    'action_request'?: GETBookingStyleRequest;
}


/**
 * 
 * @export
 * @interface GETAutoBookEmptyRequest
 */
export interface GETAutoBookEmptyRequest {
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookEmptyRequest
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyRequest
     */
    'container_number': string;
    /**
     * 
     * @type {AutoBookEmptySettings}
     * @memberof GETAutoBookEmptyRequest
     */
    'settings': AutoBookEmptySettings;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof GETAutoBookEmptyRequest
     */
    'status': AutoBookRequestStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETAutoBookEmptyRequest
     */
    'set_by_user'?: SimpleUser;
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookEmptyRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyRequest
     */
    'last_updated': string;
}


/**
 * 
 * @export
 * @interface GETAutoBookEmptyRequestMinimal
 */
export interface GETAutoBookEmptyRequestMinimal {
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'container_number': string;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'status': AutoBookRequestStatus;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'license_plate'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'set_random_license_plate'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETAutoBookEmptyRequestMinimal
     */
    'ignore_capacity_settings'?: boolean;
}


/**
 * 
 * @export
 * @interface GETAutoBookEvaluation
 */
export interface GETAutoBookEvaluation {
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEvaluation
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookEvaluation
     */
    'auto_book_request_id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEvaluation
     */
    'evaluation_time': string;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookEvaluation
     */
    'slots_reading_observed': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETAutoBookEvaluation
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {Array<ContainerNotBookableReason>}
     * @memberof GETAutoBookEvaluation
     */
    'container_not_bookable_reasons': Array<ContainerNotBookableReason>;
    /**
     * 
     * @type {Array<GETAutoBookSlotEvaluation>}
     * @memberof GETAutoBookEvaluation
     */
    'slots': Array<GETAutoBookSlotEvaluation>;
    /**
     * 
     * @type {GETBookingStyleRequest}
     * @memberof GETAutoBookEvaluation
     */
    'action_request'?: GETBookingStyleRequest;
}


/**
 * 
 * @export
 * @interface GETAutoBookRequest
 */
export interface GETAutoBookRequest {
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookRequest
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequest
     */
    'container_number': string;
    /**
     * 
     * @type {AutoBookingSettings}
     * @memberof GETAutoBookRequest
     */
    'settings': AutoBookingSettings;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof GETAutoBookRequest
     */
    'status': AutoBookRequestStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETAutoBookRequest
     */
    'set_by_user'?: SimpleUser;
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequest
     */
    'last_updated': string;
    /**
     * 
     * @type {boolean}
     * @memberof GETAutoBookRequest
     */
    'enabled': boolean;
}


/**
 * 
 * @export
 * @interface GETAutoBookRequestMinimal
 */
export interface GETAutoBookRequestMinimal {
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookRequestMinimal
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequestMinimal
     */
    'container_number': string;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof GETAutoBookRequestMinimal
     */
    'status': AutoBookRequestStatus;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequestMinimal
     */
    'master_bill_of_lading'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequestMinimal
     */
    'license_plate'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETAutoBookRequestMinimal
     */
    'chassis'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof GETAutoBookRequestMinimal
     */
    'set_random_license_plate'?: boolean;
}


/**
 * 
 * @export
 * @interface GETAutoBookSlotEvaluation
 */
export interface GETAutoBookSlotEvaluation {
    /**
     * 
     * @type {GETSlotWindow}
     * @memberof GETAutoBookSlotEvaluation
     */
    'slot': GETSlotWindow;
    /**
     * 
     * @type {Array<SlotNotBookableReason>}
     * @memberof GETAutoBookSlotEvaluation
     */
    'not_bookable_reasons': Array<SlotNotBookableReason>;
    /**
     * 
     * @type {number}
     * @memberof GETAutoBookSlotEvaluation
     */
    'score'?: number;
}
/**
 * 
 * @export
 * @interface GETBaseAppointmentSlot
 */
export interface GETBaseAppointmentSlot {
    /**
     * 
     * @type {string}
     * @memberof GETBaseAppointmentSlot
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof GETBaseAppointmentSlot
     */
    'window_end'?: string;
    /**
     * Number of appointments available at this time. `null` means at least one appointment is available, but the exact count is unknown — some terminals only report which times have appointments without providing a total count. `0` means no appointments are available.
     * @type {number}
     * @memberof GETBaseAppointmentSlot
     */
    'num_appointments_available': number;
    /**
     * Indicates an issue with this appointment time, for example it might be a \'ghost\' appointment this actually  not bookable. Please use details.warning instead.
     * @type {string}
     * @memberof GETBaseAppointmentSlot
     * @deprecated
     */
    'warning'?: string;
    /**
     * Indicates that this slot is org specific, meaning it is available for the current organization only. Please use details.org_specific instead.
     * @type {boolean}
     * @memberof GETBaseAppointmentSlot
     * @deprecated
     */
    'org_specific'?: boolean;
    /**
     * *NOTE:* Fields in this object are ommitted if they do not have a truthy value. This is to make the response more compact as these values are generally falsey and we want a \"sparse\" representation
     * @type {SlotDetails}
     * @memberof GETBaseAppointmentSlot
     */
    'details': SlotDetails;
}
/**
 * 
 * @export
 * @interface GETBookingRequest
 */
export interface GETBookingRequest {
    /**
     * 
     * @type {string}
     * @memberof GETBookingRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {ActionStatus}
     * @memberof GETBookingRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETBookingRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETBookingRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETBookingRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GETBookingRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof GETBookingRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof GETBookingRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof GETBookingRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingRequest
     */
    'action_type'?: GETBookingRequestActionTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof GETBookingRequest
     */
    'auto_book_request_id'?: number;
}

export const GETBookingRequestActionTypeEnum = {
    Book: 'book'
} as const;

export type GETBookingRequestActionTypeEnum = typeof GETBookingRequestActionTypeEnum[keyof typeof GETBookingRequestActionTypeEnum];

/**
 * This is for backwards compatibility with the old booking history API
 * @export
 * @interface GETBookingStyleRequest
 */
export interface GETBookingStyleRequest {
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {OldBookingStatus}
     * @memberof GETBookingStyleRequest
     */
    'status': OldBookingStatus;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETBookingStyleRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETBookingStyleRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GETBookingStyleRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'created': string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'action_type': GETBookingStyleRequestActionTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequest
     */
    'auto_book_request_id'?: number;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETBookingStyleRequest
     */
    'entered_in_tms_by'?: SimpleUser;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequest
     */
    'entered_in_tms_time'?: string;
}

export const GETBookingStyleRequestActionTypeEnum = {
    Book: 'book',
    BookEmpty: 'book_empty',
    Reschedule: 'reschedule',
    RescheduleEmpty: 'reschedule_empty'
} as const;

export type GETBookingStyleRequestActionTypeEnum = typeof GETBookingStyleRequestActionTypeEnum[keyof typeof GETBookingStyleRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface GETBookingStyleRequestWithApptNum
 */
export interface GETBookingStyleRequestWithApptNum {
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {OldBookingStatus}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'status': OldBookingStatus;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'created': string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'action_type': GETBookingStyleRequestWithApptNumActionTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'auto_book_request_id'?: number;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'entered_in_tms_by'?: SimpleUser;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'entered_in_tms_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETBookingStyleRequestWithApptNum
     */
    'appointment_terminal_reference'?: string;
}

export const GETBookingStyleRequestWithApptNumActionTypeEnum = {
    Book: 'book',
    BookEmpty: 'book_empty',
    Reschedule: 'reschedule',
    RescheduleEmpty: 'reschedule_empty'
} as const;

export type GETBookingStyleRequestWithApptNumActionTypeEnum = typeof GETBookingStyleRequestWithApptNumActionTypeEnum[keyof typeof GETBookingStyleRequestWithApptNumActionTypeEnum];

/**
 * 
 * @export
 * @interface GETCameraHistoryImage
 */
export interface GETCameraHistoryImage {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETCameraHistoryImage
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETCameraHistoryImage
     */
    'stream_name': string;
    /**
     * When we recorded the image from this image from the terminal\'s website
     * @type {string}
     * @memberof GETCameraHistoryImage
     */
    'observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETCameraHistoryImage
     */
    'image_url': string;
    /**
     * URL where you would go to see this terminals cameras if there were no DrayDog
     * @type {string}
     * @memberof GETCameraHistoryImage
     */
    'source_url': string;
    /**
     * 
     * @type {string}
     * @memberof GETCameraHistoryImage
     */
    'bucket_time': string;
}


/**
 * 
 * @export
 * @interface GETCameraImage
 */
export interface GETCameraImage {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETCameraImage
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETCameraImage
     */
    'stream_name': string;
    /**
     * When we recorded the image from this image from the terminal\'s website
     * @type {string}
     * @memberof GETCameraImage
     */
    'observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETCameraImage
     */
    'image_url': string;
    /**
     * URL where you would go to see this terminals cameras if there were no DrayDog
     * @type {string}
     * @memberof GETCameraImage
     */
    'source_url': string;
}


/**
 * 
 * @export
 * @interface GETCapacityBucket
 */
export interface GETCapacityBucket {
    [key: string]: any;

    /**
     * 0 is Saturday, 6 is Sunday
     * @type {number}
     * @memberof GETCapacityBucket
     */
    'day_of_week': number;
    /**
     * 0 is midnight, 23 is 11pm
     * @type {number}
     * @memberof GETCapacityBucket
     */
    'hour_of_day': number;
    /**
     * Maximum number of import appointments allow for this bucket
     * @type {number}
     * @memberof GETCapacityBucket
     */
    'max_appointments': number;
}
/**
 * 
 * @export
 * @interface GETCapacitySet
 */
export interface GETCapacitySet {
    [key: string]: any;

    /**
     * If true, this capacity set will not be affected by global capacity buckets
     * @type {boolean}
     * @memberof GETCapacitySet
     */
    'independent_capacity'?: boolean;
    /**
     * Customer to which this capacity set belongs. Must be null if this capacity set is a global capacity set
     * @type {SavedCustomer}
     * @memberof GETCapacitySet
     */
    'customer'?: SavedCustomer;
    /**
     * Container tag to which this bucket belongs. Must be null if this bucket is a \"global\" or \"customer\" capacity set
     * @type {string}
     * @memberof GETCapacitySet
     */
    'container_tag'?: string;
    /**
     * Port complex to which this capacity set belongs
     * @type {PortComplex}
     * @memberof GETCapacitySet
     */
    'port_complex': PortComplex;
    /**
     * Width of each bucket in minutes. Distinguishes multiple sets in the same scope (e.g. an hourly fleet set vs a daily fleet set).
     * @type {number}
     * @memberof GETCapacitySet
     */
    'bucket_minutes': number;
    /**
     * Which appointment direction this set governs capacity for (e.g. OUTBOUND for import, INBOUND for empties)
     * @type {TransactionDirection}
     * @memberof GETCapacitySet
     */
    'direction'?: TransactionDirection;
    /**
     * Whether this set governs capacity for loaded (import) or empty appointments
     * @type {boolean}
     * @memberof GETCapacitySet
     */
    'loaded'?: boolean;
    /**
     * Use `customer` field instead
     * @type {number}
     * @memberof GETCapacitySet
     * @deprecated
     */
    'customer_id'?: number;
    /**
     * List of capacity buckets. Empty list disables capacity management
     * @type {Array<GETCapacityBucket>}
     * @memberof GETCapacitySet
     */
    'buckets': Array<GETCapacityBucket>;
    /**
     * 
     * @type {string}
     * @memberof GETCapacitySet
     */
    'timezone': string;
    /**
     * Stable identifier for this capacity set. Use this when editing or deleting a specific set via the /sets/{id}/ endpoints.
     * @type {number}
     * @memberof GETCapacitySet
     */
    'id': number;
    /**
     * User who last edited this capacity set
     * @type {SimpleUser}
     * @memberof GETCapacitySet
     */
    'last_edited_by'?: SimpleUser;
    /**
     * Timestamp of when this capacity set was last edited
     * @type {string}
     * @memberof GETCapacitySet
     */
    'last_edited': string;
}


/**
 * 
 * @export
 * @interface GETCapacitySetAttributes
 */
export interface GETCapacitySetAttributes {
    [key: string]: any;

    /**
     * If true, this capacity set will not be affected by global capacity buckets
     * @type {boolean}
     * @memberof GETCapacitySetAttributes
     */
    'independent_capacity'?: boolean;
    /**
     * Customer to which this capacity set belongs. Must be null if this capacity set is a global capacity set
     * @type {SavedCustomer}
     * @memberof GETCapacitySetAttributes
     */
    'customer'?: SavedCustomer;
    /**
     * Container tag to which this bucket belongs. Must be null if this bucket is a \"global\" or \"customer\" capacity set
     * @type {string}
     * @memberof GETCapacitySetAttributes
     */
    'container_tag'?: string;
    /**
     * Port complex to which this capacity set belongs
     * @type {PortComplex}
     * @memberof GETCapacitySetAttributes
     */
    'port_complex': PortComplex;
    /**
     * Width of each bucket in minutes. Distinguishes multiple sets in the same scope (e.g. an hourly fleet set vs a daily fleet set).
     * @type {number}
     * @memberof GETCapacitySetAttributes
     */
    'bucket_minutes': number;
    /**
     * Which appointment direction this set governs capacity for (e.g. OUTBOUND for import, INBOUND for empties)
     * @type {TransactionDirection}
     * @memberof GETCapacitySetAttributes
     */
    'direction'?: TransactionDirection;
    /**
     * Whether this set governs capacity for loaded (import) or empty appointments
     * @type {boolean}
     * @memberof GETCapacitySetAttributes
     */
    'loaded'?: boolean;
}


/**
 * 
 * @export
 * @interface GETCapacitySets
 */
export interface GETCapacitySets {
    [key: string]: any;

    /**
     * List of capacity sets
     * @type {Array<GETCapacitySet>}
     * @memberof GETCapacitySets
     */
    'capacity_sets': Array<GETCapacitySet>;
}
/**
 * 
 * @export
 * @interface GETConnection
 */
export interface GETConnection {
    /**
     * 
     * @type {ConnectionType}
     * @memberof GETConnection
     */
    'type': ConnectionType;
    /**
     * 
     * @type {Config}
     * @memberof GETConnection
     */
    'config': Config;
    /**
     * 
     * @type {number}
     * @memberof GETConnection
     */
    'id': number;
    /**
     * 
     * @type {ConnectionValidationStatus}
     * @memberof GETConnection
     */
    'validation_status': ConnectionValidationStatus;
}


/**
 * 
 * @export
 * @interface GETConnectionMetadata
 */
export interface GETConnectionMetadata {
    /**
     * Identifier for this connection type in the DrayDog system
     * @type {ConnectionType}
     * @memberof GETConnectionMetadata
     */
    'connection_type': ConnectionType;
    /**
     * High-level category used to group connections in the UI
     * @type {ConnectionCategory}
     * @memberof GETConnectionMetadata
     */
    'connection_category': ConnectionCategory;
    /**
     * Portal used for login and validation when this connection is backed by a portal
     * @type {PortalName}
     * @memberof GETConnectionMetadata
     */
    'associated_portal'?: PortalName;
    /**
     * Display label shown in the connections UI
     * @type {string}
     * @memberof GETConnectionMetadata
     */
    'label'?: string;
    /**
     * Portal or service URL for configuring this connection
     * @type {string}
     * @memberof GETConnectionMetadata
     */
    'url'?: string;
    /**
     * Optional instructions shown when adding or editing the connection
     * @type {string}
     * @memberof GETConnectionMetadata
     */
    'note'?: string;
    /**
     * The port complexes/regions where this connection is applicable
     * @type {Array<PortComplex>}
     * @memberof GETConnectionMetadata
     */
    'region'?: Array<PortComplex>;
    /**
     * Whether DrayDog manages this connection on behalf of the org
     * @type {boolean}
     * @memberof GETConnectionMetadata
     */
    'is_dray_dog_managed'?: boolean;
    /**
     * Another connection type that makes this one optional. While the org\'s saved connection of that type is Validated, this one is skipped in setup (popup, banner, Needs attention). The settings All tab still lists it.
     * @type {ConnectionType}
     * @memberof GETConnectionMetadata
     */
    'setup_covered_by'?: ConnectionType;
    /**
     * Help Center article explaining how to set up this connection
     * @type {string}
     * @memberof GETConnectionMetadata
     */
    'help_url'?: string;
}


/**
 * 
 * @export
 * @interface GETContainer
 */
export interface GETContainer {
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'container_number': string;
    /**
     * 
     * @type {PortComplex}
     * @memberof GETContainer
     */
    'port_complex': PortComplex;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETContainer
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETContainer
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {SavedCustomer}
     * @memberof GETContainer
     */
    'customer'?: SavedCustomer;
    /**
     * 
     * @type {GETPierPass}
     * @memberof GETContainer
     */
    'pier_pass'?: GETPierPass;
    /**
     * 
     * @type {GETPierPass}
     * @memberof GETContainer
     */
    'clean_truck_fee'?: GETPierPass;
    /**
     * The import status data that we think is the most accurate/ useful source for this container based on a variety of dynamic factors like when we last received data and how reliable the source is in general.
     * @type {GETImportStatus}
     * @memberof GETContainer
     */
    'import_status'?: GETImportStatus;
    /**
     * List of all import status data, one per import status source.  Includes all statuses from all terminals where we found data, including from terminals that do not match the `last_related_terminal`. In most cases these statuses from other terminals are due to rerouting of vessels and can be ignored, but they can be useful context so we include them
     * @type {Array<GETImportStatus>}
     * @memberof GETContainer
     */
    'import_statuses': Array<GETImportStatus>;
    /**
     * The appointment that we think is the most \"current\" for this container.
     * @type {GETAppointment}
     * @memberof GETContainer
     */
    'latest_appointment'?: GETAppointment;
    /**
     * Counts of appointments **other** than the latest appointment by status
     * @type {Array<GETAppointmentStatusCount>}
     * @memberof GETContainer
     */
    'other_appointment_counts': Array<GETAppointmentStatusCount>;
    /**
     * Most relevant status data from the shipping line
     * @type {GETLineImportStatus}
     * @memberof GETContainer
     */
    'line_import_status'?: GETLineImportStatus;
    /**
     * 
     * @type {GateTransaction}
     * @memberof GETContainer
     */
    'inbound_gate_transaction'?: GateTransaction;
    /**
     * 
     * @type {GateTransaction}
     * @memberof GETContainer
     */
    'outbound_gate_transaction'?: GateTransaction;
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof GETContainer
     */
    'cycle_state': ContainerWatchState;
    /**
     * We no longer provide \'next available appointment\' information in this API. This field is still here to avoid breaking backwards compatibility
     * @type {GETNextAvailable}
     * @memberof GETContainer
     * @deprecated
     */
    'next_available_appointment'?: GETNextAvailable;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'master_bl'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'tms_reference'?: string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETContainer
     */
    'added_by_user'?: SimpleUser;
    /**
     * 
     * @type {Array<SimpleUser>}
     * @memberof GETContainer
     */
    'assigned_users': Array<SimpleUser>;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainer
     */
    'auto_book_on': boolean;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof GETContainer
     */
    'auto_book_request_status'?: AutoBookRequestStatus;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETContainer
     */
    'last_related_terminal'?: TerminalName;
    /**
     * When this container was registered with Dray Dog to be tracked. Could be registered by a user, API, or showing up as an appointment on a terminal appointment system
     * @type {string}
     * @memberof GETContainer
     */
    'watched_time': string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'discharged_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'import_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'import_appointment_pickup_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'import_out_gated_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'empty_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'empty_in_gated_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'last_free_date'?: string;
    /**
     * LFD published by the shipping line itself, from any source. `last_free_date` already accounts for it; null means we have no line LFD, which matters for lines that don\'t share it with terminals
     * @type {string}
     * @memberof GETContainer
     */
    'line_last_free_date'?: string;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainer
     */
    'tags': Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'vessel'?: string;
    /**
     * 
     * @type {SavedVesselForContainer}
     * @memberof GETContainer
     */
    'matched_vessel'?: SavedVesselForContainer;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainer
     */
    'auto_book_ignoring_capacity': boolean;
    /**
     * 
     * @type {string}
     * @memberof GETContainer
     */
    'note_body'?: string;
    /**
     * Where the container is currently located. Set automatically from TMS empty-container feeds and editable by dispatchers.
     * @type {GETLocation}
     * @memberof GETContainer
     */
    'current_location'?: GETLocation;
}


/**
 * 
 * @export
 * @interface GETContainerAvailability
 */
export interface GETContainerAvailability {
    /**
     * 
     * @type {string}
     * @memberof GETContainerAvailability
     */
    'container_number': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETContainerAvailability
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerAvailability
     */
    'has_availability': boolean;
    /**
     * If provided, this means that the availability was found via a shared terminal block, and not specifically for this container. If provided, this implies that the availability was fetched via a different container if is None it implies that this exact container was used to fetch the availability.
     * @type {string}
     * @memberof GETContainerAvailability
     */
    'from_terminal_block_availability_key'?: string;
    /**
     * 
     * @type {Array<GETBaseAppointmentSlot>}
     * @memberof GETContainerAvailability
     */
    'slots': Array<GETBaseAppointmentSlot>;
    /**
     * 
     * @type {string}
     * @memberof GETContainerAvailability
     */
    'observed': string;
}


/**
 * 
 * @export
 * @interface GETContainerBasic
 */
export interface GETContainerBasic {
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof GETContainerBasic
     */
    'cycle_state': ContainerWatchState;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETContainerBasic
     */
    'last_related_terminal'?: TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETContainerBasic
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETContainerBasic
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'import_out_gated_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'empty_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'empty_in_gated_time'?: string;
    /**
     * 
     * @type {SavedCustomer}
     * @memberof GETContainerBasic
     */
    'customer'?: SavedCustomer;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainerBasic
     */
    'tags': Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'vessel'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasic
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {GETLocation}
     * @memberof GETContainerBasic
     */
    'current_location'?: GETLocation;
    /**
     * 
     * @type {Array<SimpleUser>}
     * @memberof GETContainerBasic
     */
    'assigned_users'?: Array<SimpleUser>;
}


/**
 * 
 * @export
 * @interface GETContainerBasicWithAutoBook
 */
export interface GETContainerBasicWithAutoBook {
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof GETContainerBasicWithAutoBook
     */
    'cycle_state': ContainerWatchState;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETContainerBasicWithAutoBook
     */
    'last_related_terminal'?: TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETContainerBasicWithAutoBook
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETContainerBasicWithAutoBook
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'import_out_gated_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'empty_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'empty_in_gated_time'?: string;
    /**
     * 
     * @type {SavedCustomer}
     * @memberof GETContainerBasicWithAutoBook
     */
    'customer'?: SavedCustomer;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainerBasicWithAutoBook
     */
    'tags': Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'vessel'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETContainerBasicWithAutoBook
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {GETLocation}
     * @memberof GETContainerBasicWithAutoBook
     */
    'current_location'?: GETLocation;
    /**
     * 
     * @type {Array<SimpleUser>}
     * @memberof GETContainerBasicWithAutoBook
     */
    'assigned_users'?: Array<SimpleUser>;
    /**
     * Empty auto-book status for this container, or None if no empty auto book request exists. Not to be confused with GETContainer\'s `auto_book_request_status`, which covers the separate import auto-book subsystem.
     * @type {AutoBookRequestStatus}
     * @memberof GETContainerBasicWithAutoBook
     */
    'auto_book_empty_request_status'?: AutoBookRequestStatus;
    /**
     * True if this container\'s empty auto-book request is flagged hot (`ignore_capacity_settings`). False when there is no request.
     * @type {boolean}
     * @memberof GETContainerBasicWithAutoBook
     */
    'auto_book_empty_hot'?: boolean;
}


/**
 * 
 * @export
 * @interface GETContainerFileExport
 */
export interface GETContainerFileExport {
    /**
     * 
     * @type {Array<TerminalName>}
     * @memberof GETContainerFileExport
     */
    'terminals'?: Array<TerminalName>;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETContainerFileExport
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {Array<ContainerWatchState>}
     * @memberof GETContainerFileExport
     */
    'container_cycle_states'?: Array<ContainerWatchState>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETContainerFileExport
     */
    'customer_ids'?: Array<number>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETContainerFileExport
     */
    'excluded_customer_ids'?: Array<number>;
    /**
     * 
     * @type {number}
     * @memberof GETContainerFileExport
     */
    'customer_id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'auto_book_on'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'auto_book_off'?: boolean;
    /**
     * 
     * @type {Array<AutoBookRequestStatus>}
     * @memberof GETContainerFileExport
     */
    'auto_book_statuses'?: Array<AutoBookRequestStatus>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETContainerFileExport
     */
    'assigned_user_ids'?: Array<number>;
    /**
     * 
     * @type {Array<ContainerType>}
     * @memberof GETContainerFileExport
     */
    'container_types'?: Array<ContainerType>;
    /**
     * 
     * @type {Array<ShippingLine>}
     * @memberof GETContainerFileExport
     */
    'shipping_lines'?: Array<ShippingLine>;
    /**
     * 
     * @type {number}
     * @memberof GETContainerFileExport
     */
    'updated_in_last_minutes'?: number;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainerFileExport
     */
    'tags'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'is_untagged'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainerFileExport
     */
    'vessels'?: Array<string>;
    /**
     * 
     * @type {Array<PortComplex>}
     * @memberof GETContainerFileExport
     */
    'port_complexes'?: Array<PortComplex>;
    /**
     * 
     * @type {string}
     * @memberof GETContainerFileExport
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'vessel_eta_is_unknown'?: boolean;
    /**
     * 
     * @type {Array<HoldType>}
     * @memberof GETContainerFileExport
     */
    'hold_types'?: Array<HoldType>;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'is_green_truck'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETContainerFileExport
     */
    'needs_line_lfd_check'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETContainerFileExport
     */
    'container_numbers'?: Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETContainerFileExport
     */
    'file_type'?: GETContainerFileExportFileTypeEnum;
}

export const GETContainerFileExportFileTypeEnum = {
    Csv: 'csv',
    Xlsx: 'xlsx'
} as const;

export type GETContainerFileExportFileTypeEnum = typeof GETContainerFileExportFileTypeEnum[keyof typeof GETContainerFileExportFileTypeEnum];

/**
 * 
 * @export
 * @interface GETContainerLineAndType
 */
export interface GETContainerLineAndType {
    /**
     * 
     * @type {string}
     * @memberof GETContainerLineAndType
     */
    'container_number': string;
    /**
     * Newest known shipping line, or null if none is known. Treat as a suggestion: it is less reliable than `container_type` on older data.
     * @type {ShippingLine}
     * @memberof GETContainerLineAndType
     */
    'shipping_line'?: ShippingLine;
    /**
     * Known container type, or null if none is known. Taken from import statuses when available, as they are more reliable than appointments
     * @type {ContainerType}
     * @memberof GETContainerLineAndType
     */
    'container_type'?: ContainerType;
    /**
     * When the data behind this answer was last observed. If both fields are set this is the older of the two, so every value returned was seen at least this recently.
     * @type {string}
     * @memberof GETContainerLineAndType
     */
    'last_seen': string;
}


/**
 * 
 * @export
 * @interface GETContainerType
 */
export interface GETContainerType {
    /**
     * 
     * @type {ContainerType}
     * @memberof GETContainerType
     */
    'type': ContainerType;
}


/**
 * 
 * @export
 * @interface GETContainersBasicResponse
 */
export interface GETContainersBasicResponse {
    /**
     * Container numbers not \"watched\" (i.e tracking) for this request. To get data for these containers, you must upload these containers using [POST /containers/watch/](#operation/watch_containers_containers_watch__post) or [POST /containers/watch/via_upload/](#operation/watch_containers_via_upload_containers_watch_via_upload__post)
     * @type {Array<string>}
     * @memberof GETContainersBasicResponse
     */
    'unwatched_containers': Array<string>;
    /**
     * 
     * @type {Array<GETContainerBasic>}
     * @memberof GETContainersBasicResponse
     */
    'containers': Array<GETContainerBasic>;
    /**
     * Searched container numbers watched by this org but at a different port than the queried port_complexes filter.
     * @type {Array<string>}
     * @memberof GETContainersBasicResponse
     */
    'containers_watched_at_other_ports'?: Array<string>;
    /**
     * Searched container numbers watched at the correct port but currently in a non-empty cycle state.
     * @type {Array<string>}
     * @memberof GETContainersBasicResponse
     */
    'containers_not_currently_empty'?: Array<string>;
}
/**
 * 
 * @export
 * @interface GETContainersBasicWithAutoBookResponse
 */
export interface GETContainersBasicWithAutoBookResponse {
    /**
     * Container numbers not \"watched\" (i.e tracking) for this request. To get data for these containers, you must upload these containers using [POST /containers/watch/](#operation/watch_containers_containers_watch__post) or [POST /containers/watch/via_upload/](#operation/watch_containers_via_upload_containers_watch_via_upload__post)
     * @type {Array<string>}
     * @memberof GETContainersBasicWithAutoBookResponse
     */
    'unwatched_containers': Array<string>;
    /**
     * 
     * @type {Array<GETContainerBasicWithAutoBook>}
     * @memberof GETContainersBasicWithAutoBookResponse
     */
    'containers': Array<GETContainerBasicWithAutoBook>;
    /**
     * Searched container numbers watched by this org but at a different port than the queried port_complexes filter.
     * @type {Array<string>}
     * @memberof GETContainersBasicWithAutoBookResponse
     */
    'containers_watched_at_other_ports'?: Array<string>;
    /**
     * Searched container numbers watched at the correct port but currently in a non-empty cycle state.
     * @type {Array<string>}
     * @memberof GETContainersBasicWithAutoBookResponse
     */
    'containers_not_currently_empty'?: Array<string>;
}
/**
 * 
 * @export
 * @interface GETContainersResponse
 */
export interface GETContainersResponse {
    /**
     * Container numbers not \"watched\" (i.e tracking) for this request. To get data for these containers, you must upload these containers using [POST /containers/watch/](#operation/watch_containers_containers_watch__post) or [POST /containers/watch/via_upload/](#operation/watch_containers_via_upload_containers_watch_via_upload__post)
     * @type {Array<string>}
     * @memberof GETContainersResponse
     */
    'unwatched_containers': Array<string>;
    /**
     * 
     * @type {Array<GETContainer>}
     * @memberof GETContainersResponse
     */
    'containers': Array<GETContainer>;
    /**
     * Containers already watched at other port complexes that were searched but not found for the current port complex filter.
     * @type {Array<string>}
     * @memberof GETContainersResponse
     */
    'containers_watched_at_other_ports': Array<string>;
}
/**
 * 
 * @export
 * @interface GETCurrentTerminalGateScheduleResponse
 */
export interface GETCurrentTerminalGateScheduleResponse {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'last_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'last_parsed_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'screenshot_url': string;
    /**
     * 
     * @type {string}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'html_url'?: string;
    /**
     * 
     * @type {Array<GETGateScheduleEntryResponse>}
     * @memberof GETCurrentTerminalGateScheduleResponse
     */
    'entries': Array<GETGateScheduleEntryResponse>;
}


/**
 * 
 * @export
 * @interface GETEmptyAutoBookSlotEvaluation
 */
export interface GETEmptyAutoBookSlotEvaluation {
    /**
     * 
     * @type {GETSlotWindow}
     * @memberof GETEmptyAutoBookSlotEvaluation
     */
    'slot': GETSlotWindow;
    /**
     * 
     * @type {Array<EmptySlotNotBookableReason>}
     * @memberof GETEmptyAutoBookSlotEvaluation
     */
    'not_bookable_reasons': Array<EmptySlotNotBookableReason>;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyAutoBookSlotEvaluation
     */
    'score': number;
}
/**
 * 
 * @export
 * @interface GETEmptyContainerAvailabilityResponse
 */
export interface GETEmptyContainerAvailabilityResponse {
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {boolean}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'has_availability': boolean;
    /**
     * 
     * @type {Array<GETEmptyInAppointmentSlot>}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'slots': Array<GETEmptyInAppointmentSlot>;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyContainerAvailabilityResponse
     */
    'observed': string;
}


/**
 * 
 * @export
 * @interface GETEmptyInAppointmentSlot
 */
export interface GETEmptyInAppointmentSlot {
    /**
     * 
     * @type {string}
     * @memberof GETEmptyInAppointmentSlot
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyInAppointmentSlot
     */
    'window_end'?: string;
    /**
     * Number of appointments available at this time. `null` means at least one appointment is available, but the exact count is unknown — some terminals only report which times have appointments without providing a total count. `0` means no appointments are available.
     * @type {number}
     * @memberof GETEmptyInAppointmentSlot
     */
    'num_appointments_available'?: number;
}
/**
 * 
 * @export
 * @interface GETEmptyPickupBookingRequest
 */
export interface GETEmptyPickupBookingRequest {
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'window_start': string;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'reschedule_cancellation_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'reschedule_request_id'?: number;
    /**
     * 
     * @type {ActionStatus}
     * @memberof GETEmptyPickupBookingRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETEmptyPickupBookingRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'container_number'?: string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETEmptyPickupBookingRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GETEmptyPickupBookingRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof GETEmptyPickupBookingRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof GETEmptyPickupBookingRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'booking_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETEmptyPickupBookingRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETEmptyPickupBookingRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyPickupBookingRequest
     */
    'action_type'?: GETEmptyPickupBookingRequestActionTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof GETEmptyPickupBookingRequest
     */
    'own_chassis'?: boolean;
}

export const GETEmptyPickupBookingRequestActionTypeEnum = {
    BookEmptyPickup: 'book_empty_pickup'
} as const;

export type GETEmptyPickupBookingRequestActionTypeEnum = typeof GETEmptyPickupBookingRequestActionTypeEnum[keyof typeof GETEmptyPickupBookingRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface GETEmptyReturnRule
 */
export interface GETEmptyReturnRule {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETEmptyReturnRule
     */
    'terminal': TerminalName;
    /**
     * \"Raw\" value from source
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'shipping_line': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETEmptyReturnRule
     */
    'parsed_shipping_line'?: ShippingLine;
    /**
     * \"Raw\" value from source
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'container_type': string;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETEmptyReturnRule
     */
    'parsed_container_type'?: ContainerType;
    /**
     * The rule for this line+type+date+shift or whichever of those fields are filled out.  `\'open\'` means receiving both single and dual transactions.  `\'closed\'` means no returns allowed. `\'duals_only\'` means returns are only allowed if you have something to pickup, i.e. you have a dual transaction. `\'exempt\'` means that no appointment is required to return
     * @type {ReceivingRule}
     * @memberof GETEmptyReturnRule
     */
    'rule': ReceivingRule;
    /**
     * `None` if the rule is not date-specific. There may also be date specific rules which are then \'overrides\' of this rule
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'date'?: string;
    /**
     * `None` if the rule is not shift-specific. There may also be shift specific rules which are then \'overrides\' of this rule
     * @type {Shift}
     * @memberof GETEmptyReturnRule
     */
    'shift'?: Shift;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'group_first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'group_last_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'reading_last_parsed': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRule
     */
    'screenshot_url': string;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETEmptyReturnRule
     */
    'notes'?: Array<string>;
}


/**
 * 
 * @export
 * @interface GETEmptyReturnRulesReading
 */
export interface GETEmptyReturnRulesReading {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETEmptyReturnRulesReading
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRulesReading
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRulesReading
     */
    'last_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRulesReading
     */
    'last_parsed_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRulesReading
     */
    'screenshot_url': string;
    /**
     * 
     * @type {string}
     * @memberof GETEmptyReturnRulesReading
     */
    'html_url'?: string;
}


/**
 * 
 * @export
 * @interface GETEtaCount
 */
export interface GETEtaCount {
    /**
     * 
     * @type {string}
     * @memberof GETEtaCount
     */
    'eta': string;
    /**
     * 
     * @type {number}
     * @memberof GETEtaCount
     */
    'count': number;
}
/**
 * A tracked export booking with its per-terminal statuses, for the exports page.
 * @export
 * @interface GETExport
 */
export interface GETExport {
    /**
     * 
     * @type {string}
     * @memberof GETExport
     */
    'booking_number': string;
    /**
     * 
     * @type {ExportWatchState}
     * @memberof GETExport
     */
    'state': ExportWatchState;
    /**
     * Derived booking lifecycle. The single source for the list status filter, the per-state counts and the status tabs
     * @type {ExportBookingState}
     * @memberof GETExport
     */
    'lifecycle_state': ExportBookingState;
    /**
     * 
     * @type {PortComplex}
     * @memberof GETExport
     */
    'port_complex': PortComplex;
    /**
     * 
     * @type {Array<TerminalName>}
     * @memberof GETExport
     */
    'related_terminals': Array<TerminalName>;
    /**
     * 
     * @type {string}
     * @memberof GETExport
     */
    'shipping_line': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETExport
     */
    'parsed_shipping_line': ShippingLine;
    /**
     * 
     * @type {string}
     * @memberof GETExport
     */
    'vessel': string;
    /**
     * 
     * @type {string}
     * @memberof GETExport
     */
    'voyage': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETExport
     */
    'added_by_user': SimpleUser;
    /**
     * 
     * @type {SavedCustomer}
     * @memberof GETExport
     */
    'customer': SavedCustomer;
    /**
     * 
     * @type {Array<GETExportStatus>}
     * @memberof GETExport
     */
    'statuses': Array<GETExportStatus>;
    /**
     * Result of the org\'s last check of this booking on eModal
     * @type {GETExportLastCheck}
     * @memberof GETExport
     */
    'last_check': GETExportLastCheck;
    /**
     * Containers on the booking as the terminals report it (the list\'s TOTAL). Terminals repeat a booking\'s lines, so they are not added up: per type the largest terminal count wins. None when no terminal has reported equipment yet.
     * @type {number}
     * @memberof GETExport
     */
    'total_containers'?: number;
    /**
     * Carrier-declared quantity per container type for this booking. Empty dict means the carrier has not set a quantity; the UI falls back to the terminal total in that case.
     * @type {{ [key: string]: number; }}
     * @memberof GETExport
     */
    'carrier_quantities'?: { [key: string]: number; };
    /**
     * Vessel resolved from the booking\'s free-text vessel name. None only when the vessel name cannot be matched to a known vessel in our data at all. Check vessel_schedules for per-terminal cutoff/ERD dates.
     * @type {MatchedVesselForExport}
     * @memberof GETExport
     */
    'matched_vessel'?: MatchedVesselForExport;
    /**
     * Per-terminal schedule data for the resolved vessel. An element is only included when a schedule row exists for that (vessel, terminal) pair. Match entries to statuses[] by the terminal field — statuses[] can have multiple rows per terminal (different sources) so lengths may differ. An empty list means either no vessel was matched or no schedules are on file for the booking\'s terminals.
     * @type {Array<VesselScheduleForExport>}
     * @memberof GETExport
     */
    'vessel_schedules'?: Array<VesselScheduleForExport>;
    /**
     * Appointments (export empty pickups) drawing against this booking. Populated on GET /exports/ and GET /exports/{booking_number}; empty on the watch/upload endpoints to keep them lean
     * @type {Array<GETAppointment>}
     * @memberof GETExport
     */
    'appointments'?: Array<GETAppointment>;
}


/**
 * What the Add bookings table shows for one booking before it\'s added.
 * @export
 * @interface GETExportBookingCheck
 */
export interface GETExportBookingCheck {
    /**
     * 
     * @type {string}
     * @memberof GETExportBookingCheck
     */
    'booking_number': string;
    /**
     * 
     * @type {ExportBookingCheckStatus}
     * @memberof GETExportBookingCheck
     */
    'check_status': ExportBookingCheckStatus;
    /**
     * Readable reason for anything other than `found`
     * @type {string}
     * @memberof GETExportBookingCheck
     */
    'message': string;
    /**
     * The org already tracks this booking
     * @type {boolean}
     * @memberof GETExportBookingCheck
     */
    'already_tracked': boolean;
    /**
     * Containers on file (same rule as the list\'s TOTAL). Set only when `found`; None there too if no terminal reported equipment yet.
     * @type {number}
     * @memberof GETExportBookingCheck
     */
    'total_containers': number;
    /**
     * 
     * @type {Array<GETExportBookingTypeCount>}
     * @memberof GETExportBookingCheck
     */
    'containers_by_type': Array<GETExportBookingTypeCount>;
    /**
     * Terminals the booking is listed at
     * @type {Array<TerminalName>}
     * @memberof GETExportBookingCheck
     */
    'terminals': Array<TerminalName>;
}


/**
 * 
 * @export
 * @interface GETExportBookingChecksResponse
 */
export interface GETExportBookingChecksResponse {
    /**
     * One per requested booking, in request order
     * @type {Array<GETExportBookingCheck>}
     * @memberof GETExportBookingChecksResponse
     */
    'bookings': Array<GETExportBookingCheck>;
}
/**
 * 
 * @export
 * @interface GETExportBookingTypeCount
 */
export interface GETExportBookingTypeCount {
    /**
     * Parsed type, or None when the terminal\'s code isn\'t mapped yet
     * @type {ContainerType}
     * @memberof GETExportBookingTypeCount
     */
    'container_type': ContainerType;
    /**
     * The terminal\'s code, e.g. \'40DH\'
     * @type {string}
     * @memberof GETExportBookingTypeCount
     */
    'container_type_code': string;
    /**
     * 
     * @type {number}
     * @memberof GETExportBookingTypeCount
     */
    'booked_qty': number;
}


/**
 * 
 * @export
 * @interface GETExportEmptyPickupAvailabilityResponse
 */
export interface GETExportEmptyPickupAvailabilityResponse {
    /**
     * 
     * @type {string}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'booking_number': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {boolean}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'has_availability': boolean;
    /**
     * 
     * @type {Array<GETEmptyInAppointmentSlot>}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'slots': Array<GETEmptyInAppointmentSlot>;
    /**
     * 
     * @type {string}
     * @memberof GETExportEmptyPickupAvailabilityResponse
     */
    'observed': string;
}


/**
 * The org\'s last eModal check of a tracked booking, so the list can tell a booking eModal doesn\'t know apart from one that is still loading or whose checks are failing.
 * @export
 * @interface GETExportLastCheck
 */
export interface GETExportLastCheck {
    /**
     * `found` / `not_found`: outcome of the last completed check. `failing`: the latest attempt failed (e.g. eModal rate limiting or an expired session), so the booking\'s state is unknown. `pending`: no completed check yet.
     * @type {ExportBookingCheckStatus}
     * @memberof GETExportLastCheck
     */
    'result': ExportBookingCheckStatus;
    /**
     * When we last checked successfully (found or not found). None if we never have
     * @type {string}
     * @memberof GETExportLastCheck
     */
    'last_success_at': string;
    /**
     * When we last tried to check, successful or not. None if we never have
     * @type {string}
     * @memberof GETExportLastCheck
     */
    'last_attempt_at': string;
}


/**
 * 
 * @export
 * @interface GETExportStateCount
 */
export interface GETExportStateCount {
    /**
     * 
     * @type {ExportBookingState}
     * @memberof GETExportStateCount
     */
    'state': ExportBookingState;
    /**
     * 
     * @type {number}
     * @memberof GETExportStateCount
     */
    'count': number;
}


/**
 * A per-terminal export booking status as shown on the exports page.
 * @export
 * @interface GETExportStatus
 */
export interface GETExportStatus {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETExportStatus
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETExportStatus
     */
    'shipping_line': string;
    /**
     * 
     * @type {string}
     * @memberof GETExportStatus
     */
    'vessel': string;
    /**
     * 
     * @type {string}
     * @memberof GETExportStatus
     */
    'voyage': string;
    /**
     * 
     * @type {Array<ExportBookingHold>}
     * @memberof GETExportStatus
     */
    'holds': Array<ExportBookingHold>;
    /**
     * 
     * @type {Array<ParsedExportEquipmentQuantity>}
     * @memberof GETExportStatus
     */
    'equipment': Array<ParsedExportEquipmentQuantity>;
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof GETExportStatus
     */
    'available_to_book_by_type': { [key: string]: number; };
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof GETExportStatus
     */
    'drop_available_by_type': { [key: string]: number; };
    /**
     * 
     * @type {string}
     * @memberof GETExportStatus
     */
    'observed': string;
}


/**
 * 
 * @export
 * @interface GETExportTerminalCount
 */
export interface GETExportTerminalCount {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETExportTerminalCount
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {number}
     * @memberof GETExportTerminalCount
     */
    'count': number;
}


/**
 * 
 * @export
 * @interface GETExportsResponse
 */
export interface GETExportsResponse {
    /**
     * 
     * @type {Array<GETExport>}
     * @memberof GETExportsResponse
     */
    'exports': Array<GETExport>;
    /**
     * How many bookings match the filters, ignoring page size. Used for page buttons. Null unless `include_total` is true, so \"not counted\" never reads as \"nothing matched\".
     * @type {number}
     * @memberof GETExportsResponse
     */
    'total'?: number;
}
/**
 * 
 * @export
 * @interface GETFileExport
 */
export interface GETFileExport {
    /**
     * 
     * @type {Array<TerminalName>}
     * @memberof GETFileExport
     */
    'terminals'?: Array<TerminalName>;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETFileExport
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {Array<ContainerWatchState>}
     * @memberof GETFileExport
     */
    'container_cycle_states'?: Array<ContainerWatchState>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETFileExport
     */
    'customer_ids'?: Array<number>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETFileExport
     */
    'excluded_customer_ids'?: Array<number>;
    /**
     * 
     * @type {number}
     * @memberof GETFileExport
     */
    'customer_id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'auto_book_on'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'auto_book_off'?: boolean;
    /**
     * 
     * @type {Array<AutoBookRequestStatus>}
     * @memberof GETFileExport
     */
    'auto_book_statuses'?: Array<AutoBookRequestStatus>;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETFileExport
     */
    'assigned_user_ids'?: Array<number>;
    /**
     * 
     * @type {Array<ContainerType>}
     * @memberof GETFileExport
     */
    'container_types'?: Array<ContainerType>;
    /**
     * 
     * @type {Array<ShippingLine>}
     * @memberof GETFileExport
     */
    'shipping_lines'?: Array<ShippingLine>;
    /**
     * 
     * @type {number}
     * @memberof GETFileExport
     */
    'updated_in_last_minutes'?: number;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETFileExport
     */
    'tags'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'is_untagged'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETFileExport
     */
    'vessels'?: Array<string>;
    /**
     * 
     * @type {Array<PortComplex>}
     * @memberof GETFileExport
     */
    'port_complexes'?: Array<PortComplex>;
    /**
     * 
     * @type {string}
     * @memberof GETFileExport
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'vessel_eta_is_unknown'?: boolean;
    /**
     * 
     * @type {Array<HoldType>}
     * @memberof GETFileExport
     */
    'hold_types'?: Array<HoldType>;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'is_green_truck'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'needs_line_lfd_check'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETFileExport
     */
    'container_numbers'?: Array<string>;
    /**
     * Use `container_numbers` instead
     * @type {Array<string>}
     * @memberof GETFileExport
     */
    'containers'?: Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETFileExport
     */
    'file_type': GETFileExportFileTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'to_trinium'?: boolean;
    /**
     * 
     * @type {Array<number>}
     * @memberof GETFileExport
     */
    'appointment_ids'?: Array<number>;
    /**
     * 
     * @type {boolean}
     * @memberof GETFileExport
     */
    'limit_to_relevant_appointments'?: boolean;
}

export const GETFileExportFileTypeEnum = {
    Csv: 'csv',
    Xlsx: 'xlsx'
} as const;

export type GETFileExportFileTypeEnum = typeof GETFileExportFileTypeEnum[keyof typeof GETFileExportFileTypeEnum];

/**
 * 
 * @export
 * @interface GETGateScheduleEntryResponse
 */
export interface GETGateScheduleEntryResponse {
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleEntryResponse
     */
    'date': string;
    /**
     * 
     * @type {Shift}
     * @memberof GETGateScheduleEntryResponse
     */
    'shift': Shift;
    /**
     * 
     * @type {GateStatus}
     * @memberof GETGateScheduleEntryResponse
     */
    'status': GateStatus;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETGateScheduleEntryResponse
     */
    'notes': Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleEntryResponse
     */
    'start_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleEntryResponse
     */
    'end_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleEntryResponse
     */
    'raw_period_description': string;
}


/**
 * 
 * @export
 * @interface GETGateScheduleReading
 */
export interface GETGateScheduleReading {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETGateScheduleReading
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleReading
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleReading
     */
    'last_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleReading
     */
    'last_parsed_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleReading
     */
    'screenshot_url': string;
    /**
     * 
     * @type {string}
     * @memberof GETGateScheduleReading
     */
    'html_url'?: string;
}


/**
 * 
 * @export
 * @interface GETImportStatus
 */
export interface GETImportStatus {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETImportStatus
     */
    'terminal': TerminalName;
    /**
     * The source of the import availability data
     * @type {PortalName}
     * @memberof GETImportStatus
     */
    'source': PortalName;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof GETImportStatus
     */
    'container_number': string;
    /**
     * Date the trucker must pickup this container before incurring demurrage fees
     * @type {string}
     * @memberof GETImportStatus
     */
    'last_free_date'?: string;
    /**
     * Last free date explicitly set by the shipping line (SSL), when the portal exposes it separately from the terminal LFD. Used internally: the primary `last_free_date` is still the best LFD to show users.
     * @type {string}
     * @memberof GETImportStatus
     */
    'line_last_free_date'?: string;
    /**
     * 
     * @type {Array<LastFreeDate>}
     * @memberof GETImportStatus
     */
    'other_last_free_dates'?: Array<LastFreeDate>;
    /**
     * Also called \'Master BL\'
     * @type {string}
     * @memberof GETImportStatus
     */
    'master_bill_of_lading'?: string;
    /**
     * Company that operates the ship this container is on. Also called \'SSL\', or \'Line\'
     * @type {string}
     * @memberof GETImportStatus
     */
    'shipping_line'?: string;
    /**
     * Physical container type
     * @type {string}
     * @memberof GETImportStatus
     */
    'container_type'?: string;
    /**
     * Name of the ship this container is on
     * @type {string}
     * @memberof GETImportStatus
     */
    'vessel'?: string;
    /**
     * Location of the container. Could be area in terminal, ship, on vessel, etc.
     * @type {string}
     * @memberof GETImportStatus
     */
    'location': string;
    /**
     * 
     * @type {ImportLocation}
     * @memberof GETImportStatus
     */
    'parsed_location': ImportLocation;
    /**
     * Area of the terminal where the container is located. This is a subset/piece of the `location` field.
     * @type {string}
     * @memberof GETImportStatus
     */
    'terminal_block'?: string;
    /**
     * The terminal block availability key
     * @type {string}
     * @memberof GETImportStatus
     */
    'terminal_block_availability_key'?: string;
    /**
     * List of descriptions of holds on the container. I think anything that blocks picking up a container should go here, including TMF/congestion fee payment
     * @type {Array<string>}
     * @memberof GETImportStatus
     */
    'holds': Array<string>;
    /**
     * Demurrage is a fee for not picking up a container in time. It usually escalates to a higher rate per day as the number of days accumulate. In dollars.
     * @type {number}
     * @memberof GETImportStatus
     */
    'demurrage_fees'?: number;
    /**
     * Whether an appointment can be booked for this container or not
     * @type {boolean}
     * @memberof GETImportStatus
     */
    'ready_for_appointment': boolean;
    /**
     * Whether the container is available for pickup. A container could be ready for an appointment but not ready for pickup, as in the terminal will let you book an appointment but something else needs to happen before pickup. `None/null` means we don\'t know
     * @type {boolean}
     * @memberof GETImportStatus
     */
    'available_for_pickup'?: boolean;
    /**
     * Whether the container is wheeled or not. `None/null` means we don\'t have data on this
     * @type {boolean}
     * @memberof GETImportStatus
     */
    'wheeled'?: boolean;
    /**
     * Hazardous class of the container. `None/null` means we don\'t have data on this
     * @type {string}
     * @memberof GETImportStatus
     */
    'hazardous'?: string;
    /**
     * Whether the container is in a closed area or not. `None/null` means we don\'t have data on this
     * @type {boolean}
     * @memberof GETImportStatus
     */
    'closed_area'?: boolean;
    /**
     * Time the container out gated from the terminal
     * @type {string}
     * @memberof GETImportStatus
     */
    'out_gated_time'?: string;
    /**
     * Time the empty was received at the terminal (eModal Empty Received / EMTRC). Real ingate time when the terminal has no gate transaction.
     * @type {string}
     * @memberof GETImportStatus
     */
    'empty_received_time'?: string;
    /**
     * Time the container was discharged from the vessel
     * @type {string}
     * @memberof GETImportStatus
     */
    'discharged_time'?: string;
    /**
     * Estimated time of arrival for the vessel
     * @type {string}
     * @memberof GETImportStatus
     */
    'eta'?: string;
    /**
     * The data used to generate all other fields. Should be for a single container and be more-or-less unmodified. This data will be available to users but hidden by default
     * @type {{ [key: string]: any; }}
     * @memberof GETImportStatus
     */
    'raw_data': { [key: string]: any; };
    /**
     * A place to put extra data which seems important but doesn\'t fit in another field. Items added here will be presented to users
     * @type {{ [key: string]: any; }}
     * @memberof GETImportStatus
     */
    'extra_data': { [key: string]: any; };
    /**
     * Should be set to the time this status was read
     * @type {string}
     * @memberof GETImportStatus
     */
    'observed': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETImportStatus
     */
    'parsed_shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETImportStatus
     */
    'parsed_container_type'?: ContainerType;
    /**
     * 
     * @type {Array<ParsedHold>}
     * @memberof GETImportStatus
     */
    'parsed_holds': Array<ParsedHold>;
    /**
     * 
     * @type {number}
     * @memberof GETImportStatus
     */
    'id': number;
    /**
     * 
     * @type {number}
     * @memberof GETImportStatus
     */
    'cycle_id': number;
    /**
     * When we last received data
     * @type {string}
     * @memberof GETImportStatus
     */
    'last_observed': string;
    /**
     * When we last initiated a check
     * @type {string}
     * @memberof GETImportStatus
     */
    'last_initiated_check': string;
    /**
     * True when the portal delivers updates in real-time (e.g. via Azure Service Bus). The frontend should show \'Real-Time Connected\' instead of \'Last Checked X ago\' for these statuses.
     * @type {boolean}
     * @memberof GETImportStatus
     */
    'is_realtime'?: boolean;
}


/**
 * 
 * @export
 * @interface GETImportStatusBase
 */
export interface GETImportStatusBase {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETImportStatusBase
     */
    'terminal': TerminalName;
    /**
     * The source of the import availability data
     * @type {PortalName}
     * @memberof GETImportStatusBase
     */
    'source': PortalName;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'container_number': string;
    /**
     * Date the trucker must pickup this container before incurring demurrage fees
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'last_free_date'?: string;
    /**
     * Last free date explicitly set by the shipping line (SSL), when the portal exposes it separately from the terminal LFD. Used internally: the primary `last_free_date` is still the best LFD to show users.
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'line_last_free_date'?: string;
    /**
     * 
     * @type {Array<LastFreeDate>}
     * @memberof GETImportStatusBase
     */
    'other_last_free_dates'?: Array<LastFreeDate>;
    /**
     * Also called \'Master BL\'
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'master_bill_of_lading'?: string;
    /**
     * Company that operates the ship this container is on. Also called \'SSL\', or \'Line\'
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'shipping_line'?: string;
    /**
     * Physical container type
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'container_type'?: string;
    /**
     * Name of the ship this container is on
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'vessel'?: string;
    /**
     * Location of the container. Could be area in terminal, ship, on vessel, etc.
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'location': string;
    /**
     * 
     * @type {ImportLocation}
     * @memberof GETImportStatusBase
     */
    'parsed_location': ImportLocation;
    /**
     * Area of the terminal where the container is located. This is a subset/piece of the `location` field.
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'terminal_block'?: string;
    /**
     * The terminal block availability key
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'terminal_block_availability_key'?: string;
    /**
     * List of descriptions of holds on the container. I think anything that blocks picking up a container should go here, including TMF/congestion fee payment
     * @type {Array<string>}
     * @memberof GETImportStatusBase
     */
    'holds': Array<string>;
    /**
     * Demurrage is a fee for not picking up a container in time. It usually escalates to a higher rate per day as the number of days accumulate. In dollars.
     * @type {number}
     * @memberof GETImportStatusBase
     */
    'demurrage_fees'?: number;
    /**
     * Whether an appointment can be booked for this container or not
     * @type {boolean}
     * @memberof GETImportStatusBase
     */
    'ready_for_appointment': boolean;
    /**
     * Whether the container is available for pickup. A container could be ready for an appointment but not ready for pickup, as in the terminal will let you book an appointment but something else needs to happen before pickup. `None/null` means we don\'t know
     * @type {boolean}
     * @memberof GETImportStatusBase
     */
    'available_for_pickup'?: boolean;
    /**
     * Whether the container is wheeled or not. `None/null` means we don\'t have data on this
     * @type {boolean}
     * @memberof GETImportStatusBase
     */
    'wheeled'?: boolean;
    /**
     * Hazardous class of the container. `None/null` means we don\'t have data on this
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'hazardous'?: string;
    /**
     * Whether the container is in a closed area or not. `None/null` means we don\'t have data on this
     * @type {boolean}
     * @memberof GETImportStatusBase
     */
    'closed_area'?: boolean;
    /**
     * Time the container out gated from the terminal
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'out_gated_time'?: string;
    /**
     * Time the empty was received at the terminal (eModal Empty Received / EMTRC). Real ingate time when the terminal has no gate transaction.
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'empty_received_time'?: string;
    /**
     * Time the container was discharged from the vessel
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'discharged_time'?: string;
    /**
     * Estimated time of arrival for the vessel
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'eta'?: string;
    /**
     * The data used to generate all other fields. Should be for a single container and be more-or-less unmodified. This data will be available to users but hidden by default
     * @type {{ [key: string]: any; }}
     * @memberof GETImportStatusBase
     */
    'raw_data': { [key: string]: any; };
    /**
     * A place to put extra data which seems important but doesn\'t fit in another field. Items added here will be presented to users
     * @type {{ [key: string]: any; }}
     * @memberof GETImportStatusBase
     */
    'extra_data': { [key: string]: any; };
    /**
     * Should be set to the time this status was read
     * @type {string}
     * @memberof GETImportStatusBase
     */
    'observed': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETImportStatusBase
     */
    'parsed_shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETImportStatusBase
     */
    'parsed_container_type'?: ContainerType;
    /**
     * 
     * @type {Array<ParsedHold>}
     * @memberof GETImportStatusBase
     */
    'parsed_holds': Array<ParsedHold>;
}


/**
 * Basic fields from LineImportStatus that we want to expose to the API
 * @export
 * @interface GETLineImportStatus
 */
export interface GETLineImportStatus {
    /**
     * 
     * @type {number}
     * @memberof GETLineImportStatus
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETLineImportStatus
     */
    'container_number': string;
    /**
     * 
     * @type {any}
     * @memberof GETLineImportStatus
     */
    'status': any;
    /**
     * 
     * @type {LineStatus}
     * @memberof GETLineImportStatus
     */
    'parsed_status': LineStatus;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETLineImportStatus
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {any}
     * @memberof GETLineImportStatus
     */
    'last_free_date'?: any;
    /**
     * 
     * @type {PortalName}
     * @memberof GETLineImportStatus
     */
    'source': PortalName;
    /**
     * When this data was observed
     * @type {any}
     * @memberof GETLineImportStatus
     */
    'observed': any;
    /**
     * When we last received data
     * @type {string}
     * @memberof GETLineImportStatus
     */
    'last_observed': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETLineImportStatus
     */
    'parsed_shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETLineImportStatus
     */
    'parsed_container_type'?: ContainerType;
    /**
     * 
     * @type {any}
     * @memberof GETLineImportStatus
     */
    'master_bill_of_lading'?: any;
    /**
     * Raw data from the shipping line. Only populated when include_raw_data is requested
     * @type {{ [key: string]: any; }}
     * @memberof GETLineImportStatus
     */
    'raw_data'?: { [key: string]: any; };
}


/**
 * Location shape returned in container API responses.
 * @export
 * @interface GETLocation
 */
export interface GETLocation {
    /**
     * Human-readable label for this location, e.g. \'Reconnect Carson Yard\'. Used as the dedup key within an organization.
     * @type {string}
     * @memberof GETLocation
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof GETLocation
     */
    'address'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETLocation
     */
    'city'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETLocation
     */
    'state'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETLocation
     */
    'zip'?: string;
    /**
     * Latitude in decimal degrees (WGS84).
     * @type {number}
     * @memberof GETLocation
     */
    'latitude'?: number;
    /**
     * Longitude in decimal degrees (WGS84).
     * @type {number}
     * @memberof GETLocation
     */
    'longitude'?: number;
    /**
     * Primary key
     * @type {number}
     * @memberof GETLocation
     */
    'id': number;
}
/**
 * 
 * @export
 * @interface GETNetworkRequestMatcher
 */
export interface GETNetworkRequestMatcher {
    /**
     * Request URL must start with this prefix
     * @type {string}
     * @memberof GETNetworkRequestMatcher
     */
    'url_prefix': string;
    /**
     * Request body must contain ALL of these substrings
     * @type {Array<string>}
     * @memberof GETNetworkRequestMatcher
     */
    'request_body_contains'?: Array<string>;
}
/**
 * 
 * @export
 * @interface GETNextAvailable
 */
export interface GETNextAvailable {
    /**
     * 
     * @type {string}
     * @memberof GETNextAvailable
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof GETNextAvailable
     */
    'observed': string;
    /**
     * 
     * @type {boolean}
     * @memberof GETNextAvailable
     */
    'has_availability': boolean;
}
/**
 * 
 * @export
 * @interface GETNote
 */
export interface GETNote {
    /**
     * 
     * @type {number}
     * @memberof GETNote
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETNote
     */
    'body': string;
    /**
     * 
     * @type {string}
     * @memberof GETNote
     */
    'time': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETNote
     */
    'user'?: SimpleUser;
    /**
     * 
     * @type {boolean}
     * @memberof GETNote
     */
    'edited': boolean;
    /**
     * 
     * @type {string}
     * @memberof GETNote
     */
    'edited_at'?: string;
}
/**
 * Containers public org configuration
 * @export
 * @interface GETOrgConfig
 */
export interface GETOrgConfig {
    /**
     * 
     * @type {Array<AutoBookRequestPreset>}
     * @memberof GETOrgConfig
     */
    'auto_book_presets'?: Array<AutoBookRequestPreset>;
    /**
     * 
     * @type {Array<AutoBookEmptyRequestPreset>}
     * @memberof GETOrgConfig
     */
    'auto_book_empty_presets'?: Array<AutoBookEmptyRequestPreset>;
    /**
     * Additional email addresses that will receive auto-book notifications for LA/LB terminals
     * @type {Array<string>}
     * @memberof GETOrgConfig
     */
    'auto_book_notification_emails'?: Array<string>;
    /**
     * Additional email addresses that will receive auto-book notifications for Oakland terminals
     * @type {Array<string>}
     * @memberof GETOrgConfig
     */
    'auto_book_notification_emails_oak'?: Array<string>;
    /**
     * Additional email addresses that will receive every ERD / First Available Date / cutoff change alert for vessels of the org\'s active export bookings at LA/LB terminals
     * @type {Array<string>}
     * @memberof GETOrgConfig
     */
    'export_vessel_schedule_notification_emails'?: Array<string>;
    /**
     * Additional email addresses that will receive every ERD / First Available Date / cutoff change alert for vessels of the org\'s active export bookings at Oakland terminals
     * @type {Array<string>}
     * @memberof GETOrgConfig
     */
    'export_vessel_schedule_notification_emails_oak'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof GETOrgConfig
     */
    'prefill_apm_dummy_license_plates'?: boolean;
    /**
     * 
     * @type {Array<TerminalOrgConfig>}
     * @memberof GETOrgConfig
     */
    'terminals'?: Array<TerminalOrgConfig>;
    /**
     * Default chassis type preference for the organization. Applies to appointment booking for terminals that allow specifying what type of chassis will be used. User may always set a different value when booking. If set to `null`, frontend will uses the last value user selected or \'pool\'
     * @type {string}
     * @memberof GETOrgConfig
     */
    'default_chassis_type'?: GETOrgConfigDefaultChassisTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof GETOrgConfig
     */
    'only_management_users_can_edit_connections'?: boolean;
}

export const GETOrgConfigDefaultChassisTypeEnum = {
    Own: 'own',
    Pool: 'pool'
} as const;

export type GETOrgConfigDefaultChassisTypeEnum = typeof GETOrgConfigDefaultChassisTypeEnum[keyof typeof GETOrgConfigDefaultChassisTypeEnum];

/**
 * 
 * @export
 * @interface GETOrganization
 */
export interface GETOrganization {
    /**
     * 
     * @type {number}
     * @memberof GETOrganization
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETOrganization
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof GETOrganization
     */
    'scac'?: string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof GETOrganization
     */
    'role'?: UserRoleType;
    /**
     * 
     * @type {GETOrgConfig}
     * @memberof GETOrganization
     */
    'config': GETOrgConfig;
    /**
     * 
     * @type {Array<OrganizationSubscription>}
     * @memberof GETOrganization
     */
    'subscriptions': Array<OrganizationSubscription>;
}


/**
 * 
 * @export
 * @interface GETPierPass
 */
export interface GETPierPass {
    /**
     * 
     * @type {number}
     * @memberof GETPierPass
     */
    'id': number;
    /**
     * Terminal(s) where this container is scheduled to arrive at. Sometimes PierPass will list multiple terminals if there is a vessel re-routed, that\'s why this is an array
     * @type {Array<TerminalName>}
     * @memberof GETPierPass
     */
    'terminals': Array<TerminalName>;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof GETPierPass
     */
    'container_number': string;
    /**
     * If true, another PierPass account has \"claimed\"/paid the fee for this container. That can be totally fine because often the customer, a broker or some other party ends up paying the fee.
     * @type {boolean}
     * @memberof GETPierPass
     */
    'claimed_by_other_company': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETPierPass
     */
    'fee_paid': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETPierPass
     */
    'has_hold': boolean;
    /**
     * When this particular data was first observed. Effectively means last changed
     * @type {string}
     * @memberof GETPierPass
     */
    'observed': string;
    /**
     * When this particular data was last observed
     * @type {string}
     * @memberof GETPierPass
     */
    'last_observed'?: string;
    /**
     * Raw data from PierPass. Only populated when include_raw_data is requested
     * @type {{ [key: string]: any; }}
     * @memberof GETPierPass
     */
    'raw_data'?: { [key: string]: any; };
}
/**
 * 
 * @export
 * @interface GETPortalMetadata
 */
export interface GETPortalMetadata {
    /**
     * 
     * @type {PortalName}
     * @memberof GETPortalMetadata
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {{ [key: string]: GETActionMetadata; }}
     * @memberof GETPortalMetadata
     */
    'available_actions'?: { [key: string]: GETActionMetadata; };
}


/**
 * 
 * @export
 * @interface GETShippingLine
 */
export interface GETShippingLine {
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETShippingLine
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {string}
     * @memberof GETShippingLine
     */
    'short_desc': string;
    /**
     * 
     * @type {string}
     * @memberof GETShippingLine
     */
    'long_desc': string;
    /**
     * 
     * @type {Url}
     * @memberof GETShippingLine
     */
    'url': Url;
    /**
     * False when the line doesn\'t share its LFD with terminals, so the displayed LFD can be later than when demurrage actually starts unless we have a line LFD (`line_last_free_date`)
     * @type {boolean}
     * @memberof GETShippingLine
     */
    'shares_lfd_with_terminals': boolean;
    /**
     * Page where a user can look up the line\'s LFD, where one exists
     * @type {string}
     * @memberof GETShippingLine
     */
    'lfd_lookup_url': string;
    /**
     * Warning to show when this line\'s LFD needs checking
     * @type {string}
     * @memberof GETShippingLine
     */
    'lfd_gap_note': string;
    /**
     * Container states in which a missing line LFD needs checking. The `needs_line_lfd_check` container filter uses the same list. Empty for lines that share their LFD
     * @type {Array<ContainerWatchState>}
     * @memberof GETShippingLine
     */
    'lfd_check_states': Array<ContainerWatchState>;
}


/**
 * 
 * @export
 * @interface GETSlotWindow
 */
export interface GETSlotWindow {
    /**
     * 
     * @type {string}
     * @memberof GETSlotWindow
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof GETSlotWindow
     */
    'window_end'?: string;
}
/**
 * 
 * @export
 * @interface GETStateCount
 */
export interface GETStateCount {
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof GETStateCount
     */
    'state': ContainerWatchState;
    /**
     * 
     * @type {number}
     * @memberof GETStateCount
     */
    'count': number;
}


/**
 * 
 * @export
 * @interface GETTag
 */
export interface GETTag {
    /**
     * 
     * @type {string}
     * @memberof GETTag
     */
    'name': string;
    /**
     * 
     * @type {boolean}
     * @memberof GETTag
     */
    'default': boolean;
}
/**
 * 
 * @export
 * @interface GETTerminalAllocationMethod
 */
export interface GETTerminalAllocationMethod {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETTerminalAllocationMethod
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {boolean}
     * @memberof GETTerminalAllocationMethod
     */
    'by_shipping_line': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETTerminalAllocationMethod
     */
    'by_container_type': boolean;
}


/**
 * 
 * @export
 * @interface GETTerminalCount
 */
export interface GETTerminalCount {
    /**
     * 
     * @type {TerminalName}
     * @memberof GETTerminalCount
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {number}
     * @memberof GETTerminalCount
     */
    'count': number;
}


/**
 * 
 * @export
 * @interface GETTimeBucketAppointmentStatusCount
 */
export interface GETTimeBucketAppointmentStatusCount {
    /**
     * 
     * @type {number}
     * @memberof GETTimeBucketAppointmentStatusCount
     */
    'count': number;
    /**
     * 
     * @type {string}
     * @memberof GETTimeBucketAppointmentStatusCount
     */
    'time_bucket_start': string;
    /**
     * 
     * @type {AppointmentStatus}
     * @memberof GETTimeBucketAppointmentStatusCount
     */
    'status': AppointmentStatus;
}


/**
 * 
 * @export
 * @interface GETToken
 */
export interface GETToken {
    /**
     * 
     * @type {string}
     * @memberof GETToken
     */
    'access_token': string;
    /**
     * 
     * @type {string}
     * @memberof GETToken
     */
    'token_type': string;
    /**
     * 
     * @type {number}
     * @memberof GETToken
     */
    'user_id': number;
    /**
     * 
     * @type {number}
     * @memberof GETToken
     */
    'organization_id': number;
}
/**
 * 
 * @export
 * @interface GETTruck
 */
export interface GETTruck {
    /**
     * 
     * @type {string}
     * @memberof GETTruck
     */
    'name'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETTruck
     */
    'license_plate_number': string;
    /**
     * 
     * @type {boolean}
     * @memberof GETTruck
     */
    'archived'?: boolean;
    /**
     * Three-state green/EV (APM Green Lane) status: None = unknown (never determined), True = confirmed green (APM GreenTruck=\'Y\' or user-specified), False = confirmed not green. Auto Book excludes only confirmed-green plates at green-lane terminals.
     * @type {boolean}
     * @memberof GETTruck
     */
    'is_green'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof GETTruck
     */
    'id': number;
}
/**
 * 
 * @export
 * @interface GETTruckLicensePlate
 */
export interface GETTruckLicensePlate {
    /**
     * 
     * @type {string}
     * @memberof GETTruckLicensePlate
     */
    'plate': string;
    /**
     * Number of completed appointments that this license plate has been used for
     * @type {number}
     * @memberof GETTruckLicensePlate
     */
    'num_uses': number;
    /**
     * Name of the truck associated with this license plate
     * @type {string}
     * @memberof GETTruckLicensePlate
     */
    'name'?: string;
    /**
     * Three-state green/EV (APM Green Lane) status: None = unknown, True = confirmed green, False = confirmed not green.
     * @type {boolean}
     * @memberof GETTruckLicensePlate
     */
    'is_green'?: boolean;
}
/**
 * 
 * @export
 * @interface GETTruckWithStats
 */
export interface GETTruckWithStats {
    /**
     * 
     * @type {GETTruck}
     * @memberof GETTruckWithStats
     */
    'truck': GETTruck;
    /**
     * Number of completed appointments that this license plate has been used for
     * @type {number}
     * @memberof GETTruckWithStats
     */
    'num_completed': number;
    /**
     * Number of scheduled appointments for this license plate
     * @type {number}
     * @memberof GETTruckWithStats
     */
    'num_scheduled': number;
    /**
     * Number of missed appointments for this license plate
     * @type {number}
     * @memberof GETTruckWithStats
     */
    'num_missed': number;
}
/**
 * 
 * @export
 * @interface GETUser
 */
export interface GETUser {
    /**
     * 
     * @type {number}
     * @memberof GETUser
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETUser
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETUser
     */
    'last_name'?: string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof GETUser
     */
    'role'?: UserRoleType;
    /**
     * 
     * @type {string}
     * @memberof GETUser
     */
    'email': string;
    /**
     * 
     * @type {boolean}
     * @memberof GETUser
     */
    'pending': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof GETUser
     */
    'is_active_user': boolean;
}


/**
 * 
 * @export
 * @interface GETUserInfo
 */
export interface GETUserInfo {
    /**
     * 
     * @type {number}
     * @memberof GETUserInfo
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETUserInfo
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof GETUserInfo
     */
    'last_name': string;
    /**
     * 
     * @type {string}
     * @memberof GETUserInfo
     */
    'email': string;
    /**
     * The org this token is for
     * @type {GETOrganization}
     * @memberof GETUserInfo
     */
    'current_organization': GETOrganization;
    /**
     * List of all organizations the current user is a member of
     * @type {Array<GETOrganization>}
     * @memberof GETUserInfo
     */
    'organizations': Array<GETOrganization>;
    /**
     * Flags for beta features that are enabled for specific users for testing
     * @type {Array<string>}
     * @memberof GETUserInfo
     */
    'feature_flags': Array<string>;
    /**
     * Individual user preferences
     * @type {UserPreferences}
     * @memberof GETUserInfo
     */
    'preferences': UserPreferences;
}
/**
 * 
 * @export
 * @interface GETVesselCount
 */
export interface GETVesselCount {
    /**
     * 
     * @type {number}
     * @memberof GETVesselCount
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETVesselCount
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof GETVesselCount
     */
    'imo_number': number;
    /**
     * 
     * @type {number}
     * @memberof GETVesselCount
     */
    'count': number;
}
/**
 * 
 * @export
 * @interface GETVesselSchedule
 */
export interface GETVesselSchedule {
    /**
     * 
     * @type {number}
     * @memberof GETVesselSchedule
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'eta': string;
    /**
     * 
     * @type {number}
     * @memberof GETVesselSchedule
     */
    'kilometers_to_destination'?: number;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof GETVesselSchedule
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'last_observed'?: string;
    /**
     * 
     * @type {PortalName}
     * @memberof GETVesselSchedule
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETVesselSchedule
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'voyage_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'erd'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'first_available_date'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'cargo_cutoff'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'documentation_cutoff'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'reefer_cutoff'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'hazmat_cutoff'?: string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETVesselSchedule
     */
    'shipping_line'?: ShippingLine;
    /**
     * When we stored the most recent ERD or cutoff change, if that happened within the recent-change window. Only filled by GET /vessels/etas/ and the vessel ETA update event.
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'changed_at'?: string;
    /**
     * ERD before the most recent change, if it changed
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'previous_erd'?: string;
    /**
     * Cargo cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'previous_cargo_cutoff'?: string;
    /**
     * Documentation cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'previous_documentation_cutoff'?: string;
    /**
     * Reefer cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'previous_reefer_cutoff'?: string;
    /**
     * Hazmat cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof GETVesselSchedule
     */
    'previous_hazmat_cutoff'?: string;
}


/**
 * 
 * @export
 * @interface GETVesselSchedules
 */
export interface GETVesselSchedules {
    /**
     * 
     * @type {string}
     * @memberof GETVesselSchedules
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof GETVesselSchedules
     */
    'vessel_id': number;
    /**
     * 
     * @type {number}
     * @memberof GETVesselSchedules
     */
    'id': number;
    /**
     * 
     * @type {number}
     * @memberof GETVesselSchedules
     */
    'imo_number': number;
    /**
     * 
     * @type {GETVesselSchedule}
     * @memberof GETVesselSchedules
     */
    'eta': GETVesselSchedule;
    /**
     * 
     * @type {Array<GETVesselSchedule>}
     * @memberof GETVesselSchedules
     */
    'other_etas': Array<GETVesselSchedule>;
}
/**
 * 
 * @export
 * @interface GETWatch
 */
export interface GETWatch {
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'container_number': string;
    /**
     * 
     * @type {SavedCustomer}
     * @memberof GETWatch
     */
    'customer'?: SavedCustomer;
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof GETWatch
     */
    'state': ContainerWatchState;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'master_bill_of_lading'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GETWatch
     */
    'last_related_terminal'?: TerminalName;
    /**
     * 
     * @type {PortComplex}
     * @memberof GETWatch
     */
    'port_complex': PortComplex;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GETWatch
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GETWatch
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'import_out_gated_time'?: string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GETWatch
     */
    'added_by_user'?: SimpleUser;
    /**
     * 
     * @type {Array<SimpleUser>}
     * @memberof GETWatch
     */
    'assigned_users': Array<SimpleUser>;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'discharged_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'import_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'import_appointment_pickup_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'empty_appointment_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'empty_in_gated_time'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'import_last_free_date'?: string;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'import_line_last_free_date'?: string;
    /**
     * 
     * @type {Array<string>}
     * @memberof GETWatch
     */
    'tags': Array<string>;
    /**
     * 
     * @type {string}
     * @memberof GETWatch
     */
    'vessel'?: string;
    /**
     * 
     * @type {number}
     * @memberof GETWatch
     */
    'vessel_id'?: number;
}


/**
 * 
 * @export
 * @interface GateScheduleCheckedEvent
 */
export interface GateScheduleCheckedEvent {
    /**
     * 
     * @type {string}
     * @memberof GateScheduleCheckedEvent
     */
    'event_name'?: GateScheduleCheckedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleCheckedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleCheckedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleCheckedEvent
     */
    'check_time': string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleCheckedEvent
     */
    'first_observed': string;
    /**
     * 
     * @type {TerminalName}
     * @memberof GateScheduleCheckedEvent
     */
    'terminal': TerminalName;
}

export const GateScheduleCheckedEventEventNameEnum = {
    GateScheduleChecked: 'gate_schedule_checked'
} as const;

export type GateScheduleCheckedEventEventNameEnum = typeof GateScheduleCheckedEventEventNameEnum[keyof typeof GateScheduleCheckedEventEventNameEnum];

/**
 * 
 * @export
 * @interface GateScheduleUpdatedEvent
 */
export interface GateScheduleUpdatedEvent {
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'event_name'?: GateScheduleUpdatedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Array<GETGateScheduleEntryResponse>}
     * @memberof GateScheduleUpdatedEvent
     */
    'entries': Array<GETGateScheduleEntryResponse>;
    /**
     * 
     * @type {TerminalName}
     * @memberof GateScheduleUpdatedEvent
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'screenshot_url': string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'first_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'last_observed': string;
    /**
     * 
     * @type {string}
     * @memberof GateScheduleUpdatedEvent
     */
    'last_parsed_time'?: string;
}

export const GateScheduleUpdatedEventEventNameEnum = {
    GateSchedulesUpdated: 'gate_schedules_updated'
} as const;

export type GateScheduleUpdatedEventEventNameEnum = typeof GateScheduleUpdatedEventEventNameEnum[keyof typeof GateScheduleUpdatedEventEventNameEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const GateStatus = {
    Open: 'open',
    Closed: 'closed'
} as const;

export type GateStatus = typeof GateStatus[keyof typeof GateStatus];


/**
 * 
 * @export
 * @interface GateTransaction
 */
export interface GateTransaction {
    /**
     * 
     * @type {TerminalName}
     * @memberof GateTransaction
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {PortalName}
     * @memberof GateTransaction
     */
    'portal_name'?: PortalName;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof GateTransaction
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'time': string;
    /**
     * 
     * @type {boolean}
     * @memberof GateTransaction
     */
    'loaded': boolean;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof GateTransaction
     */
    'direction': TransactionDirection;
    /**
     * Terminal\'s unique identifier for the transaction, like ticket #
     * @type {string}
     * @memberof GateTransaction
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'drivers_license_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'truck_rfid_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'truck_license_plate_number'?: string;
    /**
     * A place to put extra data which seems important but doesn\'t fit in another field. Items added here will be presented to users
     * @type {{ [key: string]: any; }}
     * @memberof GateTransaction
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof GateTransaction
     */
    'raw_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof GateTransaction
     */
    'observed': string;
}


/**
 * Gate transaction with raw_data excluded from serialization
 * @export
 * @interface GateTransactionNoRawData
 */
export interface GateTransactionNoRawData {
    /**
     * 
     * @type {TerminalName}
     * @memberof GateTransactionNoRawData
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {PortalName}
     * @memberof GateTransactionNoRawData
     */
    'portal_name'?: PortalName;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'time': string;
    /**
     * 
     * @type {boolean}
     * @memberof GateTransactionNoRawData
     */
    'loaded': boolean;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof GateTransactionNoRawData
     */
    'direction': TransactionDirection;
    /**
     * Terminal\'s unique identifier for the transaction, like ticket #
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'drivers_license_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'truck_rfid_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'truck_license_plate_number'?: string;
    /**
     * A place to put extra data which seems important but doesn\'t fit in another field. Items added here will be presented to users
     * @type {{ [key: string]: any; }}
     * @memberof GateTransactionNoRawData
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof GateTransactionNoRawData
     */
    'observed': string;
}


/**
 * 
 * @export
 * @interface GeotabConfig
 */
export interface GeotabConfig {
    /**
     * 
     * @type {string}
     * @memberof GeotabConfig
     */
    'config_type'?: GeotabConfigConfigTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof GeotabConfig
     */
    'user': string;
    /**
     * 
     * @type {string}
     * @memberof GeotabConfig
     */
    'password': string;
    /**
     * 
     * @type {string}
     * @memberof GeotabConfig
     */
    'database'?: string;
}

export const GeotabConfigConfigTypeEnum = {
    Geotab: 'geotab'
} as const;

export type GeotabConfigConfigTypeEnum = typeof GeotabConfigConfigTypeEnum[keyof typeof GeotabConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
 */
export interface GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner {
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'id': any;
    /**
     * 
     * @type {TerminalName}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'container_number': any;
    /**
     * 
     * @type {ActionStatus}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'error_message'?: any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'created': any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'action_type': GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInnerActionTypeEnum;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'window_start': any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'reschedule_cancellation_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'reschedule_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'appointment_verification_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'organization_id': any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'connection_id': any;
    /**
     * 
     * @type {PortalName}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'requests_log_link'?: any;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'booking_number': any;
    /**
     * 
     * @type {ShippingLine}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {any}
     * @memberof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner
     */
    'own_chassis'?: any;
}

export const GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInnerActionTypeEnum = {
    BookEmptyPickup: 'book_empty_pickup'
} as const;

export type GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInnerActionTypeEnum = typeof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInnerActionTypeEnum[keyof typeof GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInnerActionTypeEnum];

/**
 * 
 * @export
 * @interface GlobalNotification
 */
export interface GlobalNotification {
    /**
     * 
     * @type {number}
     * @memberof GlobalNotification
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotification
     */
    'level': GlobalNotificationLevelEnum;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotification
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotification
     */
    'message'?: string;
    /**
     * How the frontend should present this notification: a banner (default) or a forced popup dialog.
     * @type {GlobalNotificationDisplayType}
     * @memberof GlobalNotification
     */
    'display_type'?: GlobalNotificationDisplayType;
    /**
     * Identifier of a frontend product tour to offer alongside this notification. Free text: the frontend owns the tour registry, so a key it does not recognize (or a null value) simply means no tour is offered.
     * @type {string}
     * @memberof GlobalNotification
     */
    'tour_key'?: string;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotification
     */
    'active_from': string;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotification
     */
    'active_to'?: string;
}

export const GlobalNotificationLevelEnum = {
    Success: 'success',
    Warning: 'warning',
    Info: 'info',
    Error: 'error'
} as const;

export type GlobalNotificationLevelEnum = typeof GlobalNotificationLevelEnum[keyof typeof GlobalNotificationLevelEnum];

/**
 * How the frontend should present a global notification.
 * @export
 * @enum {string}
 */

export const GlobalNotificationDisplayType = {
    Banner: 'banner',
    Popup: 'popup'
} as const;

export type GlobalNotificationDisplayType = typeof GlobalNotificationDisplayType[keyof typeof GlobalNotificationDisplayType];


/**
 * 
 * @export
 * @interface GlobalNotificationEvent
 */
export interface GlobalNotificationEvent {
    /**
     * 
     * @type {string}
     * @memberof GlobalNotificationEvent
     */
    'event_name'?: GlobalNotificationEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotificationEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof GlobalNotificationEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Notification}
     * @memberof GlobalNotificationEvent
     */
    'notification': Notification;
}

export const GlobalNotificationEventEventNameEnum = {
    GlobalNotification: 'global_notification'
} as const;

export type GlobalNotificationEventEventNameEnum = typeof GlobalNotificationEventEventNameEnum[keyof typeof GlobalNotificationEventEventNameEnum];

/**
 * 
 * @export
 * @interface HTTPValidationError
 */
export interface HTTPValidationError {
    /**
     * 
     * @type {Array<ValidationError>}
     * @memberof HTTPValidationError
     */
    'detail'?: Array<ValidationError>;
}
/**
 * 
 * @export
 * @interface HistoryEvent
 */
export interface HistoryEvent {
    /**
     * 
     * @type {ContainerEventType}
     * @memberof HistoryEvent
     */
    'type': ContainerEventType;
    /**
     * 
     * @type {string}
     * @memberof HistoryEvent
     */
    'time': string;
    /**
     * 
     * @type {Array<Change>}
     * @memberof HistoryEvent
     */
    'changes': Array<Change>;
    /**
     * 
     * @type {PortalName}
     * @memberof HistoryEvent
     */
    'source'?: PortalName;
}


/**
 * 
 * @export
 * @interface HistoryPhaseGroup
 */
export interface HistoryPhaseGroup {
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof HistoryPhaseGroup
     */
    'cycle_state': ContainerWatchState;
    /**
     * 
     * @type {TerminalName}
     * @memberof HistoryPhaseGroup
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {Array<HistoryEvent>}
     * @memberof HistoryPhaseGroup
     */
    'events': Array<HistoryEvent>;
    /**
     * 
     * @type {string}
     * @memberof HistoryPhaseGroup
     */
    'entered_phase': string;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const HoldType = {
    LineHold: 'Line Hold',
    TerminalHold: 'Terminal Hold',
    CustomsHold: 'Customs Hold',
    TmfHold: 'TMF Hold',
    CtfHold: 'CTF Hold',
    UsdaHold: 'USDA Hold',
    CustomsExamHold: 'Customs Exam Hold',
    DemurrageHold: 'Demurrage Hold',
    PermitToTransferHold: 'Permit to Transfer Hold',
    NoBillOfLadingHold: 'No Bill of Lading Hold',
    FreightHold: 'Freight Hold',
    FdaHold: 'FDA Hold',
    NoReturn: 'No Return',
    NotTruckBound: 'Not Truck Bound'
} as const;

export type HoldType = typeof HoldType[keyof typeof HoldType];


/**
 * 
 * @export
 * @interface ImportAppointmentBookedEvent
 */
export interface ImportAppointmentBookedEvent {
    /**
     * 
     * @type {SimpleUser}
     * @memberof ImportAppointmentBookedEvent
     */
    'requested_by'?: SimpleUser;
    /**
     * The id of the action request. Can be used with patch /appointments/booking/ to mark as entered in TMS.
     * @type {number}
     * @memberof ImportAppointmentBookedEvent
     */
    'action_request_id': number;
    /**
     * The appointment found after completing the action. Will be None if the appointment was not found.
     * @type {WebhookAppointment}
     * @memberof ImportAppointmentBookedEvent
     */
    'appointment'?: WebhookAppointment;
    /**
     * The url to send a patch request to mark the action as entered TMS. Send with entered_in_tms = True in the body.
     * @type {string}
     * @memberof ImportAppointmentBookedEvent
     */
    'mark_entered_in_tms_url': string;
    /**
     * The container number
     * @type {string}
     * @memberof ImportAppointmentBookedEvent
     */
    'container_number': string;
    /**
     * The terminal the appointment is at
     * @type {TerminalName}
     * @memberof ImportAppointmentBookedEvent
     */
    'terminal': TerminalName;
    /**
     * Identifies the event type. Also available in the `event_type` field
     * @type {string}
     * @memberof ImportAppointmentBookedEvent
     */
    'type'?: ImportAppointmentBookedEventTypeEnum;
}

export const ImportAppointmentBookedEventTypeEnum = {
    ImportAppointmentBooked: 'import_appointment_booked'
} as const;

export type ImportAppointmentBookedEventTypeEnum = typeof ImportAppointmentBookedEventTypeEnum[keyof typeof ImportAppointmentBookedEventTypeEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const ImportLocation = {
    OnVessel: 'on_vessel',
    InTerminal: 'in_terminal',
    OutGated: 'out_gated',
    Rail: 'rail'
} as const;

export type ImportLocation = typeof ImportLocation[keyof typeof ImportLocation];


/**
 * 
 * @export
 * @interface ImportSlotsUpdatedEvent
 */
export interface ImportSlotsUpdatedEvent {
    /**
     * 
     * @type {string}
     * @memberof ImportSlotsUpdatedEvent
     */
    'event_name'?: ImportSlotsUpdatedEventEventNameEnum;
    /**
     * Channel name is dynamic and should be in format: `private-import-slots-terminal-terminal_block_availability_key`
     * @type {string}
     * @memberof ImportSlotsUpdatedEvent
     */
    'channel': string;
    /**
     * 
     * @type {string}
     * @memberof ImportSlotsUpdatedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {GETContainerAvailability}
     * @memberof ImportSlotsUpdatedEvent
     */
    'reading': GETContainerAvailability;
}

export const ImportSlotsUpdatedEventEventNameEnum = {
    ImportSlotsUpdated: 'import_slots_updated'
} as const;

export type ImportSlotsUpdatedEventEventNameEnum = typeof ImportSlotsUpdatedEventEventNameEnum[keyof typeof ImportSlotsUpdatedEventEventNameEnum];

/**
 * \"Checked\" means checked and not observed changed.
 * @export
 * @interface ImportStatusCheckedNotification
 */
export interface ImportStatusCheckedNotification {
    /**
     * Keys are import_status.id, values are new last observed/checked time
     * @type {{ [key: string]: string; }}
     * @memberof ImportStatusCheckedNotification
     */
    'last_checked_by_import_status_ids': { [key: string]: string; };
}
/**
 * 
 * @export
 * @interface LastFreeDate
 */
export interface LastFreeDate {
    /**
     * 
     * @type {string}
     * @memberof LastFreeDate
     */
    'date': string;
    /**
     * 
     * @type {string}
     * @memberof LastFreeDate
     */
    'description': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const LicensePlateOrigin = {
    UserManual: 'user_manual',
    ManualRandom: 'manual_random',
    AutoBookUserSpecified: 'auto_book_user_specified',
    AutoBookRandom: 'auto_book_random',
    AutoBookPlatelessFallback: 'auto_book_plateless_fallback',
    AutoSetWorkflow: 'auto_set_workflow',
    Unknown: 'unknown'
} as const;

export type LicensePlateOrigin = typeof LicensePlateOrigin[keyof typeof LicensePlateOrigin];


/**
 * 
 * @export
 * @enum {string}
 */

export const LineStatus = {
    OnVessel: 'on_vessel',
    Discharged: 'discharged',
    OutGated: 'out_gated',
    Terminated: 'terminated'
} as const;

export type LineStatus = typeof LineStatus[keyof typeof LineStatus];


/**
 * 
 * @export
 * @interface LinkedAppointment
 */
export interface LinkedAppointment {
    /**
     * 
     * @type {string}
     * @memberof LinkedAppointment
     */
    'terminal_reference': string;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof LinkedAppointment
     */
    'container_number': string;
}
/**
 * 
 * @export
 * @interface ListEventTypesWsEventsTypesListGet200ResponseInner
 */
export interface ListEventTypesWsEventsTypesListGet200ResponseInner {
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'event_name'?: ListEventTypesWsEventsTypesListGet200ResponseInnerEventNameEnum;
    /**
     * Channel name is dynamic and should be in format: `private-import-slots-terminal-terminal_block_availability_key`
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'channel': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'message_id'?: any;
    /**
     * 
     * @type {GETCameraImage}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'image': GETCameraImage;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'rules': any;
    /**
     * 
     * @type {TerminalName}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'reading_first_observed': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'reading_last_parsed': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'check_time': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'first_observed': any;
    /**
     * 
     * @type {GETContainerAvailability}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'reading': GETContainerAvailability;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'has_availability': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'slots': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'observed': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'entries': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'screenshot_url': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'last_observed': any;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'last_parsed_time'?: any;
    /**
     * 
     * @type {Notification}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'notification': Notification;
    /**
     * 
     * @type {any}
     * @memberof ListEventTypesWsEventsTypesListGet200ResponseInner
     */
    'etas': any;
}

export const ListEventTypesWsEventsTypesListGet200ResponseInnerEventNameEnum = {
    ImportSlotsUpdated: 'import_slots_updated'
} as const;

export type ListEventTypesWsEventsTypesListGet200ResponseInnerEventNameEnum = typeof ListEventTypesWsEventsTypesListGet200ResponseInnerEventNameEnum[keyof typeof ListEventTypesWsEventsTypesListGet200ResponseInnerEventNameEnum];

/**
 * 
 * @export
 * @interface Location
 */
export interface Location {
    /**
     * Human-readable label for this location, e.g. \'Reconnect Carson Yard\'. Used as the dedup key within an organization.
     * @type {string}
     * @memberof Location
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof Location
     */
    'address'?: string;
    /**
     * 
     * @type {string}
     * @memberof Location
     */
    'city'?: string;
    /**
     * 
     * @type {string}
     * @memberof Location
     */
    'state'?: string;
    /**
     * 
     * @type {string}
     * @memberof Location
     */
    'zip'?: string;
    /**
     * Latitude in decimal degrees (WGS84).
     * @type {number}
     * @memberof Location
     */
    'latitude'?: number;
    /**
     * Longitude in decimal degrees (WGS84).
     * @type {number}
     * @memberof Location
     */
    'longitude'?: number;
}
/**
 * The vessel resolved from an export booking\'s free-text vessel name.  Answers \'did we identify this vessel?\' — schedule dates live in vessel_schedules. Present whenever the vessel name resolves to a known vessel in our data, even if no schedule exists for any of the booking\'s terminals.
 * @export
 * @interface MatchedVesselForExport
 */
export interface MatchedVesselForExport {
    /**
     * Vessel id
     * @type {number}
     * @memberof MatchedVesselForExport
     */
    'id': number;
    /**
     * Canonical vessel name as stored in our vessels table
     * @type {string}
     * @memberof MatchedVesselForExport
     */
    'name': string;
    /**
     * IMO number uniquely identifying the vessel
     * @type {number}
     * @memberof MatchedVesselForExport
     */
    'imo_number': number;
}
/**
 * 
 * @export
 * @interface Message
 */
export interface Message {
    /**
     * 
     * @type {string}
     * @memberof Message
     */
    'message': string;
}
/**
 * 
 * @export
 * @interface ModelObject
 */
export interface ModelObject {
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'window_start': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'reschedule_cancellation_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'reschedule_request_id'?: any;
    /**
     * 
     * @type {ActionStatus}
     * @memberof ModelObject
     */
    'status': ActionStatus;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'id': any;
    /**
     * 
     * @type {TerminalName}
     * @memberof ModelObject
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'container_number': any;
    /**
     * 
     * @type {SimpleUser}
     * @memberof ModelObject
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof ModelObject
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {any}
     * @memberof ModelObject
     */
    'error_message'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'created': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'appointment_verification_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'organization_id': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'connection_id': any;
    /**
     * 
     * @type {PortalName}
     * @memberof ModelObject
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'requests_log_link'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'action_type'?: ModelObjectActionTypeEnum;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'auto_book_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'appointment_id': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'terminal_block'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'truck_license_plate': any;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof ModelObject
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'master_bill_of_lading': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'updated': any;
    /**
     * 
     * @type {ExportEmptyPickupAppointmentSlot}
     * @memberof ModelObject
     */
    'appointment_slot': ExportEmptyPickupAppointmentSlot;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'own_chassis': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'new_appointment_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'booking_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'cancellation_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'shipping_line': any;
    /**
     * 
     * @type {ContainerType}
     * @memberof ModelObject
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'chassis_number': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'auto_book_empty_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'empty_reschedule_request_id'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'empty_container_number': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'booking_number': any;
    /**
     * 
     * @type {ActionRequest}
     * @memberof ModelObject
     */
    'action_request': ActionRequest;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'appointment_issues'?: any;
    /**
     * vessel_schedules.id of the changed row. The frontend builds `?selected_schedule=<id>` from it to open the vessel\'s change history
     * @type {any}
     * @memberof ModelObject
     */
    'schedule_id': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'vessel_name': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'imo_number': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'voyage': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'changed_at': any;
    /**
     * The recipient\'s bookings sailing on this vessel and voyage. Recipients are found through their bookings (DEV-8311), so an empty list means a caller bug, not a recipient to alert
     * @type {any}
     * @memberof ModelObject
     */
    'booking_numbers': any;
    /**
     * Each changed field with its old and new value, in the same shape as the vessel change-history endpoint
     * @type {any}
     * @memberof ModelObject
     */
    'changes': any;
    /**
     * Port complex to which this capacity set belongs
     * @type {PortComplex}
     * @memberof ModelObject
     */
    'port_complex': PortComplex;
    /**
     * Customer to which this capacity set belongs. Must be null if this capacity set is a global capacity set
     * @type {SavedCustomer}
     * @memberof ModelObject
     */
    'customer': SavedCustomer;
    /**
     * 
     * @type {GETPierPass}
     * @memberof ModelObject
     */
    'pier_pass'?: GETPierPass;
    /**
     * 
     * @type {GETPierPass}
     * @memberof ModelObject
     */
    'clean_truck_fee'?: GETPierPass;
    /**
     * Will always be `null` if `include_import_statuses=false`
     * @type {GETImportStatus}
     * @memberof ModelObject
     */
    'import_status'?: GETImportStatus;
    /**
     * List of all import status data, one per import status source.  Includes all statuses from all terminals where we found data, including from terminals that do not match the `last_related_terminal`. In most cases these statuses from other terminals are due to rerouting of vessels and can be ignored, but they can be useful context so we include them
     * @type {any}
     * @memberof ModelObject
     */
    'import_statuses': any;
    /**
     * The appointment that we think is the most \"current\" for this container.
     * @type {GETAppointment}
     * @memberof ModelObject
     */
    'latest_appointment'?: GETAppointment;
    /**
     * Counts of appointments **other** than the latest appointment by status
     * @type {any}
     * @memberof ModelObject
     */
    'other_appointment_counts': any;
    /**
     * Most relevant status data from the shipping line
     * @type {GETLineImportStatus}
     * @memberof ModelObject
     */
    'line_import_status'?: GETLineImportStatus;
    /**
     * Will always be `null` if `include_gate_transactions=false`
     * @type {GateTransaction}
     * @memberof ModelObject
     */
    'inbound_gate_transaction'?: GateTransaction;
    /**
     * Will always be `null` if `include_gate_transactions=false`
     * @type {GateTransaction}
     * @memberof ModelObject
     */
    'outbound_gate_transaction'?: GateTransaction;
    /**
     * 
     * @type {ContainerWatchState}
     * @memberof ModelObject
     */
    'cycle_state': ContainerWatchState;
    /**
     * We no longer provide \'next available appointment\' information in this API. This field is still here to avoid breaking backwards compatibility
     * @type {GETNextAvailable}
     * @memberof ModelObject
     * @deprecated
     */
    'next_available_appointment'?: GETNextAvailable;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'master_bl'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'tms_reference'?: any;
    /**
     * 
     * @type {SimpleUser}
     * @memberof ModelObject
     */
    'added_by_user': SimpleUser;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'assigned_users': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'auto_book_on': any;
    /**
     * 
     * @type {AutoBookRequestStatus}
     * @memberof ModelObject
     */
    'auto_book_request_status'?: AutoBookRequestStatus;
    /**
     * 
     * @type {TerminalName}
     * @memberof ModelObject
     */
    'last_related_terminal'?: TerminalName;
    /**
     * When this container was registered with Dray Dog to be tracked. Could be registered by a user, API, or showing up as an appointment on a terminal appointment system
     * @type {any}
     * @memberof ModelObject
     */
    'watched_time': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'discharged_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'import_appointment_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'import_appointment_pickup_number'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'import_out_gated_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'empty_appointment_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'empty_in_gated_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'last_free_date'?: any;
    /**
     * LFD published by the shipping line itself, from any source. `last_free_date` already accounts for it; null means we have no line LFD, which matters for lines that don\'t share it with terminals
     * @type {any}
     * @memberof ModelObject
     */
    'line_last_free_date'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'tags': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'vessel': any;
    /**
     * Vessel resolved from the booking\'s free-text vessel name. None only when the vessel name cannot be matched to a known vessel in our data at all. Check vessel_schedules for per-terminal cutoff/ERD dates.
     * @type {MatchedVesselForExport}
     * @memberof ModelObject
     */
    'matched_vessel'?: MatchedVesselForExport;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'vessel_eta'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'auto_book_ignoring_capacity': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'note_body'?: any;
    /**
     * Where the container is currently located. Set automatically from TMS empty-container feeds and editable by dispatchers.
     * @type {GETLocation}
     * @memberof ModelObject
     */
    'current_location'?: GETLocation;
    /**
     * 
     * @type {GETAppointment}
     * @memberof ModelObject
     */
    'appointment': GETAppointment;
    /**
     * 
     * @type {GETWatch}
     * @memberof ModelObject
     */
    'watch': GETWatch;
    /**
     * 
     * @type {ExportWatchState}
     * @memberof ModelObject
     */
    'state': ExportWatchState;
    /**
     * Derived booking lifecycle. The single source for the list status filter, the per-state counts and the status tabs
     * @type {ExportBookingState}
     * @memberof ModelObject
     */
    'lifecycle_state': ExportBookingState;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'related_terminals': any;
    /**
     * 
     * @type {ShippingLine}
     * @memberof ModelObject
     */
    'parsed_shipping_line': ShippingLine;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'statuses': any;
    /**
     * Result of the org\'s last check of this booking on eModal
     * @type {GETExportLastCheck}
     * @memberof ModelObject
     */
    'last_check': GETExportLastCheck;
    /**
     * Containers on the booking as the terminals report it (the list\'s TOTAL). Terminals repeat a booking\'s lines, so they are not added up: per type the largest terminal count wins. None when no terminal has reported equipment yet.
     * @type {any}
     * @memberof ModelObject
     */
    'total_containers'?: any;
    /**
     * Carrier-declared quantity per container type for this booking. Empty dict means the carrier has not set a quantity; the UI falls back to the terminal total in that case.
     * @type {{ [key: string]: any; }}
     * @memberof ModelObject
     */
    'carrier_quantities'?: { [key: string]: any; };
    /**
     * Per-terminal schedule data for the resolved vessel. An element is only included when a schedule row exists for that (vessel, terminal) pair. Match entries to statuses[] by the terminal field — statuses[] can have multiple rows per terminal (different sources) so lengths may differ. An empty list means either no vessel was matched or no schedules are on file for the booking\'s terminals.
     * @type {any}
     * @memberof ModelObject
     */
    'vessel_schedules'?: any;
    /**
     * Appointments (export empty pickups) drawing against this booking. Populated on GET /exports/ and GET /exports/{booking_number}; empty on the watch/upload endpoints to keep them lean
     * @type {any}
     * @memberof ModelObject
     */
    'appointments'?: any;
    /**
     * Keys are appointments.id, values are new last observed/checked time
     * @type {{ [key: string]: any; }}
     * @memberof ModelObject
     */
    'last_checked_by_appointment_ids': { [key: string]: any; };
    /**
     * Keys are import_status.id, values are new last observed/checked time
     * @type {{ [key: string]: any; }}
     * @memberof ModelObject
     */
    'last_checked_by_import_status_ids': { [key: string]: any; };
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'checks': any;
    /**
     * If true, this capacity set will not be affected by global capacity buckets
     * @type {any}
     * @memberof ModelObject
     */
    'independent_capacity'?: any;
    /**
     * Container tag to which this bucket belongs. Must be null if this bucket is a \"global\" or \"customer\" capacity set
     * @type {any}
     * @memberof ModelObject
     */
    'container_tag'?: any;
    /**
     * Width of each bucket in minutes. Distinguishes multiple sets in the same scope (e.g. an hourly fleet set vs a daily fleet set).
     * @type {any}
     * @memberof ModelObject
     */
    'bucket_minutes': any;
    /**
     * Which appointment direction this set governs capacity for (e.g. OUTBOUND for import, INBOUND for empties)
     * @type {TransactionDirection}
     * @memberof ModelObject
     */
    'direction'?: TransactionDirection;
    /**
     * Whether this set governs capacity for loaded (import) or empty appointments
     * @type {any}
     * @memberof ModelObject
     */
    'loaded'?: any;
    /**
     * Use `customer` field instead
     * @type {any}
     * @memberof ModelObject
     * @deprecated
     */
    'customer_id'?: any;
    /**
     * List of capacity buckets. Empty list disables capacity management
     * @type {any}
     * @memberof ModelObject
     */
    'buckets': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'timezone': any;
    /**
     * User who last edited this capacity set
     * @type {SimpleUser}
     * @memberof ModelObject
     */
    'last_edited_by'?: SimpleUser;
    /**
     * Timestamp of when this capacity set was last edited
     * @type {any}
     * @memberof ModelObject
     */
    'last_edited': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'first_observed': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'last_observed': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'last_parsed_time'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'screenshot_url': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'html_url'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'level': ModelObjectLevelEnum;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'title': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'message'?: any;
    /**
     * How the frontend should present this notification: a banner (default) or a forced popup dialog.
     * @type {GlobalNotificationDisplayType}
     * @memberof ModelObject
     */
    'display_type'?: GlobalNotificationDisplayType;
    /**
     * Identifier of a frontend product tour to offer alongside this notification. Free text: the frontend owns the tour registry, so a key it does not recognize (or a null value) simply means no tour is offered.
     * @type {any}
     * @memberof ModelObject
     */
    'tour_key'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'active_from': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'active_to'?: any;
    /**
     * 
     * @type {GETOrganization}
     * @memberof ModelObject
     */
    'organization': GETOrganization;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'name'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'license_plate_number': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'archived'?: any;
    /**
     * Three-state green/EV (APM Green Lane) status: None = unknown (never determined), True = confirmed green (APM GreenTruck=\'Y\' or user-specified), False = confirmed not green. Auto Book excludes only confirmed-green plates at green-lane terminals.
     * @type {any}
     * @memberof ModelObject
     */
    'is_green'?: any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'first_name': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'last_name': any;
    /**
     * 
     * @type {any}
     * @memberof ModelObject
     */
    'email': any;
    /**
     * The org this token is for
     * @type {GETOrganization}
     * @memberof ModelObject
     */
    'current_organization': GETOrganization;
    /**
     * List of all organizations the current user is a member of
     * @type {any}
     * @memberof ModelObject
     */
    'organizations': any;
    /**
     * Flags for beta features that are enabled for specific users for testing
     * @type {any}
     * @memberof ModelObject
     */
    'feature_flags': any;
    /**
     * Individual user preferences
     * @type {UserPreferences}
     * @memberof ModelObject
     */
    'preferences': UserPreferences;
}

export const ModelObjectActionTypeEnum = {
    CheckIn: 'check_in'
} as const;

export type ModelObjectActionTypeEnum = typeof ModelObjectActionTypeEnum[keyof typeof ModelObjectActionTypeEnum];
export const ModelObjectLevelEnum = {
    Success: 'success',
    Warning: 'warning',
    Info: 'info',
    Error: 'error'
} as const;

export type ModelObjectLevelEnum = typeof ModelObjectLevelEnum[keyof typeof ModelObjectLevelEnum];

/**
 * A package of data to be sent to the frontend via websocket
 * @export
 * @interface Notification
 */
export interface Notification {
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'level': NotificationLevelEnum;
    /**
     * 
     * @type {ModelObject}
     * @memberof Notification
     */
    'object'?: ModelObject;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'title'?: string;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'message'?: string;
    /**
     * 
     * @type {NotificationType}
     * @memberof Notification
     */
    'event_type': NotificationType;
}

export const NotificationLevelEnum = {
    Success: 'success',
    Warning: 'warning',
    Info: 'info',
    Error: 'error'
} as const;

export type NotificationLevelEnum = typeof NotificationLevelEnum[keyof typeof NotificationLevelEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const NotificationType = {
    AddEmptyRequestUpdated: 'add_empty_request_updated',
    AppointmentsChecked: 'appointments_checked',
    AppointmentUpdated: 'appointment_updated',
    BookingRequestUpdated: 'booking_request_updated',
    CancellationRequestUpdated: 'cancellation_request_updated',
    CapacitySetUpdated: 'capacity_set_updated',
    ContainerUpdated: 'container_updated',
    EditLicensePlateRequestUpdated: 'edit_license_plate_request_updated',
    EditChassisRequestUpdated: 'edit_chassis_request_updated',
    CheckInAppointmentRequestUpdated: 'check_in_appointment_request_updated',
    EmptyBookingRequestUpdated: 'empty_booking_request_updated',
    EmptyRescheduleRequestUpdated: 'empty_reschedule_request_updated',
    EmptyReturnRulesChecked: 'empty_return_rules_checked',
    EmptyReturnRulesUpdated: 'empty_return_rules_updated',
    ExportDatesChanged: 'export_dates_changed',
    ExportUpdated: 'export_updated',
    GenericMessage: 'generic_message',
    GlobalNotification: 'global_notification',
    ImportStatusesChecked: 'import_statuses_checked',
    LineImportStatusesChecked: 'line_import_statuses_checked',
    PierPassImportStatusesChecked: 'pier_pass_import_statuses_checked',
    OrganizationUpdated: 'organization_updated',
    RescheduleRequestUpdated: 'reschedule_request_updated',
    RetryingRequest: 'retrying_request',
    UserUpdated: 'user_updated',
    TrucksUpdated: 'trucks_updated',
    BookingVerification: 'booking_verification'
} as const;

export type NotificationType = typeof NotificationType[keyof typeof NotificationType];


/**
 * 
 * @export
 * @interface NullClass
 */
export interface NullClass {
    /**
     * 
     * @type {string}
     * @memberof NullClass
     */
    'config_type'?: NullClassConfigTypeEnum;
}

export const NullClassConfigTypeEnum = {
    Null: 'null'
} as const;

export type NullClassConfigTypeEnum = typeof NullClassConfigTypeEnum[keyof typeof NullClassConfigTypeEnum];

/**
 * This is for backwards compatibility
 * @export
 * @enum {string}
 */

export const OldBookingStatus = {
    InProgress: 'in_progress',
    Booked: 'booked',
    Failed: 'failed',
    Rejected: 'rejected'
} as const;

export type OldBookingStatus = typeof OldBookingStatus[keyof typeof OldBookingStatus];


/**
 * 
 * @export
 * @interface OrgAndRole
 */
export interface OrgAndRole {
    /**
     * 
     * @type {number}
     * @memberof OrgAndRole
     */
    'organization_id': number;
    /**
     * 
     * @type {UserRoleType}
     * @memberof OrgAndRole
     */
    'role': UserRoleType;
}


/**
 * 
 * @export
 * @interface OrganizationNotificationEvent
 */
export interface OrganizationNotificationEvent {
    /**
     * 
     * @type {string}
     * @memberof OrganizationNotificationEvent
     */
    'event_name'?: OrganizationNotificationEventEventNameEnum;
    /**
     * Channel name is dynamic and should be in format: `private-organization-notifications-org_id`
     * @type {string}
     * @memberof OrganizationNotificationEvent
     */
    'channel': string;
    /**
     * 
     * @type {string}
     * @memberof OrganizationNotificationEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Notification}
     * @memberof OrganizationNotificationEvent
     */
    'notification': Notification;
}

export const OrganizationNotificationEventEventNameEnum = {
    OrganizationNotification: 'organization_notification'
} as const;

export type OrganizationNotificationEventEventNameEnum = typeof OrganizationNotificationEventEventNameEnum[keyof typeof OrganizationNotificationEventEventNameEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const OrganizationSubscription = {
    Uas: 'uas',
    DrayDogMain: 'dray_dog_main',
    DrayDogOakland: 'dray_dog_oakland',
    Terminal: 'terminal'
} as const;

export type OrganizationSubscription = typeof OrganizationSubscription[keyof typeof OrganizationSubscription];


/**
 * 
 * @export
 * @interface OrganizationUpdated
 */
export interface OrganizationUpdated {
    /**
     * 
     * @type {GETOrganization}
     * @memberof OrganizationUpdated
     */
    'organization': GETOrganization;
}
/**
 * 
 * @export
 * @interface PATCHBookingRequest
 */
export interface PATCHBookingRequest {
    /**
     * 
     * @type {boolean}
     * @memberof PATCHBookingRequest
     */
    'entered_in_tms': boolean;
}
/**
 * 
 * @export
 * @interface PATCHContainers
 */
export interface PATCHContainers {
    /**
     * 
     * @type {Array<string>}
     * @memberof PATCHContainers
     */
    'container_numbers': Array<string>;
    /**
     * 
     * @type {number}
     * @memberof PATCHContainers
     */
    'customer_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof PATCHContainers
     */
    'master_bl'?: string;
    /**
     * 
     * @type {number}
     * @memberof PATCHContainers
     */
    'location_id'?: number;
    /**
     * Set the watch\'s current_location to a (possibly new) location. The address is deduplicated within the org. Mutually exclusive with `location_id`. To clear the current_location back to NULL, call `DELETE /containers/current_location/` instead.
     * @type {Location}
     * @memberof PATCHContainers
     */
    'location'?: Location;
}
/**
 * The user-authored fields on an export booking watch. Fields left unset are not written — this is a partial update, not a replace.
 * @export
 * @interface PATCHExports
 */
export interface PATCHExports {
    /**
     * 
     * @type {Array<string>}
     * @memberof PATCHExports
     */
    'booking_numbers': Array<string>;
    /**
     * 
     * @type {number}
     * @memberof PATCHExports
     */
    'customer_id'?: number;
    /**
     * Carrier-declared quantity per container type. Merge — only the keys sent are updated; omit to leave all quantities unchanged. Send null for a key to remove it. Send 0 to set responsible quantity to zero.
     * @type {{ [key: string]: number; }}
     * @memberof PATCHExports
     */
    'carrier_quantities'?: { [key: string]: number; };
}
/**
 * 
 * @export
 * @interface PATCHScopeIndependentCapacity
 */
export interface PATCHScopeIndependentCapacity {
    /**
     * 
     * @type {number}
     * @memberof PATCHScopeIndependentCapacity
     */
    'customer_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof PATCHScopeIndependentCapacity
     */
    'container_tag'?: string;
    /**
     * 
     * @type {PortComplex}
     * @memberof PATCHScopeIndependentCapacity
     */
    'port_complex'?: PortComplex;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof PATCHScopeIndependentCapacity
     */
    'direction'?: TransactionDirection;
    /**
     * 
     * @type {boolean}
     * @memberof PATCHScopeIndependentCapacity
     */
    'loaded'?: boolean;
    /**
     * New independent_capacity value for every set in the scope
     * @type {boolean}
     * @memberof PATCHScopeIndependentCapacity
     */
    'independent_capacity': boolean;
}


/**
 * Mutable fields for patching a test-mode appointment.  Only fields explicitly provided in the request body are updated. Fields omitted from the request body are left unchanged.
 * @export
 * @interface PATCHTestModeAppointment
 */
export interface PATCHTestModeAppointment {
    /**
     * 
     * @type {AppointmentStatus}
     * @memberof PATCHTestModeAppointment
     */
    'status'?: AppointmentStatus;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'window_start'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'window_end'?: string;
    /**
     * 
     * @type {TransactionDirection}
     * @memberof PATCHTestModeAppointment
     */
    'direction'?: TransactionDirection;
    /**
     * 
     * @type {boolean}
     * @memberof PATCHTestModeAppointment
     */
    'loaded'?: boolean;
    /**
     * 
     * @type {TerminalName}
     * @memberof PATCHTestModeAppointment
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'container_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'display_terminal_reference'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'shipping_line'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'container_type'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'carrier_scac'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'driver_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'drivers_licence_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'truck_license_plate_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'terminal_block'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'gate_code'?: string;
    /**
     * 
     * @type {string}
     * @memberof PATCHTestModeAppointment
     */
    'actual_departure'?: string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof PATCHTestModeAppointment
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof PATCHTestModeAppointment
     */
    'raw_data'?: { [key: string]: any; };
    /**
     * 
     * @type {PortalName}
     * @memberof PATCHTestModeAppointment
     */
    'source'?: PortalName;
}


/**
 * 
 * @export
 * @interface PATCHUpdateCustomer
 */
export interface PATCHUpdateCustomer {
    /**
     * 
     * @type {Array<string>}
     * @memberof PATCHUpdateCustomer
     */
    'container_numbers': Array<string>;
    /**
     * 
     * @type {number}
     * @memberof PATCHUpdateCustomer
     */
    'customer_id'?: number;
}
/**
 * 
 * @export
 * @interface POSTAcceptCompanyInvite
 */
export interface POSTAcceptCompanyInvite {
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'token': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'company_address_line_1': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'company_address_line_2': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'company_city': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'company_state': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'company_zip_code': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'signer_first_name': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'signer_last_name': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'signer_role': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'signer_email': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInvite
     */
    'signer_phone': string;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAcceptCompanyInvite
     */
    'terms_of_service_checked': boolean;
}
/**
 * 
 * @export
 * @interface POSTAcceptCompanyInviteResponse
 */
export interface POSTAcceptCompanyInviteResponse {
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptCompanyInviteResponse
     */
    'user_activation_link': string;
}
/**
 * 
 * @export
 * @interface POSTAcceptInvite
 */
export interface POSTAcceptInvite {
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptInvite
     */
    'token': string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof POSTAcceptInvite
     */
    'role': UserRoleType;
}


/**
 * 
 * @export
 * @interface POSTAcceptInvites
 */
export interface POSTAcceptInvites {
    /**
     * 
     * @type {string}
     * @memberof POSTAcceptInvites
     */
    'token': string;
    /**
     * 
     * @type {Array<OrgAndRole>}
     * @memberof POSTAcceptInvites
     */
    'organization_roles': Array<OrgAndRole>;
}
/**
 * 
 * @export
 * @interface POSTActivateUser
 */
export interface POSTActivateUser {
    /**
     * 
     * @type {string}
     * @memberof POSTActivateUser
     */
    'token': string;
    /**
     * 
     * @type {string}
     * @memberof POSTActivateUser
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof POSTActivateUser
     */
    'last_name': string;
    /**
     * 
     * @type {string}
     * @memberof POSTActivateUser
     */
    'password': string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof POSTActivateUser
     */
    'role': UserRoleType;
}


/**
 * 
 * @export
 * @interface POSTAddEmpty
 */
export interface POSTAddEmpty {
    /**
     * ID of the existing import appointment to add the empty to. You could get the appointment ID from the `id` attributed of the appointments returned by [GET /appointments/](#operation/list_appointments_appointments__get), among other places. Must be an import appointment
     * @type {number}
     * @memberof POSTAddEmpty
     */
    'import_appointment_id': number;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTAddEmpty
     */
    'empty_container_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTAddEmpty
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTAddEmpty
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof POSTAddEmpty
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof POSTAddEmpty
     */
    'truck_license_plate'?: string;
}


/**
 * 
 * @export
 * @interface POSTAppointmentAssignmentData
 */
export interface POSTAppointmentAssignmentData {
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAppointmentAssignmentData
     */
    'appointment_ids': Array<number>;
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAppointmentAssignmentData
     */
    'user_ids': Array<number>;
}
/**
 * 
 * @export
 * @interface POSTAppointmentBooking
 */
export interface POSTAppointmentBooking {
    /**
     * 
     * @type {POSTAppointmentSlotForBooking}
     * @memberof POSTAppointmentBooking
     */
    'slot': POSTAppointmentSlotForBooking;
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentBooking
     */
    'master_bill_of_lading'?: string;
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentBooking
     */
    'truck_license_plate'?: string;
    /**
     * When True and `truck_license_plate` is empty, the server picks a recent plate from the org\'s pool and records the booking with origin=MANUAL_RANDOM. Defaults to False so existing clients behave as before.
     * @type {boolean}
     * @memberof POSTAppointmentBooking
     */
    'set_random_license_plate'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof POSTAppointmentBooking
     */
    'existing_appointment_id_to_cancel'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAppointmentBooking
     */
    'own_chassis'?: boolean;
}
/**
 * 
 * @export
 * @interface POSTAppointmentRefresh
 */
export interface POSTAppointmentRefresh {
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTAppointmentRefresh
     */
    'container_number': string;
    /**
     * 
     * @type {PortalName}
     * @memberof POSTAppointmentRefresh
     */
    'portal': PortalName;
}


/**
 * 
 * @export
 * @interface POSTAppointmentRefreshResponse
 */
export interface POSTAppointmentRefreshResponse {
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentRefreshResponse
     */
    'message': string;
    /**
     * 
     * @type {Array<SkippedAppointmentRefresh>}
     * @memberof POSTAppointmentRefreshResponse
     */
    'skipped': Array<SkippedAppointmentRefresh>;
}
/**
 * 
 * @export
 * @interface POSTAppointmentSlotForBooking
 */
export interface POSTAppointmentSlotForBooking {
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentSlotForBooking
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentSlotForBooking
     */
    'window_end'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof POSTAppointmentSlotForBooking
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentSlotForBooking
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof POSTAppointmentSlotForBooking
     */
    'from_terminal_block_availability_key'?: string;
    /**
     * The time the slot was observed. This should be taken from the `observed` value returned in the [GET /appointments/availability/{container_number}/](#operation/get_available_slots_for_container_appointments_availability__container_number___get) endpoint. This helps the server respond to properly to certain race-condition edge-cases
     * @type {string}
     * @memberof POSTAppointmentSlotForBooking
     */
    'observed'?: string;
}


/**
 * 
 * @export
 * @interface POSTAppointmentTags
 */
export interface POSTAppointmentTags {
    /**
     * Appointments to tag or untag (containerless export empty pickups)
     * @type {Array<number>}
     * @memberof POSTAppointmentTags
     */
    'appointment_ids': Array<number>;
    /**
     * Tags to add or remove, from the org\'s container tag vocabulary
     * @type {Array<string>}
     * @memberof POSTAppointmentTags
     */
    'tags': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTAssignmentData
 */
export interface POSTAssignmentData {
    /**
     * 
     * @type {Array<TerminalName>}
     * @memberof POSTAssignmentData
     */
    'terminals'?: Array<TerminalName>;
    /**
     * 
     * @type {TerminalName}
     * @memberof POSTAssignmentData
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {Array<ContainerWatchState>}
     * @memberof POSTAssignmentData
     */
    'container_cycle_states'?: Array<ContainerWatchState>;
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAssignmentData
     */
    'customer_ids'?: Array<number>;
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAssignmentData
     */
    'excluded_customer_ids'?: Array<number>;
    /**
     * 
     * @type {number}
     * @memberof POSTAssignmentData
     */
    'customer_id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'auto_book_on'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'auto_book_off'?: boolean;
    /**
     * 
     * @type {Array<AutoBookRequestStatus>}
     * @memberof POSTAssignmentData
     */
    'auto_book_statuses'?: Array<AutoBookRequestStatus>;
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAssignmentData
     */
    'assigned_user_ids'?: Array<number>;
    /**
     * 
     * @type {Array<ContainerType>}
     * @memberof POSTAssignmentData
     */
    'container_types'?: Array<ContainerType>;
    /**
     * 
     * @type {Array<ShippingLine>}
     * @memberof POSTAssignmentData
     */
    'shipping_lines'?: Array<ShippingLine>;
    /**
     * 
     * @type {number}
     * @memberof POSTAssignmentData
     */
    'updated_in_last_minutes'?: number;
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTAssignmentData
     */
    'tags'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'is_untagged'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTAssignmentData
     */
    'vessels'?: Array<string>;
    /**
     * 
     * @type {Array<PortComplex>}
     * @memberof POSTAssignmentData
     */
    'port_complexes'?: Array<PortComplex>;
    /**
     * 
     * @type {string}
     * @memberof POSTAssignmentData
     */
    'vessel_eta'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'vessel_eta_is_unknown'?: boolean;
    /**
     * 
     * @type {Array<HoldType>}
     * @memberof POSTAssignmentData
     */
    'hold_types'?: Array<HoldType>;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'is_green_truck'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAssignmentData
     */
    'needs_line_lfd_check'?: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTAssignmentData
     */
    'container_numbers'?: Array<string>;
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTAssignmentData
     */
    'user_ids': Array<number>;
}


/**
 * 
 * @export
 * @interface POSTAutoBookEmptyRequest
 */
export interface POSTAutoBookEmptyRequest {
    /**
     * 
     * @type {string}
     * @memberof POSTAutoBookEmptyRequest
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof POSTAutoBookEmptyRequest
     */
    'id'?: number;
    /**
     * 
     * @type {AutoBookEmptySettings}
     * @memberof POSTAutoBookEmptyRequest
     */
    'settings': AutoBookEmptySettings;
}
/**
 * 
 * @export
 * @interface POSTAutoBookRequest
 */
export interface POSTAutoBookRequest {
    /**
     * 
     * @type {string}
     * @memberof POSTAutoBookRequest
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof POSTAutoBookRequest
     */
    'id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof POSTAutoBookRequest
     */
    'enabled': boolean;
    /**
     * 
     * @type {AutoBookingSettings}
     * @memberof POSTAutoBookRequest
     */
    'settings': AutoBookingSettings;
}
/**
 * 
 * @export
 * @interface POSTBookingRequestStatuses
 */
export interface POSTBookingRequestStatuses {
    /**
     * 
     * @type {Array<number>}
     * @memberof POSTBookingRequestStatuses
     */
    'request_ids': Array<number>;
}
/**
 * 
 * @export
 * @interface POSTBulkCheckImportStatuses
 */
export interface POSTBulkCheckImportStatuses {
    /**
     * list of container numbers
     * @type {Array<string>}
     * @memberof POSTBulkCheckImportStatuses
     */
    'container_numbers': Array<string>;
    /**
     * Whether to query Port Optimizer for import statuses
     * @type {boolean}
     * @memberof POSTBulkCheckImportStatuses
     */
    'use_port_optimizer'?: boolean;
    /**
     * Whether to query eModal ASB for import statuses. If this is set to `true` and the container is NOT found in Port Optimizer or ASB, currently this API times out due to limitations in ASB
     * @type {boolean}
     * @memberof POSTBulkCheckImportStatuses
     */
    'use_emodal_asb'?: boolean;
}
/**
 * 
 * @export
 * @interface POSTCapacity
 */
export interface POSTCapacity {
    [key: string]: any;

    /**
     * List of capacity buckets. Empty list disables capacity management
     * @type {Array<GETCapacityBucket>}
     * @memberof POSTCapacity
     */
    'buckets': Array<GETCapacityBucket>;
    /**
     * 
     * @type {string}
     * @memberof POSTCapacity
     */
    'timezone': string;
    /**
     * Customer ID to which this bucket belongs. Must be null if this bucket is a \"global\" or \"tags\" capacity set
     * @type {number}
     * @memberof POSTCapacity
     */
    'customer_id'?: number;
    /**
     * Container tag to which this bucket belongs. Must be null if this bucket is a \"global\" or \"customer\" capacity set
     * @type {string}
     * @memberof POSTCapacity
     */
    'container_tag'?: string;
    /**
     * If true, this capacity set will not be affected by global capacity buckets
     * @type {boolean}
     * @memberof POSTCapacity
     */
    'independent_capacity'?: boolean;
    /**
     * Port complex to which this capacity set belongs
     * @type {PortComplex}
     * @memberof POSTCapacity
     */
    'port_complex'?: PortComplex;
    /**
     * Which appointment direction this set governs capacity for (e.g. OUTBOUND for import, INBOUND for empties)
     * @type {TransactionDirection}
     * @memberof POSTCapacity
     */
    'direction'?: TransactionDirection;
    /**
     * Whether this set governs capacity for loaded (import) or empty appointments
     * @type {boolean}
     * @memberof POSTCapacity
     */
    'loaded'?: boolean;
}


/**
 * 
 * @export
 * @interface POSTCheckIn
 */
export interface POSTCheckIn {
    /**
     * 
     * @type {number}
     * @memberof POSTCheckIn
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof POSTCheckIn
     */
    'license_plate'?: string;
}
/**
 * 
 * @export
 * @interface POSTConnection
 */
export interface POSTConnection {
    /**
     * 
     * @type {ConnectionType}
     * @memberof POSTConnection
     */
    'type': ConnectionType;
    /**
     * 
     * @type {Config}
     * @memberof POSTConnection
     */
    'config': Config;
}


/**
 * 
 * @export
 * @interface POSTContainerNumbers
 */
export interface POSTContainerNumbers {
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTContainerNumbers
     */
    'container_numbers': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTContainerWatch
 */
export interface POSTContainerWatch {
    /**
     * 
     * @type {string}
     * @memberof POSTContainerWatch
     */
    'container_number': string;
    /**
     * 
     * @type {number}
     * @memberof POSTContainerWatch
     */
    'customer_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof POSTContainerWatch
     */
    'master_bl'?: string;
    /**
     * Tags to apply to the container. This field will not remove any existing tags on the container, but will add to them.
     * @type {Array<string>}
     * @memberof POSTContainerWatch
     */
    'tags'?: Array<string>;
    /**
     * 
     * @type {PortComplex}
     * @memberof POSTContainerWatch
     */
    'port_complex'?: PortComplex;
}


/**
 * 
 * @export
 * @interface POSTEditChassis
 */
export interface POSTEditChassis {
    /**
     * 
     * @type {number}
     * @memberof POSTEditChassis
     */
    'appointment_id': number;
    /**
     * 
     * @type {boolean}
     * @memberof POSTEditChassis
     */
    'own_chassis': boolean;
}
/**
 * 
 * @export
 * @interface POSTEditLicensePlate
 */
export interface POSTEditLicensePlate {
    /**
     * 
     * @type {number}
     * @memberof POSTEditLicensePlate
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof POSTEditLicensePlate
     */
    'license_plate': string;
}
/**
 * 
 * @export
 * @interface POSTEmptyAppointmentBooking
 */
export interface POSTEmptyAppointmentBooking {
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTEmptyAppointmentBooking
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyAppointmentBooking
     */
    'truck_license_plate'?: string;
    /**
     * When True and `truck_license_plate` is empty, the server picks a recent plate from the org\'s pool and records the booking with origin=MANUAL_RANDOM. Defaults to False so existing clients behave as before.
     * @type {boolean}
     * @memberof POSTEmptyAppointmentBooking
     */
    'set_random_license_plate'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyAppointmentBooking
     */
    'chassis_number': string;
    /**
     * 
     * @type {boolean}
     * @memberof POSTEmptyAppointmentBooking
     */
    'own_chassis'?: boolean;
    /**
     * 
     * @type {POSTEmptyInAppointmentSlot}
     * @memberof POSTEmptyAppointmentBooking
     */
    'slot': POSTEmptyInAppointmentSlot;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTEmptyAppointmentBooking
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTEmptyAppointmentBooking
     */
    'container_type': ContainerType;
}


/**
 * 
 * @export
 * @interface POSTEmptyAppointmentBookingBulk
 */
export interface POSTEmptyAppointmentBookingBulk {
    /**
     * 
     * @type {POSTEmptyInAppointmentSlot}
     * @memberof POSTEmptyAppointmentBookingBulk
     */
    'slot': POSTEmptyInAppointmentSlot;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTEmptyAppointmentBookingBulk
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTEmptyAppointmentBookingBulk
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {Array<POSTEmptyAppointmentBookingContainerInfo>}
     * @memberof POSTEmptyAppointmentBookingBulk
     */
    'containers': Array<POSTEmptyAppointmentBookingContainerInfo>;
}


/**
 * 
 * @export
 * @interface POSTEmptyAppointmentBookingContainerInfo
 */
export interface POSTEmptyAppointmentBookingContainerInfo {
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTEmptyAppointmentBookingContainerInfo
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyAppointmentBookingContainerInfo
     */
    'truck_license_plate'?: string;
    /**
     * When True and `truck_license_plate` is empty, the server picks a recent plate from the org\'s pool and records the booking with origin=MANUAL_RANDOM. Defaults to False so existing clients behave as before.
     * @type {boolean}
     * @memberof POSTEmptyAppointmentBookingContainerInfo
     */
    'set_random_license_plate'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyAppointmentBookingContainerInfo
     */
    'chassis_number': string;
    /**
     * 
     * @type {boolean}
     * @memberof POSTEmptyAppointmentBookingContainerInfo
     */
    'own_chassis'?: boolean;
}
/**
 * 
 * @export
 * @interface POSTEmptyInAppointmentSlot
 */
export interface POSTEmptyInAppointmentSlot {
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyInAppointmentSlot
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyInAppointmentSlot
     */
    'window_end'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof POSTEmptyInAppointmentSlot
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof POSTEmptyInAppointmentSlot
     */
    'group_observed': string;
}


/**
 * Bulk-by-construction: book `quantity` empty pickups against one booking at one terminal, for a single container type.  A booking can hold multiple container types; book each type with its own call. `units` holds either one config applied to every unit, or exactly one per unit — no in-between.
 * @export
 * @interface POSTEmptyPickupBooking
 */
export interface POSTEmptyPickupBooking {
    /**
     * 
     * @type {TerminalName}
     * @memberof POSTEmptyPickupBooking
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTEmptyPickupBooking
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTEmptyPickupBooking
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {number}
     * @memberof POSTEmptyPickupBooking
     */
    'quantity': number;
    /**
     * 
     * @type {Array<EmptyPickupBookingUnit>}
     * @memberof POSTEmptyPickupBooking
     */
    'units': Array<EmptyPickupBookingUnit>;
}


/**
 * 
 * @export
 * @interface POSTEmptyReturnRulesIfChanged
 */
export interface POSTEmptyReturnRulesIfChanged {
    /**
     * Any \"readings\" that are passed here will not be returned if they are still the latest reading for their respective terminals
     * @type {Array<TerminalReadingMetadata>}
     * @memberof POSTEmptyReturnRulesIfChanged
     */
    'readings_in_cache': Array<TerminalReadingMetadata>;
}
/**
 * Bookings to check before adding them. Usually one (one row of the Add bookings table); a pasted set of rows can be checked in one call.
 * @export
 * @interface POSTExportBookingCheck
 */
export interface POSTExportBookingCheck {
    /**
     * list of booking numbers
     * @type {Array<string>}
     * @memberof POSTExportBookingCheck
     */
    'booking_numbers': Array<string>;
}
/**
 * Request body for watching export bookings. Send `bookings` (one item per row, each with its own customer) or the older `booking_numbers`, not both.
 * @export
 * @interface POSTExportBookingWatch
 */
export interface POSTExportBookingWatch {
    /**
     * The bookings to track, one per row
     * @type {Array<POSTExportBookingWatchItem>}
     * @memberof POSTExportBookingWatch
     */
    'bookings'?: Array<POSTExportBookingWatchItem>;
    /**
     * Older form of `bookings`: booking numbers only, all getting the request-level `customer_id`
     * @type {Array<string>}
     * @memberof POSTExportBookingWatch
     */
    'booking_numbers'?: Array<string>;
    /**
     * Customer for every booking that doesn\'t name its own. Updates the customer on bookings that are already tracked. Leave unset to keep the current one.
     * @type {number}
     * @memberof POSTExportBookingWatch
     */
    'customer_id'?: number;
}
/**
 * One row of the Add bookings table.
 * @export
 * @interface POSTExportBookingWatchItem
 */
export interface POSTExportBookingWatchItem {
    /**
     * 
     * @type {string}
     * @memberof POSTExportBookingWatchItem
     */
    'booking_number': string;
    /**
     * Customer for this booking. Falls back to the request-level `customer_id`. Updates the customer on a booking that is already tracked.
     * @type {number}
     * @memberof POSTExportBookingWatchItem
     */
    'customer_id'?: number;
}
/**
 * 
 * @export
 * @interface POSTImportStatusRefresh
 */
export interface POSTImportStatusRefresh {
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTImportStatusRefresh
     */
    'container_number': string;
    /**
     * 
     * @type {PortalName}
     * @memberof POSTImportStatusRefresh
     */
    'portal': PortalName;
    /**
     * 
     * @type {TerminalName}
     * @memberof POSTImportStatusRefresh
     */
    'terminal'?: TerminalName;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTImportStatusRefresh
     */
    'shipping_line'?: ShippingLine;
}


/**
 * 
 * @export
 * @interface POSTInviteUser
 */
export interface POSTInviteUser {
    /**
     * 
     * @type {string}
     * @memberof POSTInviteUser
     */
    'email': string;
    /**
     * 
     * @type {UserRoleType}
     * @memberof POSTInviteUser
     */
    'role'?: UserRoleType;
}


/**
 * 
 * @export
 * @interface POSTMarkResponse
 */
export interface POSTMarkResponse {
    /**
     * Container numbers not \"watched\" (i.e tracking) for this request. To get data for these containers, you must upload these containers using [POST /containers/watch/](#operation/watch_containers_containers_watch__post) or [POST /containers/watch/via_upload/](#operation/watch_containers_via_upload_containers_watch_via_upload__post)
     * @type {Array<string>}
     * @memberof POSTMarkResponse
     */
    'unwatched_containers': Array<string>;
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTMarkResponse
     */
    'already_marked_containers': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTNetworkRequestBody
 */
export interface POSTNetworkRequestBody {
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'extension_version'?: string;
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'method'?: string;
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'request_body': string;
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'response_body': string;
    /**
     * 
     * @type {number}
     * @memberof POSTNetworkRequestBody
     */
    'status_code'?: number;
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestBody
     */
    'trucker_scac': string;
    /**
     * 
     * @type {PortalName}
     * @memberof POSTNetworkRequestBody
     */
    'portal'?: PortalName;
}


/**
 * 
 * @export
 * @interface POSTNetworkRequestResponse
 */
export interface POSTNetworkRequestResponse {
    /**
     * 
     * @type {string}
     * @memberof POSTNetworkRequestResponse
     */
    'message': string;
    /**
     * 
     * @type {boolean}
     * @memberof POSTNetworkRequestResponse
     */
    'success': boolean;
    /**
     * Version of the browser extension
     * @type {string}
     * @memberof POSTNetworkRequestResponse
     */
    'version'?: string;
}
/**
 * 
 * @export
 * @interface POSTNote
 */
export interface POSTNote {
    /**
     * Note text
     * @type {string}
     * @memberof POSTNote
     */
    'body': string;
}
/**
 * 
 * @export
 * @interface POSTOrganizationBillingSession
 */
export interface POSTOrganizationBillingSession {
    /**
     * 
     * @type {string}
     * @memberof POSTOrganizationBillingSession
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof POSTOrganizationBillingSession
     */
    'return_url': string;
}
/**
 * 
 * @export
 * @interface POSTRemainingCapacity
 */
export interface POSTRemainingCapacity {
    /**
     * 
     * @type {number}
     * @memberof POSTRemainingCapacity
     */
    'customer_id'?: number;
    /**
     * Port complex to calculate remaining capacities for
     * @type {PortComplex}
     * @memberof POSTRemainingCapacity
     */
    'port_complex'?: PortComplex;
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTRemainingCapacity
     */
    'container_tags': Array<string>;
    /**
     * Start of the time period to calculate remaining capacities for. Defaults to now
     * @type {string}
     * @memberof POSTRemainingCapacity
     */
    'time_period_start'?: string;
    /**
     * End of the time period to calculate remaining capacities for. Defaults to 7 days from now
     * @type {string}
     * @memberof POSTRemainingCapacity
     */
    'time_period_end'?: string;
}


/**
 * 
 * @export
 * @interface POSTRemainingCapacityResponse
 */
export interface POSTRemainingCapacityResponse {
    /**
     * 
     * @type {Array<CapacitySetRemainingCapacities>}
     * @memberof POSTRemainingCapacityResponse
     */
    'applicable_capacity_sets': Array<CapacitySetRemainingCapacities>;
}
/**
 * 
 * @export
 * @interface POSTRescheduleEmptyAppointmentBooking
 */
export interface POSTRescheduleEmptyAppointmentBooking {
    /**
     * 
     * @type {POSTEmptyInAppointmentSlot}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'slot': POSTEmptyInAppointmentSlot;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'container_type': ContainerType;
    /**
     * ID of the existing empty appointment to reschedule. You can get the appointment ID from the `id` field of the appointments returned by [GET /appointments/](#operation/list_appointments_appointments__get)
     * @type {number}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'existing_appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'truck_license_plate'?: string;
    /**
     * When True and `truck_license_plate` is empty, the server picks a recent plate from the org\'s pool and records the booking with origin=MANUAL_RANDOM. Defaults to False so existing clients behave as before.
     * @type {boolean}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'set_random_license_plate'?: boolean;
    /**
     * Pool chassis number. Leave empty to keep the chassis number from the appointment being rescheduled; it is only dropped when `own_chassis` is True (an own-chassis move doesn\'t carry the pool chassis over).
     * @type {string}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'chassis_number'?: string;
    /**
     * Whether the trucker is using their own chassis. Leave unset to keep the choice recorded on the appointment being rescheduled.
     * @type {boolean}
     * @memberof POSTRescheduleEmptyAppointmentBooking
     */
    'own_chassis'?: boolean;
}


/**
 * 
 * @export
 * @interface POSTResendSignupEmailResponse
 */
export interface POSTResendSignupEmailResponse {
    /**
     * 
     * @type {string}
     * @memberof POSTResendSignupEmailResponse
     */
    'message': string;
}
/**
 * 
 * @export
 * @interface POSTSetLineAndType
 */
export interface POSTSetLineAndType {
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTSetLineAndType
     */
    'container_numbers': Array<string>;
    /**
     * Shipping line to set on containers missing one
     * @type {ShippingLine}
     * @memberof POSTSetLineAndType
     */
    'shipping_line'?: ShippingLine;
    /**
     * Container type to set on containers missing one
     * @type {ContainerType}
     * @memberof POSTSetLineAndType
     */
    'container_type'?: ContainerType;
}


/**
 * 
 * @export
 * @interface POSTSetLineAndTypeResponse
 */
export interface POSTSetLineAndTypeResponse {
    /**
     * Container numbers not \"watched\" (i.e tracking) for this request. To get data for these containers, you must upload these containers using [POST /containers/watch/](#operation/watch_containers_containers_watch__post) or [POST /containers/watch/via_upload/](#operation/watch_containers_via_upload_containers_watch_via_upload__post)
     * @type {Array<string>}
     * @memberof POSTSetLineAndTypeResponse
     */
    'unwatched_containers': Array<string>;
    /**
     * Containers whose shipping line was left unchanged because it was already set (we never override existing data)
     * @type {Array<string>}
     * @memberof POSTSetLineAndTypeResponse
     */
    'line_skipped_already_set': Array<string>;
    /**
     * Containers whose container type was left unchanged because it was already set (we never override existing data)
     * @type {Array<string>}
     * @memberof POSTSetLineAndTypeResponse
     */
    'type_skipped_already_set': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTSetUpUserResponse
 */
export interface POSTSetUpUserResponse {
    /**
     * 
     * @type {boolean}
     * @memberof POSTSetUpUserResponse
     */
    'is_user_set_up': boolean;
    /**
     * 
     * @type {Array<PendingOrganization>}
     * @memberof POSTSetUpUserResponse
     */
    'pending_organizations': Array<PendingOrganization>;
}
/**
 * 
 * @export
 * @interface POSTSwapEmpty
 */
export interface POSTSwapEmpty {
    /**
     * ID of an existing empty appointment that you want to change the container number of. You could get the appointment ID from the `id` attributed of the appointments returned by [GET /appointments/](#operation/list_appointments_appointments__get), among other places
     * @type {number}
     * @memberof POSTSwapEmpty
     */
    'appointment_id'?: number;
    /**
     * Used in combination with existing_container_number to find the  appointment to swap empty with. Use appointment_id if possible
     * @type {string}
     * @memberof POSTSwapEmpty
     */
    'terminal_reference'?: string;
    /**
     * Used in combination with terminal_reference
     * @type {string}
     * @memberof POSTSwapEmpty
     */
    'existing_container_number'?: string;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof POSTSwapEmpty
     */
    'empty_container_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof POSTSwapEmpty
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof POSTSwapEmpty
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof POSTSwapEmpty
     */
    'chassis_number'?: string;
}


/**
 * 
 * @export
 * @interface POSTTag
 */
export interface POSTTag {
    /**
     * 
     * @type {string}
     * @memberof POSTTag
     */
    'name': string;
}
/**
 * 
 * @export
 * @interface POSTTagEvents
 */
export interface POSTTagEvents {
    /**
     * Container numbers to tag or untag
     * @type {Array<string>}
     * @memberof POSTTagEvents
     */
    'container_numbers': Array<string>;
    /**
     * Tags to add or remove depending on the operation
     * @type {Array<string>}
     * @memberof POSTTagEvents
     */
    'tags': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTTokenViaPortOptimizerToken
 */
export interface POSTTokenViaPortOptimizerToken {
    /**
     * 
     * @type {string}
     * @memberof POSTTokenViaPortOptimizerToken
     */
    'token': string;
}
/**
 * 
 * @export
 * @interface POSTTruck
 */
export interface POSTTruck {
    /**
     * 
     * @type {string}
     * @memberof POSTTruck
     */
    'name'?: string;
    /**
     * 
     * @type {string}
     * @memberof POSTTruck
     */
    'license_plate_number': string;
}
/**
 * 
 * @export
 * @interface POSTUploadContainersResponse
 */
export interface POSTUploadContainersResponse {
    /**
     * 
     * @type {Array<string>}
     * @memberof POSTUploadContainersResponse
     */
    'container_numbers': Array<string>;
    /**
     * 
     * @type {Array<ContainerUploadError>}
     * @memberof POSTUploadContainersResponse
     */
    'errors': Array<ContainerUploadError>;
}
/**
 * `exports` holds every booking that is now tracked, new or already tracked.
 * @export
 * @interface POSTWatchExportsResponse
 */
export interface POSTWatchExportsResponse {
    /**
     * 
     * @type {Array<GETExport>}
     * @memberof POSTWatchExportsResponse
     */
    'exports': Array<GETExport>;
    /**
     * How many bookings match the filters, ignoring page size. Used for page buttons. Null unless `include_total` is true, so \"not counted\" never reads as \"nothing matched\".
     * @type {number}
     * @memberof POSTWatchExportsResponse
     */
    'total'?: number;
    /**
     * Booking numbers that were not tracked, each with the reason
     * @type {Array<ExportBookingUploadError>}
     * @memberof POSTWatchExportsResponse
     */
    'errors': Array<ExportBookingUploadError>;
    /**
     * Booking numbers the org was already tracking. Not an error: they are in `exports`, with the customer from this request.
     * @type {Array<string>}
     * @memberof POSTWatchExportsResponse
     */
    'already_tracked': Array<string>;
}
/**
 * 
 * @export
 * @interface POSTWebhook
 */
export interface POSTWebhook {
    /**
     * Callback url to send events to. Must be HTTPS
     * @type {string}
     * @memberof POSTWebhook
     */
    'url': string;
    /**
     * Optionally include an authorization header to send with the request. We will use it as the Authorization header when posting to your callback url. Recommended for security.
     * @type {string}
     * @memberof POSTWebhook
     */
    'authorization_header'?: string;
    /**
     * List of event types to subscribe to. At least one is required.
     * @type {Array<WebhookEventType>}
     * @memberof POSTWebhook
     */
    'event_types': Array<WebhookEventType>;
}
/**
 * 
 * @export
 * @interface PUTConnection
 */
export interface PUTConnection {
    /**
     * 
     * @type {ConnectionType}
     * @memberof PUTConnection
     */
    'type': ConnectionType;
    /**
     * 
     * @type {Config}
     * @memberof PUTConnection
     */
    'config': Config;
    /**
     * 
     * @type {number}
     * @memberof PUTConnection
     */
    'id': number;
}


/**
 * 
 * @export
 * @interface PUTTruck
 */
export interface PUTTruck {
    /**
     * 
     * @type {string}
     * @memberof PUTTruck
     */
    'name'?: string;
    /**
     * 
     * @type {string}
     * @memberof PUTTruck
     */
    'license_plate_number': string;
    /**
     * 
     * @type {boolean}
     * @memberof PUTTruck
     */
    'archived'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PUTTruck
     */
    'is_green'?: boolean;
}
/**
 * An equipment-order line with its container type resolved to our enum via the backend mapping tables (the scraped model only carries the raw code).
 * @export
 * @interface ParsedExportEquipmentQuantity
 */
export interface ParsedExportEquipmentQuantity {
    /**
     * Line size/type code, e.g. \'40DH\'
     * @type {string}
     * @memberof ParsedExportEquipmentQuantity
     */
    'container_type_code': string;
    /**
     * ISO size/type, e.g. \'45G1\'
     * @type {string}
     * @memberof ParsedExportEquipmentQuantity
     */
    'container_type_iso'?: string;
    /**
     * 
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'booked_qty'?: number;
    /**
     * 
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'issued_qty'?: number;
    /**
     * 
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'received_qty'?: number;
    /**
     * Pick empty (MP) remaining — from PreGate issuedQty+appointmentCount when available, else pending_qty_mp from datahub. None means not yet observed (pre-refresh row); 0 means genuinely zero.
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'pick_remaining'?: number;
    /**
     * Drop full (XD) remaining — from PreGate receivedQty+appointmentCount when available, else pending_qty_xd from datahub. None means not yet observed (pre-refresh row); 0 means genuinely zero.
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'drop_remaining'?: number;
    /**
     * Pick empty (MP) appointments the terminal has on this booking and size type, across all carriers (PreGate appointmentCount). None means PreGate did not return the line or its count; 0 means genuinely zero.
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'pick_scheduled'?: number;
    /**
     * Drop full (XD) appointments the terminal has on this booking and size type, across all carriers (PreGate appointmentCount). None means PreGate did not return the line or its count; 0 means genuinely zero.
     * @type {number}
     * @memberof ParsedExportEquipmentQuantity
     */
    'drop_scheduled'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof ParsedExportEquipmentQuantity
     */
    'is_hazmat'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ParsedExportEquipmentQuantity
     */
    'is_reefer'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ParsedExportEquipmentQuantity
     */
    'is_over_dimensional'?: boolean;
    /**
     * Raw hazmat class info for this line
     * @type {Array<{ [key: string]: any; }>}
     * @memberof ParsedExportEquipmentQuantity
     */
    'hazinfo'?: Array<{ [key: string]: any; }>;
    /**
     * 
     * @type {ContainerType}
     * @memberof ParsedExportEquipmentQuantity
     */
    'parsed_container_type'?: ContainerType;
}


/**
 * 
 * @export
 * @interface ParsedHold
 */
export interface ParsedHold {
    /**
     * 
     * @type {HoldType}
     * @memberof ParsedHold
     */
    'type'?: HoldType;
    /**
     * 
     * @type {string}
     * @memberof ParsedHold
     */
    'raw_type': string;
}


/**
 * 
 * @export
 * @interface PendingOrganization
 */
export interface PendingOrganization {
    /**
     * 
     * @type {string}
     * @memberof PendingOrganization
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof PendingOrganization
     */
    'org_id': number;
}
/**
 * 
 * @export
 * @interface PingRequest
 */
export interface PingRequest {
    /**
     * 
     * @type {string}
     * @memberof PingRequest
     */
    'extension_version'?: string;
    /**
     * 
     * @type {string}
     * @memberof PingRequest
     */
    'active_url': string;
    /**
     * 
     * @type {PortalName}
     * @memberof PingRequest
     */
    'portal': PortalName;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const PortComplex = {
    LaLb: 'la_lb',
    Oak: 'oak'
} as const;

export type PortComplex = typeof PortComplex[keyof typeof PortComplex];


/**
 * 
 * @export
 * @interface PortProApiConfig
 */
export interface PortProApiConfig {
    /**
     * 
     * @type {string}
     * @memberof PortProApiConfig
     */
    'config_type'?: PortProApiConfigConfigTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'auto_enter_appointments_from_extension'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'auto_enter_appointments_in_tms'?: boolean;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'enter_appointments_when_added_to_tms_late'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'auto_enter_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'dont_enter_dual_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'send_pickup_number'?: boolean;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'add_new_containers_from_tms_to_dray_dog'?: boolean;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'sync_empty_containers'?: boolean;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'sync_terminated_containers'?: boolean;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: boolean;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'add_dual_note'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof PortProApiConfig
     */
    'client_id': string;
    /**
     * 
     * @type {string}
     * @memberof PortProApiConfig
     */
    'client_secret': string;
    /**
     * 
     * @type {string}
     * @memberof PortProApiConfig
     */
    'refresh_token'?: string;
    /**
     * 
     * @type {{ [key: string]: string; }}
     * @memberof PortProApiConfig
     */
    'tms_terminal_mapping'?: { [key: string]: string; };
    /**
     * AI-suggested terminal mappings. Keys are DrayDog terminals, values are lists of TMS terminal names that matched.
     * @type {{ [key: string]: any; }}
     * @memberof PortProApiConfig
     */
    'tms_terminal_mapping_suggestions'?: { [key: string]: any; };
    /**
     * If provided, only loads from this branch will be imported. This is useful for customers who have multiple branches and want to import loads from a specific branch. Only use if you are confident
     * @type {string}
     * @memberof PortProApiConfig
     */
    'branch_name'?: string;
    /**
     * Override for the Port Pro API base URL (e.g. for customers on a self-hosted instance). Defaults to the standard hosted endpoint when unset.
     * @type {string}
     * @memberof PortProApiConfig
     */
    'base_url'?: string;
    /**
     * If true, send pickupFromTime/pickupToTime/returnFromTime/returnToTime as naive local datetime strings (no UTC offset) instead of offset-aware ISO8601. Some self-hosted Port Pro instances don\'t apply the offset when rendering appointment times in their UI, they display the UTC-shifted clock value verbatim instead of converting back to local time. Sending a naive string gives their (apparently non-timezone-aware) display nothing to misinterpret. Only enable for a connection confirmed to need this workaround.
     * @type {boolean}
     * @memberof PortProApiConfig
     */
    'send_naive_appointment_times'?: boolean;
}

export const PortProApiConfigConfigTypeEnum = {
    PortProApi: 'port_pro_api'
} as const;

export type PortProApiConfigConfigTypeEnum = typeof PortProApiConfigConfigTypeEnum[keyof typeof PortProApiConfigConfigTypeEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const PortalName = {
    ItsApi: 'its_api',
    Hyundai: 'hyundai',
    One: 'one',
    Cosco: 'cosco',
    Oocl: 'oocl',
    Emodal: 'emodal',
    EmodalAsb: 'emodal_asb',
    Everport: 'everport',
    EverportNoLogin: 'everport_no_login',
    Tti: 'tti',
    TtiNoLogin: 'tti_no_login',
    LbctApi: 'lbct_api',
    Lbct: 'lbct',
    LbctTrucker: 'lbct_trucker',
    FmsCommunityPortal: 'fms_community_portal',
    FmsVoyageControls: 'fms_voyage_controls',
    FmsGoogle: 'fms_google',
    Yti: 'yti',
    YtiVoyageControls: 'yti_voyage_controls',
    YtiLynx: 'yti_lynx',
    YtiGoogle: 'yti_google',
    YtiGateLink: 'yti_gate_link',
    Wbct: 'wbct',
    WbctNoLogin: 'wbct_no_login',
    WbctApi: 'wbct_api',
    ApmTermpoint: 'apm_termpoint',
    ApmTermpointApi: 'apm_termpoint_api',
    ApmEir: 'apm_eir',
    ApmTops: 'apm_tops',
    Apm: 'apm',
    Its: 'its',
    ItsNoLogin: 'its_no_login',
    PierATideworks: 'pier_a_tideworks',
    PctTideworks: 'pct_tideworks',
    ShippersExpressTideworks: 'shippers_express_tideworks',
    ShippersLongbeachTideworks: 'shippers_longbeach_tideworks',
    Trapac: 'trapac',
    TrapacApi: 'trapac_api',
    PierPass: 'pier_pass',
    PortOptimizer: 'port_optimizer',
    PortOptimizerApi: 'port_optimizer_api',
    FmsApi: 'fms_api',
    VesselFinder: 'vessel_finder',
    GoogleSearchApi: 'google_search_api',
    OictTideworks: 'oict_tideworks'
} as const;

export type PortalName = typeof PortalName[keyof typeof PortalName];


/**
 * 
 * @export
 * @enum {string}
 */

export const PreferenceOption = {
    NotAllowed: 'not_allowed',
    Low: 'low',
    Medium: 'medium',
    High: 'high'
} as const;

export type PreferenceOption = typeof PreferenceOption[keyof typeof PreferenceOption];


/**
 * 
 * @export
 * @interface ProfitToolsBlackFlyConnectConfig
 */
export interface ProfitToolsBlackFlyConnectConfig {
    /**
     * 
     * @type {string}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'config_type'?: ProfitToolsBlackFlyConnectConfigConfigTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'auto_enter_appointments_from_extension'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'auto_enter_appointments_in_tms'?: boolean;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'enter_appointments_when_added_to_tms_late'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'auto_enter_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'dont_enter_dual_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'send_pickup_number'?: boolean;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'add_new_containers_from_tms_to_dray_dog'?: boolean;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'sync_empty_containers'?: boolean;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'sync_terminated_containers'?: boolean;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: boolean;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {boolean}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'add_dual_note'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'api_key': string;
    /**
     * 
     * @type {string}
     * @memberof ProfitToolsBlackFlyConnectConfig
     */
    'time_zone'?: string;
}

export const ProfitToolsBlackFlyConnectConfigConfigTypeEnum = {
    ProfitToolsBlackflyConnect: 'profit_tools_blackfly_connect'
} as const;

export type ProfitToolsBlackFlyConnectConfigConfigTypeEnum = typeof ProfitToolsBlackFlyConnectConfigConfigTypeEnum[keyof typeof ProfitToolsBlackFlyConnectConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface ProfitToolsCoreAPIConfig
 */
export interface ProfitToolsCoreAPIConfig {
    /**
     * 
     * @type {string}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'config_type'?: ProfitToolsCoreAPIConfigConfigTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'auto_enter_appointments_from_extension'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'auto_enter_appointments_in_tms'?: boolean;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'enter_appointments_when_added_to_tms_late'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'auto_enter_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'dont_enter_dual_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'send_pickup_number'?: boolean;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'add_new_containers_from_tms_to_dray_dog'?: boolean;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'sync_empty_containers'?: boolean;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'sync_terminated_containers'?: boolean;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: boolean;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'add_dual_note'?: boolean;
    /**
     * Unique identifier for the TMS we are connecting to
     * @type {string}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'tms_id': string;
    /**
     * If True, set the appointment time on the start of the leg. Example: set on mount-empty instead of the dismount-empty event
     * @type {boolean}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'set_empty_appointment_time_on_leg_start'?: boolean;
    /**
     * 
     * @type {{ [key: string]: string; }}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'tms_terminal_mapping'?: { [key: string]: string; };
    /**
     * AI-suggested terminal mappings. Keys are DrayDog terminals, values are lists of TMS terminal names that matched.
     * @type {{ [key: string]: any; }}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'tms_terminal_mapping_suggestions'?: { [key: string]: any; };
    /**
     * Timezone of the TMS
     * @type {string}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'timezone'?: string;
    /**
     * If provided, only loads from these divisions will be imported
     * @type {Array<string>}
     * @memberof ProfitToolsCoreAPIConfig
     */
    'limit_to_division_ids'?: Array<string>;
}

export const ProfitToolsCoreAPIConfigConfigTypeEnum = {
    ProfitToolsCoreApi: 'profit_tools_core_api'
} as const;

export type ProfitToolsCoreAPIConfigConfigTypeEnum = typeof ProfitToolsCoreAPIConfigConfigTypeEnum[keyof typeof ProfitToolsCoreAPIConfigConfigTypeEnum];

/**
 * 
 * @export
 * @enum {string}
 */

export const ReceivingRule = {
    Open: 'open',
    Closed: 'closed',
    DualsOnly: 'duals_only',
    Exempt: 'exempt'
} as const;

export type ReceivingRule = typeof ReceivingRule[keyof typeof ReceivingRule];


/**
 * 
 * @export
 * @interface ReconciliationAddItem
 */
export interface ReconciliationAddItem {
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ReconciliationAddItem
     */
    'status': ContainerStatus;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'location'?: string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'customer_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'master_bill_of_lading'?: string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'shipping_line'?: string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationAddItem
     */
    'container_type'?: string;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const ReconciliationChangeType = {
    Status: 'status'
} as const;

export type ReconciliationChangeType = typeof ReconciliationChangeType[keyof typeof ReconciliationChangeType];


/**
 * 
 * @export
 * @interface ReconciliationInvalidItem
 */
export interface ReconciliationInvalidItem {
    /**
     * 
     * @type {string}
     * @memberof ReconciliationInvalidItem
     */
    'entry': string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationInvalidItem
     */
    'reason': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const ReconciliationMode = {
    Partial: 'partial',
    Complete: 'complete'
} as const;

export type ReconciliationMode = typeof ReconciliationMode[keyof typeof ReconciliationMode];


/**
 * 
 * @export
 * @interface ReconciliationNoChangeItem
 */
export interface ReconciliationNoChangeItem {
    /**
     * 
     * @type {string}
     * @memberof ReconciliationNoChangeItem
     */
    'container_number': string;
}
/**
 * 
 * @export
 * @interface ReconciliationRemoveItem
 */
export interface ReconciliationRemoveItem {
    /**
     * 
     * @type {string}
     * @memberof ReconciliationRemoveItem
     */
    'container_number': string;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ReconciliationRemoveItem
     */
    'status': ContainerStatus;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationRemoveItem
     */
    'location'?: string;
    /**
     * 
     * @type {string}
     * @memberof ReconciliationRemoveItem
     */
    'customer_name'?: string;
}


/**
 * 
 * @export
 * @interface ReconciliationUpdateItem
 */
export interface ReconciliationUpdateItem {
    /**
     * 
     * @type {string}
     * @memberof ReconciliationUpdateItem
     */
    'container_number': string;
    /**
     * 
     * @type {ReconciliationChangeType}
     * @memberof ReconciliationUpdateItem
     */
    'change_type': ReconciliationChangeType;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ReconciliationUpdateItem
     */
    'current_value': ContainerStatus;
    /**
     * 
     * @type {ContainerStatus}
     * @memberof ReconciliationUpdateItem
     */
    'new_value': ContainerStatus;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const RejectionClassNameEnum = {
    TruckingCompanyNotAuthorizedByLine: 'TruckingCompanyNotAuthorizedByLine',
    EmptyContainerReturnRejectedDueToNotOnHired: 'EmptyContainerReturnRejectedDueToNotOnHired',
    GateScheduleInformationUnavailableException: 'GateScheduleInformationUnavailableException',
    ActionRejectedException: 'ActionRejectedException',
    EmptyContainerReturnRejectedException: 'EmptyContainerReturnRejectedException',
    ProxyConnectionFailed: 'ProxyConnectionFailed',
    TooManyRequestsException: 'TooManyRequestsException',
    PortalRateLimitedException: 'PortalRateLimitedException',
    AppointmentIsUsedException: 'AppointmentIsUsedException',
    NoAccessToTerminalException: 'NoAccessToTerminalException',
    RequestBlockedException: 'RequestBlockedException',
    BadHttpResponseCodeNoSentry: 'BadHTTPResponseCodeNoSentry',
    EModalBookingSystemErrorException: 'EModalBookingSystemErrorException',
    RescheduleRejectedDueToAppointmentStatusException: 'RescheduleRejectedDueToAppointmentStatusException',
    TerminalOperatingSystemFailure: 'TerminalOperatingSystemFailure',
    TaskTimeoutException: 'TaskTimeoutException',
    BadHttpResponse: 'BadHTTPResponse',
    AppointmentRescheduleRejectedException: 'AppointmentRescheduleRejectedException',
    DatabaseLockAcquisitionFailureException: 'DatabaseLockAcquisitionFailureException',
    SessionExpiredException: 'SessionExpiredException',
    AppointmentCancellationRejectedException: 'AppointmentCancellationRejectedException',
    TruncatedResponseException: 'TruncatedResponseException',
    ContainerNotAvailableBookingException: 'ContainerNotAvailableBookingException',
    TwoFactorAuthenticationRequiredException: 'TwoFactorAuthenticationRequiredException',
    InvalidPinException: 'InvalidPINException',
    OpenAiQuotaExceededException: 'OpenAIQuotaExceededException',
    InvalidLicensePlateException: 'InvalidLicensePlateException',
    AppointmentDoesNotExistAnymoreException: 'AppointmentDoesNotExistAnymoreException',
    AppointmentInformationUnavailableException: 'AppointmentInformationUnavailableException',
    AppointmentAlreadyBookedException: 'AppointmentAlreadyBookedException',
    TruckingCompanyBannedByLine: 'TruckingCompanyBannedByLine',
    EmptyContainerReturnRejectedDueToReceivingRulesException: 'EmptyContainerReturnRejectedDueToReceivingRulesException',
    EmptyReturnInformationUnavailableException: 'EmptyReturnInformationUnavailableException',
    ApplicationError: 'ApplicationError',
    OrganizationDoesNotHavePermission: 'OrganizationDoesNotHavePermission',
    EmptyInAppointmentAlreadyBookedException: 'EmptyInAppointmentAlreadyBookedException',
    PortalRateLimitExhaustedException: 'PortalRateLimitExhaustedException',
    UserAccountNotAssociatedToCompanyException: 'UserAccountNotAssociatedToCompanyException',
    PortalDownForMaintenanceException: 'PortalDownForMaintenanceException',
    ContainerAlreadyInGatedException: 'ContainerAlreadyInGatedException',
    BadHttpResponseContentType: 'BadHTTPResponseContentType',
    InvalidCredentialsException: 'InvalidCredentialsException',
    PortalDownException: 'PortalDownException',
    AppointmentBookingRejectedException: 'AppointmentBookingRejectedException',
    ContainerAlreadyOutGatedException: 'ContainerAlreadyOutGatedException',
    BadHttpResponseCode: 'BadHTTPResponseCode',
    RefreshTokenExpiredException: 'RefreshTokenExpiredException',
    SolveCaptchaFailureException: 'SolveCaptchaFailureException',
    AppointmentAlreadyCancelledException: 'AppointmentAlreadyCancelledException',
    FailWorkflowSilentlyException: 'FailWorkflowSilentlyException',
    PortalOutageTemporalException: 'PortalOutageTemporalException',
    BrowserBaseSessionTimeoutError: 'BrowserBaseSessionTimeoutError',
    NotAnImportContainerBookingException: 'NotAnImportContainerBookingException',
    AppointmentSlotNotAvailableException: 'AppointmentSlotNotAvailableException',
    TemporalFailure: 'TemporalFailure',
    TruncatedJsonException: 'TruncatedJsonException',
    TruckBannedException: 'TruckBannedException',
    OpenAiServiceUnavailableException: 'OpenAIServiceUnavailableException',
    TruckLicensePlateAlreadyUsed: 'TruckLicensePlateAlreadyUsed',
    VesselNotFoundException: 'VesselNotFoundException',
    ConflictingAppointmentUpdateException: 'ConflictingAppointmentUpdateException',
    ContainerAvailabilityInformationUnavailableException: 'ContainerAvailabilityInformationUnavailableException',
    AppointmentConflictException: 'AppointmentConflictException'
} as const;

export type RejectionClassNameEnum = typeof RejectionClassNameEnum[keyof typeof RejectionClassNameEnum];


/**
 * 
 * @export
 * @interface RelativeToLFDPreference
 */
export interface RelativeToLFDPreference {
    /**
     * 
     * @type {number}
     * @memberof RelativeToLFDPreference
     */
    'days_to_lfd': number;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof RelativeToLFDPreference
     */
    'preference': PreferenceOption;
}


/**
 * 
 * @export
 * @interface RemainingCapacityPeriod
 */
export interface RemainingCapacityPeriod {
    [key: string]: any;

    /**
     * 
     * @type {string}
     * @memberof RemainingCapacityPeriod
     */
    'start': string;
    /**
     * 
     * @type {string}
     * @memberof RemainingCapacityPeriod
     */
    'end': string;
    /**
     * Maximum number of appointments. Subtract `num_booked` to get remaining capacity
     * @type {number}
     * @memberof RemainingCapacityPeriod
     */
    'capacity': number;
    /**
     * Number of appointments already booked. Subtract from `capacity` to get remaining capacity
     * @type {number}
     * @memberof RemainingCapacityPeriod
     */
    'num_booked': number;
}
/**
 * 
 * @export
 * @interface RescheduleEvent
 */
export interface RescheduleEvent {
    /**
     * 
     * @type {SimpleUser}
     * @memberof RescheduleEvent
     */
    'requested_by'?: SimpleUser;
    /**
     * The id of the action request. Can be used with patch /appointments/booking/ to mark as entered in TMS.
     * @type {number}
     * @memberof RescheduleEvent
     */
    'action_request_id': number;
    /**
     * The appointment found after completing the action. Will be None if the appointment was not found.
     * @type {WebhookAppointment}
     * @memberof RescheduleEvent
     */
    'appointment'?: WebhookAppointment;
    /**
     * The url to send a patch request to mark the action as entered TMS. Send with entered_in_tms = True in the body.
     * @type {string}
     * @memberof RescheduleEvent
     */
    'mark_entered_in_tms_url': string;
    /**
     * The container number
     * @type {string}
     * @memberof RescheduleEvent
     */
    'container_number': string;
    /**
     * The terminal the appointment is at
     * @type {TerminalName}
     * @memberof RescheduleEvent
     */
    'terminal': TerminalName;
    /**
     * Identifies the event type. Also available in the `event_type` field
     * @type {string}
     * @memberof RescheduleEvent
     */
    'type'?: RescheduleEventTypeEnum;
}

export const RescheduleEventTypeEnum = {
    AppointmentRescheduled: 'appointment_rescheduled'
} as const;

export type RescheduleEventTypeEnum = typeof RescheduleEventTypeEnum[keyof typeof RescheduleEventTypeEnum];

/**
 * 
 * @export
 * @interface RescheduleRequest
 */
export interface RescheduleRequest {
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'window_start': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof RescheduleRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof RescheduleRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'container_number': string;
    /**
     * 
     * @type {SimpleUser}
     * @memberof RescheduleRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof RescheduleRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof RescheduleRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof RescheduleRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'auto_book_request_id'?: number;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'truck_license_plate': string;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'terminal_block'?: string;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'new_appointment_id'?: number;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof RescheduleRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'master_bill_of_lading': string;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'booking_request_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof RescheduleRequest
     */
    'cancellation_request_id'?: number;
    /**
     * 
     * @type {AppointmentSlot}
     * @memberof RescheduleRequest
     */
    'appointment_slot'?: AppointmentSlot;
    /**
     * 
     * @type {string}
     * @memberof RescheduleRequest
     */
    'action_type'?: RescheduleRequestActionTypeEnum;
}

export const RescheduleRequestActionTypeEnum = {
    Reschedule: 'reschedule'
} as const;

export type RescheduleRequestActionTypeEnum = typeof RescheduleRequestActionTypeEnum[keyof typeof RescheduleRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface SFTPConfig
 */
export interface SFTPConfig {
    /**
     * 
     * @type {string}
     * @memberof SFTPConfig
     */
    'config_type'?: SFTPConfigConfigTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'auto_enter_appointments_from_extension'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'auto_enter_appointments_in_tms'?: boolean;
    /**
     * If `true`, when an existing watch already in appointed state has its `tms_reference` newly populated by a TMS sync, trigger TMS appointment entry if the config settings also set the tms entry settings to allow it for the appointment type.
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'enter_appointments_when_added_to_tms_late'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'auto_enter_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'dont_enter_dual_empty_appointments_in_tms'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'send_pickup_number'?: boolean;
    /**
     * If `true`, we will add new containers from the TMS to Dray Dog
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'add_new_containers_from_tms_to_dray_dog'?: boolean;
    /**
     * If `true`, ingest the TMS empty-container list (ECL) feed.
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'sync_empty_containers'?: boolean;
    /**
     * If `true`, ingest TMS-reported terminations (empty returned) and mark matching watches terminated if not already. Defaults OFF: enable per-connection after verifying the TMS reports returns reliably.
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'sync_terminated_containers'?: boolean;
    /**
     * If `true`, we will attempt to identify containers cancelled in the TMS and remove them from Dray Dog
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'remove_containers_cancelled_in_tms_from_dray_dog'?: boolean;
    /**
     * If `true`, we will add a note to the appointment in the TMS to indicate that the appointment is part of a dual
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'add_dual_note'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof SFTPConfig
     */
    'host'?: string;
    /**
     * 
     * @type {number}
     * @memberof SFTPConfig
     */
    'port'?: number;
    /**
     * 
     * @type {string}
     * @memberof SFTPConfig
     */
    'user': string;
    /**
     * 
     * @type {string}
     * @memberof SFTPConfig
     */
    'password': string;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'adjust_over_midnight_to_previous_day'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof SFTPConfig
     */
    'adjust_over_midnight_to_previous_day_before_hour'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof SFTPConfig
     */
    'load_new_containers'?: boolean;
    /**
     * 
     * @type {{ [key: string]: string; }}
     * @memberof SFTPConfig
     */
    'tms_terminal_mapping'?: { [key: string]: string; };
    /**
     * 
     * @type {FileFormat}
     * @memberof SFTPConfig
     */
    'file_format'?: FileFormat;
}

export const SFTPConfigConfigTypeEnum = {
    Sftp: 'sftp'
} as const;

export type SFTPConfigConfigTypeEnum = typeof SFTPConfigConfigTypeEnum[keyof typeof SFTPConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface SavedCustomer
 */
export interface SavedCustomer {
    /**
     * 
     * @type {string}
     * @memberof SavedCustomer
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof SavedCustomer
     */
    'id': number;
}
/**
 * 
 * @export
 * @interface SavedEmptyInAppointmentSlot
 */
export interface SavedEmptyInAppointmentSlot {
    /**
     * The earliest time the driver can arrive for their appointment
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'window_start': string;
    /**
     * The latest time the driver can arrive for their appointment
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'window_end'?: string;
    /**
     * 
     * @type {TerminalName}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'terminal': TerminalName;
    /**
     * If `false` then this is just a placeholder to indicate noappointments are available
     * @type {boolean}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'has_availability'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'num_appointments_available'?: number;
    /**
     * The best unique identifier for the appointment slot time
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'terminal_reference'?: string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'extra_data'?: { [key: string]: any; };
    /**
     * 
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'observed': string;
    /**
     * ISO 6346 container number format
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'container_number'?: string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'shipping_line'?: ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'container_type'?: ContainerType;
    /**
     * 
     * @type {string}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'group_observed': string;
    /**
     * 
     * @type {number}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'organization_id'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof SavedEmptyInAppointmentSlot
     */
    'org_specific'?: boolean;
}


/**
 * 
 * @export
 * @interface SavedVesselForContainer
 */
export interface SavedVesselForContainer {
    [key: string]: any;

    /**
     * 
     * @type {string}
     * @memberof SavedVesselForContainer
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof SavedVesselForContainer
     */
    'id': number;
    /**
     * 
     * @type {number}
     * @memberof SavedVesselForContainer
     */
    'imo_number': number;
}
/**
 * 
 * @export
 * @interface SavedWebhook
 */
export interface SavedWebhook {
    /**
     * 
     * @type {string}
     * @memberof SavedWebhook
     */
    'url': string;
    /**
     * 
     * @type {number}
     * @memberof SavedWebhook
     */
    'added_by_user_id': number;
    /**
     * 
     * @type {string}
     * @memberof SavedWebhook
     */
    'authorization_header'?: string;
    /**
     * 
     * @type {Array<WebhookEventType>}
     * @memberof SavedWebhook
     */
    'event_types': Array<WebhookEventType>;
    /**
     * 
     * @type {string}
     * @memberof SavedWebhook
     */
    'created': string;
    /**
     * 
     * @type {string}
     * @memberof SavedWebhook
     */
    'updated': string;
    /**
     * 
     * @type {number}
     * @memberof SavedWebhook
     */
    'id': number;
    /**
     * If `true`, no events will be sent to this webhook. In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
     * @type {boolean}
     * @memberof SavedWebhook
     */
    'disabled': boolean;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const Shift = {
    First: 'first',
    Second: 'second',
    Third: 'third'
} as const;

export type Shift = typeof Shift[keyof typeof Shift];


/**
 * 
 * @export
 * @enum {string}
 */

export const ShippingLine = {
    Anlc: 'ANLC',
    Aplu: 'APLU',
    Banr: 'BANR',
    Ccs: 'CCS',
    Culu: 'CULU',
    Cmdu: 'CMDU',
    Ccsg: 'CCSG',
    Cosu: 'COSU',
    Eglv: 'EGLV',
    Hlcu: 'HLCU',
    Hduj: 'HDUJ',
    Hdmu: 'HDMU',
    Kmtu: 'KMTU',
    Maeu: 'MAEU',
    Mats: 'MATS',
    Llhp: 'LLHP',
    Mscu: 'MSCU',
    Npdt: 'NPDT',
    Pshi: 'PSHI',
    Pllu: 'PLLU',
    Oney: 'ONEY',
    Oolu: 'OOLU',
    Sqqy: 'SQQY',
    Ssph: 'SSPH',
    Sjbv: 'SJBV',
    Smlm: 'SMLM',
    Chvw: 'CHVW',
    Tjfh: 'TJFH',
    Txzj: 'TXZJ',
    Tgr: 'TGR',
    Whlc: 'WHLC',
    Xcls: 'XCLS',
    Ymja: 'YMJA',
    Ymlu: 'YMLU',
    Zimu: 'ZIMU'
} as const;

export type ShippingLine = typeof ShippingLine[keyof typeof ShippingLine];


/**
 * 
 * @export
 * @interface SimpleUser
 */
export interface SimpleUser {
    /**
     * 
     * @type {number}
     * @memberof SimpleUser
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof SimpleUser
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof SimpleUser
     */
    'last_name'?: string;
}
/**
 * 
 * @export
 * @interface SkippedAppointmentRefresh
 */
export interface SkippedAppointmentRefresh {
    /**
     * 
     * @type {string}
     * @memberof SkippedAppointmentRefresh
     */
    'container_number': string;
    /**
     * 
     * @type {PortalName}
     * @memberof SkippedAppointmentRefresh
     */
    'portal': PortalName;
    /**
     * 
     * @type {string}
     * @memberof SkippedAppointmentRefresh
     */
    'reason': string;
}


/**
 * 
 * @export
 * @interface SlotDetails
 */
export interface SlotDetails {
    /**
     * Some terminals will \"recommend\" certain times that should provider a better turn time (faster processing time) based on the location of the container in the stack or other factors. This field indicates times that are recommended, as specified by the terminal
     * @type {boolean}
     * @memberof SlotDetails
     */
    'recommended'?: boolean;
    /**
     * Indicates an issue with this appointment time, for example it might be a \'ghost\' appointment this actually  not bookable
     * @type {string}
     * @memberof SlotDetails
     */
    'warning'?: string;
    /**
     * Indicates that this slot is not bookable, for example it might be a \'ghost\' appointment this actually  not bookable
     * @type {boolean}
     * @memberof SlotDetails
     */
    'not_bookable'?: boolean;
    /**
     * Indicates that this slot is org specific, meaning it is available for the current organization only
     * @type {boolean}
     * @memberof SlotDetails
     */
    'org_specific'?: boolean;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const SlotNotBookableReason = {
    OrgSpecificSlotForDifferentOrg: 'org_specific_slot_for_different_org',
    SaturdayNotAllowed: 'saturday_not_allowed',
    SundayNotAllowed: 'sunday_not_allowed',
    FridaySecondShiftNotAllowed: 'friday_second_shift_not_allowed',
    MondayFirstShiftNotAllowed: 'monday_first_shift_not_allowed',
    TimePreferenceNotAllowed: 'time_preference_not_allowed',
    WithinMinAdvancedNotice: 'within_min_advanced_notice',
    CustomDayPreferenceIsNotAllowed: 'custom_day_preference_is_not_allowed',
    DayOfWeekPreferenceNotAllowed: 'day_of_week_preference_not_allowed',
    DayNotInRelativeToLfdPreferences: 'day_not_in_relative_to_lfd_preferences',
    SameOrLowerPriorityThanExistingAppointment: 'same_or_lower_priority_than_existing_appointment',
    OutOfCapacity: 'out_of_capacity',
    MaxAutoBookAttempts: 'max_auto_book_attempts',
    SlotFullyConsumed: 'slot_fully_consumed',
    SlotMarkedGhost: 'slot_marked_ghost',
    GateClosed: 'gate_closed'
} as const;

export type SlotNotBookableReason = typeof SlotNotBookableReason[keyof typeof SlotNotBookableReason];


/**
 * 
 * @export
 * @interface SlotNotFoundResponse
 */
export interface SlotNotFoundResponse {
    /**
     * 
     * @type {string}
     * @memberof SlotNotFoundResponse
     */
    'message': string;
    /**
     * 
     * @type {GETContainerAvailability}
     * @memberof SlotNotFoundResponse
     */
    'new_availability': GETContainerAvailability;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const SlotTrigger = {
    AutoBook: 'auto_book',
    Manual: 'manual',
    Synthetic: 'synthetic'
} as const;

export type SlotTrigger = typeof SlotTrigger[keyof typeof SlotTrigger];


/**
 * 
 * @export
 * @enum {string}
 */

export const SortOptions = {
    LastFreeDay: 'last_free_day',
    ImportAppointmentTime: 'import_appointment_time',
    AppointmentTime: 'appointment_time',
    ImportOutGatedTime: 'import_out_gated_time',
    EmptyInGatedTime: 'empty_in_gated_time',
    VesselEta: 'vessel_eta',
    PassedContainersOrder: 'passed_containers_order',
    AppointmentStart: 'appointment_start',
    GateTime: 'gate_time',
    Random: 'random'
} as const;

export type SortOptions = typeof SortOptions[keyof typeof SortOptions];


/**
 * 
 * @export
 * @interface StartedEmptyBookingRequest
 */
export interface StartedEmptyBookingRequest {
    /**
     * 
     * @type {number}
     * @memberof StartedEmptyBookingRequest
     */
    'request_id': number;
    /**
     * 
     * @type {ActionType}
     * @memberof StartedEmptyBookingRequest
     */
    'type': ActionType;
    /**
     * 
     * @type {string}
     * @memberof StartedEmptyBookingRequest
     */
    'container_number': string;
}


/**
 * 
 * @export
 * @interface StartedEmptyPickupBooking
 */
export interface StartedEmptyPickupBooking {
    /**
     * 
     * @type {number}
     * @memberof StartedEmptyPickupBooking
     */
    'unit_index': number;
    /**
     * 
     * @type {number}
     * @memberof StartedEmptyPickupBooking
     */
    'request_id': number;
}
/**
 * 
 * @export
 * @interface SwapEmptyRequest
 */
export interface SwapEmptyRequest {
    /**
     * 
     * @type {number}
     * @memberof SwapEmptyRequest
     */
    'id': number;
    /**
     * 
     * @type {TerminalName}
     * @memberof SwapEmptyRequest
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'container_number': string;
    /**
     * 
     * @type {ActionStatus}
     * @memberof SwapEmptyRequest
     */
    'status': ActionStatus;
    /**
     * 
     * @type {SimpleUser}
     * @memberof SwapEmptyRequest
     */
    'requested_by'?: SimpleUser;
    /**
     * 
     * @type {ErrorClassName}
     * @memberof SwapEmptyRequest
     */
    'error_class_name'?: ErrorClassName;
    /**
     * Raw error message from the underlying terminal or data source (e.g. the terminal portal, an external API, or an internal timeout).
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'error_message'?: string;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'created': string;
    /**
     * 
     * @type {number}
     * @memberof SwapEmptyRequest
     */
    'appointment_verification_id'?: number;
    /**
     * 
     * @type {number}
     * @memberof SwapEmptyRequest
     */
    'organization_id': number;
    /**
     * 
     * @type {number}
     * @memberof SwapEmptyRequest
     */
    'connection_id': number;
    /**
     * 
     * @type {PortalName}
     * @memberof SwapEmptyRequest
     */
    'portal_name': PortalName;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'requests_log_link'?: string;
    /**
     * 
     * @type {number}
     * @memberof SwapEmptyRequest
     */
    'appointment_id': number;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'empty_container_number': string;
    /**
     * 
     * @type {ShippingLine}
     * @memberof SwapEmptyRequest
     */
    'shipping_line': ShippingLine;
    /**
     * 
     * @type {ContainerType}
     * @memberof SwapEmptyRequest
     */
    'container_type': ContainerType;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'chassis_number'?: string;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'truck_license_plate'?: string;
    /**
     * 
     * @type {LicensePlateOrigin}
     * @memberof SwapEmptyRequest
     */
    'license_plate_origin'?: LicensePlateOrigin;
    /**
     * 
     * @type {string}
     * @memberof SwapEmptyRequest
     */
    'action_type'?: SwapEmptyRequestActionTypeEnum;
}

export const SwapEmptyRequestActionTypeEnum = {
    SwapEmpty: 'swap_empty'
} as const;

export type SwapEmptyRequestActionTypeEnum = typeof SwapEmptyRequestActionTypeEnum[keyof typeof SwapEmptyRequestActionTypeEnum];

/**
 * 
 * @export
 * @interface TerminalCredentialStatus
 */
export interface TerminalCredentialStatus {
    /**
     * Terminal these statuses apply to
     * @type {TerminalName}
     * @memberof TerminalCredentialStatus
     */
    'terminal': TerminalName;
    /**
     * Credential status for import availability/status tracking
     * @type {CredentialRequirement}
     * @memberof TerminalCredentialStatus
     */
    'tracking': CredentialRequirement;
    /**
     * Credential status for booking appointments
     * @type {CredentialRequirement}
     * @memberof TerminalCredentialStatus
     */
    'booking': CredentialRequirement;
}


/**
 * 
 * @export
 * @interface TerminalMappingSuggestionsResponse
 */
export interface TerminalMappingSuggestionsResponse {
    /**
     * Mapping of DrayDog terminal to list of TMS terminal names
     * @type {{ [key: string]: any; }}
     * @memberof TerminalMappingSuggestionsResponse
     */
    'suggestions': { [key: string]: any; };
}
/**
 * 
 * @export
 * @enum {string}
 */

export const TerminalName = {
    StAlam: 'st_alam',
    Apm: 'apm',
    StCarson: 'st_carson',
    Everport: 'everport',
    Fms: 'fms',
    Its: 'its',
    Lbct: 'lbct',
    StLb: 'st_lb',
    MatsonLb: 'matson_lb',
    Pct: 'pct',
    PierA: 'pier_a',
    Trapac: 'trapac',
    Tti: 'tti',
    Wbct: 'wbct',
    Yti: 'yti',
    Oict: 'oict',
    TrapacOak: 'trapac_oak',
    EverportOak: 'everport_oak'
} as const;

export type TerminalName = typeof TerminalName[keyof typeof TerminalName];


/**
 * 
 * @export
 * @interface TerminalOrgConfig
 */
export interface TerminalOrgConfig {
    /**
     * 
     * @type {TerminalName}
     * @memberof TerminalOrgConfig
     */
    'terminal': TerminalName;
    /**
     * Whether the terminal has private slots that can be booked by users
     * @type {boolean}
     * @memberof TerminalOrgConfig
     */
    'has_private_slots'?: boolean;
    /**
     * Whether to pre-fill a random license plate when booking or Auto Booking an appointment. Applies to the frontend only
     * @type {boolean}
     * @memberof TerminalOrgConfig
     */
    'prefill_license_plates'?: boolean;
    /**
     * Only meaningful at a Green Lane terminal (e.g. APM). When False (default), green/EV trucks are excluded from Auto Book random plate selection so mixed fleets are not locked into an unswappable green plate; enable for all-EV fleets that want green trucks used.
     * @type {boolean}
     * @memberof TerminalOrgConfig
     */
    'allow_green_trucks_in_auto_book'?: boolean;
    /**
     * If no license plate has been set 2 hours before the appointment, set a random unused license plate
     * @type {boolean}
     * @memberof TerminalOrgConfig
     */
    'set_unused_license_plate_if_no_plate_set_as_appointment_approaches'?: boolean;
}


/**
 * Used by the client to indicate readings that it has in it\'s cache and need not be returned if that is still the latest reading
 * @export
 * @interface TerminalReadingMetadata
 */
export interface TerminalReadingMetadata {
    /**
     * The terminal this reading is for
     * @type {TerminalName}
     * @memberof TerminalReadingMetadata
     */
    'terminal': TerminalName;
    /**
     * When the reading was first seen
     * @type {string}
     * @memberof TerminalReadingMetadata
     */
    'reading_first_observed': string;
    /**
     * When the reading was last parsed
     * @type {string}
     * @memberof TerminalReadingMetadata
     */
    'reading_last_parsed': string;
}


/**
 * 
 * @export
 * @interface TerminalReadingRules
 */
export interface TerminalReadingRules {
    /**
     * Indicates which terminal this group of rules is for
     * @type {TerminalName}
     * @memberof TerminalReadingRules
     */
    'terminal': TerminalName;
    /**
     * When we last checked rules at this terminal, i.e. how \"current\" these rules are
     * @type {string}
     * @memberof TerminalReadingRules
     */
    'reading_first_observed': string;
    /**
     * When the reading was last parsed. This may change if we \"re-parse\" a rule
     * @type {string}
     * @memberof TerminalReadingRules
     */
    'reading_last_parsed': string;
    /**
     * When we last checked data for this terminal
     * @type {string}
     * @memberof TerminalReadingRules
     */
    'reading_last_observed': string;
    /**
     * `true` if the reading information passed to the API is still the same latest reading for the terminal
     * @type {boolean}
     * @memberof TerminalReadingRules
     */
    'cache_valid': boolean;
    /**
     * Return rules for this terminal. `null` if `cache_valid` is `true`
     * @type {Array<GETEmptyReturnRule>}
     * @memberof TerminalReadingRules
     */
    'rules'?: Array<GETEmptyReturnRule>;
}


/**
 * 
 * @export
 * @interface TimePreference
 */
export interface TimePreference {
    /**
     * Indicates this preference applies to appointment slots after this (local) time but before any other time preference entries
     * @type {string}
     * @memberof TimePreference
     */
    'start_local': string;
    /**
     * 
     * @type {PreferenceOption}
     * @memberof TimePreference
     */
    'preference': PreferenceOption;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const TransactionDirection = {
    Inbound: 'inbound',
    Outbound: 'outbound'
} as const;

export type TransactionDirection = typeof TransactionDirection[keyof typeof TransactionDirection];


/**
 * 
 * @export
 * @interface Url
 */
export interface Url {
}
/**
 * 
 * @export
 * @interface UserNameAndPasswordConfig
 */
export interface UserNameAndPasswordConfig {
    /**
     * 
     * @type {string}
     * @memberof UserNameAndPasswordConfig
     */
    'config_type'?: UserNameAndPasswordConfigConfigTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof UserNameAndPasswordConfig
     */
    'user': string;
    /**
     * 
     * @type {string}
     * @memberof UserNameAndPasswordConfig
     */
    'password': string;
}

export const UserNameAndPasswordConfigConfigTypeEnum = {
    UserNameAndPassword: 'user_name_and_password'
} as const;

export type UserNameAndPasswordConfigConfigTypeEnum = typeof UserNameAndPasswordConfigConfigTypeEnum[keyof typeof UserNameAndPasswordConfigConfigTypeEnum];

/**
 * 
 * @export
 * @interface UserNotificationEvent
 */
export interface UserNotificationEvent {
    /**
     * 
     * @type {string}
     * @memberof UserNotificationEvent
     */
    'event_name'?: UserNotificationEventEventNameEnum;
    /**
     * Channel name is dynamic and should be in format: `private-user-notifications-org_id-user_id`
     * @type {string}
     * @memberof UserNotificationEvent
     */
    'channel': string;
    /**
     * 
     * @type {string}
     * @memberof UserNotificationEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Notification}
     * @memberof UserNotificationEvent
     */
    'notification': Notification;
}

export const UserNotificationEventEventNameEnum = {
    UserNotification: 'user_notification'
} as const;

export type UserNotificationEventEventNameEnum = typeof UserNotificationEventEventNameEnum[keyof typeof UserNotificationEventEventNameEnum];

/**
 * 
 * @export
 * @interface UserPreferences
 */
export interface UserPreferences {
    /**
     * 
     * @type {Array<string>}
     * @memberof UserPreferences
     */
    'feature_flags'?: Array<string>;
    /**
     * In minutes, the interval at which auto book email notifications will be sent if the appointment is over 12 hours away
     * @type {number}
     * @memberof UserPreferences
     */
    'auto_book_notification_interval_minutes'?: number;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const UserRoleType = {
    CustomerService: 'Customer Service',
    Management: 'Management',
    Dispatch: 'Dispatch',
    SubCarrier: 'Sub-Carrier',
    Other: 'Other'
} as const;

export type UserRoleType = typeof UserRoleType[keyof typeof UserRoleType];


/**
 * 
 * @export
 * @interface ValidationError
 */
export interface ValidationError {
    /**
     * 
     * @type {Array<ValidationErrorLocInner>}
     * @memberof ValidationError
     */
    'loc': Array<ValidationErrorLocInner>;
    /**
     * 
     * @type {string}
     * @memberof ValidationError
     */
    'msg': string;
    /**
     * 
     * @type {string}
     * @memberof ValidationError
     */
    'type': string;
}
/**
 * 
 * @export
 * @interface ValidationErrorLocInner
 */
export interface ValidationErrorLocInner {
}
/**
 * 
 * @export
 * @interface VesselETAsUpdatedEvent
 */
export interface VesselETAsUpdatedEvent {
    /**
     * 
     * @type {string}
     * @memberof VesselETAsUpdatedEvent
     */
    'event_name'?: VesselETAsUpdatedEventEventNameEnum;
    /**
     * 
     * @type {string}
     * @memberof VesselETAsUpdatedEvent
     */
    'channel'?: string;
    /**
     * 
     * @type {string}
     * @memberof VesselETAsUpdatedEvent
     */
    'message_id'?: string;
    /**
     * 
     * @type {Array<GETVesselSchedules>}
     * @memberof VesselETAsUpdatedEvent
     */
    'etas': Array<GETVesselSchedules>;
}

export const VesselETAsUpdatedEventEventNameEnum = {
    VesselEtaUpdated: 'vessel_eta_updated'
} as const;

export type VesselETAsUpdatedEventEventNameEnum = typeof VesselETAsUpdatedEventEventNameEnum[keyof typeof VesselETAsUpdatedEventEventNameEnum];

/**
 * 
 * @export
 * @interface VesselScheduleChange
 */
export interface VesselScheduleChange {
    /**
     * 
     * @type {VesselScheduleField}
     * @memberof VesselScheduleChange
     */
    'field': VesselScheduleField;
    /**
     * 
     * @type {FieldChangeValueType}
     * @memberof VesselScheduleChange
     */
    'value_type': FieldChangeValueType;
    /**
     * 
     * @type {string}
     * @memberof VesselScheduleChange
     */
    'from_value': string;
    /**
     * 
     * @type {string}
     * @memberof VesselScheduleChange
     */
    'to_value': string;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const VesselScheduleEventType = {
    EtaFound: 'eta_found',
    ScheduleUpdated: 'schedule_updated'
} as const;

export type VesselScheduleEventType = typeof VesselScheduleEventType[keyof typeof VesselScheduleEventType];


/**
 * 
 * @export
 * @enum {string}
 */

export const VesselScheduleField = {
    Eta: 'eta',
    Erd: 'erd',
    FirstAvailableDate: 'first_available_date',
    CargoCutoff: 'cargo_cutoff',
    DocumentationCutoff: 'documentation_cutoff',
    ReeferCutoff: 'reefer_cutoff',
    HazmatCutoff: 'hazmat_cutoff'
} as const;

export type VesselScheduleField = typeof VesselScheduleField[keyof typeof VesselScheduleField];


/**
 * Schedule data for the resolved vessel at one terminal.  Only included in vessel_schedules when a schedule row was found for that (vessel, terminal) pair — omitted rather than filled with nulls so the frontend can distinguish \'matched vessel, no schedule for this terminal\' from \'no data\'.  Match entries to statuses[] by the terminal field, not by position — statuses[] can have multiple rows per terminal (different sources), so lengths may differ.
 * @export
 * @interface VesselScheduleForExport
 */
export interface VesselScheduleForExport {
    /**
     * Terminal this schedule applies to
     * @type {TerminalName}
     * @memberof VesselScheduleForExport
     */
    'terminal': TerminalName;
    /**
     * Voyage number
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'voyage_number'?: string;
    /**
     * Estimated time of arrival
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'eta'?: string;
    /**
     * Earliest receiving date and time. Local midnight when the terminal publishes only a date
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'erd'?: string;
    /**
     * First available date: earliest date an empty can be picked up for the vessel (informational, usually a few days before the ERD)
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'first_available_date'?: string;
    /**
     * Cargo cutoff datetime
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'cargo_cutoff'?: string;
    /**
     * Documentation cutoff datetime
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'documentation_cutoff'?: string;
    /**
     * Reefer cutoff datetime
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'reefer_cutoff'?: string;
    /**
     * Hazmat cutoff datetime
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'hazmat_cutoff'?: string;
    /**
     * When this schedule data was last scraped
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'last_observed'?: string;
    /**
     * When the ERD or a cutoff most recently changed, if that happened within the recent-change window. None if nothing changed recently.
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'changed_at'?: string;
    /**
     * ERD before the most recent change, if it changed
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'previous_erd'?: string;
    /**
     * Cargo cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'previous_cargo_cutoff'?: string;
    /**
     * Documentation cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'previous_documentation_cutoff'?: string;
    /**
     * Reefer cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'previous_reefer_cutoff'?: string;
    /**
     * Hazmat cutoff before the most recent change, if it changed
     * @type {string}
     * @memberof VesselScheduleForExport
     */
    'previous_hazmat_cutoff'?: string;
}


/**
 * 
 * @export
 * @interface VesselScheduleHistory
 */
export interface VesselScheduleHistory {
    /**
     * 
     * @type {Array<VesselScheduleHistoryEvent>}
     * @memberof VesselScheduleHistory
     */
    'events': Array<VesselScheduleHistoryEvent>;
}
/**
 * 
 * @export
 * @interface VesselScheduleHistoryEvent
 */
export interface VesselScheduleHistoryEvent {
    /**
     * 
     * @type {VesselScheduleEventType}
     * @memberof VesselScheduleHistoryEvent
     */
    'type': VesselScheduleEventType;
    /**
     * 
     * @type {string}
     * @memberof VesselScheduleHistoryEvent
     */
    'time': string;
    /**
     * 
     * @type {PortalName}
     * @memberof VesselScheduleHistoryEvent
     */
    'source': PortalName;
    /**
     * 
     * @type {Array<VesselScheduleChange>}
     * @memberof VesselScheduleHistoryEvent
     */
    'changes': Array<VesselScheduleChange>;
}


/**
 * 
 * @export
 * @interface WebhookAppointment
 */
export interface WebhookAppointment {
    /**
     * 
     * @type {TerminalName}
     * @memberof WebhookAppointment
     */
    'terminal': TerminalName;
    /**
     * 
     * @type {string}
     * @memberof WebhookAppointment
     */
    'container_number': string;
    /**
     * 
     * @type {string}
     * @memberof WebhookAppointment
     */
    'window_start': string;
    /**
     * 
     * @type {string}
     * @memberof WebhookAppointment
     */
    'window_end'?: string;
    /**
     * The license plate set on the appointment
     * @type {string}
     * @memberof WebhookAppointment
     */
    'truck_license_plate'?: string;
    /**
     * The terminal appointment number
     * @type {string}
     * @memberof WebhookAppointment
     */
    'terminal_reference': string;
    /**
     * The pin required to pickup the container, may be different from the terminal reference depending on the terminal
     * @type {string}
     * @memberof WebhookAppointment
     */
    'pickup_number'?: string;
    /**
     * The direction of the appointment from the terminal
     * @type {TransactionDirection}
     * @memberof WebhookAppointment
     */
    'direction': TransactionDirection;
    /**
     * Whether the container is loaded or empty
     * @type {boolean}
     * @memberof WebhookAppointment
     */
    'loaded': boolean;
}


/**
 * Represents a model for a webhook event
 * @export
 * @interface WebhookEvent
 */
export interface WebhookEvent {
    /**
     * Also available in the `type` field of the `event` so that this value may be used as a type discriminator.
     * @type {WebhookEventType}
     * @memberof WebhookEvent
     */
    'event_type': WebhookEventType;
    /**
     * 
     * @type {Event}
     * @memberof WebhookEvent
     */
    'event': Event;
}


/**
 * 
 * @export
 * @enum {string}
 */

export const WebhookEventType = {
    ImportAppointmentBooked: 'import_appointment_booked',
    EmptyAppointmentBooked: 'empty_appointment_booked',
    AppointmentRescheduled: 'appointment_rescheduled',
    EditAppointmentLicensePlate: 'edit_appointment_license_plate'
} as const;

export type WebhookEventType = typeof WebhookEventType[keyof typeof WebhookEventType];


/**
 * 
 * @export
 * @enum {string}
 */

export const WebsocketChannel = {
    PrivateEmptyReturnRules: 'private-empty-return-rules',
    GateSchedule: 'gate-schedule',
    PrivateVesselEtas: 'private-vessel-etas',
    PrivateEmptyReturnRuleChecks: 'private-empty-return-rule-checks',
    GateScheduleChecks: 'gate-schedule-checks',
    PrivateCacheGlobalNotifications: 'private-cache-global-notifications',
    GateCameras: 'gate-cameras',
    Dynamic: 'dynamic'
} as const;

export type WebsocketChannel = typeof WebsocketChannel[keyof typeof WebsocketChannel];



/**
 * AppointmentBookingApi - axios parameter creator
 * @export
 */
export const AppointmentBookingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Book Appointment
         * @param {POSTAppointmentBooking} pOSTAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookAppointmentAppointmentsBookingBookPost: async (pOSTAppointmentBooking: POSTAppointmentBooking, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentBooking' is not null or undefined
            assertParamExists('bookAppointmentAppointmentsBookingBookPost', 'pOSTAppointmentBooking', pOSTAppointmentBooking)
            const localVarPath = `/appointments/booking/book/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentBooking, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Book Empty Appointment
         * @param {POSTEmptyAppointmentBooking} pOSTEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookEmptyAppointmentAppointmentsBookingBookEmptyPost: async (pOSTEmptyAppointmentBooking: POSTEmptyAppointmentBooking, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTEmptyAppointmentBooking' is not null or undefined
            assertParamExists('bookEmptyAppointmentAppointmentsBookingBookEmptyPost', 'pOSTEmptyAppointmentBooking', pOSTEmptyAppointmentBooking)
            const localVarPath = `/appointments/booking/book_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEmptyAppointmentBooking, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Bulk Book Empty Appointments
         * @param {POSTEmptyAppointmentBookingBulk} pOSTEmptyAppointmentBookingBulk 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost: async (pOSTEmptyAppointmentBookingBulk: POSTEmptyAppointmentBookingBulk, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTEmptyAppointmentBookingBulk' is not null or undefined
            assertParamExists('bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost', 'pOSTEmptyAppointmentBookingBulk', pOSTEmptyAppointmentBookingBulk)
            const localVarPath = `/appointments/booking/book_empty/bulk/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEmptyAppointmentBookingBulk, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Cancel Appointments
         * @param {BodyCancelAppointmentsAppointmentsBookingDelete} bodyCancelAppointmentsAppointmentsBookingDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointmentsAppointmentsBookingDelete: async (bodyCancelAppointmentsAppointmentsBookingDelete: BodyCancelAppointmentsAppointmentsBookingDelete, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyCancelAppointmentsAppointmentsBookingDelete' is not null or undefined
            assertParamExists('cancelAppointmentsAppointmentsBookingDelete', 'bodyCancelAppointmentsAppointmentsBookingDelete', bodyCancelAppointmentsAppointmentsBookingDelete)
            const localVarPath = `/appointments/booking/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyCancelAppointmentsAppointmentsBookingDelete, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Check In
         * @param {POSTCheckIn} pOSTCheckIn 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkInAppointmentsBookingCheckInPost: async (pOSTCheckIn: POSTCheckIn, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTCheckIn' is not null or undefined
            assertParamExists('checkInAppointmentsBookingCheckInPost', 'pOSTCheckIn', pOSTCheckIn)
            const localVarPath = `/appointments/booking/check_in/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTCheckIn, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Edit Appointment Add Empty
         * @param {POSTAddEmpty} pOSTAddEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editAppointmentAddEmptyAppointmentsBookingAddEmptyPost: async (pOSTAddEmpty: POSTAddEmpty, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAddEmpty' is not null or undefined
            assertParamExists('editAppointmentAddEmptyAppointmentsBookingAddEmptyPost', 'pOSTAddEmpty', pOSTAddEmpty)
            const localVarPath = `/appointments/booking/add_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAddEmpty, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Edit Appointment Swap Empty
         * @param {POSTSwapEmpty} pOSTSwapEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost: async (pOSTSwapEmpty: POSTSwapEmpty, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTSwapEmpty' is not null or undefined
            assertParamExists('editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost', 'pOSTSwapEmpty', pOSTSwapEmpty)
            const localVarPath = `/appointments/booking/swap_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTSwapEmpty, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Edit Chassis
         * @param {POSTEditChassis} pOSTEditChassis 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editChassisAppointmentsBookingEditChassisPost: async (pOSTEditChassis: POSTEditChassis, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTEditChassis' is not null or undefined
            assertParamExists('editChassisAppointmentsBookingEditChassisPost', 'pOSTEditChassis', pOSTEditChassis)
            const localVarPath = `/appointments/booking/edit_chassis/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEditChassis, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Edit License Plate
         * @param {POSTEditLicensePlate} pOSTEditLicensePlate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editLicensePlateAppointmentsBookingEditLicensePlatePost: async (pOSTEditLicensePlate: POSTEditLicensePlate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTEditLicensePlate' is not null or undefined
            assertParamExists('editLicensePlateAppointmentsBookingEditLicensePlatePost', 'pOSTEditLicensePlate', pOSTEditLicensePlate)
            const localVarPath = `/appointments/booking/edit_license_plate/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEditLicensePlate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Used to enter an action request for an appointment into your TMS if you have a TMS connection configured
         * @summary Enter Action Request In Tms
         * @param {number} actionRequestId 
         * @param {ActionType} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost: async (actionRequestId: number, actionType?: ActionType, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'actionRequestId' is not null or undefined
            assertParamExists('enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost', 'actionRequestId', actionRequestId)
            const localVarPath = `/appointments/booking/history/{action_request_id}/enter_in_tms/`
                .replace(`{${"action_request_id"}}`, encodeURIComponent(String(actionRequestId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (actionType !== undefined) {
                localVarQueryParameter['action_type'] = actionType;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Action Request Statuses
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {ActionType} [action] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost: async (pOSTBookingRequestStatuses: POSTBookingRequestStatuses, action?: ActionType, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTBookingRequestStatuses' is not null or undefined
            assertParamExists('getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost', 'pOSTBookingRequestStatuses', pOSTBookingRequestStatuses)
            const localVarPath = `/appointments/booking/action_request_statuses/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (action !== undefined) {
                localVarQueryParameter['action'] = action;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTBookingRequestStatuses, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Available Empty Appointment Slots
         * @param {TerminalName} terminal 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {string} [containerNumber] 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet: async (terminal: TerminalName, shippingLine: ShippingLine, containerType: ContainerType, containerNumber?: string, forceRefresh?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet', 'terminal', terminal)
            // verify required parameter 'shippingLine' is not null or undefined
            assertParamExists('getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet', 'shippingLine', shippingLine)
            // verify required parameter 'containerType' is not null or undefined
            assertParamExists('getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet', 'containerType', containerType)
            const localVarPath = `/appointments/availability/empty/{terminal}/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (shippingLine !== undefined) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType !== undefined) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (containerNumber !== undefined) {
                localVarQueryParameter['container_number'] = containerNumber;
            }

            if (forceRefresh !== undefined) {
                localVarQueryParameter['force_refresh'] = forceRefresh;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Available Export Empty Pickup Slots
         * @param {TerminalName} terminal 
         * @param {string} bookingNumber 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet: async (terminal: TerminalName, bookingNumber: string, shippingLine: ShippingLine, containerType: ContainerType, forceRefresh?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet', 'terminal', terminal)
            // verify required parameter 'bookingNumber' is not null or undefined
            assertParamExists('getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet', 'bookingNumber', bookingNumber)
            // verify required parameter 'shippingLine' is not null or undefined
            assertParamExists('getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet', 'shippingLine', shippingLine)
            // verify required parameter 'containerType' is not null or undefined
            assertParamExists('getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet', 'containerType', containerType)
            const localVarPath = `/appointments/availability/export_empty_pickup/{terminal}/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (bookingNumber !== undefined) {
                localVarQueryParameter['booking_number'] = bookingNumber;
            }

            if (shippingLine !== undefined) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType !== undefined) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (forceRefresh !== undefined) {
                localVarQueryParameter['force_refresh'] = forceRefresh;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Available Slots For Container
         * @param {string} containerNumber 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {TerminalName} [terminal] If provided, will return slots for this terminal. If not provided, we will use saved import status data to determine the terminal, raising an error if we have no saved data
         * @param {string} [terminalBlockAvailabilityKey] If provided, will only return cached slots for this terminal block availability key. **If you don\&#39;t know don\&#39;t pass this option**
         * @param {boolean} [lookupTerminalBlockAvailabilityKey] If true, we will check for saved locations for this container and find a relevant terminal block availability key. This can make appointment slot querying much faster.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet: async (containerNumber: string, forceRefresh?: boolean, terminal?: TerminalName, terminalBlockAvailabilityKey?: string, lookupTerminalBlockAvailabilityKey?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet', 'containerNumber', containerNumber)
            const localVarPath = `/appointments/availability/{container_number}/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (forceRefresh !== undefined) {
                localVarQueryParameter['force_refresh'] = forceRefresh;
            }

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (terminalBlockAvailabilityKey !== undefined) {
                localVarQueryParameter['terminal_block_availability_key'] = terminalBlockAvailabilityKey;
            }

            if (lookupTerminalBlockAvailabilityKey !== undefined) {
                localVarQueryParameter['lookup_terminal_block_availability_key'] = lookupTerminalBlockAvailabilityKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves booking history based on container numbers and specific time frames.  If container numbers are provided, it fetches the booking history for those containers from the last year. If no container numbers are provided, it fetches the booking history for the last 5 days. If user id is provided, it fetches the booking history for the past 30 days.
         * @summary Get Booking History
         * @param {Array<number>} [requestedByUserId] 
         * @param {TerminalName} [terminal] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBookingHistoryAppointmentsBookingHistoryGet: async (requestedByUserId?: Array<number>, terminal?: TerminalName, containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/booking/history/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (requestedByUserId) {
                localVarQueryParameter['requested_by_user_id'] = requestedByUserId;
            }

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Booking Request Status
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost: async (pOSTBookingRequestStatuses: POSTBookingRequestStatuses, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTBookingRequestStatuses' is not null or undefined
            assertParamExists('getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost', 'pOSTBookingRequestStatuses', pOSTBookingRequestStatuses)
            const localVarPath = `/appointments/booking/booking_request_statuses/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTBookingRequestStatuses, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Empty Appointment Slot Allocation Methods
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet: async (portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/availability/empty/allocation_methods/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Fetches historical license plates used for booking at a specific portal, ordered by number of times used.
         * @summary Get License Plate Options
         * @param {TerminalName} terminal 
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet: async (terminal: TerminalName, after: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet', 'terminal', terminal)
            // verify required parameter 'after' is not null or undefined
            assertParamExists('getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet', 'after', after)
            const localVarPath = `/appointments/booking/license_plate_options/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Used for marking an action request as entered in TMS. Only supports book, reschedule, and book empty
         * @summary Patch Booking History
         * @param {number} mutationRequestId 
         * @param {PATCHBookingRequest} pATCHBookingRequest 
         * @param {PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch: async (mutationRequestId: number, pATCHBookingRequest: PATCHBookingRequest, actionType?: PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'mutationRequestId' is not null or undefined
            assertParamExists('patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch', 'mutationRequestId', mutationRequestId)
            // verify required parameter 'pATCHBookingRequest' is not null or undefined
            assertParamExists('patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch', 'pATCHBookingRequest', pATCHBookingRequest)
            const localVarPath = `/appointments/booking/history/{mutation_request_id}/`
                .replace(`{${"mutation_request_id"}}`, encodeURIComponent(String(mutationRequestId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (actionType !== undefined) {
                localVarQueryParameter['action_type'] = actionType;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHBookingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Reschedule Empty Appointment
         * @param {POSTRescheduleEmptyAppointmentBooking} pOSTRescheduleEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost: async (pOSTRescheduleEmptyAppointmentBooking: POSTRescheduleEmptyAppointmentBooking, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTRescheduleEmptyAppointmentBooking' is not null or undefined
            assertParamExists('rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost', 'pOSTRescheduleEmptyAppointmentBooking', pOSTRescheduleEmptyAppointmentBooking)
            const localVarPath = `/appointments/booking/reschedule_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTRescheduleEmptyAppointmentBooking, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentBookingApi - functional programming interface
 * @export
 */
export const AppointmentBookingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentBookingApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Book Appointment
         * @param {POSTAppointmentBooking} pOSTAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking: POSTAppointmentBooking, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.bookAppointmentAppointmentsBookingBookPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Book Empty Appointment
         * @param {POSTEmptyAppointmentBooking} pOSTEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking: POSTEmptyAppointmentBooking, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.bookEmptyAppointmentAppointmentsBookingBookEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Bulk Book Empty Appointments
         * @param {POSTEmptyAppointmentBookingBulk} pOSTEmptyAppointmentBookingBulk 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk: POSTEmptyAppointmentBookingBulk, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkBookEmptyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Cancel Appointments
         * @param {BodyCancelAppointmentsAppointmentsBookingDelete} bodyCancelAppointmentsAppointmentsBookingDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete: BodyCancelAppointmentsAppointmentsBookingDelete, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CancelAppointmentsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.cancelAppointmentsAppointmentsBookingDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Check In
         * @param {POSTCheckIn} pOSTCheckIn 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkInAppointmentsBookingCheckInPost(pOSTCheckIn: POSTCheckIn, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkInAppointmentsBookingCheckInPost(pOSTCheckIn, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.checkInAppointmentsBookingCheckInPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Edit Appointment Add Empty
         * @param {POSTAddEmpty} pOSTAddEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty: POSTAddEmpty, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.editAppointmentAddEmptyAppointmentsBookingAddEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Edit Appointment Swap Empty
         * @param {POSTSwapEmpty} pOSTSwapEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty: POSTSwapEmpty, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Edit Chassis
         * @param {POSTEditChassis} pOSTEditChassis 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis: POSTEditChassis, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.editChassisAppointmentsBookingEditChassisPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Edit License Plate
         * @param {POSTEditLicensePlate} pOSTEditLicensePlate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate: POSTEditLicensePlate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.editLicensePlateAppointmentsBookingEditLicensePlatePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Used to enter an action request for an appointment into your TMS if you have a TMS connection configured
         * @summary Enter Action Request In Tms
         * @param {number} actionRequestId 
         * @param {ActionType} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId: number, actionType?: ActionType, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId, actionType, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Action Request Statuses
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {ActionType} [action] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, action?: ActionType, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses, action, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Available Empty Appointment Slots
         * @param {TerminalName} terminal 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {string} [containerNumber] 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal: TerminalName, shippingLine: ShippingLine, containerType: ContainerType, containerNumber?: string, forceRefresh?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETEmptyContainerAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal, shippingLine, containerType, containerNumber, forceRefresh, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Available Export Empty Pickup Slots
         * @param {TerminalName} terminal 
         * @param {string} bookingNumber 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal: TerminalName, bookingNumber: string, shippingLine: ShippingLine, containerType: ContainerType, forceRefresh?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETExportEmptyPickupAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal, bookingNumber, shippingLine, containerType, forceRefresh, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Available Slots For Container
         * @param {string} containerNumber 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {TerminalName} [terminal] If provided, will return slots for this terminal. If not provided, we will use saved import status data to determine the terminal, raising an error if we have no saved data
         * @param {string} [terminalBlockAvailabilityKey] If provided, will only return cached slots for this terminal block availability key. **If you don\&#39;t know don\&#39;t pass this option**
         * @param {boolean} [lookupTerminalBlockAvailabilityKey] If true, we will check for saved locations for this container and find a relevant terminal block availability key. This can make appointment slot querying much faster.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber: string, forceRefresh?: boolean, terminal?: TerminalName, terminalBlockAvailabilityKey?: string, lookupTerminalBlockAvailabilityKey?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETContainerAvailability>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber, forceRefresh, terminal, terminalBlockAvailabilityKey, lookupTerminalBlockAvailabilityKey, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves booking history based on container numbers and specific time frames.  If container numbers are provided, it fetches the booking history for those containers from the last year. If no container numbers are provided, it fetches the booking history for the last 5 days. If user id is provided, it fetches the booking history for the past 30 days.
         * @summary Get Booking History
         * @param {Array<number>} [requestedByUserId] 
         * @param {TerminalName} [terminal] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId?: Array<number>, terminal?: TerminalName, containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETBookingStyleRequestWithApptNum>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId, terminal, containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getBookingHistoryAppointmentsBookingHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Booking Request Status
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETBookingRequest>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Empty Appointment Slot Allocation Methods
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTerminalAllocationMethod>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Fetches historical license plates used for booking at a specific portal, ordered by number of times used.
         * @summary Get License Plate Options
         * @param {TerminalName} terminal 
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal: TerminalName, after: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruckLicensePlate>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal, after, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Used for marking an action request as entered in TMS. Only supports book, reschedule, and book empty
         * @summary Patch Booking History
         * @param {number} mutationRequestId 
         * @param {PATCHBookingRequest} pATCHBookingRequest 
         * @param {PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId: number, pATCHBookingRequest: PATCHBookingRequest, actionType?: PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETBookingStyleRequest>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId, pATCHBookingRequest, actionType, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Reschedule Empty Appointment
         * @param {POSTRescheduleEmptyAppointmentBooking} pOSTRescheduleEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking: POSTRescheduleEmptyAppointmentBooking, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentActionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentBookingApi.rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentBookingApi - factory interface
 * @export
 */
export const AppointmentBookingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentBookingApiFp(configuration)
    return {
        /**
         * 
         * @summary Book Appointment
         * @param {POSTAppointmentBooking} pOSTAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking: POSTAppointmentBooking, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Book Empty Appointment
         * @param {POSTEmptyAppointmentBooking} pOSTEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking: POSTEmptyAppointmentBooking, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Bulk Book Empty Appointments
         * @param {POSTEmptyAppointmentBookingBulk} pOSTEmptyAppointmentBookingBulk 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk: POSTEmptyAppointmentBookingBulk, options?: any): AxiosPromise<BulkBookEmptyResponse> {
            return localVarFp.bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Cancel Appointments
         * @param {BodyCancelAppointmentsAppointmentsBookingDelete} bodyCancelAppointmentsAppointmentsBookingDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete: BodyCancelAppointmentsAppointmentsBookingDelete, options?: any): AxiosPromise<CancelAppointmentsResponse> {
            return localVarFp.cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Check In
         * @param {POSTCheckIn} pOSTCheckIn 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkInAppointmentsBookingCheckInPost(pOSTCheckIn: POSTCheckIn, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.checkInAppointmentsBookingCheckInPost(pOSTCheckIn, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Edit Appointment Add Empty
         * @param {POSTAddEmpty} pOSTAddEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty: POSTAddEmpty, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Edit Appointment Swap Empty
         * @param {POSTSwapEmpty} pOSTSwapEmpty 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty: POSTSwapEmpty, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Edit Chassis
         * @param {POSTEditChassis} pOSTEditChassis 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis: POSTEditChassis, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Edit License Plate
         * @param {POSTEditLicensePlate} pOSTEditLicensePlate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate: POSTEditLicensePlate, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate, options).then((request) => request(axios, basePath));
        },
        /**
         * Used to enter an action request for an appointment into your TMS if you have a TMS connection configured
         * @summary Enter Action Request In Tms
         * @param {number} actionRequestId 
         * @param {ActionType} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId: number, actionType?: ActionType, options?: any): AxiosPromise<Message> {
            return localVarFp.enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId, actionType, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Action Request Statuses
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {ActionType} [action] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, action?: ActionType, options?: any): AxiosPromise<Array<GetActionRequestStatusesAppointmentsBookingActionRequestStatusesPost200ResponseInner>> {
            return localVarFp.getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses, action, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Available Empty Appointment Slots
         * @param {TerminalName} terminal 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {string} [containerNumber] 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal: TerminalName, shippingLine: ShippingLine, containerType: ContainerType, containerNumber?: string, forceRefresh?: boolean, options?: any): AxiosPromise<GETEmptyContainerAvailabilityResponse> {
            return localVarFp.getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal, shippingLine, containerType, containerNumber, forceRefresh, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Available Export Empty Pickup Slots
         * @param {TerminalName} terminal 
         * @param {string} bookingNumber 
         * @param {ShippingLine} shippingLine 
         * @param {ContainerType} containerType 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal: TerminalName, bookingNumber: string, shippingLine: ShippingLine, containerType: ContainerType, forceRefresh?: boolean, options?: any): AxiosPromise<GETExportEmptyPickupAvailabilityResponse> {
            return localVarFp.getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal, bookingNumber, shippingLine, containerType, forceRefresh, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Available Slots For Container
         * @param {string} containerNumber 
         * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
         * @param {TerminalName} [terminal] If provided, will return slots for this terminal. If not provided, we will use saved import status data to determine the terminal, raising an error if we have no saved data
         * @param {string} [terminalBlockAvailabilityKey] If provided, will only return cached slots for this terminal block availability key. **If you don\&#39;t know don\&#39;t pass this option**
         * @param {boolean} [lookupTerminalBlockAvailabilityKey] If true, we will check for saved locations for this container and find a relevant terminal block availability key. This can make appointment slot querying much faster.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber: string, forceRefresh?: boolean, terminal?: TerminalName, terminalBlockAvailabilityKey?: string, lookupTerminalBlockAvailabilityKey?: boolean, options?: any): AxiosPromise<GETContainerAvailability> {
            return localVarFp.getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber, forceRefresh, terminal, terminalBlockAvailabilityKey, lookupTerminalBlockAvailabilityKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves booking history based on container numbers and specific time frames.  If container numbers are provided, it fetches the booking history for those containers from the last year. If no container numbers are provided, it fetches the booking history for the last 5 days. If user id is provided, it fetches the booking history for the past 30 days.
         * @summary Get Booking History
         * @param {Array<number>} [requestedByUserId] 
         * @param {TerminalName} [terminal] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId?: Array<number>, terminal?: TerminalName, containerNumbers?: string, options?: any): AxiosPromise<Array<GETBookingStyleRequestWithApptNum>> {
            return localVarFp.getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId, terminal, containerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Booking Request Status
         * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, options?: any): AxiosPromise<Array<GETBookingRequest>> {
            return localVarFp.getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Empty Appointment Slot Allocation Methods
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETTerminalAllocationMethod>> {
            return localVarFp.getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Fetches historical license plates used for booking at a specific portal, ordered by number of times used.
         * @summary Get License Plate Options
         * @param {TerminalName} terminal 
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal: TerminalName, after: string, options?: any): AxiosPromise<Array<GETTruckLicensePlate>> {
            return localVarFp.getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal, after, options).then((request) => request(axios, basePath));
        },
        /**
         * Used for marking an action request as entered in TMS. Only supports book, reschedule, and book empty
         * @summary Patch Booking History
         * @param {number} mutationRequestId 
         * @param {PATCHBookingRequest} pATCHBookingRequest 
         * @param {PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum} [actionType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId: number, pATCHBookingRequest: PATCHBookingRequest, actionType?: PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum, options?: any): AxiosPromise<GETBookingStyleRequest> {
            return localVarFp.patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId, pATCHBookingRequest, actionType, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Reschedule Empty Appointment
         * @param {POSTRescheduleEmptyAppointmentBooking} pOSTRescheduleEmptyAppointmentBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking: POSTRescheduleEmptyAppointmentBooking, options?: any): AxiosPromise<AppointmentActionResponse> {
            return localVarFp.rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentBookingApi - object-oriented interface
 * @export
 * @class AppointmentBookingApi
 * @extends {BaseAPI}
 */
export class AppointmentBookingApi extends BaseAPI {
    /**
     * 
     * @summary Book Appointment
     * @param {POSTAppointmentBooking} pOSTAppointmentBooking 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking: POSTAppointmentBooking, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).bookAppointmentAppointmentsBookingBookPost(pOSTAppointmentBooking, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Book Empty Appointment
     * @param {POSTEmptyAppointmentBooking} pOSTEmptyAppointmentBooking 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking: POSTEmptyAppointmentBooking, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).bookEmptyAppointmentAppointmentsBookingBookEmptyPost(pOSTEmptyAppointmentBooking, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Bulk Book Empty Appointments
     * @param {POSTEmptyAppointmentBookingBulk} pOSTEmptyAppointmentBookingBulk 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk: POSTEmptyAppointmentBookingBulk, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).bulkBookEmptyAppointmentsAppointmentsBookingBookEmptyBulkPost(pOSTEmptyAppointmentBookingBulk, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Cancel Appointments
     * @param {BodyCancelAppointmentsAppointmentsBookingDelete} bodyCancelAppointmentsAppointmentsBookingDelete 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete: BodyCancelAppointmentsAppointmentsBookingDelete, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).cancelAppointmentsAppointmentsBookingDelete(bodyCancelAppointmentsAppointmentsBookingDelete, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Check In
     * @param {POSTCheckIn} pOSTCheckIn 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public checkInAppointmentsBookingCheckInPost(pOSTCheckIn: POSTCheckIn, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).checkInAppointmentsBookingCheckInPost(pOSTCheckIn, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Edit Appointment Add Empty
     * @param {POSTAddEmpty} pOSTAddEmpty 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty: POSTAddEmpty, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).editAppointmentAddEmptyAppointmentsBookingAddEmptyPost(pOSTAddEmpty, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Edit Appointment Swap Empty
     * @param {POSTSwapEmpty} pOSTSwapEmpty 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty: POSTSwapEmpty, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).editAppointmentSwapEmptyAppointmentsBookingSwapEmptyPost(pOSTSwapEmpty, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Edit Chassis
     * @param {POSTEditChassis} pOSTEditChassis 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis: POSTEditChassis, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).editChassisAppointmentsBookingEditChassisPost(pOSTEditChassis, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Edit License Plate
     * @param {POSTEditLicensePlate} pOSTEditLicensePlate 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate: POSTEditLicensePlate, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).editLicensePlateAppointmentsBookingEditLicensePlatePost(pOSTEditLicensePlate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Used to enter an action request for an appointment into your TMS if you have a TMS connection configured
     * @summary Enter Action Request In Tms
     * @param {number} actionRequestId 
     * @param {ActionType} [actionType] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId: number, actionType?: ActionType, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).enterActionRequestInTmsAppointmentsBookingHistoryActionRequestIdEnterInTmsPost(actionRequestId, actionType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Action Request Statuses
     * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
     * @param {ActionType} [action] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, action?: ActionType, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getActionRequestStatusesAppointmentsBookingActionRequestStatusesPost(pOSTBookingRequestStatuses, action, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Available Empty Appointment Slots
     * @param {TerminalName} terminal 
     * @param {ShippingLine} shippingLine 
     * @param {ContainerType} containerType 
     * @param {string} [containerNumber] 
     * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal: TerminalName, shippingLine: ShippingLine, containerType: ContainerType, containerNumber?: string, forceRefresh?: boolean, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getAvailableEmptyAppointmentSlotsAppointmentsAvailabilityEmptyTerminalGet(terminal, shippingLine, containerType, containerNumber, forceRefresh, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Available Export Empty Pickup Slots
     * @param {TerminalName} terminal 
     * @param {string} bookingNumber 
     * @param {ShippingLine} shippingLine 
     * @param {ContainerType} containerType 
     * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal: TerminalName, bookingNumber: string, shippingLine: ShippingLine, containerType: ContainerType, forceRefresh?: boolean, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getAvailableExportEmptyPickupSlotsAppointmentsAvailabilityExportEmptyPickupTerminalGet(terminal, bookingNumber, shippingLine, containerType, forceRefresh, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Available Slots For Container
     * @param {string} containerNumber 
     * @param {boolean} [forceRefresh] If false, API may return cached results. If true, API will always fetch fresh data from the underlying appointment system
     * @param {TerminalName} [terminal] If provided, will return slots for this terminal. If not provided, we will use saved import status data to determine the terminal, raising an error if we have no saved data
     * @param {string} [terminalBlockAvailabilityKey] If provided, will only return cached slots for this terminal block availability key. **If you don\&#39;t know don\&#39;t pass this option**
     * @param {boolean} [lookupTerminalBlockAvailabilityKey] If true, we will check for saved locations for this container and find a relevant terminal block availability key. This can make appointment slot querying much faster.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber: string, forceRefresh?: boolean, terminal?: TerminalName, terminalBlockAvailabilityKey?: string, lookupTerminalBlockAvailabilityKey?: boolean, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getAvailableSlotsForContainerAppointmentsAvailabilityContainerNumberGet(containerNumber, forceRefresh, terminal, terminalBlockAvailabilityKey, lookupTerminalBlockAvailabilityKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves booking history based on container numbers and specific time frames.  If container numbers are provided, it fetches the booking history for those containers from the last year. If no container numbers are provided, it fetches the booking history for the last 5 days. If user id is provided, it fetches the booking history for the past 30 days.
     * @summary Get Booking History
     * @param {Array<number>} [requestedByUserId] 
     * @param {TerminalName} [terminal] 
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId?: Array<number>, terminal?: TerminalName, containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getBookingHistoryAppointmentsBookingHistoryGet(requestedByUserId, terminal, containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Booking Request Status
     * @param {POSTBookingRequestStatuses} pOSTBookingRequestStatuses 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses: POSTBookingRequestStatuses, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getBookingRequestStatusAppointmentsBookingBookingRequestStatusesPost(pOSTBookingRequestStatuses, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Empty Appointment Slot Allocation Methods
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getEmptyAppointmentSlotAllocationMethodsAppointmentsAvailabilityEmptyAllocationMethodsGet(portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Fetches historical license plates used for booking at a specific portal, ordered by number of times used.
     * @summary Get License Plate Options
     * @param {TerminalName} terminal 
     * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal: TerminalName, after: string, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).getLicensePlateOptionsAppointmentsBookingLicensePlateOptionsGet(terminal, after, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Used for marking an action request as entered in TMS. Only supports book, reschedule, and book empty
     * @summary Patch Booking History
     * @param {number} mutationRequestId 
     * @param {PATCHBookingRequest} pATCHBookingRequest 
     * @param {PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum} [actionType] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId: number, pATCHBookingRequest: PATCHBookingRequest, actionType?: PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).patchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatch(mutationRequestId, pATCHBookingRequest, actionType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Reschedule Empty Appointment
     * @param {POSTRescheduleEmptyAppointmentBooking} pOSTRescheduleEmptyAppointmentBooking 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentBookingApi
     */
    public rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking: POSTRescheduleEmptyAppointmentBooking, options?: RawAxiosRequestConfig) {
        return AppointmentBookingApiFp(this.configuration).rescheduleEmptyAppointmentAppointmentsBookingRescheduleEmptyPost(pOSTRescheduleEmptyAppointmentBooking, options).then((request) => request(this.axios, this.basePath));
    }
}

/**
 * @export
 */
export const PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum = {
    Book: 'book',
    Reschedule: 'reschedule',
    BookEmpty: 'book_empty'
} as const;
export type PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum = typeof PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum[keyof typeof PatchBookingHistoryAppointmentsBookingHistoryMutationRequestIdPatchActionTypeEnum];


/**
 * AppointmentCapacitiesApi - axios parameter creator
 * @export
 */
export const AppointmentCapacitiesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new capacity set in a scope, returning the saved set.
         * @summary Create Capacity Set
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCapacitySetAppointmentsCapacitiesSetsPost: async (pOSTCapacity: POSTCapacity, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTCapacity' is not null or undefined
            assertParamExists('createCapacitySetAppointmentsCapacitiesSetsPost', 'pOSTCapacity', pOSTCapacity)
            const localVarPath = `/appointments/capacities/sets/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTCapacity, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete Capacity Set
         * @param {BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete} [bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCapacitySetAppointmentsCapacitiesSetDelete: async (bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete?: BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/capacities/set/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete a single capacity set by id without touching its siblings.
         * @summary Delete Capacity Set By Id
         * @param {number} setId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete: async (setId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'setId' is not null or undefined
            assertParamExists('deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete', 'setId', setId)
            const localVarPath = `/appointments/capacities/sets/{set_id}/`
                .replace(`{${"set_id"}}`, encodeURIComponent(String(setId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Appointment Capacity Sets
         * @param {number} [customerId] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet: async (customerId?: number, direction?: TransactionDirection, loaded?: boolean, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/capacities/sets/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (loaded !== undefined) {
                localVarQueryParameter['loaded'] = loaded;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns remaining capacities (by time period) for applicable capacity sets.  For performance+simplicity, this does not count in-progress bookings as consuming capacity, unlike Auto Book which does consider these.
         * @summary Get Remaining Capacity
         * @param {POSTRemainingCapacity} pOSTRemainingCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRemainingCapacityAppointmentsCapacitiesRemainingPost: async (pOSTRemainingCapacity: POSTRemainingCapacity, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTRemainingCapacity' is not null or undefined
            assertParamExists('getRemainingCapacityAppointmentsCapacitiesRemainingPost', 'pOSTRemainingCapacity', pOSTRemainingCapacity)
            const localVarPath = `/appointments/capacities/remaining/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTRemainingCapacity, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Flip independent_capacity for every set in a scope at once.
         * @summary Patch Scope Independent Capacity
         * @param {PATCHScopeIndependentCapacity} pATCHScopeIndependentCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch: async (pATCHScopeIndependentCapacity: PATCHScopeIndependentCapacity, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pATCHScopeIndependentCapacity' is not null or undefined
            assertParamExists('patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch', 'pATCHScopeIndependentCapacity', pATCHScopeIndependentCapacity)
            const localVarPath = `/appointments/capacities/sets/scope/independent-capacity/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHScopeIndependentCapacity, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Save Appointment Capacities
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAppointmentCapacitiesAppointmentsCapacitiesPost: async (pOSTCapacity: POSTCapacity, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTCapacity' is not null or undefined
            assertParamExists('saveAppointmentCapacitiesAppointmentsCapacitiesPost', 'pOSTCapacity', pOSTCapacity)
            const localVarPath = `/appointments/capacities/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTCapacity, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replace an existing capacity set\'s buckets and scalar fields by id.
         * @summary Update Capacity Set
         * @param {number} setId 
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCapacitySetAppointmentsCapacitiesSetsSetIdPut: async (setId: number, pOSTCapacity: POSTCapacity, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'setId' is not null or undefined
            assertParamExists('updateCapacitySetAppointmentsCapacitiesSetsSetIdPut', 'setId', setId)
            // verify required parameter 'pOSTCapacity' is not null or undefined
            assertParamExists('updateCapacitySetAppointmentsCapacitiesSetsSetIdPut', 'pOSTCapacity', pOSTCapacity)
            const localVarPath = `/appointments/capacities/sets/{set_id}/`
                .replace(`{${"set_id"}}`, encodeURIComponent(String(setId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTCapacity, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentCapacitiesApi - functional programming interface
 * @export
 */
export const AppointmentCapacitiesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentCapacitiesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new capacity set in a scope, returning the saved set.
         * @summary Create Capacity Set
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETCapacitySet>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.createCapacitySetAppointmentsCapacitiesSetsPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete Capacity Set
         * @param {BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete} [bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete?: BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.deleteCapacitySetAppointmentsCapacitiesSetDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Delete a single capacity set by id without touching its siblings.
         * @summary Delete Capacity Set By Id
         * @param {number} setId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Appointment Capacity Sets
         * @param {number} [customerId] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId?: number, direction?: TransactionDirection, loaded?: boolean, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETCapacitySets>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId, direction, loaded, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns remaining capacities (by time period) for applicable capacity sets.  For performance+simplicity, this does not count in-progress bookings as consuming capacity, unlike Auto Book which does consider these.
         * @summary Get Remaining Capacity
         * @param {POSTRemainingCapacity} pOSTRemainingCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity: POSTRemainingCapacity, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTRemainingCapacityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.getRemainingCapacityAppointmentsCapacitiesRemainingPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Flip independent_capacity for every set in a scope at once.
         * @summary Patch Scope Independent Capacity
         * @param {PATCHScopeIndependentCapacity} pATCHScopeIndependentCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity: PATCHScopeIndependentCapacity, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Save Appointment Capacities
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.saveAppointmentCapacitiesAppointmentsCapacitiesPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replace an existing capacity set\'s buckets and scalar fields by id.
         * @summary Update Capacity Set
         * @param {number} setId 
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId: number, pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETCapacitySet>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId, pOSTCapacity, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentCapacitiesApi.updateCapacitySetAppointmentsCapacitiesSetsSetIdPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentCapacitiesApi - factory interface
 * @export
 */
export const AppointmentCapacitiesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentCapacitiesApiFp(configuration)
    return {
        /**
         * Create a new capacity set in a scope, returning the saved set.
         * @summary Create Capacity Set
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity: POSTCapacity, options?: any): AxiosPromise<GETCapacitySet> {
            return localVarFp.createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete Capacity Set
         * @param {BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete} [bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete?: BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options?: any): AxiosPromise<any> {
            return localVarFp.deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete a single capacity set by id without touching its siblings.
         * @summary Delete Capacity Set By Id
         * @param {number} setId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId: number, options?: any): AxiosPromise<any> {
            return localVarFp.deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Appointment Capacity Sets
         * @param {number} [customerId] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId?: number, direction?: TransactionDirection, loaded?: boolean, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<GETCapacitySets> {
            return localVarFp.getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId, direction, loaded, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns remaining capacities (by time period) for applicable capacity sets.  For performance+simplicity, this does not count in-progress bookings as consuming capacity, unlike Auto Book which does consider these.
         * @summary Get Remaining Capacity
         * @param {POSTRemainingCapacity} pOSTRemainingCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity: POSTRemainingCapacity, options?: any): AxiosPromise<POSTRemainingCapacityResponse> {
            return localVarFp.getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity, options).then((request) => request(axios, basePath));
        },
        /**
         * Flip independent_capacity for every set in a scope at once.
         * @summary Patch Scope Independent Capacity
         * @param {PATCHScopeIndependentCapacity} pATCHScopeIndependentCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity: PATCHScopeIndependentCapacity, options?: any): AxiosPromise<any> {
            return localVarFp.patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Save Appointment Capacities
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity: POSTCapacity, options?: any): AxiosPromise<any> {
            return localVarFp.saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity, options).then((request) => request(axios, basePath));
        },
        /**
         * Replace an existing capacity set\'s buckets and scalar fields by id.
         * @summary Update Capacity Set
         * @param {number} setId 
         * @param {POSTCapacity} pOSTCapacity 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId: number, pOSTCapacity: POSTCapacity, options?: any): AxiosPromise<GETCapacitySet> {
            return localVarFp.updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId, pOSTCapacity, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentCapacitiesApi - object-oriented interface
 * @export
 * @class AppointmentCapacitiesApi
 * @extends {BaseAPI}
 */
export class AppointmentCapacitiesApi extends BaseAPI {
    /**
     * Create a new capacity set in a scope, returning the saved set.
     * @summary Create Capacity Set
     * @param {POSTCapacity} pOSTCapacity 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).createCapacitySetAppointmentsCapacitiesSetsPost(pOSTCapacity, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete Capacity Set
     * @param {BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete} [bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete?: BodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).deleteCapacitySetAppointmentsCapacitiesSetDelete(bodyDeleteCapacitySetAppointmentsCapacitiesSetDelete, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete a single capacity set by id without touching its siblings.
     * @summary Delete Capacity Set By Id
     * @param {number} setId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId: number, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(setId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Appointment Capacity Sets
     * @param {number} [customerId] 
     * @param {TransactionDirection} [direction] 
     * @param {boolean} [loaded] 
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId?: number, direction?: TransactionDirection, loaded?: boolean, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(customerId, direction, loaded, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns remaining capacities (by time period) for applicable capacity sets.  For performance+simplicity, this does not count in-progress bookings as consuming capacity, unlike Auto Book which does consider these.
     * @summary Get Remaining Capacity
     * @param {POSTRemainingCapacity} pOSTRemainingCapacity 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity: POSTRemainingCapacity, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).getRemainingCapacityAppointmentsCapacitiesRemainingPost(pOSTRemainingCapacity, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Flip independent_capacity for every set in a scope at once.
     * @summary Patch Scope Independent Capacity
     * @param {PATCHScopeIndependentCapacity} pATCHScopeIndependentCapacity 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity: PATCHScopeIndependentCapacity, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(pATCHScopeIndependentCapacity, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Save Appointment Capacities
     * @param {POSTCapacity} pOSTCapacity 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).saveAppointmentCapacitiesAppointmentsCapacitiesPost(pOSTCapacity, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replace an existing capacity set\'s buckets and scalar fields by id.
     * @summary Update Capacity Set
     * @param {number} setId 
     * @param {POSTCapacity} pOSTCapacity 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentCapacitiesApi
     */
    public updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId: number, pOSTCapacity: POSTCapacity, options?: RawAxiosRequestConfig) {
        return AppointmentCapacitiesApiFp(this.configuration).updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(setId, pOSTCapacity, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentsApi - axios parameter creator
 * @export
 */
export const AppointmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Assign one or more users to containerless appointments (export empty pickups without a container yet). Containered appointments are assigned via their container with /containers/assign/, which is the source of truth once one exists
         * @summary Assign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignAppointmentsAppointmentsAssignPost: async (pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentAssignmentData' is not null or undefined
            assertParamExists('assignAppointmentsAppointmentsAssignPost', 'pOSTAppointmentAssignmentData', pOSTAppointmentAssignmentData)
            const localVarPath = `/appointments/assign/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentAssignmentData, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary File Export
         * @param {GETFileExport} gETFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileExportAppointmentsFileExportPost: async (gETFileExport: GETFileExport, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'gETFileExport' is not null or undefined
            assertParamExists('fileExportAppointmentsFileExportPost', 'gETFileExport', gETFileExport)
            const localVarPath = `/appointments/file_export/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gETFileExport, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Appointment Counts
         * @param {number} [timeBucketHours] 
         * @param {string} [after] 
         * @param {string} [before] 
         * @param {TerminalName} [terminal] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {number} [customerId] 
         * @param {Array<string>} [containerTags] Limit to appointments for containers with all of these tags
         * @param {string} [excludeCustomerIds] Comma separated list
         * @param {Array<string>} [excludeContainerTags] Limit to appointments for containers without these tags
         * @param {string} [timezone] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCountsAppointmentsCountsGet: async (timeBucketHours?: number, after?: string, before?: string, terminal?: TerminalName, direction?: TransactionDirection, loaded?: boolean, customerId?: number, containerTags?: Array<string>, excludeCustomerIds?: string, excludeContainerTags?: Array<string>, timezone?: string, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (timeBucketHours !== undefined) {
                localVarQueryParameter['time_bucket_hours'] = timeBucketHours;
            }

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }

            if (before !== undefined) {
                localVarQueryParameter['before'] = (before as any instanceof Date) ?
                    (before as any).toISOString() :
                    before;
            }

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (loaded !== undefined) {
                localVarQueryParameter['loaded'] = loaded;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (containerTags) {
                localVarQueryParameter['container_tags'] = containerTags;
            }

            if (excludeCustomerIds !== undefined) {
                localVarQueryParameter['exclude_customer_ids'] = excludeCustomerIds;
            }

            if (excludeContainerTags) {
                localVarQueryParameter['exclude_container_tags'] = excludeContainerTags;
            }

            if (timezone !== undefined) {
                localVarQueryParameter['timezone'] = timezone;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List **ALL** appointments with various filter criteria, possibly returning multiple appointments for a given container.  The [GET /containers/](#operation/get_containers_containers__get) API also includes appointments, but it only includes at most one appointment per container (the \"most current\" appointment), which might be more preferably depending on your objective
         * @summary List Appointments
         * @param {TerminalName} [terminal] Limit to appointments made at this terminal
         * @param {Array<number>} [id] Limit to appointments with these id
         * @param {string} [after] Limit to appointments with window start after this date and time. It is **highly** recommended to pass this parameter to limit the result set size, particularly if you are not passing &#x60;container_numbers&#x60;.  Appointments do not usually change once their &#x60;window_start&#x60; is a day or two in the past, so limiting to appointments after yesterday or the day before is a good technique
         * @param {string} [before] Limit to appointments with window start before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments with this direction
         * @param {boolean} [loaded] Limit to appointments with this loaded status
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentsAppointmentsGet: async (terminal?: TerminalName, id?: Array<number>, after?: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, page?: number, pageSize?: number, portComplexes?: Array<PortComplex>, containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (id) {
                localVarQueryParameter['id'] = id;
            }

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }

            if (before !== undefined) {
                localVarQueryParameter['before'] = (before as any instanceof Date) ?
                    (before as any).toISOString() :
                    before;
            }

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (loaded !== undefined) {
                localVarQueryParameter['loaded'] = loaded;
            }

            if (statuses) {
                localVarQueryParameter['statuses'] = statuses;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Load appointments with related container data. Will include one entry *per appointment*, so potentially there will be multiple entries for a single container. Inclusion of some fields is configurable.  Pay particular attention to the `limit_to_relevant_appointments` query param
         * @summary List Appointments With Container Info
         * @param {string} after Only load appointments with a &#x60;window_start&#x60; at or after this date and time 
         * @param {string} [before] Only load appointments with a &#x60;window_start&#x60; before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments for moves in this direction
         * @param {boolean} [loaded] Limit to appointments for moves with this loaded status. i.e. &#x60;true&#x60; limits to loaded containers, &#x60;false&#x60; limits to empty containers
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {boolean} [includeGateTransactions] If &#x60;false&#x60;, we skip loading Gate Transactions and the &#x60;inbound_gate_transaction&#x60; and &#x60;outbound_gate_transaction&#x60; fields will be set to &#x60;null&#x60;
         * @param {boolean} [includeImportStatuses] If &#x60;false&#x60;, we skip loading Import Statuses and the &#x60;import_status&#x60; field will be set to &#x60;null&#x60;
         * @param {boolean} [limitToRelevantAppointments] If &#x60;true&#x60;, only return the most relevant appointment per move type.  **Example:** If we have a scheduled and cancelled load out appointments for a single container, we\&#39;d return the scheduled one only
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet: async (after: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, includeGateTransactions?: boolean, includeImportStatuses?: boolean, limitToRelevantAppointments?: boolean, containerNumbers?: string, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'after' is not null or undefined
            assertParamExists('listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet', 'after', after)
            const localVarPath = `/appointments/with_container_info/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }

            if (before !== undefined) {
                localVarQueryParameter['before'] = (before as any instanceof Date) ?
                    (before as any).toISOString() :
                    before;
            }

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (loaded !== undefined) {
                localVarQueryParameter['loaded'] = loaded;
            }

            if (statuses) {
                localVarQueryParameter['statuses'] = statuses;
            }

            if (includeGateTransactions !== undefined) {
                localVarQueryParameter['include_gate_transactions'] = includeGateTransactions;
            }

            if (includeImportStatuses !== undefined) {
                localVarQueryParameter['include_import_statuses'] = includeImportStatuses;
            }

            if (limitToRelevantAppointments !== undefined) {
                localVarQueryParameter['limit_to_relevant_appointments'] = limitToRelevantAppointments;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Refresh appointments for all portals
         * @summary Refresh Appointments
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshAppointmentsAppointmentsRefreshPost: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/appointments/refresh/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Refresh appointments on demand for specific containers at their terminals.  Reuses the same list-appointments pipeline as the periodic sync, scoped to the connection(s) for the requested containers\' portals. Mirrors the periodic path: only VALIDATED connections with appointment loading enabled are used, and the portal-outage gate is respected. Containers are skipped and reported (never failing the whole request) when the portal cannot list appointments, has no usable connection, is in an outage, or the connection is currently throttled.
         * @summary Refresh Appointments For Containers
         * @param {Array<POSTAppointmentRefresh>} pOSTAppointmentRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshAppointmentsForContainersAppointmentsRefreshForContainersPost: async (pOSTAppointmentRefresh: Array<POSTAppointmentRefresh>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentRefresh' is not null or undefined
            assertParamExists('refreshAppointmentsForContainersAppointmentsRefreshForContainersPost', 'pOSTAppointmentRefresh', pOSTAppointmentRefresh)
            const localVarPath = `/appointments/refresh_for_containers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentRefresh, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add tags to containerless appointments (export empty pickups).  Same vocabulary as container tags. Appointments that already have a container number must be tagged via the container tag endpoints — the container is the row once one exists.
         * @summary Tag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tagAppointmentsAppointmentsTagPost: async (pOSTAppointmentTags: POSTAppointmentTags, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentTags' is not null or undefined
            assertParamExists('tagAppointmentsAppointmentsTagPost', 'pOSTAppointmentTags', pOSTAppointmentTags)
            const localVarPath = `/appointments/tag/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentTags, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove user assignment from the given appointments
         * @summary Unassign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignAppointmentsAppointmentsUnassignPost: async (pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentAssignmentData' is not null or undefined
            assertParamExists('unassignAppointmentsAppointmentsUnassignPost', 'pOSTAppointmentAssignmentData', pOSTAppointmentAssignmentData)
            const localVarPath = `/appointments/unassign/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentAssignmentData, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove tags from containerless appointments.  Appointments that already have a container number must be untagged via the container endpoints — same rule as /appointments/tag/.
         * @summary Untag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        untagAppointmentsAppointmentsUntagPost: async (pOSTAppointmentTags: POSTAppointmentTags, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAppointmentTags' is not null or undefined
            assertParamExists('untagAppointmentsAppointmentsUntagPost', 'pOSTAppointmentTags', pOSTAppointmentTags)
            const localVarPath = `/appointments/untag/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAppointmentTags, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentsApi - functional programming interface
 * @export
 */
export const AppointmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Assign one or more users to containerless appointments (export empty pickups without a container yet). Containered appointments are assigned via their container with /containers/assign/, which is the source of truth once one exists
         * @summary Assign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.assignAppointmentsAppointmentsAssignPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary File Export
         * @param {GETFileExport} gETFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async fileExportAppointmentsFileExportPost(gETFileExport: GETFileExport, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.fileExportAppointmentsFileExportPost(gETFileExport, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.fileExportAppointmentsFileExportPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Appointment Counts
         * @param {number} [timeBucketHours] 
         * @param {string} [after] 
         * @param {string} [before] 
         * @param {TerminalName} [terminal] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {number} [customerId] 
         * @param {Array<string>} [containerTags] Limit to appointments for containers with all of these tags
         * @param {string} [excludeCustomerIds] Comma separated list
         * @param {Array<string>} [excludeContainerTags] Limit to appointments for containers without these tags
         * @param {string} [timezone] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentCountsAppointmentsCountsGet(timeBucketHours?: number, after?: string, before?: string, terminal?: TerminalName, direction?: TransactionDirection, loaded?: boolean, customerId?: number, containerTags?: Array<string>, excludeCustomerIds?: string, excludeContainerTags?: Array<string>, timezone?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTimeBucketAppointmentStatusCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentCountsAppointmentsCountsGet(timeBucketHours, after, before, terminal, direction, loaded, customerId, containerTags, excludeCustomerIds, excludeContainerTags, timezone, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.getAppointmentCountsAppointmentsCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List **ALL** appointments with various filter criteria, possibly returning multiple appointments for a given container.  The [GET /containers/](#operation/get_containers_containers__get) API also includes appointments, but it only includes at most one appointment per container (the \"most current\" appointment), which might be more preferably depending on your objective
         * @summary List Appointments
         * @param {TerminalName} [terminal] Limit to appointments made at this terminal
         * @param {Array<number>} [id] Limit to appointments with these id
         * @param {string} [after] Limit to appointments with window start after this date and time. It is **highly** recommended to pass this parameter to limit the result set size, particularly if you are not passing &#x60;container_numbers&#x60;.  Appointments do not usually change once their &#x60;window_start&#x60; is a day or two in the past, so limiting to appointments after yesterday or the day before is a good technique
         * @param {string} [before] Limit to appointments with window start before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments with this direction
         * @param {boolean} [loaded] Limit to appointments with this loaded status
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentsAppointmentsGet(terminal?: TerminalName, id?: Array<number>, after?: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, page?: number, pageSize?: number, portComplexes?: Array<PortComplex>, containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAppointment>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentsAppointmentsGet(terminal, id, after, before, direction, loaded, statuses, page, pageSize, portComplexes, containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.listAppointmentsAppointmentsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Load appointments with related container data. Will include one entry *per appointment*, so potentially there will be multiple entries for a single container. Inclusion of some fields is configurable.  Pay particular attention to the `limit_to_relevant_appointments` query param
         * @summary List Appointments With Container Info
         * @param {string} after Only load appointments with a &#x60;window_start&#x60; at or after this date and time 
         * @param {string} [before] Only load appointments with a &#x60;window_start&#x60; before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments for moves in this direction
         * @param {boolean} [loaded] Limit to appointments for moves with this loaded status. i.e. &#x60;true&#x60; limits to loaded containers, &#x60;false&#x60; limits to empty containers
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {boolean} [includeGateTransactions] If &#x60;false&#x60;, we skip loading Gate Transactions and the &#x60;inbound_gate_transaction&#x60; and &#x60;outbound_gate_transaction&#x60; fields will be set to &#x60;null&#x60;
         * @param {boolean} [includeImportStatuses] If &#x60;false&#x60;, we skip loading Import Statuses and the &#x60;import_status&#x60; field will be set to &#x60;null&#x60;
         * @param {boolean} [limitToRelevantAppointments] If &#x60;true&#x60;, only return the most relevant appointment per move type.  **Example:** If we have a scheduled and cancelled load out appointments for a single container, we\&#39;d return the scheduled one only
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, includeGateTransactions?: boolean, includeImportStatuses?: boolean, limitToRelevantAppointments?: boolean, containerNumbers?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAppointmentWithContainer>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after, before, direction, loaded, statuses, includeGateTransactions, includeImportStatuses, limitToRelevantAppointments, containerNumbers, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Refresh appointments for all portals
         * @summary Refresh Appointments
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async refreshAppointmentsAppointmentsRefreshPost(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.refreshAppointmentsAppointmentsRefreshPost(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.refreshAppointmentsAppointmentsRefreshPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Refresh appointments on demand for specific containers at their terminals.  Reuses the same list-appointments pipeline as the periodic sync, scoped to the connection(s) for the requested containers\' portals. Mirrors the periodic path: only VALIDATED connections with appointment loading enabled are used, and the portal-outage gate is respected. Containers are skipped and reported (never failing the whole request) when the portal cannot list appointments, has no usable connection, is in an outage, or the connection is currently throttled.
         * @summary Refresh Appointments For Containers
         * @param {Array<POSTAppointmentRefresh>} pOSTAppointmentRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh: Array<POSTAppointmentRefresh>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTAppointmentRefreshResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.refreshAppointmentsForContainersAppointmentsRefreshForContainersPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Add tags to containerless appointments (export empty pickups).  Same vocabulary as container tags. Appointments that already have a container number must be tagged via the container tag endpoints — the container is the row once one exists.
         * @summary Tag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.tagAppointmentsAppointmentsTagPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Remove user assignment from the given appointments
         * @summary Unassign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.unassignAppointmentsAppointmentsUnassignPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Remove tags from containerless appointments.  Appointments that already have a container number must be untagged via the container endpoints — same rule as /appointments/tag/.
         * @summary Untag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.untagAppointmentsAppointmentsUntagPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentsApi - factory interface
 * @export
 */
export const AppointmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentsApiFp(configuration)
    return {
        /**
         * Assign one or more users to containerless appointments (export empty pickups without a container yet). Containered appointments are assigned via their container with /containers/assign/, which is the source of truth once one exists
         * @summary Assign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: any): AxiosPromise<any> {
            return localVarFp.assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary File Export
         * @param {GETFileExport} gETFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileExportAppointmentsFileExportPost(gETFileExport: GETFileExport, options?: any): AxiosPromise<any> {
            return localVarFp.fileExportAppointmentsFileExportPost(gETFileExport, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Appointment Counts
         * @param {number} [timeBucketHours] 
         * @param {string} [after] 
         * @param {string} [before] 
         * @param {TerminalName} [terminal] 
         * @param {TransactionDirection} [direction] 
         * @param {boolean} [loaded] 
         * @param {number} [customerId] 
         * @param {Array<string>} [containerTags] Limit to appointments for containers with all of these tags
         * @param {string} [excludeCustomerIds] Comma separated list
         * @param {Array<string>} [excludeContainerTags] Limit to appointments for containers without these tags
         * @param {string} [timezone] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCountsAppointmentsCountsGet(timeBucketHours?: number, after?: string, before?: string, terminal?: TerminalName, direction?: TransactionDirection, loaded?: boolean, customerId?: number, containerTags?: Array<string>, excludeCustomerIds?: string, excludeContainerTags?: Array<string>, timezone?: string, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETTimeBucketAppointmentStatusCount>> {
            return localVarFp.getAppointmentCountsAppointmentsCountsGet(timeBucketHours, after, before, terminal, direction, loaded, customerId, containerTags, excludeCustomerIds, excludeContainerTags, timezone, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * List **ALL** appointments with various filter criteria, possibly returning multiple appointments for a given container.  The [GET /containers/](#operation/get_containers_containers__get) API also includes appointments, but it only includes at most one appointment per container (the \"most current\" appointment), which might be more preferably depending on your objective
         * @summary List Appointments
         * @param {TerminalName} [terminal] Limit to appointments made at this terminal
         * @param {Array<number>} [id] Limit to appointments with these id
         * @param {string} [after] Limit to appointments with window start after this date and time. It is **highly** recommended to pass this parameter to limit the result set size, particularly if you are not passing &#x60;container_numbers&#x60;.  Appointments do not usually change once their &#x60;window_start&#x60; is a day or two in the past, so limiting to appointments after yesterday or the day before is a good technique
         * @param {string} [before] Limit to appointments with window start before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments with this direction
         * @param {boolean} [loaded] Limit to appointments with this loaded status
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentsAppointmentsGet(terminal?: TerminalName, id?: Array<number>, after?: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, page?: number, pageSize?: number, portComplexes?: Array<PortComplex>, containerNumbers?: string, options?: any): AxiosPromise<Array<GETAppointment>> {
            return localVarFp.listAppointmentsAppointmentsGet(terminal, id, after, before, direction, loaded, statuses, page, pageSize, portComplexes, containerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Load appointments with related container data. Will include one entry *per appointment*, so potentially there will be multiple entries for a single container. Inclusion of some fields is configurable.  Pay particular attention to the `limit_to_relevant_appointments` query param
         * @summary List Appointments With Container Info
         * @param {string} after Only load appointments with a &#x60;window_start&#x60; at or after this date and time 
         * @param {string} [before] Only load appointments with a &#x60;window_start&#x60; before this date and time
         * @param {TransactionDirection} [direction] Limit to appointments for moves in this direction
         * @param {boolean} [loaded] Limit to appointments for moves with this loaded status. i.e. &#x60;true&#x60; limits to loaded containers, &#x60;false&#x60; limits to empty containers
         * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
         * @param {boolean} [includeGateTransactions] If &#x60;false&#x60;, we skip loading Gate Transactions and the &#x60;inbound_gate_transaction&#x60; and &#x60;outbound_gate_transaction&#x60; fields will be set to &#x60;null&#x60;
         * @param {boolean} [includeImportStatuses] If &#x60;false&#x60;, we skip loading Import Statuses and the &#x60;import_status&#x60; field will be set to &#x60;null&#x60;
         * @param {boolean} [limitToRelevantAppointments] If &#x60;true&#x60;, only return the most relevant appointment per move type.  **Example:** If we have a scheduled and cancelled load out appointments for a single container, we\&#39;d return the scheduled one only
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, includeGateTransactions?: boolean, includeImportStatuses?: boolean, limitToRelevantAppointments?: boolean, containerNumbers?: string, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETAppointmentWithContainer>> {
            return localVarFp.listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after, before, direction, loaded, statuses, includeGateTransactions, includeImportStatuses, limitToRelevantAppointments, containerNumbers, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Refresh appointments for all portals
         * @summary Refresh Appointments
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshAppointmentsAppointmentsRefreshPost(options?: any): AxiosPromise<any> {
            return localVarFp.refreshAppointmentsAppointmentsRefreshPost(options).then((request) => request(axios, basePath));
        },
        /**
         * Refresh appointments on demand for specific containers at their terminals.  Reuses the same list-appointments pipeline as the periodic sync, scoped to the connection(s) for the requested containers\' portals. Mirrors the periodic path: only VALIDATED connections with appointment loading enabled are used, and the portal-outage gate is respected. Containers are skipped and reported (never failing the whole request) when the portal cannot list appointments, has no usable connection, is in an outage, or the connection is currently throttled.
         * @summary Refresh Appointments For Containers
         * @param {Array<POSTAppointmentRefresh>} pOSTAppointmentRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh: Array<POSTAppointmentRefresh>, options?: any): AxiosPromise<POSTAppointmentRefreshResponse> {
            return localVarFp.refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh, options).then((request) => request(axios, basePath));
        },
        /**
         * Add tags to containerless appointments (export empty pickups).  Same vocabulary as container tags. Appointments that already have a container number must be tagged via the container tag endpoints — the container is the row once one exists.
         * @summary Tag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: any): AxiosPromise<any> {
            return localVarFp.tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove user assignment from the given appointments
         * @summary Unassign Appointments
         * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: any): AxiosPromise<any> {
            return localVarFp.unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove tags from containerless appointments.  Appointments that already have a container number must be untagged via the container endpoints — same rule as /appointments/tag/.
         * @summary Untag Appointments
         * @param {POSTAppointmentTags} pOSTAppointmentTags 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: any): AxiosPromise<any> {
            return localVarFp.untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentsApi - object-oriented interface
 * @export
 * @class AppointmentsApi
 * @extends {BaseAPI}
 */
export class AppointmentsApi extends BaseAPI {
    /**
     * Assign one or more users to containerless appointments (export empty pickups without a container yet). Containered appointments are assigned via their container with /containers/assign/, which is the source of truth once one exists
     * @summary Assign Appointments
     * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).assignAppointmentsAppointmentsAssignPost(pOSTAppointmentAssignmentData, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary File Export
     * @param {GETFileExport} gETFileExport 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public fileExportAppointmentsFileExportPost(gETFileExport: GETFileExport, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).fileExportAppointmentsFileExportPost(gETFileExport, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Appointment Counts
     * @param {number} [timeBucketHours] 
     * @param {string} [after] 
     * @param {string} [before] 
     * @param {TerminalName} [terminal] 
     * @param {TransactionDirection} [direction] 
     * @param {boolean} [loaded] 
     * @param {number} [customerId] 
     * @param {Array<string>} [containerTags] Limit to appointments for containers with all of these tags
     * @param {string} [excludeCustomerIds] Comma separated list
     * @param {Array<string>} [excludeContainerTags] Limit to appointments for containers without these tags
     * @param {string} [timezone] 
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public getAppointmentCountsAppointmentsCountsGet(timeBucketHours?: number, after?: string, before?: string, terminal?: TerminalName, direction?: TransactionDirection, loaded?: boolean, customerId?: number, containerTags?: Array<string>, excludeCustomerIds?: string, excludeContainerTags?: Array<string>, timezone?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).getAppointmentCountsAppointmentsCountsGet(timeBucketHours, after, before, terminal, direction, loaded, customerId, containerTags, excludeCustomerIds, excludeContainerTags, timezone, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List **ALL** appointments with various filter criteria, possibly returning multiple appointments for a given container.  The [GET /containers/](#operation/get_containers_containers__get) API also includes appointments, but it only includes at most one appointment per container (the \"most current\" appointment), which might be more preferably depending on your objective
     * @summary List Appointments
     * @param {TerminalName} [terminal] Limit to appointments made at this terminal
     * @param {Array<number>} [id] Limit to appointments with these id
     * @param {string} [after] Limit to appointments with window start after this date and time. It is **highly** recommended to pass this parameter to limit the result set size, particularly if you are not passing &#x60;container_numbers&#x60;.  Appointments do not usually change once their &#x60;window_start&#x60; is a day or two in the past, so limiting to appointments after yesterday or the day before is a good technique
     * @param {string} [before] Limit to appointments with window start before this date and time
     * @param {TransactionDirection} [direction] Limit to appointments with this direction
     * @param {boolean} [loaded] Limit to appointments with this loaded status
     * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public listAppointmentsAppointmentsGet(terminal?: TerminalName, id?: Array<number>, after?: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, page?: number, pageSize?: number, portComplexes?: Array<PortComplex>, containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).listAppointmentsAppointmentsGet(terminal, id, after, before, direction, loaded, statuses, page, pageSize, portComplexes, containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Load appointments with related container data. Will include one entry *per appointment*, so potentially there will be multiple entries for a single container. Inclusion of some fields is configurable.  Pay particular attention to the `limit_to_relevant_appointments` query param
     * @summary List Appointments With Container Info
     * @param {string} after Only load appointments with a &#x60;window_start&#x60; at or after this date and time 
     * @param {string} [before] Only load appointments with a &#x60;window_start&#x60; before this date and time
     * @param {TransactionDirection} [direction] Limit to appointments for moves in this direction
     * @param {boolean} [loaded] Limit to appointments for moves with this loaded status. i.e. &#x60;true&#x60; limits to loaded containers, &#x60;false&#x60; limits to empty containers
     * @param {Array<AppointmentStatus>} [statuses] Limit to appointments with these statuses
     * @param {boolean} [includeGateTransactions] If &#x60;false&#x60;, we skip loading Gate Transactions and the &#x60;inbound_gate_transaction&#x60; and &#x60;outbound_gate_transaction&#x60; fields will be set to &#x60;null&#x60;
     * @param {boolean} [includeImportStatuses] If &#x60;false&#x60;, we skip loading Import Statuses and the &#x60;import_status&#x60; field will be set to &#x60;null&#x60;
     * @param {boolean} [limitToRelevantAppointments] If &#x60;true&#x60;, only return the most relevant appointment per move type.  **Example:** If we have a scheduled and cancelled load out appointments for a single container, we\&#39;d return the scheduled one only
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after: string, before?: string, direction?: TransactionDirection, loaded?: boolean, statuses?: Array<AppointmentStatus>, includeGateTransactions?: boolean, includeImportStatuses?: boolean, limitToRelevantAppointments?: boolean, containerNumbers?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(after, before, direction, loaded, statuses, includeGateTransactions, includeImportStatuses, limitToRelevantAppointments, containerNumbers, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Refresh appointments for all portals
     * @summary Refresh Appointments
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public refreshAppointmentsAppointmentsRefreshPost(options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).refreshAppointmentsAppointmentsRefreshPost(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Refresh appointments on demand for specific containers at their terminals.  Reuses the same list-appointments pipeline as the periodic sync, scoped to the connection(s) for the requested containers\' portals. Mirrors the periodic path: only VALIDATED connections with appointment loading enabled are used, and the portal-outage gate is respected. Containers are skipped and reported (never failing the whole request) when the portal cannot list appointments, has no usable connection, is in an outage, or the connection is currently throttled.
     * @summary Refresh Appointments For Containers
     * @param {Array<POSTAppointmentRefresh>} pOSTAppointmentRefresh 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh: Array<POSTAppointmentRefresh>, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).refreshAppointmentsForContainersAppointmentsRefreshForContainersPost(pOSTAppointmentRefresh, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add tags to containerless appointments (export empty pickups).  Same vocabulary as container tags. Appointments that already have a container number must be tagged via the container tag endpoints — the container is the row once one exists.
     * @summary Tag Appointments
     * @param {POSTAppointmentTags} pOSTAppointmentTags 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).tagAppointmentsAppointmentsTagPost(pOSTAppointmentTags, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove user assignment from the given appointments
     * @summary Unassign Appointments
     * @param {POSTAppointmentAssignmentData} pOSTAppointmentAssignmentData 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData: POSTAppointmentAssignmentData, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).unassignAppointmentsAppointmentsUnassignPost(pOSTAppointmentAssignmentData, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove tags from containerless appointments.  Appointments that already have a container number must be untagged via the container endpoints — same rule as /appointments/tag/.
     * @summary Untag Appointments
     * @param {POSTAppointmentTags} pOSTAppointmentTags 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AppointmentsApi
     */
    public untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags: POSTAppointmentTags, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).untagAppointmentsAppointmentsUntagPost(pOSTAppointmentTags, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AutoBookApi - axios parameter creator
 * @export
 */
export const AutoBookApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Cancel Auto Books
         * @param {BodyCancelAutoBooksAppointmentsBookingAutoCancelPost} bodyCancelAutoBooksAppointmentsBookingAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAutoBooksAppointmentsBookingAutoCancelPost: async (bodyCancelAutoBooksAppointmentsBookingAutoCancelPost: BodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyCancelAutoBooksAppointmentsBookingAutoCancelPost' is not null or undefined
            assertParamExists('cancelAutoBooksAppointmentsBookingAutoCancelPost', 'bodyCancelAutoBooksAppointmentsBookingAutoCancelPost', bodyCancelAutoBooksAppointmentsBookingAutoCancelPost)
            const localVarPath = `/appointments/booking/auto/cancel/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a list of auto book evaluations for a given container number. If there were potentially applicable slots, slots are evaluated against the auto book request settings. Container level evaluation reasons for not booking are also included.
         * @summary Load Auto Book Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet: async (containerNumber: string, after?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet', 'containerNumber', containerNumber)
            const localVarPath = `/appointments/booking/auto/evaluations/{container_number}/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Load Auto Book Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet: async (containerNumber: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet', 'containerNumber', containerNumber)
            const localVarPath = `/appointments/booking/auto/{container_number}/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Fetch all auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Settings Bulk
         * @param {BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost} bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingsBulkAppointmentsBookingAutoListPost: async (bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost: BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost' is not null or undefined
            assertParamExists('loadAutoBookSettingsBulkAppointmentsBookingAutoListPost', 'bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost', bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost)
            const localVarPath = `/appointments/booking/auto/list/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Load Auto Book Settings Bulk Minimal
         * @param {BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost} bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost: async (bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost: BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost' is not null or undefined
            assertParamExists('loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost', 'bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost', bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost)
            const localVarPath = `/appointments/booking/auto/list/minimal/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * If you pass \'id\' in the POSTAutoBookRequest, this request will update the existing settings for that auto book request \'id\'. If \'id\' is not passed but there is an existing request, that request will be updated if the existing request was last updated within 30 days. Otherwise, a new request will be created and any existing requests will be deleted.  If master_bill_of_lading is set, the container watch will be also updated with the new master bill of lading.
         * @summary Save Auto Book Settings
         * @param {Array<POSTAutoBookRequest>} pOSTAutoBookRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAutoBookSettingsAppointmentsBookingAutoPost: async (pOSTAutoBookRequest: Array<POSTAutoBookRequest>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAutoBookRequest' is not null or undefined
            assertParamExists('saveAutoBookSettingsAppointmentsBookingAutoPost', 'pOSTAutoBookRequest', pOSTAutoBookRequest)
            const localVarPath = `/appointments/booking/auto/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAutoBookRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AutoBookApi - functional programming interface
 * @export
 */
export const AutoBookApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AutoBookApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Cancel Auto Books
         * @param {BodyCancelAutoBooksAppointmentsBookingAutoCancelPost} bodyCancelAutoBooksAppointmentsBookingAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost: BodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.cancelAutoBooksAppointmentsBookingAutoCancelPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a list of auto book evaluations for a given container number. If there were potentially applicable slots, slots are evaluated against the auto book request settings. Container level evaluation reasons for not booking are also included.
         * @summary Load Auto Book Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookEvaluation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber, after, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Load Auto Book Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETAutoBookRequest>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Fetch all auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Settings Bulk
         * @param {BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost} bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost: BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookRequest>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.loadAutoBookSettingsBulkAppointmentsBookingAutoListPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Load Auto Book Settings Bulk Minimal
         * @param {BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost} bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost: BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookRequestMinimal>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * If you pass \'id\' in the POSTAutoBookRequest, this request will update the existing settings for that auto book request \'id\'. If \'id\' is not passed but there is an existing request, that request will be updated if the existing request was last updated within 30 days. Otherwise, a new request will be created and any existing requests will be deleted.  If master_bill_of_lading is set, the container watch will be also updated with the new master bill of lading.
         * @summary Save Auto Book Settings
         * @param {Array<POSTAutoBookRequest>} pOSTAutoBookRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest: Array<POSTAutoBookRequest>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookApi.saveAutoBookSettingsAppointmentsBookingAutoPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AutoBookApi - factory interface
 * @export
 */
export const AutoBookApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AutoBookApiFp(configuration)
    return {
        /**
         * 
         * @summary Cancel Auto Books
         * @param {BodyCancelAutoBooksAppointmentsBookingAutoCancelPost} bodyCancelAutoBooksAppointmentsBookingAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost: BodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options?: any): AxiosPromise<any> {
            return localVarFp.cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a list of auto book evaluations for a given container number. If there were potentially applicable slots, slots are evaluated against the auto book request settings. Container level evaluation reasons for not booking are also included.
         * @summary Load Auto Book Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: any): AxiosPromise<Array<GETAutoBookEvaluation>> {
            return localVarFp.loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber, after, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Load Auto Book Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber: string, options?: any): AxiosPromise<GETAutoBookRequest> {
            return localVarFp.loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Fetch all auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Settings Bulk
         * @param {BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost} bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost: BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options?: any): AxiosPromise<Array<GETAutoBookRequest>> {
            return localVarFp.loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Load Auto Book Settings Bulk Minimal
         * @param {BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost} bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost: BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options?: any): AxiosPromise<Array<GETAutoBookRequestMinimal>> {
            return localVarFp.loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options).then((request) => request(axios, basePath));
        },
        /**
         * If you pass \'id\' in the POSTAutoBookRequest, this request will update the existing settings for that auto book request \'id\'. If \'id\' is not passed but there is an existing request, that request will be updated if the existing request was last updated within 30 days. Otherwise, a new request will be created and any existing requests will be deleted.  If master_bill_of_lading is set, the container watch will be also updated with the new master bill of lading.
         * @summary Save Auto Book Settings
         * @param {Array<POSTAutoBookRequest>} pOSTAutoBookRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest: Array<POSTAutoBookRequest>, options?: any): AxiosPromise<any> {
            return localVarFp.saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AutoBookApi - object-oriented interface
 * @export
 * @class AutoBookApi
 * @extends {BaseAPI}
 */
export class AutoBookApi extends BaseAPI {
    /**
     * 
     * @summary Cancel Auto Books
     * @param {BodyCancelAutoBooksAppointmentsBookingAutoCancelPost} bodyCancelAutoBooksAppointmentsBookingAutoCancelPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost: BodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).cancelAutoBooksAppointmentsBookingAutoCancelPost(bodyCancelAutoBooksAppointmentsBookingAutoCancelPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a list of auto book evaluations for a given container number. If there were potentially applicable slots, slots are evaluated against the auto book request settings. Container level evaluation reasons for not booking are also included.
     * @summary Load Auto Book Evaluations
     * @param {string} containerNumber 
     * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).loadAutoBookEvaluationsAppointmentsBookingAutoEvaluationsContainerNumberGet(containerNumber, after, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Load Auto Book Setting For Container
     * @param {string} containerNumber 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber: string, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).loadAutoBookSettingForContainerAppointmentsBookingAutoContainerNumberGet(containerNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Fetch all auto book settings for the given container numbers in one call. Returns an empty list if none are found.
     * @summary Load Auto Book Settings Bulk
     * @param {BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost} bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost: BodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).loadAutoBookSettingsBulkAppointmentsBookingAutoListPost(bodyLoadAutoBookSettingsBulkAppointmentsBookingAutoListPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Load Auto Book Settings Bulk Minimal
     * @param {BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost} bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost: BodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).loadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost(bodyLoadAutoBookSettingsBulkMinimalAppointmentsBookingAutoListMinimalPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * If you pass \'id\' in the POSTAutoBookRequest, this request will update the existing settings for that auto book request \'id\'. If \'id\' is not passed but there is an existing request, that request will be updated if the existing request was last updated within 30 days. Otherwise, a new request will be created and any existing requests will be deleted.  If master_bill_of_lading is set, the container watch will be also updated with the new master bill of lading.
     * @summary Save Auto Book Settings
     * @param {Array<POSTAutoBookRequest>} pOSTAutoBookRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookApi
     */
    public saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest: Array<POSTAutoBookRequest>, options?: RawAxiosRequestConfig) {
        return AutoBookApiFp(this.configuration).saveAutoBookSettingsAppointmentsBookingAutoPost(pOSTAutoBookRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AutoBookEmptyApi - axios parameter creator
 * @export
 */
export const AutoBookEmptyApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Cancel Auto Book Empties
         * @param {BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost} bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost: async (bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost: BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost' is not null or undefined
            assertParamExists('cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost', 'bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost', bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost)
            const localVarPath = `/appointments/booking/empty/auto/cancel/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns empty auto book evaluations for a container so users can see why a container did or did not book. Per-slot scoring and container-level reasons are both included.
         * @summary Load Auto Book Empty Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet: async (containerNumber: string, after?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet', 'containerNumber', containerNumber)
            const localVarPath = `/appointments/booking/empty/auto/evaluations/{container_number}/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Load Auto Book Empty Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet: async (containerNumber: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet', 'containerNumber', containerNumber)
            const localVarPath = `/appointments/booking/empty/auto/{container_number}/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Fetch all empty auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Empty Settings Bulk
         * @param {BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost} bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost: async (bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost: BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost' is not null or undefined
            assertParamExists('loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost', 'bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost', bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost)
            const localVarPath = `/appointments/booking/empty/auto/list/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Load Auto Book Empty Settings Bulk Minimal
         * @param {BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost} bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost: async (bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost: BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost' is not null or undefined
            assertParamExists('loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost', 'bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost', bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost)
            const localVarPath = `/appointments/booking/empty/auto/list/minimal/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create or update empty auto-book preferences for one or more containers.  If \'id\' is passed, the matching request is updated. If \'id\' is omitted but a request already exists for the container and was last updated within 30 days, that request is updated instead of creating a new one; otherwise a new request is created and any existing request for the container is deleted.  Saving always activates auto-book (status REQUESTED). Use POST /cancel/ to turn auto-book off.  A container must be in out-gated, empty, or empty-appointment-booked state before it can be enrolled.
         * @summary Save Auto Book Empty Settings
         * @param {Array<POSTAutoBookEmptyRequest>} pOSTAutoBookEmptyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost: async (pOSTAutoBookEmptyRequest: Array<POSTAutoBookEmptyRequest>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAutoBookEmptyRequest' is not null or undefined
            assertParamExists('saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost', 'pOSTAutoBookEmptyRequest', pOSTAutoBookEmptyRequest)
            const localVarPath = `/appointments/booking/empty/auto/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAutoBookEmptyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AutoBookEmptyApi - functional programming interface
 * @export
 */
export const AutoBookEmptyApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AutoBookEmptyApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Cancel Auto Book Empties
         * @param {BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost} bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost: BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns empty auto book evaluations for a container so users can see why a container did or did not book. Per-slot scoring and container-level reasons are both included.
         * @summary Load Auto Book Empty Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookEmptyEvaluation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber, after, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Load Auto Book Empty Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETAutoBookEmptyRequest>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Fetch all empty auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Empty Settings Bulk
         * @param {BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost} bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost: BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookEmptyRequest>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Load Auto Book Empty Settings Bulk Minimal
         * @param {BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost} bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost: BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETAutoBookEmptyRequestMinimal>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Create or update empty auto-book preferences for one or more containers.  If \'id\' is passed, the matching request is updated. If \'id\' is omitted but a request already exists for the container and was last updated within 30 days, that request is updated instead of creating a new one; otherwise a new request is created and any existing request for the container is deleted.  Saving always activates auto-book (status REQUESTED). Use POST /cancel/ to turn auto-book off.  A container must be in out-gated, empty, or empty-appointment-booked state before it can be enrolled.
         * @summary Save Auto Book Empty Settings
         * @param {Array<POSTAutoBookEmptyRequest>} pOSTAutoBookEmptyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest: Array<POSTAutoBookEmptyRequest>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AutoBookEmptyApi.saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AutoBookEmptyApi - factory interface
 * @export
 */
export const AutoBookEmptyApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AutoBookEmptyApiFp(configuration)
    return {
        /**
         * 
         * @summary Cancel Auto Book Empties
         * @param {BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost} bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost: BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options?: any): AxiosPromise<any> {
            return localVarFp.cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns empty auto book evaluations for a container so users can see why a container did or did not book. Per-slot scoring and container-level reasons are both included.
         * @summary Load Auto Book Empty Evaluations
         * @param {string} containerNumber 
         * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: any): AxiosPromise<Array<GETAutoBookEmptyEvaluation>> {
            return localVarFp.loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber, after, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Load Auto Book Empty Setting For Container
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber: string, options?: any): AxiosPromise<GETAutoBookEmptyRequest> {
            return localVarFp.loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Fetch all empty auto book settings for the given container numbers in one call. Returns an empty list if none are found.
         * @summary Load Auto Book Empty Settings Bulk
         * @param {BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost} bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost: BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options?: any): AxiosPromise<Array<GETAutoBookEmptyRequest>> {
            return localVarFp.loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Load Auto Book Empty Settings Bulk Minimal
         * @param {BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost} bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost: BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options?: any): AxiosPromise<Array<GETAutoBookEmptyRequestMinimal>> {
            return localVarFp.loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options).then((request) => request(axios, basePath));
        },
        /**
         * Create or update empty auto-book preferences for one or more containers.  If \'id\' is passed, the matching request is updated. If \'id\' is omitted but a request already exists for the container and was last updated within 30 days, that request is updated instead of creating a new one; otherwise a new request is created and any existing request for the container is deleted.  Saving always activates auto-book (status REQUESTED). Use POST /cancel/ to turn auto-book off.  A container must be in out-gated, empty, or empty-appointment-booked state before it can be enrolled.
         * @summary Save Auto Book Empty Settings
         * @param {Array<POSTAutoBookEmptyRequest>} pOSTAutoBookEmptyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest: Array<POSTAutoBookEmptyRequest>, options?: any): AxiosPromise<any> {
            return localVarFp.saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AutoBookEmptyApi - object-oriented interface
 * @export
 * @class AutoBookEmptyApi
 * @extends {BaseAPI}
 */
export class AutoBookEmptyApi extends BaseAPI {
    /**
     * 
     * @summary Cancel Auto Book Empties
     * @param {BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost} bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost: BodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).cancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost(bodyCancelAutoBookEmptiesAppointmentsBookingEmptyAutoCancelPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns empty auto book evaluations for a container so users can see why a container did or did not book. Per-slot scoring and container-level reasons are both included.
     * @summary Load Auto Book Empty Evaluations
     * @param {string} containerNumber 
     * @param {string} [after] Only return evaluations after this time. Defaults to 7 days ago.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber: string, after?: string, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).loadAutoBookEmptyEvaluationsAppointmentsBookingEmptyAutoEvaluationsContainerNumberGet(containerNumber, after, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Load Auto Book Empty Setting For Container
     * @param {string} containerNumber 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber: string, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).loadAutoBookEmptySettingForContainerAppointmentsBookingEmptyAutoContainerNumberGet(containerNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Fetch all empty auto book settings for the given container numbers in one call. Returns an empty list if none are found.
     * @summary Load Auto Book Empty Settings Bulk
     * @param {BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost} bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost: BodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).loadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost(bodyLoadAutoBookEmptySettingsBulkAppointmentsBookingEmptyAutoListPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Load Auto Book Empty Settings Bulk Minimal
     * @param {BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost} bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost: BodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).loadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost(bodyLoadAutoBookEmptySettingsBulkMinimalAppointmentsBookingEmptyAutoListMinimalPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create or update empty auto-book preferences for one or more containers.  If \'id\' is passed, the matching request is updated. If \'id\' is omitted but a request already exists for the container and was last updated within 30 days, that request is updated instead of creating a new one; otherwise a new request is created and any existing request for the container is deleted.  Saving always activates auto-book (status REQUESTED). Use POST /cancel/ to turn auto-book off.  A container must be in out-gated, empty, or empty-appointment-booked state before it can be enrolled.
     * @summary Save Auto Book Empty Settings
     * @param {Array<POSTAutoBookEmptyRequest>} pOSTAutoBookEmptyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AutoBookEmptyApi
     */
    public saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest: Array<POSTAutoBookEmptyRequest>, options?: RawAxiosRequestConfig) {
        return AutoBookEmptyApiFp(this.configuration).saveAutoBookEmptySettingsAppointmentsBookingEmptyAutoPost(pOSTAutoBookEmptyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * CamerasApi - axios parameter creator
 * @export
 */
export const CamerasApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Limited to one day of history
         * @summary Get Stream History
         * @param {TerminalName} terminal 
         * @param {string} stream 
         * @param {string} date 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamHistoryCameraStreamsHistoryGet: async (terminal: TerminalName, stream: string, date: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getStreamHistoryCameraStreamsHistoryGet', 'terminal', terminal)
            // verify required parameter 'stream' is not null or undefined
            assertParamExists('getStreamHistoryCameraStreamsHistoryGet', 'stream', stream)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('getStreamHistoryCameraStreamsHistoryGet', 'date', date)
            const localVarPath = `/camera_streams/history/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (stream !== undefined) {
                localVarQueryParameter['stream'] = stream;
            }

            if (date !== undefined) {
                localVarQueryParameter['date'] = (date as any instanceof Date) ?
                    (date as any).toISOString().substring(0,10) :
                    date;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get latest camera images for each terminals default stream
         * @summary Get Streams
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamsCameraStreamsGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/camera_streams/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get latest images for all cameras at a particularly terminals
         * @summary Get Streams For Terminal
         * @param {TerminalName} terminalName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamsForTerminalCameraStreamsTerminalTerminalNameGet: async (terminalName: TerminalName, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminalName' is not null or undefined
            assertParamExists('getStreamsForTerminalCameraStreamsTerminalTerminalNameGet', 'terminalName', terminalName)
            const localVarPath = `/camera_streams/terminal/{terminal_name}/`
                .replace(`{${"terminal_name"}}`, encodeURIComponent(String(terminalName)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CamerasApi - functional programming interface
 * @export
 */
export const CamerasApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CamerasApiAxiosParamCreator(configuration)
    return {
        /**
         * Limited to one day of history
         * @summary Get Stream History
         * @param {TerminalName} terminal 
         * @param {string} stream 
         * @param {string} date 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getStreamHistoryCameraStreamsHistoryGet(terminal: TerminalName, stream: string, date: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETCameraHistoryImage>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getStreamHistoryCameraStreamsHistoryGet(terminal, stream, date, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CamerasApi.getStreamHistoryCameraStreamsHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get latest camera images for each terminals default stream
         * @summary Get Streams
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getStreamsCameraStreamsGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETCameraImage>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getStreamsCameraStreamsGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CamerasApi.getStreamsCameraStreamsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get latest images for all cameras at a particularly terminals
         * @summary Get Streams For Terminal
         * @param {TerminalName} terminalName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName: TerminalName, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETCameraImage>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CamerasApi.getStreamsForTerminalCameraStreamsTerminalTerminalNameGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CamerasApi - factory interface
 * @export
 */
export const CamerasApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CamerasApiFp(configuration)
    return {
        /**
         * Limited to one day of history
         * @summary Get Stream History
         * @param {TerminalName} terminal 
         * @param {string} stream 
         * @param {string} date 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamHistoryCameraStreamsHistoryGet(terminal: TerminalName, stream: string, date: string, options?: any): AxiosPromise<Array<GETCameraHistoryImage>> {
            return localVarFp.getStreamHistoryCameraStreamsHistoryGet(terminal, stream, date, options).then((request) => request(axios, basePath));
        },
        /**
         * Get latest camera images for each terminals default stream
         * @summary Get Streams
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamsCameraStreamsGet(options?: any): AxiosPromise<Array<GETCameraImage>> {
            return localVarFp.getStreamsCameraStreamsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Get latest images for all cameras at a particularly terminals
         * @summary Get Streams For Terminal
         * @param {TerminalName} terminalName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName: TerminalName, options?: any): AxiosPromise<Array<GETCameraImage>> {
            return localVarFp.getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CamerasApi - object-oriented interface
 * @export
 * @class CamerasApi
 * @extends {BaseAPI}
 */
export class CamerasApi extends BaseAPI {
    /**
     * Limited to one day of history
     * @summary Get Stream History
     * @param {TerminalName} terminal 
     * @param {string} stream 
     * @param {string} date 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CamerasApi
     */
    public getStreamHistoryCameraStreamsHistoryGet(terminal: TerminalName, stream: string, date: string, options?: RawAxiosRequestConfig) {
        return CamerasApiFp(this.configuration).getStreamHistoryCameraStreamsHistoryGet(terminal, stream, date, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get latest camera images for each terminals default stream
     * @summary Get Streams
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CamerasApi
     */
    public getStreamsCameraStreamsGet(options?: RawAxiosRequestConfig) {
        return CamerasApiFp(this.configuration).getStreamsCameraStreamsGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get latest images for all cameras at a particularly terminals
     * @summary Get Streams For Terminal
     * @param {TerminalName} terminalName 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CamerasApi
     */
    public getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName: TerminalName, options?: RawAxiosRequestConfig) {
        return CamerasApiFp(this.configuration).getStreamsForTerminalCameraStreamsTerminalTerminalNameGet(terminalName, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ConnectionsApi - axios parameter creator
 * @export
 */
export const ConnectionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Activate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateConnectionsIdActivatePut: async (id: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('activateConnectionsIdActivatePut', 'id', id)
            const localVarPath = `/connections/{id}/activate/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deactivate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateConnectionsIdDeactivatePut: async (id: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deactivateConnectionsIdDeactivatePut', 'id', id)
            const localVarPath = `/connections/{id}/deactivate/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete Connections
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteConnectionsConnectionsConnectionIdDelete: async (connectionId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('deleteConnectionsConnectionsConnectionIdDelete', 'connectionId', connectionId)
            const localVarPath = `/connections/{connection_id}/`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List Connections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionsConnectionsGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/connections/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List Editable Connection Types
         * @param {OrganizationSubscription} [subscription] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEditableConnectionTypesConnectionsEditableTypesGet: async (subscription?: OrganizationSubscription, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/connections/editable_types/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (subscription !== undefined) {
                localVarQueryParameter['subscription'] = subscription;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Per-terminal tracking and booking credential status for the org\'s port complexes. Clients prompt the user when a status is `failing` or `not_set_up`.
         * @summary List Terminal Credential Statuses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTerminalCredentialStatusesConnectionsTerminalStatusesGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/connections/terminal_statuses/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Mark a connection as not used. This will create a new connection, or update an existing connection, with a validation status of NOT_USED.
         * @summary Mark Connection Not Used
         * @param {ConnectionType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markConnectionNotUsedConnectionsTypeMarkNotUsedPost: async (type: ConnectionType, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'type' is not null or undefined
            assertParamExists('markConnectionNotUsedConnectionsTypeMarkNotUsedPost', 'type', type)
            const localVarPath = `/connections/{type}/mark_not_used/`
                .replace(`{${"type"}}`, encodeURIComponent(String(type)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Save And Validate Connections
         * @param {Array<POSTConnection>} pOSTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAndValidateConnectionsConnectionsSaveAndValidatePost: async (pOSTConnection: Array<POSTConnection>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTConnection' is not null or undefined
            assertParamExists('saveAndValidateConnectionsConnectionsSaveAndValidatePost', 'pOSTConnection', pOSTConnection)
            const localVarPath = `/connections/save_and_validate/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTConnection, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Fetch TMS terminals and use AI to suggest mappings to DrayDog terminals.
         * @summary Suggest Terminal Mapping
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost: async (connectionId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost', 'connectionId', connectionId)
            const localVarPath = `/connections/{connection_id}/suggest_terminal_mapping/`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update Connection
         * @param {PUTConnection} pUTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateConnectionConnectionsPut: async (pUTConnection: PUTConnection, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pUTConnection' is not null or undefined
            assertParamExists('updateConnectionConnectionsPut', 'pUTConnection', pUTConnection)
            const localVarPath = `/connections/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pUTConnection, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ConnectionsApi - functional programming interface
 * @export
 */
export const ConnectionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ConnectionsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Activate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activateConnectionsIdActivatePut(id: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activateConnectionsIdActivatePut(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.activateConnectionsIdActivatePut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deactivate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateConnectionsIdDeactivatePut(id: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateConnectionsIdDeactivatePut(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.deactivateConnectionsIdDeactivatePut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete Connections
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteConnectionsConnectionsConnectionIdDelete(connectionId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteConnectionsConnectionsConnectionIdDelete(connectionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.deleteConnectionsConnectionsConnectionIdDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List Connections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionsConnectionsGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETConnection>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionsConnectionsGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.listConnectionsConnectionsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List Editable Connection Types
         * @param {OrganizationSubscription} [subscription] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listEditableConnectionTypesConnectionsEditableTypesGet(subscription?: OrganizationSubscription, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETConnectionMetadata>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listEditableConnectionTypesConnectionsEditableTypesGet(subscription, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.listEditableConnectionTypesConnectionsEditableTypesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Per-terminal tracking and booking credential status for the org\'s port complexes. Clients prompt the user when a status is `failing` or `not_set_up`.
         * @summary List Terminal Credential Statuses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<TerminalCredentialStatus>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.listTerminalCredentialStatusesConnectionsTerminalStatusesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Mark a connection as not used. This will create a new connection, or update an existing connection, with a validation status of NOT_USED.
         * @summary Mark Connection Not Used
         * @param {ConnectionType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type: ConnectionType, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETConnection>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.markConnectionNotUsedConnectionsTypeMarkNotUsedPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Save And Validate Connections
         * @param {Array<POSTConnection>} pOSTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection: Array<POSTConnection>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETConnection>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.saveAndValidateConnectionsConnectionsSaveAndValidatePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Fetch TMS terminals and use AI to suggest mappings to DrayDog terminals.
         * @summary Suggest Terminal Mapping
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TerminalMappingSuggestionsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Update Connection
         * @param {PUTConnection} pUTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateConnectionConnectionsPut(pUTConnection: PUTConnection, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateConnectionConnectionsPut(pUTConnection, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ConnectionsApi.updateConnectionConnectionsPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ConnectionsApi - factory interface
 * @export
 */
export const ConnectionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ConnectionsApiFp(configuration)
    return {
        /**
         * 
         * @summary Activate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateConnectionsIdActivatePut(id: number, options?: any): AxiosPromise<any> {
            return localVarFp.activateConnectionsIdActivatePut(id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deactivate
         * @param {number} id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateConnectionsIdDeactivatePut(id: number, options?: any): AxiosPromise<any> {
            return localVarFp.deactivateConnectionsIdDeactivatePut(id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete Connections
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteConnectionsConnectionsConnectionIdDelete(connectionId: number, options?: any): AxiosPromise<any> {
            return localVarFp.deleteConnectionsConnectionsConnectionIdDelete(connectionId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List Connections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionsConnectionsGet(options?: any): AxiosPromise<Array<GETConnection>> {
            return localVarFp.listConnectionsConnectionsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List Editable Connection Types
         * @param {OrganizationSubscription} [subscription] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEditableConnectionTypesConnectionsEditableTypesGet(subscription?: OrganizationSubscription, options?: any): AxiosPromise<Array<GETConnectionMetadata>> {
            return localVarFp.listEditableConnectionTypesConnectionsEditableTypesGet(subscription, options).then((request) => request(axios, basePath));
        },
        /**
         * Per-terminal tracking and booking credential status for the org\'s port complexes. Clients prompt the user when a status is `failing` or `not_set_up`.
         * @summary List Terminal Credential Statuses
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options?: any): AxiosPromise<Array<TerminalCredentialStatus>> {
            return localVarFp.listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Mark a connection as not used. This will create a new connection, or update an existing connection, with a validation status of NOT_USED.
         * @summary Mark Connection Not Used
         * @param {ConnectionType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type: ConnectionType, options?: any): AxiosPromise<GETConnection> {
            return localVarFp.markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Save And Validate Connections
         * @param {Array<POSTConnection>} pOSTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection: Array<POSTConnection>, options?: any): AxiosPromise<Array<GETConnection>> {
            return localVarFp.saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection, options).then((request) => request(axios, basePath));
        },
        /**
         * Fetch TMS terminals and use AI to suggest mappings to DrayDog terminals.
         * @summary Suggest Terminal Mapping
         * @param {number} connectionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId: number, options?: any): AxiosPromise<TerminalMappingSuggestionsResponse> {
            return localVarFp.suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update Connection
         * @param {PUTConnection} pUTConnection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateConnectionConnectionsPut(pUTConnection: PUTConnection, options?: any): AxiosPromise<any> {
            return localVarFp.updateConnectionConnectionsPut(pUTConnection, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ConnectionsApi - object-oriented interface
 * @export
 * @class ConnectionsApi
 * @extends {BaseAPI}
 */
export class ConnectionsApi extends BaseAPI {
    /**
     * 
     * @summary Activate
     * @param {number} id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public activateConnectionsIdActivatePut(id: number, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).activateConnectionsIdActivatePut(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deactivate
     * @param {number} id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public deactivateConnectionsIdDeactivatePut(id: number, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).deactivateConnectionsIdDeactivatePut(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete Connections
     * @param {number} connectionId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public deleteConnectionsConnectionsConnectionIdDelete(connectionId: number, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).deleteConnectionsConnectionsConnectionIdDelete(connectionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List Connections
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public listConnectionsConnectionsGet(options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).listConnectionsConnectionsGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List Editable Connection Types
     * @param {OrganizationSubscription} [subscription] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public listEditableConnectionTypesConnectionsEditableTypesGet(subscription?: OrganizationSubscription, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).listEditableConnectionTypesConnectionsEditableTypesGet(subscription, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Per-terminal tracking and booking credential status for the org\'s port complexes. Clients prompt the user when a status is `failing` or `not_set_up`.
     * @summary List Terminal Credential Statuses
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).listTerminalCredentialStatusesConnectionsTerminalStatusesGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Mark a connection as not used. This will create a new connection, or update an existing connection, with a validation status of NOT_USED.
     * @summary Mark Connection Not Used
     * @param {ConnectionType} type 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type: ConnectionType, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).markConnectionNotUsedConnectionsTypeMarkNotUsedPost(type, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Save And Validate Connections
     * @param {Array<POSTConnection>} pOSTConnection 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection: Array<POSTConnection>, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).saveAndValidateConnectionsConnectionsSaveAndValidatePost(pOSTConnection, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Fetch TMS terminals and use AI to suggest mappings to DrayDog terminals.
     * @summary Suggest Terminal Mapping
     * @param {number} connectionId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId: number, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).suggestTerminalMappingConnectionsConnectionIdSuggestTerminalMappingPost(connectionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Update Connection
     * @param {PUTConnection} pUTConnection 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ConnectionsApi
     */
    public updateConnectionConnectionsPut(pUTConnection: PUTConnection, options?: RawAxiosRequestConfig) {
        return ConnectionsApiFp(this.configuration).updateConnectionConnectionsPut(pUTConnection, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContainerEventsApi - axios parameter creator
 * @export
 */
export const ContainerEventsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Use to indicate containers that are now empty and ready to be picked up and returned to a terminal  There is no way for Dray Dog to know when a container has been emptied other users telling us via this API
         * @summary Mark Containers As Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsEmptyContainersEventsMarkAsEmptyPost: async (pOSTContainerNumbers: POSTContainerNumbers, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTContainerNumbers' is not null or undefined
            assertParamExists('markContainersAsEmptyContainersEventsMarkAsEmptyPost', 'pOSTContainerNumbers', pOSTContainerNumbers)
            const localVarPath = `/containers/events/mark_as_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTContainerNumbers, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reverses a prior mark-as-empty for containers that were incorrectly flagged.
         * @summary Mark Containers As Not Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost: async (pOSTContainerNumbers: POSTContainerNumbers, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTContainerNumbers' is not null or undefined
            assertParamExists('markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost', 'pOSTContainerNumbers', pOSTContainerNumbers)
            const localVarPath = `/containers/events/mark_as_not_empty/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTContainerNumbers, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Use to indicate containers that have been returned to a terminal and no longer need to be considered for empty returns.  Dray Dog identifies many but not all container terminations, so it is important to have a way for users to clear containers from their \"to return\" list
         * @summary Mark Containers As Terminated
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsTerminatedContainersEventsMarkAsTerminatedPost: async (pOSTContainerNumbers: POSTContainerNumbers, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTContainerNumbers' is not null or undefined
            assertParamExists('markContainersAsTerminatedContainersEventsMarkAsTerminatedPost', 'pOSTContainerNumbers', pOSTContainerNumbers)
            const localVarPath = `/containers/events/mark_as_terminated/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTContainerNumbers, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Manually set the shipping line and/or container type for containers where we could not determine it from fetched data.  Only containers currently missing the field are updated; any container that already has a value is reported back and left unchanged. The change is recorded as a watch event attributed to the calling user, and newer fetched data will supersede it.
         * @summary Set Line And Type
         * @param {POSTSetLineAndType} pOSTSetLineAndType 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setLineAndTypeContainersEventsSetLineAndTypePost: async (pOSTSetLineAndType: POSTSetLineAndType, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTSetLineAndType' is not null or undefined
            assertParamExists('setLineAndTypeContainersEventsSetLineAndTypePost', 'pOSTSetLineAndType', pOSTSetLineAndType)
            const localVarPath = `/containers/events/set_line_and_type/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTSetLineAndType, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add tags to a container watch. Tags are \"additive\" and the operation idempotent, meaning: - Existing tags will never be removed - Adding a tag multiple times will not result in duplicates or raise an error  You can also think of tags as a \"set\" and this is an \"add\" to set operation.  If you use a tag that is not yet created it will be automatically created for you.
         * @summary Tag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tagContainerContainersEventsTagPost: async (pOSTTagEvents: POSTTagEvents, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTTagEvents' is not null or undefined
            assertParamExists('tagContainerContainersEventsTagPost', 'pOSTTagEvents', pOSTTagEvents)
            const localVarPath = `/containers/events/tag/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTagEvents, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove tags from a container watch. Tags are \"subtractive\" and the operation is idempotent, meaning: - Removing a tag that does not exist will not raise an error - Existing tags that you do not pass will not be removed  You can also think of tags as a \"set\" and this is a \"remove\" from set operation.  No error will be raised if a tag value is used that has not been created.F
         * @summary Untag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        untagContainerContainersEventsUntagPost: async (pOSTTagEvents: POSTTagEvents, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTTagEvents' is not null or undefined
            assertParamExists('untagContainerContainersEventsUntagPost', 'pOSTTagEvents', pOSTTagEvents)
            const localVarPath = `/containers/events/untag/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTagEvents, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContainerEventsApi - functional programming interface
 * @export
 */
export const ContainerEventsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContainerEventsApiAxiosParamCreator(configuration)
    return {
        /**
         * Use to indicate containers that are now empty and ready to be picked up and returned to a terminal  There is no way for Dray Dog to know when a container has been emptied other users telling us via this API
         * @summary Mark Containers As Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTMarkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.markContainersAsEmptyContainersEventsMarkAsEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reverses a prior mark-as-empty for containers that were incorrectly flagged.
         * @summary Mark Containers As Not Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTMarkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Use to indicate containers that have been returned to a terminal and no longer need to be considered for empty returns.  Dray Dog identifies many but not all container terminations, so it is important to have a way for users to clear containers from their \"to return\" list
         * @summary Mark Containers As Terminated
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTMarkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.markContainersAsTerminatedContainersEventsMarkAsTerminatedPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Manually set the shipping line and/or container type for containers where we could not determine it from fetched data.  Only containers currently missing the field are updated; any container that already has a value is reported back and left unchanged. The change is recorded as a watch event attributed to the calling user, and newer fetched data will supersede it.
         * @summary Set Line And Type
         * @param {POSTSetLineAndType} pOSTSetLineAndType 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType: POSTSetLineAndType, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTSetLineAndTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.setLineAndTypeContainersEventsSetLineAndTypePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Add tags to a container watch. Tags are \"additive\" and the operation idempotent, meaning: - Existing tags will never be removed - Adding a tag multiple times will not result in duplicates or raise an error  You can also think of tags as a \"set\" and this is an \"add\" to set operation.  If you use a tag that is not yet created it will be automatically created for you.
         * @summary Tag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async tagContainerContainersEventsTagPost(pOSTTagEvents: POSTTagEvents, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.tagContainerContainersEventsTagPost(pOSTTagEvents, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.tagContainerContainersEventsTagPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Remove tags from a container watch. Tags are \"subtractive\" and the operation is idempotent, meaning: - Removing a tag that does not exist will not raise an error - Existing tags that you do not pass will not be removed  You can also think of tags as a \"set\" and this is a \"remove\" from set operation.  No error will be raised if a tag value is used that has not been created.F
         * @summary Untag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async untagContainerContainersEventsUntagPost(pOSTTagEvents: POSTTagEvents, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.untagContainerContainersEventsUntagPost(pOSTTagEvents, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerEventsApi.untagContainerContainersEventsUntagPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContainerEventsApi - factory interface
 * @export
 */
export const ContainerEventsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContainerEventsApiFp(configuration)
    return {
        /**
         * Use to indicate containers that are now empty and ready to be picked up and returned to a terminal  There is no way for Dray Dog to know when a container has been emptied other users telling us via this API
         * @summary Mark Containers As Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: any): AxiosPromise<POSTMarkResponse> {
            return localVarFp.markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Reverses a prior mark-as-empty for containers that were incorrectly flagged.
         * @summary Mark Containers As Not Empty
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: any): AxiosPromise<POSTMarkResponse> {
            return localVarFp.markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Use to indicate containers that have been returned to a terminal and no longer need to be considered for empty returns.  Dray Dog identifies many but not all container terminations, so it is important to have a way for users to clear containers from their \"to return\" list
         * @summary Mark Containers As Terminated
         * @param {POSTContainerNumbers} pOSTContainerNumbers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers: POSTContainerNumbers, options?: any): AxiosPromise<POSTMarkResponse> {
            return localVarFp.markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Manually set the shipping line and/or container type for containers where we could not determine it from fetched data.  Only containers currently missing the field are updated; any container that already has a value is reported back and left unchanged. The change is recorded as a watch event attributed to the calling user, and newer fetched data will supersede it.
         * @summary Set Line And Type
         * @param {POSTSetLineAndType} pOSTSetLineAndType 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType: POSTSetLineAndType, options?: any): AxiosPromise<POSTSetLineAndTypeResponse> {
            return localVarFp.setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType, options).then((request) => request(axios, basePath));
        },
        /**
         * Add tags to a container watch. Tags are \"additive\" and the operation idempotent, meaning: - Existing tags will never be removed - Adding a tag multiple times will not result in duplicates or raise an error  You can also think of tags as a \"set\" and this is an \"add\" to set operation.  If you use a tag that is not yet created it will be automatically created for you.
         * @summary Tag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tagContainerContainersEventsTagPost(pOSTTagEvents: POSTTagEvents, options?: any): AxiosPromise<any> {
            return localVarFp.tagContainerContainersEventsTagPost(pOSTTagEvents, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove tags from a container watch. Tags are \"subtractive\" and the operation is idempotent, meaning: - Removing a tag that does not exist will not raise an error - Existing tags that you do not pass will not be removed  You can also think of tags as a \"set\" and this is a \"remove\" from set operation.  No error will be raised if a tag value is used that has not been created.F
         * @summary Untag Container
         * @param {POSTTagEvents} pOSTTagEvents 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        untagContainerContainersEventsUntagPost(pOSTTagEvents: POSTTagEvents, options?: any): AxiosPromise<any> {
            return localVarFp.untagContainerContainersEventsUntagPost(pOSTTagEvents, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContainerEventsApi - object-oriented interface
 * @export
 * @class ContainerEventsApi
 * @extends {BaseAPI}
 */
export class ContainerEventsApi extends BaseAPI {
    /**
     * Use to indicate containers that are now empty and ready to be picked up and returned to a terminal  There is no way for Dray Dog to know when a container has been emptied other users telling us via this API
     * @summary Mark Containers As Empty
     * @param {POSTContainerNumbers} pOSTContainerNumbers 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).markContainersAsEmptyContainersEventsMarkAsEmptyPost(pOSTContainerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reverses a prior mark-as-empty for containers that were incorrectly flagged.
     * @summary Mark Containers As Not Empty
     * @param {POSTContainerNumbers} pOSTContainerNumbers 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).markContainersAsNotEmptyContainersEventsMarkAsNotEmptyPost(pOSTContainerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Use to indicate containers that have been returned to a terminal and no longer need to be considered for empty returns.  Dray Dog identifies many but not all container terminations, so it is important to have a way for users to clear containers from their \"to return\" list
     * @summary Mark Containers As Terminated
     * @param {POSTContainerNumbers} pOSTContainerNumbers 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers: POSTContainerNumbers, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).markContainersAsTerminatedContainersEventsMarkAsTerminatedPost(pOSTContainerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Manually set the shipping line and/or container type for containers where we could not determine it from fetched data.  Only containers currently missing the field are updated; any container that already has a value is reported back and left unchanged. The change is recorded as a watch event attributed to the calling user, and newer fetched data will supersede it.
     * @summary Set Line And Type
     * @param {POSTSetLineAndType} pOSTSetLineAndType 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType: POSTSetLineAndType, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).setLineAndTypeContainersEventsSetLineAndTypePost(pOSTSetLineAndType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add tags to a container watch. Tags are \"additive\" and the operation idempotent, meaning: - Existing tags will never be removed - Adding a tag multiple times will not result in duplicates or raise an error  You can also think of tags as a \"set\" and this is an \"add\" to set operation.  If you use a tag that is not yet created it will be automatically created for you.
     * @summary Tag Container
     * @param {POSTTagEvents} pOSTTagEvents 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public tagContainerContainersEventsTagPost(pOSTTagEvents: POSTTagEvents, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).tagContainerContainersEventsTagPost(pOSTTagEvents, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove tags from a container watch. Tags are \"subtractive\" and the operation is idempotent, meaning: - Removing a tag that does not exist will not raise an error - Existing tags that you do not pass will not be removed  You can also think of tags as a \"set\" and this is a \"remove\" from set operation.  No error will be raised if a tag value is used that has not been created.F
     * @summary Untag Container
     * @param {POSTTagEvents} pOSTTagEvents 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerEventsApi
     */
    public untagContainerContainersEventsUntagPost(pOSTTagEvents: POSTTagEvents, options?: RawAxiosRequestConfig) {
        return ContainerEventsApiFp(this.configuration).untagContainerContainersEventsUntagPost(pOSTTagEvents, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContainerNotesApi - axios parameter creator
 * @export
 */
export const ContainerNotesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a note for a container. Notes are visible to all users in the org.
         * @summary Create Note
         * @param {string} containerNumber 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createNoteContainersContainerNumberNotesPost: async (containerNumber: string, pOSTNote: POSTNote, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('createNoteContainersContainerNumberNotesPost', 'containerNumber', containerNumber)
            // verify required parameter 'pOSTNote' is not null or undefined
            assertParamExists('createNoteContainersContainerNumberNotesPost', 'pOSTNote', pOSTNote)
            const localVarPath = `/containers/{container_number}/notes/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTNote, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete a note. Only the original author may delete their note.
         * @summary Delete Note
         * @param {number} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNoteContainersNotesNoteIdDelete: async (noteId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'noteId' is not null or undefined
            assertParamExists('deleteNoteContainersNotesNoteIdDelete', 'noteId', noteId)
            const localVarPath = `/containers/notes/{note_id}/`
                .replace(`{${"note_id"}}`, encodeURIComponent(String(noteId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Edit a note. Only the original author may edit their note.
         * @summary Edit Note
         * @param {number} noteId 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editNoteContainersNotesNoteIdPut: async (noteId: number, pOSTNote: POSTNote, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'noteId' is not null or undefined
            assertParamExists('editNoteContainersNotesNoteIdPut', 'noteId', noteId)
            // verify required parameter 'pOSTNote' is not null or undefined
            assertParamExists('editNoteContainersNotesNoteIdPut', 'pOSTNote', pOSTNote)
            const localVarPath = `/containers/notes/{note_id}/`
                .replace(`{${"note_id"}}`, encodeURIComponent(String(noteId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTNote, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all notes for a container, newest first. Visible to all org users.
         * @summary List Notes
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listNotesContainersContainerNumberNotesGet: async (containerNumber: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('listNotesContainersContainerNumberNotesGet', 'containerNumber', containerNumber)
            const localVarPath = `/containers/{container_number}/notes/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContainerNotesApi - functional programming interface
 * @export
 */
export const ContainerNotesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContainerNotesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a note for a container. Notes are visible to all users in the org.
         * @summary Create Note
         * @param {string} containerNumber 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createNoteContainersContainerNumberNotesPost(containerNumber: string, pOSTNote: POSTNote, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETNote>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createNoteContainersContainerNumberNotesPost(containerNumber, pOSTNote, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerNotesApi.createNoteContainersContainerNumberNotesPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Delete a note. Only the original author may delete their note.
         * @summary Delete Note
         * @param {number} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteNoteContainersNotesNoteIdDelete(noteId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteNoteContainersNotesNoteIdDelete(noteId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerNotesApi.deleteNoteContainersNotesNoteIdDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Edit a note. Only the original author may edit their note.
         * @summary Edit Note
         * @param {number} noteId 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editNoteContainersNotesNoteIdPut(noteId: number, pOSTNote: POSTNote, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETNote>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editNoteContainersNotesNoteIdPut(noteId, pOSTNote, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerNotesApi.editNoteContainersNotesNoteIdPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List all notes for a container, newest first. Visible to all org users.
         * @summary List Notes
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listNotesContainersContainerNumberNotesGet(containerNumber: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETNote>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listNotesContainersContainerNumberNotesGet(containerNumber, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerNotesApi.listNotesContainersContainerNumberNotesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContainerNotesApi - factory interface
 * @export
 */
export const ContainerNotesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContainerNotesApiFp(configuration)
    return {
        /**
         * Create a note for a container. Notes are visible to all users in the org.
         * @summary Create Note
         * @param {string} containerNumber 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createNoteContainersContainerNumberNotesPost(containerNumber: string, pOSTNote: POSTNote, options?: any): AxiosPromise<GETNote> {
            return localVarFp.createNoteContainersContainerNumberNotesPost(containerNumber, pOSTNote, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete a note. Only the original author may delete their note.
         * @summary Delete Note
         * @param {number} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNoteContainersNotesNoteIdDelete(noteId: number, options?: any): AxiosPromise<any> {
            return localVarFp.deleteNoteContainersNotesNoteIdDelete(noteId, options).then((request) => request(axios, basePath));
        },
        /**
         * Edit a note. Only the original author may edit their note.
         * @summary Edit Note
         * @param {number} noteId 
         * @param {POSTNote} pOSTNote 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editNoteContainersNotesNoteIdPut(noteId: number, pOSTNote: POSTNote, options?: any): AxiosPromise<GETNote> {
            return localVarFp.editNoteContainersNotesNoteIdPut(noteId, pOSTNote, options).then((request) => request(axios, basePath));
        },
        /**
         * List all notes for a container, newest first. Visible to all org users.
         * @summary List Notes
         * @param {string} containerNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listNotesContainersContainerNumberNotesGet(containerNumber: string, options?: any): AxiosPromise<Array<GETNote>> {
            return localVarFp.listNotesContainersContainerNumberNotesGet(containerNumber, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContainerNotesApi - object-oriented interface
 * @export
 * @class ContainerNotesApi
 * @extends {BaseAPI}
 */
export class ContainerNotesApi extends BaseAPI {
    /**
     * Create a note for a container. Notes are visible to all users in the org.
     * @summary Create Note
     * @param {string} containerNumber 
     * @param {POSTNote} pOSTNote 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerNotesApi
     */
    public createNoteContainersContainerNumberNotesPost(containerNumber: string, pOSTNote: POSTNote, options?: RawAxiosRequestConfig) {
        return ContainerNotesApiFp(this.configuration).createNoteContainersContainerNumberNotesPost(containerNumber, pOSTNote, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete a note. Only the original author may delete their note.
     * @summary Delete Note
     * @param {number} noteId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerNotesApi
     */
    public deleteNoteContainersNotesNoteIdDelete(noteId: number, options?: RawAxiosRequestConfig) {
        return ContainerNotesApiFp(this.configuration).deleteNoteContainersNotesNoteIdDelete(noteId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Edit a note. Only the original author may edit their note.
     * @summary Edit Note
     * @param {number} noteId 
     * @param {POSTNote} pOSTNote 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerNotesApi
     */
    public editNoteContainersNotesNoteIdPut(noteId: number, pOSTNote: POSTNote, options?: RawAxiosRequestConfig) {
        return ContainerNotesApiFp(this.configuration).editNoteContainersNotesNoteIdPut(noteId, pOSTNote, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all notes for a container, newest first. Visible to all org users.
     * @summary List Notes
     * @param {string} containerNumber 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerNotesApi
     */
    public listNotesContainersContainerNumberNotesGet(containerNumber: string, options?: RawAxiosRequestConfig) {
        return ContainerNotesApiFp(this.configuration).listNotesContainersContainerNumberNotesGet(containerNumber, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContainerTagsApi - axios parameter creator
 * @export
 */
export const ContainerTagsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Create Tag
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTagContainersTagsPost: async (pOSTTag: POSTTag, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTTag' is not null or undefined
            assertParamExists('createTagContainersTagsPost', 'pOSTTag', pOSTTag)
            const localVarPath = `/containers/tags/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTag, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.  Deprecated: use DELETE /containers/tags/?tag_name= instead.
         * @summary Delete Tag By Path
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTagByPathContainersTagsTagNameDelete: async (tagName: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tagName' is not null or undefined
            assertParamExists('deleteTagByPathContainersTagsTagNameDelete', 'tagName', tagName)
            const localVarPath = `/containers/tags/{tag_name}/`
                .replace(`{${"tag_name"}}`, encodeURIComponent(String(tagName)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.
         * @summary Delete Tag
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTagContainersTagsDelete: async (tagName: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tagName' is not null or undefined
            assertParamExists('deleteTagContainersTagsDelete', 'tagName', tagName)
            const localVarPath = `/containers/tags/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (tagName !== undefined) {
                localVarQueryParameter['tag_name'] = tagName;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns available tags for labeling/categorizing containers  There are default tags and user created tags.  To create a new tag you can either use [POST /containers/tags/](#operation/create_tag_containers_tags__post) API or you can call the [POST /containers/events/tag/](#operation/tag_container_containers_events_tag__post) API  with a tag that has not yet been created and it will be created automatically
         * @summary Get Tags
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTagsContainersTagsGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/tags/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Rename Tag
         * @param {string} tagName 
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        renameTagContainersTagsTagNamePut: async (tagName: string, pOSTTag: POSTTag, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tagName' is not null or undefined
            assertParamExists('renameTagContainersTagsTagNamePut', 'tagName', tagName)
            // verify required parameter 'pOSTTag' is not null or undefined
            assertParamExists('renameTagContainersTagsTagNamePut', 'pOSTTag', pOSTTag)
            const localVarPath = `/containers/tags/{tag_name}/`
                .replace(`{${"tag_name"}}`, encodeURIComponent(String(tagName)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTag, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContainerTagsApi - functional programming interface
 * @export
 */
export const ContainerTagsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContainerTagsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Create Tag
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createTagContainersTagsPost(pOSTTag: POSTTag, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createTagContainersTagsPost(pOSTTag, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerTagsApi.createTagContainersTagsPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.  Deprecated: use DELETE /containers/tags/?tag_name= instead.
         * @summary Delete Tag By Path
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTagByPathContainersTagsTagNameDelete(tagName: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTagByPathContainersTagsTagNameDelete(tagName, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerTagsApi.deleteTagByPathContainersTagsTagNameDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.
         * @summary Delete Tag
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTagContainersTagsDelete(tagName: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTagContainersTagsDelete(tagName, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerTagsApi.deleteTagContainersTagsDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns available tags for labeling/categorizing containers  There are default tags and user created tags.  To create a new tag you can either use [POST /containers/tags/](#operation/create_tag_containers_tags__post) API or you can call the [POST /containers/events/tag/](#operation/tag_container_containers_events_tag__post) API  with a tag that has not yet been created and it will be created automatically
         * @summary Get Tags
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTagsContainersTagsGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTag>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTagsContainersTagsGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerTagsApi.getTagsContainersTagsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Rename Tag
         * @param {string} tagName 
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async renameTagContainersTagsTagNamePut(tagName: string, pOSTTag: POSTTag, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.renameTagContainersTagsTagNamePut(tagName, pOSTTag, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainerTagsApi.renameTagContainersTagsTagNamePut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContainerTagsApi - factory interface
 * @export
 */
export const ContainerTagsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContainerTagsApiFp(configuration)
    return {
        /**
         * 
         * @summary Create Tag
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTagContainersTagsPost(pOSTTag: POSTTag, options?: any): AxiosPromise<any> {
            return localVarFp.createTagContainersTagsPost(pOSTTag, options).then((request) => request(axios, basePath));
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.  Deprecated: use DELETE /containers/tags/?tag_name= instead.
         * @summary Delete Tag By Path
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTagByPathContainersTagsTagNameDelete(tagName: string, options?: any): AxiosPromise<any> {
            return localVarFp.deleteTagByPathContainersTagsTagNameDelete(tagName, options).then((request) => request(axios, basePath));
        },
        /**
         * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.
         * @summary Delete Tag
         * @param {string} tagName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTagContainersTagsDelete(tagName: string, options?: any): AxiosPromise<any> {
            return localVarFp.deleteTagContainersTagsDelete(tagName, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns available tags for labeling/categorizing containers  There are default tags and user created tags.  To create a new tag you can either use [POST /containers/tags/](#operation/create_tag_containers_tags__post) API or you can call the [POST /containers/events/tag/](#operation/tag_container_containers_events_tag__post) API  with a tag that has not yet been created and it will be created automatically
         * @summary Get Tags
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTagsContainersTagsGet(options?: any): AxiosPromise<Array<GETTag>> {
            return localVarFp.getTagsContainersTagsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Rename Tag
         * @param {string} tagName 
         * @param {POSTTag} pOSTTag 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        renameTagContainersTagsTagNamePut(tagName: string, pOSTTag: POSTTag, options?: any): AxiosPromise<any> {
            return localVarFp.renameTagContainersTagsTagNamePut(tagName, pOSTTag, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContainerTagsApi - object-oriented interface
 * @export
 * @class ContainerTagsApi
 * @extends {BaseAPI}
 */
export class ContainerTagsApi extends BaseAPI {
    /**
     * 
     * @summary Create Tag
     * @param {POSTTag} pOSTTag 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerTagsApi
     */
    public createTagContainersTagsPost(pOSTTag: POSTTag, options?: RawAxiosRequestConfig) {
        return ContainerTagsApiFp(this.configuration).createTagContainersTagsPost(pOSTTag, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.  Deprecated: use DELETE /containers/tags/?tag_name= instead.
     * @summary Delete Tag By Path
     * @param {string} tagName 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerTagsApi
     */
    public deleteTagByPathContainersTagsTagNameDelete(tagName: string, options?: RawAxiosRequestConfig) {
        return ContainerTagsApiFp(this.configuration).deleteTagByPathContainersTagsTagNameDelete(tagName, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **WARNING:** This will remove any capacity sets that use this tag and remove the tag from all containers and export appointments.
     * @summary Delete Tag
     * @param {string} tagName 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerTagsApi
     */
    public deleteTagContainersTagsDelete(tagName: string, options?: RawAxiosRequestConfig) {
        return ContainerTagsApiFp(this.configuration).deleteTagContainersTagsDelete(tagName, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns available tags for labeling/categorizing containers  There are default tags and user created tags.  To create a new tag you can either use [POST /containers/tags/](#operation/create_tag_containers_tags__post) API or you can call the [POST /containers/events/tag/](#operation/tag_container_containers_events_tag__post) API  with a tag that has not yet been created and it will be created automatically
     * @summary Get Tags
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerTagsApi
     */
    public getTagsContainersTagsGet(options?: RawAxiosRequestConfig) {
        return ContainerTagsApiFp(this.configuration).getTagsContainersTagsGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Rename Tag
     * @param {string} tagName 
     * @param {POSTTag} pOSTTag 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainerTagsApi
     */
    public renameTagContainersTagsTagNamePut(tagName: string, pOSTTag: POSTTag, options?: RawAxiosRequestConfig) {
        return ContainerTagsApiFp(this.configuration).renameTagContainersTagsTagNamePut(tagName, pOSTTag, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContainersApi - axios parameter creator
 * @export
 */
export const ContainersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Apply a previewed reconciliation. Per-container failures land in `errors`; unhandled exceptions roll back the whole transaction.
         * @summary Apply Empties Reconciliation Endpoint
         * @param {ApplyReconciliationRequest} applyReconciliationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost: async (applyReconciliationRequest: ApplyReconciliationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'applyReconciliationRequest' is not null or undefined
            assertParamExists('applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost', 'applyReconciliationRequest', applyReconciliationRequest)
            const localVarPath = `/containers/empties/reconcile/apply/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(applyReconciliationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Assign containers to a user
         * @summary Assign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignContainersContainersAssignPost: async (pOSTAssignmentData: POSTAssignmentData, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAssignmentData' is not null or undefined
            assertParamExists('assignContainersContainersAssignPost', 'pOSTAssignmentData', pOSTAssignmentData)
            const localVarPath = `/containers/assign/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAssignmentData, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Bulk check import statuses for containers via Port Optimizer and/or ASB. Does not create any container watches.  Returns 504 if ASB data does not arrive within the timeout window.
         * @summary Bulk Check Import Statuses
         * @param {POSTBulkCheckImportStatuses} pOSTBulkCheckImportStatuses 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkCheckImportStatusesContainersImportStatusesBulkCheckPost: async (pOSTBulkCheckImportStatuses: POSTBulkCheckImportStatuses, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTBulkCheckImportStatuses' is not null or undefined
            assertParamExists('bulkCheckImportStatusesContainersImportStatusesBulkCheckPost', 'pOSTBulkCheckImportStatuses', pOSTBulkCheckImportStatuses)
            const localVarPath = `/containers/import_statuses/bulk_check/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTBulkCheckImportStatuses, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete Current Location
         * @param {DELETECurrentLocation} dELETECurrentLocation 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCurrentLocationContainersCurrentLocationDelete: async (dELETECurrentLocation: DELETECurrentLocation, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'dELETECurrentLocation' is not null or undefined
            assertParamExists('deleteCurrentLocationContainersCurrentLocationDelete', 'dELETECurrentLocation', dELETECurrentLocation)
            const localVarPath = `/containers/current_location/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(dELETECurrentLocation, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary File Export
         * @param {GETContainerFileExport} gETContainerFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileExportContainersFileExportPost: async (gETContainerFileExport: GETContainerFileExport, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'gETContainerFileExport' is not null or undefined
            assertParamExists('fileExportContainersFileExportPost', 'gETContainerFileExport', gETContainerFileExport)
            const localVarPath = `/containers/file_export/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gETContainerFileExport, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns container counts grouped by vessel ETA date.
         * @summary Get Container Counts By Eta
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByEtaContainersVesselEtaCountsGet: async (terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/vessel_eta_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Container Counts By State
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByStateContainersStateCountsGet: async (terminal?: Array<TerminalName>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/state_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Container Counts By Terminal
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByTerminalContainersTerminalCountsGet: async (containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/terminal_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Container History
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getContainerHistoryContainersContainerNumberHistoryGet: async (containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('getContainerHistoryContainersContainerNumberHistoryGet', 'containerNumber', containerNumber)
            const localVarPath = `/containers/{container_number}/history/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (includeRawDataChanges !== undefined) {
                localVarQueryParameter['include_raw_data_changes'] = includeRawDataChanges;
            }

            if (includeAppointmentChanges !== undefined) {
                localVarQueryParameter['include_appointment_changes'] = includeAppointmentChanges;
            }

            if (includeImportStatusChanges !== undefined) {
                localVarQueryParameter['include_import_status_changes'] = includeImportStatusChanges;
            }

            if (includeGateTransactions !== undefined) {
                localVarQueryParameter['include_gate_transactions'] = includeGateTransactions;
            }

            if (includeBookingRequests !== undefined) {
                localVarQueryParameter['include_booking_requests'] = includeBookingRequests;
            }

            if (includeEmptyBookingRequests !== undefined) {
                localVarQueryParameter['include_empty_booking_requests'] = includeEmptyBookingRequests;
            }

            if (includeCancellationRequests !== undefined) {
                localVarQueryParameter['include_cancellation_requests'] = includeCancellationRequests;
            }

            if (includeRescheduleRequests !== undefined) {
                localVarQueryParameter['include_reschedule_requests'] = includeRescheduleRequests;
            }

            if (includeEditAppointmentLicensePlateRequests !== undefined) {
                localVarQueryParameter['include_edit_appointment_license_plate_requests'] = includeEditAppointmentLicensePlateRequests;
            }

            if (includeAddEmptyRequests !== undefined) {
                localVarQueryParameter['include_add_empty_requests'] = includeAddEmptyRequests;
            }

            if (includeEditChassisRequests !== undefined) {
                localVarQueryParameter['include_edit_chassis_requests'] = includeEditChassisRequests;
            }

            if (includeCheckInRequests !== undefined) {
                localVarQueryParameter['include_check_in_requests'] = includeCheckInRequests;
            }

            if (includeAutoBookChanges !== undefined) {
                localVarQueryParameter['include_auto_book_changes'] = includeAutoBookChanges;
            }

            if (includeAutoBookEmptyChanges !== undefined) {
                localVarQueryParameter['include_auto_book_empty_changes'] = includeAutoBookEmptyChanges;
            }

            if (includeWatchChanges !== undefined) {
                localVarQueryParameter['include_watch_changes'] = includeWatchChanges;
            }

            if (includeContainerWatchEvents !== undefined) {
                localVarQueryParameter['include_container_watch_events'] = includeContainerWatchEvents;
            }

            if (includeBrowserExtensionActions !== undefined) {
                localVarQueryParameter['include_browser_extension_actions'] = includeBrowserExtensionActions;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * This API groups container changes into lifecycle phases
         * @summary Get Container History Grouped
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet: async (containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'containerNumber' is not null or undefined
            assertParamExists('getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet', 'containerNumber', containerNumber)
            const localVarPath = `/containers/{container_number}/history/grouped/`
                .replace(`{${"container_number"}}`, encodeURIComponent(String(containerNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (includeRawDataChanges !== undefined) {
                localVarQueryParameter['include_raw_data_changes'] = includeRawDataChanges;
            }

            if (includeAppointmentChanges !== undefined) {
                localVarQueryParameter['include_appointment_changes'] = includeAppointmentChanges;
            }

            if (includeImportStatusChanges !== undefined) {
                localVarQueryParameter['include_import_status_changes'] = includeImportStatusChanges;
            }

            if (includeGateTransactions !== undefined) {
                localVarQueryParameter['include_gate_transactions'] = includeGateTransactions;
            }

            if (includeBookingRequests !== undefined) {
                localVarQueryParameter['include_booking_requests'] = includeBookingRequests;
            }

            if (includeEmptyBookingRequests !== undefined) {
                localVarQueryParameter['include_empty_booking_requests'] = includeEmptyBookingRequests;
            }

            if (includeCancellationRequests !== undefined) {
                localVarQueryParameter['include_cancellation_requests'] = includeCancellationRequests;
            }

            if (includeRescheduleRequests !== undefined) {
                localVarQueryParameter['include_reschedule_requests'] = includeRescheduleRequests;
            }

            if (includeAddEmptyRequests !== undefined) {
                localVarQueryParameter['include_add_empty_requests'] = includeAddEmptyRequests;
            }

            if (includeEditAppointmentLicensePlateRequests !== undefined) {
                localVarQueryParameter['include_edit_appointment_license_plate_requests'] = includeEditAppointmentLicensePlateRequests;
            }

            if (includeEditChassisRequests !== undefined) {
                localVarQueryParameter['include_edit_chassis_requests'] = includeEditChassisRequests;
            }

            if (includeCheckInRequests !== undefined) {
                localVarQueryParameter['include_check_in_requests'] = includeCheckInRequests;
            }

            if (includeAutoBookChanges !== undefined) {
                localVarQueryParameter['include_auto_book_changes'] = includeAutoBookChanges;
            }

            if (includeAutoBookEmptyChanges !== undefined) {
                localVarQueryParameter['include_auto_book_empty_changes'] = includeAutoBookEmptyChanges;
            }

            if (includeWatchChanges !== undefined) {
                localVarQueryParameter['include_watch_changes'] = includeWatchChanges;
            }

            if (includeContainerWatchEvents !== undefined) {
                localVarQueryParameter['include_container_watch_events'] = includeContainerWatchEvents;
            }

            if (includeBrowserExtensionActions !== undefined) {
                localVarQueryParameter['include_browser_extension_actions'] = includeBrowserExtensionActions;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Container Types
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerTypesContainersTypesGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/types/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A higher performance/lighter weight containers API that only returns basic container information
         * @summary Get Containers Basic
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicContainersBasicGet: async (importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/basic/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (importOutGatedAfter !== undefined) {
                localVarQueryParameter['import_out_gated_after'] = (importOutGatedAfter as any instanceof Date) ?
                    (importOutGatedAfter as any).toISOString() :
                    importOutGatedAfter;
            }

            if (importOutGatedBefore !== undefined) {
                localVarQueryParameter['import_out_gated_before'] = (importOutGatedBefore as any instanceof Date) ?
                    (importOutGatedBefore as any).toISOString() :
                    importOutGatedBefore;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Purpose-built for the Empties list: everything `/basic/with_unwatched/` returns, plus each container\'s empty auto-book status, so that view can render its bot icon in one call instead of a second request to the auto-book-empty endpoints.  Kept as its own endpoint rather than an `include_auto_book` flag on `/basic/with_unwatched/`, since that endpoint has exactly one caller (the Empties list) — a flag only pays for itself when multiple callers need different shapes of the same response; with a single caller that always wants the field, it\'s really a distinct resource.
         * @summary Get Containers Basic With Unwatched And Auto Book
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet: async (importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/basic/with_unwatched/with_auto_book/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (importOutGatedAfter !== undefined) {
                localVarQueryParameter['import_out_gated_after'] = (importOutGatedAfter as any instanceof Date) ?
                    (importOutGatedAfter as any).toISOString() :
                    importOutGatedAfter;
            }

            if (importOutGatedBefore !== undefined) {
                localVarQueryParameter['import_out_gated_before'] = (importOutGatedBefore as any instanceof Date) ?
                    (importOutGatedBefore as any).toISOString() :
                    importOutGatedBefore;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Like `/basic/` (lightweight container list) but also reports which of the searched `container_numbers` aren\'t tracked by this org, so callers (the Empties list) can surface an \"untracked\" state with a Start-tracking CTA.  Kept as a separate endpoint rather than extending `/basic/` so that endpoint\'s bare-array contract stays stable for its other callers (a prior attempt to change `/basic/` itself broke booking lookups and was reverted).
         * @summary Get Containers Basic With Unwatched
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet: async (importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/basic/with_unwatched/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (importOutGatedAfter !== undefined) {
                localVarQueryParameter['import_out_gated_after'] = (importOutGatedAfter as any instanceof Date) ?
                    (importOutGatedAfter as any).toISOString() :
                    importOutGatedAfter;
            }

            if (importOutGatedBefore !== undefined) {
                localVarQueryParameter['import_out_gated_before'] = (importOutGatedBefore as any instanceof Date) ?
                    (importOutGatedBefore as any).toISOString() :
                    importOutGatedBefore;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * This is the primary API for getting information about containers. It consolidates most information we might have about a container into a single endpoint, including import status (availability), appointments and gate transactions.  Even if you only want one of these things, you may want to use this API as it has the most filtering options and is well optimized
         * @summary Get Containers
         * @param {SortOptions} [sortBy] &#x60;\&#39;appointment_start\&#39;&#x60; and &#x60;\&#39;gate_time\&#39;&#x60; are deprecated options here for backwards compatibility
         * @param {GetContainersContainersGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many rows before returning results. An alternate form of pagination
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersContainersGet: async (sortBy?: SortOptions, sortOrder?: GetContainersContainersGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (sortBy !== undefined) {
                localVarQueryParameter['sort_by'] = sortBy;
            }

            if (sortOrder !== undefined) {
                localVarQueryParameter['sort_order'] = sortOrder;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vessels) {
                localVarQueryParameter['vessels'] = vessels;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersLineAndTypeContainersLineAndTypeGet: async (containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/line_and_type/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Shipping Lines
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getShippingLinesContainersShippingLinesGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/shipping_lines/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns available vessels of containers
         * @summary Get Vessel Counts By Terminal
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVesselCountsByTerminalContainersVesselCountsGet: async (terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/vessel_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (containerCycleStates) {
                localVarQueryParameter['container_cycle_states'] = containerCycleStates;
            }

            if (cycleState) {
                localVarQueryParameter['cycle_state'] = cycleState;
            }

            if (containerCycleState) {
                localVarQueryParameter['container_cycle_state'] = containerCycleState;
            }

            if (vesselEta !== undefined) {
                localVarQueryParameter['vessel_eta'] = (vesselEta as any instanceof Date) ?
                    (vesselEta as any).toISOString().substring(0,10) :
                    vesselEta;
            }

            if (vesselEtaIsUnknown !== undefined) {
                localVarQueryParameter['vessel_eta_is_unknown'] = vesselEtaIsUnknown;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (autoBookOn !== undefined) {
                localVarQueryParameter['auto_book_on'] = autoBookOn;
            }

            if (autoBookOff !== undefined) {
                localVarQueryParameter['auto_book_off'] = autoBookOff;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (holdType) {
                localVarQueryParameter['hold_type'] = holdType;
            }

            if (updatedInLastMinutes !== undefined) {
                localVarQueryParameter['updated_in_last_minutes'] = updatedInLastMinutes;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (isGreenTruck !== undefined) {
                localVarQueryParameter['is_green_truck'] = isGreenTruck;
            }

            if (needsLineLfdCheck !== undefined) {
                localVarQueryParameter['needs_line_lfd_check'] = needsLineLfdCheck;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }

            if (assignedUserId) {
                localVarQueryParameter['assigned_user_id'] = assignedUserId;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Refresh container import status at associated portals/terminals at user request
         * @summary Manual Refresh Import Statuses
         * @param {Array<POSTImportStatusRefresh>} pOSTImportStatusRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        manualRefreshImportStatusesContainersImportStatusesRefreshPost: async (pOSTImportStatusRefresh: Array<POSTImportStatusRefresh>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTImportStatusRefresh' is not null or undefined
            assertParamExists('manualRefreshImportStatusesContainersImportStatusesRefreshPost', 'pOSTImportStatusRefresh', pOSTImportStatusRefresh)
            const localVarPath = `/containers/import_statuses/refresh/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTImportStatusRefresh, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Patch Containers
         * @param {PATCHContainers} pATCHContainers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchContainersContainersPatch: async (pATCHContainers: PATCHContainers, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pATCHContainers' is not null or undefined
            assertParamExists('patchContainersContainersPatch', 'pATCHContainers', pATCHContainers)
            const localVarPath = `/containers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHContainers, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Preview empties reconciliation.  Accepts either a CSV/XLSX file upload or pasted container numbers (free text). Returns a categorized diff against the org\'s current empties — nothing is applied until the client explicitly confirms via a separate apply endpoint.
         * @summary Preview Empties Reconciliation
         * @param {ReconciliationMode} mode 
         * @param {ContainerStatus} defaultStatus 
         * @param {File} [file] 
         * @param {string} [containerNumbers] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost: async (mode: ReconciliationMode, defaultStatus: ContainerStatus, file?: File, containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'mode' is not null or undefined
            assertParamExists('previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost', 'mode', mode)
            // verify required parameter 'defaultStatus' is not null or undefined
            assertParamExists('previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost', 'defaultStatus', defaultStatus)
            const localVarPath = `/containers/empties/reconcile/preview/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


            if (mode !== undefined) { 
                localVarFormParams.append('mode', mode as any);
            }
    
            if (defaultStatus !== undefined) { 
                localVarFormParams.append('default_status', defaultStatus as any);
            }
    
            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
    
            if (containerNumbers !== undefined) { 
                localVarFormParams.append('container_numbers', containerNumbers as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stops tracking one or more containers. They will no longer show up in any views after this call.
         * @summary Stop Watching Containers
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        stopWatchingContainersContainersUnwatchPost: async (requestBody: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'requestBody' is not null or undefined
            assertParamExists('stopWatchingContainersContainersUnwatchPost', 'requestBody', requestBody)
            const localVarPath = `/containers/unwatch/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(requestBody, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Unassign users from containers
         * @summary Unassign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignContainersContainersUnassignPost: async (pOSTAssignmentData: POSTAssignmentData, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAssignmentData' is not null or undefined
            assertParamExists('unassignContainersContainersUnassignPost', 'pOSTAssignmentData', pOSTAssignmentData)
            const localVarPath = `/containers/unassign/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAssignmentData, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deprecated: Use PATCH / instead.
         * @summary Update Customer
         * @param {PATCHUpdateCustomer} pATCHUpdateCustomer 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        updateCustomerContainersUpdateCustomerPatch: async (pATCHUpdateCustomer: PATCHUpdateCustomer, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pATCHUpdateCustomer' is not null or undefined
            assertParamExists('updateCustomerContainersUpdateCustomerPatch', 'pATCHUpdateCustomer', pATCHUpdateCustomer)
            const localVarPath = `/containers/update_customer/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHUpdateCustomer, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add containers to watch. Containers must be watched and tracking at a terminal before data for a container can be loaded or an appointment booked.  This API can be called many times with the same containers and no duplicates will be created. If different assigned users, customers or any other fields are specified and the container has already been uploaded, those fields will be updated.
         * @summary Watch Containers
         * @param {Array<POSTContainerWatch>} pOSTContainerWatch 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchContainersContainersWatchPost: async (pOSTContainerWatch: Array<POSTContainerWatch>, assignToUserId?: Array<number>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTContainerWatch' is not null or undefined
            assertParamExists('watchContainersContainersWatchPost', 'pOSTContainerWatch', pOSTContainerWatch)
            const localVarPath = `/containers/watch/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (assignToUserId) {
                localVarQueryParameter['assign_to_user_id'] = assignToUserId;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTContainerWatch, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Supports upload via .csv or .xlsx file
         * @summary Watch Containers Via Upload
         * @param {File} file 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {number} [customerId] Customer ID for this upload
         * @param {PortComplex} [portComplex] Port complex to be assigned to all containers in the upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchContainersViaUploadContainersWatchViaUploadPost: async (file: File, assignToUserId?: Array<number>, customerId?: number, portComplex?: PortComplex, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('watchContainersViaUploadContainersWatchViaUploadPost', 'file', file)
            const localVarPath = `/containers/watch/via_upload/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (assignToUserId) {
                localVarQueryParameter['assign_to_user_id'] = assignToUserId;
            }

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }

            if (portComplex !== undefined) {
                localVarQueryParameter['port_complex'] = portComplex;
            }


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContainersApi - functional programming interface
 * @export
 */
export const ContainersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContainersApiAxiosParamCreator(configuration)
    return {
        /**
         * Apply a previewed reconciliation. Per-container failures land in `errors`; unhandled exceptions roll back the whole transaction.
         * @summary Apply Empties Reconciliation Endpoint
         * @param {ApplyReconciliationRequest} applyReconciliationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest: ApplyReconciliationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApplyReconciliationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Assign containers to a user
         * @summary Assign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignContainersContainersAssignPost(pOSTAssignmentData: POSTAssignmentData, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignContainersContainersAssignPost(pOSTAssignmentData, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.assignContainersContainersAssignPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Bulk check import statuses for containers via Port Optimizer and/or ASB. Does not create any container watches.  Returns 504 if ASB data does not arrive within the timeout window.
         * @summary Bulk Check Import Statuses
         * @param {POSTBulkCheckImportStatuses} pOSTBulkCheckImportStatuses 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses: POSTBulkCheckImportStatuses, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETImportStatusBase>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.bulkCheckImportStatusesContainersImportStatusesBulkCheckPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete Current Location
         * @param {DELETECurrentLocation} dELETECurrentLocation 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation: DELETECurrentLocation, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<string>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.deleteCurrentLocationContainersCurrentLocationDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary File Export
         * @param {GETContainerFileExport} gETContainerFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async fileExportContainersFileExportPost(gETContainerFileExport: GETContainerFileExport, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.fileExportContainersFileExportPost(gETContainerFileExport, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.fileExportContainersFileExportPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns container counts grouped by vessel ETA date.
         * @summary Get Container Counts By Eta
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainerCountsByEtaContainersVesselEtaCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETEtaCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerCountsByEtaContainersVesselEtaCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vessels, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerCountsByEtaContainersVesselEtaCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Container Counts By State
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainerCountsByStateContainersStateCountsGet(terminal?: Array<TerminalName>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETStateCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerCountsByStateContainersStateCountsGet(terminal, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerCountsByStateContainersStateCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Container Counts By Terminal
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTerminalCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerCountsByTerminalContainersTerminalCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Container History
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async getContainerHistoryContainersContainerNumberHistoryGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HistoryEvent>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerHistoryContainersContainerNumberHistoryGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeEditAppointmentLicensePlateRequests, includeAddEmptyRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerHistoryContainersContainerNumberHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * This API groups container changes into lifecycle phases
         * @summary Get Container History Grouped
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HistoryPhaseGroup>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeAddEmptyRequests, includeEditAppointmentLicensePlateRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Container Types
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainerTypesContainersTypesGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETContainerType>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainerTypesContainersTypesGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainerTypesContainersTypesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A higher performance/lighter weight containers API that only returns basic container information
         * @summary Get Containers Basic
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersBasicContainersBasicGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETContainerBasic>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersBasicContainersBasicGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainersBasicContainersBasicGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Purpose-built for the Empties list: everything `/basic/with_unwatched/` returns, plus each container\'s empty auto-book status, so that view can render its bot icon in one call instead of a second request to the auto-book-empty endpoints.  Kept as its own endpoint rather than an `include_auto_book` flag on `/basic/with_unwatched/`, since that endpoint has exactly one caller (the Empties list) — a flag only pays for itself when multiple callers need different shapes of the same response; with a single caller that always wants the field, it\'s really a distinct resource.
         * @summary Get Containers Basic With Unwatched And Auto Book
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETContainersBasicWithAutoBookResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Like `/basic/` (lightweight container list) but also reports which of the searched `container_numbers` aren\'t tracked by this org, so callers (the Empties list) can surface an \"untracked\" state with a Start-tracking CTA.  Kept as a separate endpoint rather than extending `/basic/` so that endpoint\'s bare-array contract stays stable for its other callers (a prior attempt to change `/basic/` itself broke booking lookups and was reverted).
         * @summary Get Containers Basic With Unwatched
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETContainersBasicResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * This is the primary API for getting information about containers. It consolidates most information we might have about a container into a single endpoint, including import status (availability), appointments and gate transactions.  Even if you only want one of these things, you may want to use this API as it has the most filtering options and is well optimized
         * @summary Get Containers
         * @param {SortOptions} [sortBy] &#x60;\&#39;appointment_start\&#39;&#x60; and &#x60;\&#39;gate_time\&#39;&#x60; are deprecated options here for backwards compatibility
         * @param {GetContainersContainersGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many rows before returning results. An alternate form of pagination
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersContainersGet(sortBy?: SortOptions, sortOrder?: GetContainersContainersGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETContainersResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersContainersGet(sortBy, sortOrder, page, pageSize, offset, terminal, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainersContainersGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETContainerLineAndType>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getContainersLineAndTypeContainersLineAndTypeGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Shipping Lines
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getShippingLinesContainersShippingLinesGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETShippingLine>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getShippingLinesContainersShippingLinesGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getShippingLinesContainersShippingLinesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns available vessels of containers
         * @summary Get Vessel Counts By Terminal
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVesselCountsByTerminalContainersVesselCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETVesselCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVesselCountsByTerminalContainersVesselCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.getVesselCountsByTerminalContainersVesselCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Refresh container import status at associated portals/terminals at user request
         * @summary Manual Refresh Import Statuses
         * @param {Array<POSTImportStatusRefresh>} pOSTImportStatusRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh: Array<POSTImportStatusRefresh>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.manualRefreshImportStatusesContainersImportStatusesRefreshPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Patch Containers
         * @param {PATCHContainers} pATCHContainers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async patchContainersContainersPatch(pATCHContainers: PATCHContainers, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<string>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.patchContainersContainersPatch(pATCHContainers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.patchContainersContainersPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Preview empties reconciliation.  Accepts either a CSV/XLSX file upload or pasted container numbers (free text). Returns a categorized diff against the org\'s current empties — nothing is applied until the client explicitly confirms via a separate apply endpoint.
         * @summary Preview Empties Reconciliation
         * @param {ReconciliationMode} mode 
         * @param {ContainerStatus} defaultStatus 
         * @param {File} [file] 
         * @param {string} [containerNumbers] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode: ReconciliationMode, defaultStatus: ContainerStatus, file?: File, containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EmptiesReconciliationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode, defaultStatus, file, containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stops tracking one or more containers. They will no longer show up in any views after this call.
         * @summary Stop Watching Containers
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async stopWatchingContainersContainersUnwatchPost(requestBody: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.stopWatchingContainersContainersUnwatchPost(requestBody, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.stopWatchingContainersContainersUnwatchPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Unassign users from containers
         * @summary Unassign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unassignContainersContainersUnassignPost(pOSTAssignmentData: POSTAssignmentData, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unassignContainersContainersUnassignPost(pOSTAssignmentData, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.unassignContainersContainersUnassignPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deprecated: Use PATCH / instead.
         * @summary Update Customer
         * @param {PATCHUpdateCustomer} pATCHUpdateCustomer 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer: PATCHUpdateCustomer, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<string>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.updateCustomerContainersUpdateCustomerPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Add containers to watch. Containers must be watched and tracking at a terminal before data for a container can be loaded or an appointment booked.  This API can be called many times with the same containers and no duplicates will be created. If different assigned users, customers or any other fields are specified and the container has already been uploaded, those fields will be updated.
         * @summary Watch Containers
         * @param {Array<POSTContainerWatch>} pOSTContainerWatch 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async watchContainersContainersWatchPost(pOSTContainerWatch: Array<POSTContainerWatch>, assignToUserId?: Array<number>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTUploadContainersResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.watchContainersContainersWatchPost(pOSTContainerWatch, assignToUserId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.watchContainersContainersWatchPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Supports upload via .csv or .xlsx file
         * @summary Watch Containers Via Upload
         * @param {File} file 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {number} [customerId] Customer ID for this upload
         * @param {PortComplex} [portComplex] Port complex to be assigned to all containers in the upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async watchContainersViaUploadContainersWatchViaUploadPost(file: File, assignToUserId?: Array<number>, customerId?: number, portComplex?: PortComplex, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTUploadContainersResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.watchContainersViaUploadContainersWatchViaUploadPost(file, assignToUserId, customerId, portComplex, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContainersApi.watchContainersViaUploadContainersWatchViaUploadPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContainersApi - factory interface
 * @export
 */
export const ContainersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContainersApiFp(configuration)
    return {
        /**
         * Apply a previewed reconciliation. Per-container failures land in `errors`; unhandled exceptions roll back the whole transaction.
         * @summary Apply Empties Reconciliation Endpoint
         * @param {ApplyReconciliationRequest} applyReconciliationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest: ApplyReconciliationRequest, options?: any): AxiosPromise<ApplyReconciliationResponse> {
            return localVarFp.applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Assign containers to a user
         * @summary Assign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignContainersContainersAssignPost(pOSTAssignmentData: POSTAssignmentData, options?: any): AxiosPromise<any> {
            return localVarFp.assignContainersContainersAssignPost(pOSTAssignmentData, options).then((request) => request(axios, basePath));
        },
        /**
         * Bulk check import statuses for containers via Port Optimizer and/or ASB. Does not create any container watches.  Returns 504 if ASB data does not arrive within the timeout window.
         * @summary Bulk Check Import Statuses
         * @param {POSTBulkCheckImportStatuses} pOSTBulkCheckImportStatuses 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses: POSTBulkCheckImportStatuses, options?: any): AxiosPromise<Array<GETImportStatusBase>> {
            return localVarFp.bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete Current Location
         * @param {DELETECurrentLocation} dELETECurrentLocation 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation: DELETECurrentLocation, options?: any): AxiosPromise<Array<string>> {
            return localVarFp.deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary File Export
         * @param {GETContainerFileExport} gETContainerFileExport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileExportContainersFileExportPost(gETContainerFileExport: GETContainerFileExport, options?: any): AxiosPromise<any> {
            return localVarFp.fileExportContainersFileExportPost(gETContainerFileExport, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns container counts grouped by vessel ETA date.
         * @summary Get Container Counts By Eta
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByEtaContainersVesselEtaCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETEtaCount>> {
            return localVarFp.getContainerCountsByEtaContainersVesselEtaCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vessels, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Container Counts By State
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByStateContainersStateCountsGet(terminal?: Array<TerminalName>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETStateCount>> {
            return localVarFp.getContainerCountsByStateContainersStateCountsGet(terminal, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Container Counts By Terminal
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETTerminalCount>> {
            return localVarFp.getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Container History
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getContainerHistoryContainersContainerNumberHistoryGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: any): AxiosPromise<Array<HistoryEvent>> {
            return localVarFp.getContainerHistoryContainersContainerNumberHistoryGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeEditAppointmentLicensePlateRequests, includeAddEmptyRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options).then((request) => request(axios, basePath));
        },
        /**
         * This API groups container changes into lifecycle phases
         * @summary Get Container History Grouped
         * @param {string} containerNumber 
         * @param {boolean} [includeRawDataChanges] 
         * @param {boolean} [includeAppointmentChanges] 
         * @param {boolean} [includeImportStatusChanges] 
         * @param {boolean} [includeGateTransactions] 
         * @param {boolean} [includeBookingRequests] 
         * @param {boolean} [includeEmptyBookingRequests] 
         * @param {boolean} [includeCancellationRequests] 
         * @param {boolean} [includeRescheduleRequests] 
         * @param {boolean} [includeAddEmptyRequests] 
         * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
         * @param {boolean} [includeEditChassisRequests] 
         * @param {boolean} [includeCheckInRequests] 
         * @param {boolean} [includeAutoBookChanges] 
         * @param {boolean} [includeAutoBookEmptyChanges] 
         * @param {boolean} [includeWatchChanges] 
         * @param {boolean} [includeContainerWatchEvents] 
         * @param {boolean} [includeBrowserExtensionActions] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: any): AxiosPromise<Array<HistoryPhaseGroup>> {
            return localVarFp.getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeAddEmptyRequests, includeEditAppointmentLicensePlateRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Container Types
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainerTypesContainersTypesGet(options?: any): AxiosPromise<Array<GETContainerType>> {
            return localVarFp.getContainerTypesContainersTypesGet(options).then((request) => request(axios, basePath));
        },
        /**
         * A higher performance/lighter weight containers API that only returns basic container information
         * @summary Get Containers Basic
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicContainersBasicGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETContainerBasic>> {
            return localVarFp.getContainersBasicContainersBasicGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Purpose-built for the Empties list: everything `/basic/with_unwatched/` returns, plus each container\'s empty auto-book status, so that view can render its bot icon in one call instead of a second request to the auto-book-empty endpoints.  Kept as its own endpoint rather than an `include_auto_book` flag on `/basic/with_unwatched/`, since that endpoint has exactly one caller (the Empties list) — a flag only pays for itself when multiple callers need different shapes of the same response; with a single caller that always wants the field, it\'s really a distinct resource.
         * @summary Get Containers Basic With Unwatched And Auto Book
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<GETContainersBasicWithAutoBookResponse> {
            return localVarFp.getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Like `/basic/` (lightweight container list) but also reports which of the searched `container_numbers` aren\'t tracked by this org, so callers (the Empties list) can surface an \"untracked\" state with a Start-tracking CTA.  Kept as a separate endpoint rather than extending `/basic/` so that endpoint\'s bare-array contract stays stable for its other callers (a prior attempt to change `/basic/` itself broke booking lookups and was reverted).
         * @summary Get Containers Basic With Unwatched
         * @param {string} [importOutGatedAfter] 
         * @param {string} [importOutGatedBefore] 
         * @param {number} [limit] 
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<GETContainersBasicResponse> {
            return localVarFp.getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * This is the primary API for getting information about containers. It consolidates most information we might have about a container into a single endpoint, including import status (availability), appointments and gate transactions.  Even if you only want one of these things, you may want to use this API as it has the most filtering options and is well optimized
         * @summary Get Containers
         * @param {SortOptions} [sortBy] &#x60;\&#39;appointment_start\&#39;&#x60; and &#x60;\&#39;gate_time\&#39;&#x60; are deprecated options here for backwards compatibility
         * @param {GetContainersContainersGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many rows before returning results. An alternate form of pagination
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersContainersGet(sortBy?: SortOptions, sortOrder?: GetContainersContainersGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<GETContainersResponse> {
            return localVarFp.getContainersContainersGet(sortBy, sortOrder, page, pageSize, offset, terminal, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: any): AxiosPromise<Array<GETContainerLineAndType>> {
            return localVarFp.getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Shipping Lines
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getShippingLinesContainersShippingLinesGet(options?: any): AxiosPromise<Array<GETShippingLine>> {
            return localVarFp.getShippingLinesContainersShippingLinesGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns available vessels of containers
         * @summary Get Vessel Counts By Terminal
         * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
         * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
         * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
         * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
         * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
         * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
         * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
         * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
         * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<HoldType>} [holdType] 
         * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
         * @param {Array<string>} [tags] Limit to containers with all of these tags
         * @param {boolean} [isUntagged] Limit to containers with no tags.
         * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVesselCountsByTerminalContainersVesselCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETVesselCount>> {
            return localVarFp.getVesselCountsByTerminalContainersVesselCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Refresh container import status at associated portals/terminals at user request
         * @summary Manual Refresh Import Statuses
         * @param {Array<POSTImportStatusRefresh>} pOSTImportStatusRefresh 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh: Array<POSTImportStatusRefresh>, options?: any): AxiosPromise<any> {
            return localVarFp.manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Patch Containers
         * @param {PATCHContainers} pATCHContainers 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchContainersContainersPatch(pATCHContainers: PATCHContainers, options?: any): AxiosPromise<Array<string>> {
            return localVarFp.patchContainersContainersPatch(pATCHContainers, options).then((request) => request(axios, basePath));
        },
        /**
         * Preview empties reconciliation.  Accepts either a CSV/XLSX file upload or pasted container numbers (free text). Returns a categorized diff against the org\'s current empties — nothing is applied until the client explicitly confirms via a separate apply endpoint.
         * @summary Preview Empties Reconciliation
         * @param {ReconciliationMode} mode 
         * @param {ContainerStatus} defaultStatus 
         * @param {File} [file] 
         * @param {string} [containerNumbers] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode: ReconciliationMode, defaultStatus: ContainerStatus, file?: File, containerNumbers?: string, options?: any): AxiosPromise<EmptiesReconciliationResponse> {
            return localVarFp.previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode, defaultStatus, file, containerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Stops tracking one or more containers. They will no longer show up in any views after this call.
         * @summary Stop Watching Containers
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        stopWatchingContainersContainersUnwatchPost(requestBody: Array<string>, options?: any): AxiosPromise<any> {
            return localVarFp.stopWatchingContainersContainersUnwatchPost(requestBody, options).then((request) => request(axios, basePath));
        },
        /**
         * Unassign users from containers
         * @summary Unassign Containers
         * @param {POSTAssignmentData} pOSTAssignmentData 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignContainersContainersUnassignPost(pOSTAssignmentData: POSTAssignmentData, options?: any): AxiosPromise<any> {
            return localVarFp.unassignContainersContainersUnassignPost(pOSTAssignmentData, options).then((request) => request(axios, basePath));
        },
        /**
         * Deprecated: Use PATCH / instead.
         * @summary Update Customer
         * @param {PATCHUpdateCustomer} pATCHUpdateCustomer 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer: PATCHUpdateCustomer, options?: any): AxiosPromise<Array<string>> {
            return localVarFp.updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer, options).then((request) => request(axios, basePath));
        },
        /**
         * Add containers to watch. Containers must be watched and tracking at a terminal before data for a container can be loaded or an appointment booked.  This API can be called many times with the same containers and no duplicates will be created. If different assigned users, customers or any other fields are specified and the container has already been uploaded, those fields will be updated.
         * @summary Watch Containers
         * @param {Array<POSTContainerWatch>} pOSTContainerWatch 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchContainersContainersWatchPost(pOSTContainerWatch: Array<POSTContainerWatch>, assignToUserId?: Array<number>, options?: any): AxiosPromise<POSTUploadContainersResponse> {
            return localVarFp.watchContainersContainersWatchPost(pOSTContainerWatch, assignToUserId, options).then((request) => request(axios, basePath));
        },
        /**
         * Supports upload via .csv or .xlsx file
         * @summary Watch Containers Via Upload
         * @param {File} file 
         * @param {Array<number>} [assignToUserId] User IDs to assign to
         * @param {number} [customerId] Customer ID for this upload
         * @param {PortComplex} [portComplex] Port complex to be assigned to all containers in the upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchContainersViaUploadContainersWatchViaUploadPost(file: File, assignToUserId?: Array<number>, customerId?: number, portComplex?: PortComplex, options?: any): AxiosPromise<POSTUploadContainersResponse> {
            return localVarFp.watchContainersViaUploadContainersWatchViaUploadPost(file, assignToUserId, customerId, portComplex, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContainersApi - object-oriented interface
 * @export
 * @class ContainersApi
 * @extends {BaseAPI}
 */
export class ContainersApi extends BaseAPI {
    /**
     * Apply a previewed reconciliation. Per-container failures land in `errors`; unhandled exceptions roll back the whole transaction.
     * @summary Apply Empties Reconciliation Endpoint
     * @param {ApplyReconciliationRequest} applyReconciliationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest: ApplyReconciliationRequest, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).applyEmptiesReconciliationEndpointContainersEmptiesReconcileApplyPost(applyReconciliationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Assign containers to a user
     * @summary Assign Containers
     * @param {POSTAssignmentData} pOSTAssignmentData 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public assignContainersContainersAssignPost(pOSTAssignmentData: POSTAssignmentData, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).assignContainersContainersAssignPost(pOSTAssignmentData, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Bulk check import statuses for containers via Port Optimizer and/or ASB. Does not create any container watches.  Returns 504 if ASB data does not arrive within the timeout window.
     * @summary Bulk Check Import Statuses
     * @param {POSTBulkCheckImportStatuses} pOSTBulkCheckImportStatuses 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses: POSTBulkCheckImportStatuses, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).bulkCheckImportStatusesContainersImportStatusesBulkCheckPost(pOSTBulkCheckImportStatuses, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete Current Location
     * @param {DELETECurrentLocation} dELETECurrentLocation 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation: DELETECurrentLocation, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).deleteCurrentLocationContainersCurrentLocationDelete(dELETECurrentLocation, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary File Export
     * @param {GETContainerFileExport} gETContainerFileExport 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public fileExportContainersFileExportPost(gETContainerFileExport: GETContainerFileExport, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).fileExportContainersFileExportPost(gETContainerFileExport, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns container counts grouped by vessel ETA date.
     * @summary Get Container Counts By Eta
     * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerCountsByEtaContainersVesselEtaCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerCountsByEtaContainersVesselEtaCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vessels, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Container Counts By State
     * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerCountsByStateContainersStateCountsGet(terminal?: Array<TerminalName>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerCountsByStateContainersStateCountsGet(terminal, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Container Counts By Terminal
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerCountsByTerminalContainersTerminalCountsGet(containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Container History
     * @param {string} containerNumber 
     * @param {boolean} [includeRawDataChanges] 
     * @param {boolean} [includeAppointmentChanges] 
     * @param {boolean} [includeImportStatusChanges] 
     * @param {boolean} [includeGateTransactions] 
     * @param {boolean} [includeBookingRequests] 
     * @param {boolean} [includeEmptyBookingRequests] 
     * @param {boolean} [includeCancellationRequests] 
     * @param {boolean} [includeRescheduleRequests] 
     * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
     * @param {boolean} [includeAddEmptyRequests] 
     * @param {boolean} [includeEditChassisRequests] 
     * @param {boolean} [includeCheckInRequests] 
     * @param {boolean} [includeAutoBookChanges] 
     * @param {boolean} [includeAutoBookEmptyChanges] 
     * @param {boolean} [includeWatchChanges] 
     * @param {boolean} [includeContainerWatchEvents] 
     * @param {boolean} [includeBrowserExtensionActions] 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerHistoryContainersContainerNumberHistoryGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerHistoryContainersContainerNumberHistoryGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeEditAppointmentLicensePlateRequests, includeAddEmptyRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * This API groups container changes into lifecycle phases
     * @summary Get Container History Grouped
     * @param {string} containerNumber 
     * @param {boolean} [includeRawDataChanges] 
     * @param {boolean} [includeAppointmentChanges] 
     * @param {boolean} [includeImportStatusChanges] 
     * @param {boolean} [includeGateTransactions] 
     * @param {boolean} [includeBookingRequests] 
     * @param {boolean} [includeEmptyBookingRequests] 
     * @param {boolean} [includeCancellationRequests] 
     * @param {boolean} [includeRescheduleRequests] 
     * @param {boolean} [includeAddEmptyRequests] 
     * @param {boolean} [includeEditAppointmentLicensePlateRequests] 
     * @param {boolean} [includeEditChassisRequests] 
     * @param {boolean} [includeCheckInRequests] 
     * @param {boolean} [includeAutoBookChanges] 
     * @param {boolean} [includeAutoBookEmptyChanges] 
     * @param {boolean} [includeWatchChanges] 
     * @param {boolean} [includeContainerWatchEvents] 
     * @param {boolean} [includeBrowserExtensionActions] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber: string, includeRawDataChanges?: boolean, includeAppointmentChanges?: boolean, includeImportStatusChanges?: boolean, includeGateTransactions?: boolean, includeBookingRequests?: boolean, includeEmptyBookingRequests?: boolean, includeCancellationRequests?: boolean, includeRescheduleRequests?: boolean, includeAddEmptyRequests?: boolean, includeEditAppointmentLicensePlateRequests?: boolean, includeEditChassisRequests?: boolean, includeCheckInRequests?: boolean, includeAutoBookChanges?: boolean, includeAutoBookEmptyChanges?: boolean, includeWatchChanges?: boolean, includeContainerWatchEvents?: boolean, includeBrowserExtensionActions?: boolean, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerHistoryGroupedContainersContainerNumberHistoryGroupedGet(containerNumber, includeRawDataChanges, includeAppointmentChanges, includeImportStatusChanges, includeGateTransactions, includeBookingRequests, includeEmptyBookingRequests, includeCancellationRequests, includeRescheduleRequests, includeAddEmptyRequests, includeEditAppointmentLicensePlateRequests, includeEditChassisRequests, includeCheckInRequests, includeAutoBookChanges, includeAutoBookEmptyChanges, includeWatchChanges, includeContainerWatchEvents, includeBrowserExtensionActions, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Container Types
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainerTypesContainersTypesGet(options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainerTypesContainersTypesGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A higher performance/lighter weight containers API that only returns basic container information
     * @summary Get Containers Basic
     * @param {string} [importOutGatedAfter] 
     * @param {string} [importOutGatedBefore] 
     * @param {number} [limit] 
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainersBasicContainersBasicGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainersBasicContainersBasicGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Purpose-built for the Empties list: everything `/basic/with_unwatched/` returns, plus each container\'s empty auto-book status, so that view can render its bot icon in one call instead of a second request to the auto-book-empty endpoints.  Kept as its own endpoint rather than an `include_auto_book` flag on `/basic/with_unwatched/`, since that endpoint has exactly one caller (the Empties list) — a flag only pays for itself when multiple callers need different shapes of the same response; with a single caller that always wants the field, it\'s really a distinct resource.
     * @summary Get Containers Basic With Unwatched And Auto Book
     * @param {string} [importOutGatedAfter] 
     * @param {string} [importOutGatedBefore] 
     * @param {number} [limit] 
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Like `/basic/` (lightweight container list) but also reports which of the searched `container_numbers` aren\'t tracked by this org, so callers (the Empties list) can surface an \"untracked\" state with a Start-tracking CTA.  Kept as a separate endpoint rather than extending `/basic/` so that endpoint\'s bare-array contract stays stable for its other callers (a prior attempt to change `/basic/` itself broke booking lookups and was reverted).
     * @summary Get Containers Basic With Unwatched
     * @param {string} [importOutGatedAfter] 
     * @param {string} [importOutGatedBefore] 
     * @param {number} [limit] 
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter?: string, importOutGatedBefore?: string, limit?: number, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainersBasicWithUnwatchedContainersBasicWithUnwatchedGet(importOutGatedAfter, importOutGatedBefore, limit, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * This is the primary API for getting information about containers. It consolidates most information we might have about a container into a single endpoint, including import status (availability), appointments and gate transactions.  Even if you only want one of these things, you may want to use this API as it has the most filtering options and is well optimized
     * @summary Get Containers
     * @param {SortOptions} [sortBy] &#x60;\&#39;appointment_start\&#39;&#x60; and &#x60;\&#39;gate_time\&#39;&#x60; are deprecated options here for backwards compatibility
     * @param {GetContainersContainersGetSortOrderEnum} [sortOrder] 
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {number} [offset] Skip this many rows before returning results. An alternate form of pagination
     * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<string>} [vessels] Limit to containers that are associated with these vessels
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainersContainersGet(sortBy?: SortOptions, sortOrder?: GetContainersContainersGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vessels?: Array<string>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainersContainersGet(sortBy, sortOrder, page, pageSize, offset, terminal, containerCycleStates, cycleState, containerCycleState, vessels, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
     * @summary Get Containers Line And Type
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Shipping Lines
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getShippingLinesContainersShippingLinesGet(options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getShippingLinesContainersShippingLinesGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns available vessels of containers
     * @summary Get Vessel Counts By Terminal
     * @param {Array<TerminalName>} [terminal] Limit to containers that were most recently active at one of these terminals. Repeat the parameter to pass several. An empty list does not filter.
     * @param {Array<ContainerWatchState>} [containerCycleStates] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [cycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {Array<ContainerWatchState>} [containerCycleState] Limit to containers identified as being in the passed container life cycle state(s)
     * @param {string} [vesselEta] Filter to containers with vessel ETA matching this exact date. If provided, the &#x60;vessel_eta_is_unknown&#x60; filter is ignored. To filter to containers with no vessel ETA, omit this parameter and use &#x60;vessel_eta_is_unknown&#x3D;true&#x60; instead.
     * @param {boolean} [vesselEtaIsUnknown] Filter to containers with no vessel ETA (vessel_eta is NULL). Only applies when &#x60;vessel_eta&#x60; is not provided. If &#x60;vessel_eta&#x60; is provided, this filter is ignored. Set to &#x60;true&#x60; to filter to containers with unknown vessel ETA.
     * @param {Array<number>} [customerIds] Limit to containers for one or more customers. Pass &#x60;-1&#x60; to include containers with no customer.
     * @param {Array<number>} [excludedCustomerIds] Hide containers for these customers. Pass &#x60;-1&#x60; to hide containers with no customer. An empty list does not filter. Unlike &#x60;customer_ids&#x60;, customers created later are still shown.
     * @param {number} [customerId] Deprecated: use &#x60;customer_ids&#x60; instead.
     * @param {boolean} [autoBookOn] Not quite the inverse of &#x60;auto_book_on&#x60;
     * @param {boolean} [autoBookOff] Includes all containers that DO NOT have an active Auto Book request. 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<HoldType>} [holdType] 
     * @param {number} [updatedInLastMinutes] Limit to containers that have been updated in the last N minutes. Updates may include state change, user assignments, customer set, and more.
     * @param {Array<string>} [tags] Limit to containers with all of these tags
     * @param {boolean} [isUntagged] Limit to containers with no tags.
     * @param {boolean} [isGreenTruck] Limit to containers with a confirmed green/EV truck on their appointment. Only applies at Green Lane terminals (e.g. APM). Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {boolean} [needsLineLfdCheck] Limit to containers whose shipping line does not share its LFD with terminals and for which we have no line LFD from any source, in any state where an LFD is displayed. See &#x60;shares_lfd_with_terminals&#x60; and &#x60;lfd_check_states&#x60; on &#x60;/containers/shipping_lines/&#x60;. Only &#x60;true&#x60; is meaningful — &#x60;false&#x60; is treated the same as omitting the parameter (no filter applied).
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {Array<number>} [assignedUserId] Limit to containers assigned to one or more users. To filter to containers with no assigned users, pass &#x60;-1&#x60;
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public getVesselCountsByTerminalContainersVesselCountsGet(terminal?: Array<TerminalName>, containerCycleStates?: Array<ContainerWatchState>, cycleState?: Array<ContainerWatchState>, containerCycleState?: Array<ContainerWatchState>, vesselEta?: string, vesselEtaIsUnknown?: boolean, customerIds?: Array<number>, excludedCustomerIds?: Array<number>, customerId?: number, autoBookOn?: boolean, autoBookOff?: boolean, containerType?: Array<ContainerType>, shippingLine?: Array<ShippingLine>, holdType?: Array<HoldType>, updatedInLastMinutes?: number, tags?: Array<string>, isUntagged?: boolean, isGreenTruck?: boolean, needsLineLfdCheck?: boolean, containerNumbers?: string, assignedUserId?: Array<number>, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).getVesselCountsByTerminalContainersVesselCountsGet(terminal, containerCycleStates, cycleState, containerCycleState, vesselEta, vesselEtaIsUnknown, customerIds, excludedCustomerIds, customerId, autoBookOn, autoBookOff, containerType, shippingLine, holdType, updatedInLastMinutes, tags, isUntagged, isGreenTruck, needsLineLfdCheck, containerNumbers, assignedUserId, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Refresh container import status at associated portals/terminals at user request
     * @summary Manual Refresh Import Statuses
     * @param {Array<POSTImportStatusRefresh>} pOSTImportStatusRefresh 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh: Array<POSTImportStatusRefresh>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).manualRefreshImportStatusesContainersImportStatusesRefreshPost(pOSTImportStatusRefresh, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Patch Containers
     * @param {PATCHContainers} pATCHContainers 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public patchContainersContainersPatch(pATCHContainers: PATCHContainers, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).patchContainersContainersPatch(pATCHContainers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Preview empties reconciliation.  Accepts either a CSV/XLSX file upload or pasted container numbers (free text). Returns a categorized diff against the org\'s current empties — nothing is applied until the client explicitly confirms via a separate apply endpoint.
     * @summary Preview Empties Reconciliation
     * @param {ReconciliationMode} mode 
     * @param {ContainerStatus} defaultStatus 
     * @param {File} [file] 
     * @param {string} [containerNumbers] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode: ReconciliationMode, defaultStatus: ContainerStatus, file?: File, containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).previewEmptiesReconciliationContainersEmptiesReconcilePreviewPost(mode, defaultStatus, file, containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stops tracking one or more containers. They will no longer show up in any views after this call.
     * @summary Stop Watching Containers
     * @param {Array<string>} requestBody 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public stopWatchingContainersContainersUnwatchPost(requestBody: Array<string>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).stopWatchingContainersContainersUnwatchPost(requestBody, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Unassign users from containers
     * @summary Unassign Containers
     * @param {POSTAssignmentData} pOSTAssignmentData 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public unassignContainersContainersUnassignPost(pOSTAssignmentData: POSTAssignmentData, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).unassignContainersContainersUnassignPost(pOSTAssignmentData, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deprecated: Use PATCH / instead.
     * @summary Update Customer
     * @param {PATCHUpdateCustomer} pATCHUpdateCustomer 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer: PATCHUpdateCustomer, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).updateCustomerContainersUpdateCustomerPatch(pATCHUpdateCustomer, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add containers to watch. Containers must be watched and tracking at a terminal before data for a container can be loaded or an appointment booked.  This API can be called many times with the same containers and no duplicates will be created. If different assigned users, customers or any other fields are specified and the container has already been uploaded, those fields will be updated.
     * @summary Watch Containers
     * @param {Array<POSTContainerWatch>} pOSTContainerWatch 
     * @param {Array<number>} [assignToUserId] User IDs to assign to
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public watchContainersContainersWatchPost(pOSTContainerWatch: Array<POSTContainerWatch>, assignToUserId?: Array<number>, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).watchContainersContainersWatchPost(pOSTContainerWatch, assignToUserId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Supports upload via .csv or .xlsx file
     * @summary Watch Containers Via Upload
     * @param {File} file 
     * @param {Array<number>} [assignToUserId] User IDs to assign to
     * @param {number} [customerId] Customer ID for this upload
     * @param {PortComplex} [portComplex] Port complex to be assigned to all containers in the upload
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ContainersApi
     */
    public watchContainersViaUploadContainersWatchViaUploadPost(file: File, assignToUserId?: Array<number>, customerId?: number, portComplex?: PortComplex, options?: RawAxiosRequestConfig) {
        return ContainersApiFp(this.configuration).watchContainersViaUploadContainersWatchViaUploadPost(file, assignToUserId, customerId, portComplex, options).then((request) => request(this.axios, this.basePath));
    }
}

/**
 * @export
 */
export const GetContainersContainersGetSortOrderEnum = {
    Ascending: 'ascending',
    Descending: 'descending'
} as const;
export type GetContainersContainersGetSortOrderEnum = typeof GetContainersContainersGetSortOrderEnum[keyof typeof GetContainersContainersGetSortOrderEnum];


/**
 * CustomersApi - axios parameter creator
 * @export
 */
export const CustomersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Add Customers
         * @param {Array<Customer>} customer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addCustomersCustomersPost: async (customer: Array<Customer>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'customer' is not null or undefined
            assertParamExists('addCustomersCustomersPost', 'customer', customer)
            const localVarPath = `/customers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(customer, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete Customers
         * @param {BodyDeleteCustomersCustomersDelete} bodyDeleteCustomersCustomersDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCustomersCustomersDelete: async (bodyDeleteCustomersCustomersDelete: BodyDeleteCustomersCustomersDelete, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyDeleteCustomersCustomersDelete' is not null or undefined
            assertParamExists('deleteCustomersCustomersDelete', 'bodyDeleteCustomersCustomersDelete', bodyDeleteCustomersCustomersDelete)
            const localVarPath = `/customers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyDeleteCustomersCustomersDelete, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Customers
         * @param {string} [q] 
         * @param {number} [limit] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCustomersCustomersGet: async (q?: string, limit?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/customers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (q !== undefined) {
                localVarQueryParameter['q'] = q;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Merge Customers
         * @param {BodyMergeCustomersCustomersMergePost} bodyMergeCustomersCustomersMergePost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mergeCustomersCustomersMergePost: async (bodyMergeCustomersCustomersMergePost: BodyMergeCustomersCustomersMergePost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyMergeCustomersCustomersMergePost' is not null or undefined
            assertParamExists('mergeCustomersCustomersMergePost', 'bodyMergeCustomersCustomersMergePost', bodyMergeCustomersCustomersMergePost)
            const localVarPath = `/customers/merge/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyMergeCustomersCustomersMergePost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update Customer
         * @param {SavedCustomer} savedCustomer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCustomerCustomersUpdatePost: async (savedCustomer: SavedCustomer, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedCustomer' is not null or undefined
            assertParamExists('updateCustomerCustomersUpdatePost', 'savedCustomer', savedCustomer)
            const localVarPath = `/customers/update/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(savedCustomer, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CustomersApi - functional programming interface
 * @export
 */
export const CustomersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CustomersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Add Customers
         * @param {Array<Customer>} customer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addCustomersCustomersPost(customer: Array<Customer>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SavedCustomer>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addCustomersCustomersPost(customer, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CustomersApi.addCustomersCustomersPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete Customers
         * @param {BodyDeleteCustomersCustomersDelete} bodyDeleteCustomersCustomersDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete: BodyDeleteCustomersCustomersDelete, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SavedCustomer>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CustomersApi.deleteCustomersCustomersDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Customers
         * @param {string} [q] 
         * @param {number} [limit] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCustomersCustomersGet(q?: string, limit?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SavedCustomer>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCustomersCustomersGet(q, limit, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CustomersApi.getCustomersCustomersGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Merge Customers
         * @param {BodyMergeCustomersCustomersMergePost} bodyMergeCustomersCustomersMergePost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost: BodyMergeCustomersCustomersMergePost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedCustomer>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CustomersApi.mergeCustomersCustomersMergePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Update Customer
         * @param {SavedCustomer} savedCustomer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCustomerCustomersUpdatePost(savedCustomer: SavedCustomer, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedCustomer>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCustomerCustomersUpdatePost(savedCustomer, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CustomersApi.updateCustomerCustomersUpdatePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CustomersApi - factory interface
 * @export
 */
export const CustomersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CustomersApiFp(configuration)
    return {
        /**
         * 
         * @summary Add Customers
         * @param {Array<Customer>} customer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addCustomersCustomersPost(customer: Array<Customer>, options?: any): AxiosPromise<Array<SavedCustomer>> {
            return localVarFp.addCustomersCustomersPost(customer, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete Customers
         * @param {BodyDeleteCustomersCustomersDelete} bodyDeleteCustomersCustomersDelete 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete: BodyDeleteCustomersCustomersDelete, options?: any): AxiosPromise<Array<SavedCustomer>> {
            return localVarFp.deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Customers
         * @param {string} [q] 
         * @param {number} [limit] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCustomersCustomersGet(q?: string, limit?: number, options?: any): AxiosPromise<Array<SavedCustomer>> {
            return localVarFp.getCustomersCustomersGet(q, limit, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Merge Customers
         * @param {BodyMergeCustomersCustomersMergePost} bodyMergeCustomersCustomersMergePost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost: BodyMergeCustomersCustomersMergePost, options?: any): AxiosPromise<SavedCustomer> {
            return localVarFp.mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update Customer
         * @param {SavedCustomer} savedCustomer 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCustomerCustomersUpdatePost(savedCustomer: SavedCustomer, options?: any): AxiosPromise<SavedCustomer> {
            return localVarFp.updateCustomerCustomersUpdatePost(savedCustomer, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CustomersApi - object-oriented interface
 * @export
 * @class CustomersApi
 * @extends {BaseAPI}
 */
export class CustomersApi extends BaseAPI {
    /**
     * 
     * @summary Add Customers
     * @param {Array<Customer>} customer 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CustomersApi
     */
    public addCustomersCustomersPost(customer: Array<Customer>, options?: RawAxiosRequestConfig) {
        return CustomersApiFp(this.configuration).addCustomersCustomersPost(customer, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete Customers
     * @param {BodyDeleteCustomersCustomersDelete} bodyDeleteCustomersCustomersDelete 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CustomersApi
     */
    public deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete: BodyDeleteCustomersCustomersDelete, options?: RawAxiosRequestConfig) {
        return CustomersApiFp(this.configuration).deleteCustomersCustomersDelete(bodyDeleteCustomersCustomersDelete, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Customers
     * @param {string} [q] 
     * @param {number} [limit] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CustomersApi
     */
    public getCustomersCustomersGet(q?: string, limit?: number, options?: RawAxiosRequestConfig) {
        return CustomersApiFp(this.configuration).getCustomersCustomersGet(q, limit, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Merge Customers
     * @param {BodyMergeCustomersCustomersMergePost} bodyMergeCustomersCustomersMergePost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CustomersApi
     */
    public mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost: BodyMergeCustomersCustomersMergePost, options?: RawAxiosRequestConfig) {
        return CustomersApiFp(this.configuration).mergeCustomersCustomersMergePost(bodyMergeCustomersCustomersMergePost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Update Customer
     * @param {SavedCustomer} savedCustomer 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CustomersApi
     */
    public updateCustomerCustomersUpdatePost(savedCustomer: SavedCustomer, options?: RawAxiosRequestConfig) {
        return CustomersApiFp(this.configuration).updateCustomerCustomersUpdatePost(savedCustomer, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * EmptyReturnRulesApi - axios parameter creator
 * @export
 */
export const EmptyReturnRulesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Load the most recent empty return rules reading for
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestReadingsEmptyReturnRulesReadingsLatestGet: async (portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/empty_return_rules/readings/latest/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Latest Rules
         * @param {TerminalName} [terminal] 
         * @param {ShippingLine} [shippingLine] 
         * @param {ContainerType} [containerType] 
         * @param {boolean} [openOnly] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestRulesEmptyReturnRulesRulesLatestGet: async (terminal?: TerminalName, shippingLine?: ShippingLine, containerType?: ContainerType, openOnly?: boolean, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/empty_return_rules/rules/latest/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (shippingLine !== undefined) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType !== undefined) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (openOnly !== undefined) {
                localVarQueryParameter['open_only'] = openOnly;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * An API to allow \"syncing\" of the latest rules for each terminal.  The user can indicate what terminal \"rule readings\" it has cached locally, and only rules for terminals that are missing or have changed be returned. There will be a result per terminal, but terminal\'s where the current value in client cache is up to date will have no rules set.  The only filtering option is `terminal` because this API is for syncing rules as they are loaded. Rules are loaded by terminal, so filtering by terminal is OK but other filter parameters would return incomplete sets which could cause confusion. Filtering by shipping line or container type should be done on the client side.
         * @summary Get Latest Rules If Changed
         * @param {POSTEmptyReturnRulesIfChanged} pOSTEmptyReturnRulesIfChanged 
         * @param {TerminalName} [terminal] Limit results to this terminal only
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost: async (pOSTEmptyReturnRulesIfChanged: POSTEmptyReturnRulesIfChanged, terminal?: TerminalName, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTEmptyReturnRulesIfChanged' is not null or undefined
            assertParamExists('getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost', 'pOSTEmptyReturnRulesIfChanged', pOSTEmptyReturnRulesIfChanged)
            const localVarPath = `/empty_return_rules/rules/latest/if_changed/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEmptyReturnRulesIfChanged, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingFileEmptyReturnRulesReadingsFilesPathGet: async (path: string, download?: boolean, filename?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'path' is not null or undefined
            assertParamExists('getReadingFileEmptyReturnRulesReadingsFilesPathGet', 'path', path)
            const localVarPath = `/empty_return_rules/readings/files/{path}`
                .replace(`{${"path"}}`, encodeURIComponent(String(path)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (download !== undefined) {
                localVarQueryParameter['download'] = download;
            }

            if (filename !== undefined) {
                localVarQueryParameter['filename'] = filename;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet: async (terminal: TerminalName, observedAfter?: string, observedBefore?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet', 'terminal', terminal)
            const localVarPath = `/empty_return_rules/readings/{terminal}/history/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (observedAfter !== undefined) {
                localVarQueryParameter['observed_after'] = (observedAfter as any instanceof Date) ?
                    (observedAfter as any).toISOString() :
                    observedAfter;
            }

            if (observedBefore !== undefined) {
                localVarQueryParameter['observed_before'] = (observedBefore as any instanceof Date) ?
                    (observedBefore as any).toISOString() :
                    observedBefore;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Use [GET /empty_return_rules/rules/latest/](#operation/get_latest_rules_empty_return_rules_rules_latest__get) with the `terminal` query parameter instead
         * @summary Get Rules By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet: async (terminal: TerminalName, readingFirstObserved: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet', 'terminal', terminal)
            // verify required parameter 'readingFirstObserved' is not null or undefined
            assertParamExists('getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet', 'readingFirstObserved', readingFirstObserved)
            const localVarPath = `/empty_return_rules/rules/by_terminal/{terminal}/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (readingFirstObserved !== undefined) {
                localVarQueryParameter['reading_first_observed'] = (readingFirstObserved as any instanceof Date) ?
                    (readingFirstObserved as any).toISOString() :
                    readingFirstObserved;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * EmptyReturnRulesApi - functional programming interface
 * @export
 */
export const EmptyReturnRulesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = EmptyReturnRulesApiAxiosParamCreator(configuration)
    return {
        /**
         * Load the most recent empty return rules reading for
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETEmptyReturnRulesReading>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getLatestReadingsEmptyReturnRulesReadingsLatestGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Latest Rules
         * @param {TerminalName} [terminal] 
         * @param {ShippingLine} [shippingLine] 
         * @param {ContainerType} [containerType] 
         * @param {boolean} [openOnly] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLatestRulesEmptyReturnRulesRulesLatestGet(terminal?: TerminalName, shippingLine?: ShippingLine, containerType?: ContainerType, openOnly?: boolean, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETEmptyReturnRule>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLatestRulesEmptyReturnRulesRulesLatestGet(terminal, shippingLine, containerType, openOnly, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getLatestRulesEmptyReturnRulesRulesLatestGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * An API to allow \"syncing\" of the latest rules for each terminal.  The user can indicate what terminal \"rule readings\" it has cached locally, and only rules for terminals that are missing or have changed be returned. There will be a result per terminal, but terminal\'s where the current value in client cache is up to date will have no rules set.  The only filtering option is `terminal` because this API is for syncing rules as they are loaded. Rules are loaded by terminal, so filtering by terminal is OK but other filter parameters would return incomplete sets which could cause confusion. Filtering by shipping line or container type should be done on the client side.
         * @summary Get Latest Rules If Changed
         * @param {POSTEmptyReturnRulesIfChanged} pOSTEmptyReturnRulesIfChanged 
         * @param {TerminalName} [terminal] Limit results to this terminal only
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged: POSTEmptyReturnRulesIfChanged, terminal?: TerminalName, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<TerminalReadingRules>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged, terminal, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReadingFileEmptyReturnRulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReadingFileEmptyReturnRulesReadingsFilesPathGet(path, download, filename, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getReadingFileEmptyReturnRulesReadingsFilesPathGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETEmptyReturnRulesReading>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Use [GET /empty_return_rules/rules/latest/](#operation/get_latest_rules_empty_return_rules_rules_latest__get) with the `terminal` query parameter instead
         * @summary Get Rules By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETEmptyReturnRule>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal, readingFirstObserved, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['EmptyReturnRulesApi.getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * EmptyReturnRulesApi - factory interface
 * @export
 */
export const EmptyReturnRulesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = EmptyReturnRulesApiFp(configuration)
    return {
        /**
         * Load the most recent empty return rules reading for
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETEmptyReturnRulesReading>> {
            return localVarFp.getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Latest Rules
         * @param {TerminalName} [terminal] 
         * @param {ShippingLine} [shippingLine] 
         * @param {ContainerType} [containerType] 
         * @param {boolean} [openOnly] 
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestRulesEmptyReturnRulesRulesLatestGet(terminal?: TerminalName, shippingLine?: ShippingLine, containerType?: ContainerType, openOnly?: boolean, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETEmptyReturnRule>> {
            return localVarFp.getLatestRulesEmptyReturnRulesRulesLatestGet(terminal, shippingLine, containerType, openOnly, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * An API to allow \"syncing\" of the latest rules for each terminal.  The user can indicate what terminal \"rule readings\" it has cached locally, and only rules for terminals that are missing or have changed be returned. There will be a result per terminal, but terminal\'s where the current value in client cache is up to date will have no rules set.  The only filtering option is `terminal` because this API is for syncing rules as they are loaded. Rules are loaded by terminal, so filtering by terminal is OK but other filter parameters would return incomplete sets which could cause confusion. Filtering by shipping line or container type should be done on the client side.
         * @summary Get Latest Rules If Changed
         * @param {POSTEmptyReturnRulesIfChanged} pOSTEmptyReturnRulesIfChanged 
         * @param {TerminalName} [terminal] Limit results to this terminal only
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged: POSTEmptyReturnRulesIfChanged, terminal?: TerminalName, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<TerminalReadingRules>> {
            return localVarFp.getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged, terminal, portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingFileEmptyReturnRulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: any): AxiosPromise<any> {
            return localVarFp.getReadingFileEmptyReturnRulesReadingsFilesPathGet(path, download, filename, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: any): AxiosPromise<Array<GETEmptyReturnRulesReading>> {
            return localVarFp.getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options).then((request) => request(axios, basePath));
        },
        /**
         * Use [GET /empty_return_rules/rules/latest/](#operation/get_latest_rules_empty_return_rules_rules_latest__get) with the `terminal` query parameter instead
         * @summary Get Rules By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: any): AxiosPromise<Array<GETEmptyReturnRule>> {
            return localVarFp.getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal, readingFirstObserved, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * EmptyReturnRulesApi - object-oriented interface
 * @export
 * @class EmptyReturnRulesApi
 * @extends {BaseAPI}
 */
export class EmptyReturnRulesApi extends BaseAPI {
    /**
     * Load the most recent empty return rules reading for
     * @summary Get Latest Readings
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getLatestReadingsEmptyReturnRulesReadingsLatestGet(portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Latest Rules
     * @param {TerminalName} [terminal] 
     * @param {ShippingLine} [shippingLine] 
     * @param {ContainerType} [containerType] 
     * @param {boolean} [openOnly] 
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getLatestRulesEmptyReturnRulesRulesLatestGet(terminal?: TerminalName, shippingLine?: ShippingLine, containerType?: ContainerType, openOnly?: boolean, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getLatestRulesEmptyReturnRulesRulesLatestGet(terminal, shippingLine, containerType, openOnly, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * An API to allow \"syncing\" of the latest rules for each terminal.  The user can indicate what terminal \"rule readings\" it has cached locally, and only rules for terminals that are missing or have changed be returned. There will be a result per terminal, but terminal\'s where the current value in client cache is up to date will have no rules set.  The only filtering option is `terminal` because this API is for syncing rules as they are loaded. Rules are loaded by terminal, so filtering by terminal is OK but other filter parameters would return incomplete sets which could cause confusion. Filtering by shipping line or container type should be done on the client side.
     * @summary Get Latest Rules If Changed
     * @param {POSTEmptyReturnRulesIfChanged} pOSTEmptyReturnRulesIfChanged 
     * @param {TerminalName} [terminal] Limit results to this terminal only
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged: POSTEmptyReturnRulesIfChanged, terminal?: TerminalName, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(pOSTEmptyReturnRulesIfChanged, terminal, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Reading File
     * @param {string} path 
     * @param {boolean} [download] 
     * @param {string} [filename] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getReadingFileEmptyReturnRulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getReadingFileEmptyReturnRulesReadingsFilesPathGet(path, download, filename, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Readings History
     * @param {TerminalName} terminal 
     * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
     * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getReadingsHistoryEmptyReturnRulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Use [GET /empty_return_rules/rules/latest/](#operation/get_latest_rules_empty_return_rules_rules_latest__get) with the `terminal` query parameter instead
     * @summary Get Rules By Terminal
     * @param {TerminalName} terminal 
     * @param {string} readingFirstObserved 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof EmptyReturnRulesApi
     */
    public getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: RawAxiosRequestConfig) {
        return EmptyReturnRulesApiFp(this.configuration).getRulesByTerminalEmptyReturnRulesRulesByTerminalTerminalGet(terminal, readingFirstObserved, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ExportsApi - axios parameter creator
 * @export
 */
export const ExportsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Book multiple container-less empty pickups against one watched export booking.  Bulk by construction: `quantity` fans out into N independent workflows, each with its own request row, so a single bad unit fails only itself (409 if any failed).
         * @summary Book Empty Pickups
         * @param {string} bookingNumber 
         * @param {POSTEmptyPickupBooking} pOSTEmptyPickupBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost: async (bookingNumber: string, pOSTEmptyPickupBooking: POSTEmptyPickupBooking, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bookingNumber' is not null or undefined
            assertParamExists('bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost', 'bookingNumber', bookingNumber)
            // verify required parameter 'pOSTEmptyPickupBooking' is not null or undefined
            assertParamExists('bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost', 'pOSTEmptyPickupBooking', pOSTEmptyPickupBooking)
            const localVarPath = `/exports/{booking_number}/book-empty-pickup/`
                .replace(`{${"booking_number"}}`, encodeURIComponent(String(bookingNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTEmptyPickupBooking, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Check bookings before adding them: whether each is found, not found, invalid or couldn\'t be checked yet (`pending`), whether the org already tracks it, and its containers on file. Always 200: the check is advisory, and adding must work whatever it says. Creates no watches. A booking anyone looked up in the last 30 minutes answers without a portal call.
         * @summary Check Export Bookings
         * @param {POSTExportBookingCheck} pOSTExportBookingCheck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkExportBookingsExportsBookingsCheckPost: async (pOSTExportBookingCheck: POSTExportBookingCheck, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTExportBookingCheck' is not null or undefined
            assertParamExists('checkExportBookingsExportsBookingsCheckPost', 'pOSTExportBookingCheck', pOSTExportBookingCheck)
            const localVarPath = `/exports/bookings/check/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTExportBookingCheck, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * This org\'s \"My Active Vessels\" for the Schedule board.  Separate from `/vessels/etas/`, which is also pushed to every org over a shared websocket and so can\'t carry per-org data.
         * @summary Get Active Vessel Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveVesselSchedulesExportsActiveVesselSchedulesGet: async (portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/exports/active_vessel_schedules/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Export Counts By State
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByStateExportsStateCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportCountsByStateExportsStateCountsGet: async (bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByStateExportsStateCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/exports/state_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (bookingNumber !== undefined) {
                localVarQueryParameter['booking_number'] = bookingNumber;
            }

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (cargoTypes) {
                localVarQueryParameter['cargo_types'] = cargoTypes;
            }

            if (holds) {
                localVarQueryParameter['holds'] = holds;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }

            if (lifecycleState) {
                localVarQueryParameter['lifecycle_state'] = lifecycleState;
            }

            if (portComplex) {
                localVarQueryParameter['port_complex'] = portComplex;
            }

            if (erdFrom !== undefined) {
                localVarQueryParameter['erd_from'] = (erdFrom as any instanceof Date) ?
                    (erdFrom as any).toISOString().substring(0,10) :
                    erdFrom;
            }

            if (erdTo !== undefined) {
                localVarQueryParameter['erd_to'] = (erdTo as any instanceof Date) ?
                    (erdTo as any).toISOString().substring(0,10) :
                    erdTo;
            }

            if (cutoffFrom !== undefined) {
                localVarQueryParameter['cutoff_from'] = (cutoffFrom as any instanceof Date) ?
                    (cutoffFrom as any).toISOString().substring(0,10) :
                    cutoffFrom;
            }

            if (cutoffTo !== undefined) {
                localVarQueryParameter['cutoff_to'] = (cutoffTo as any instanceof Date) ?
                    (cutoffTo as any).toISOString().substring(0,10) :
                    cutoffTo;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (assignedUserIds) {
                localVarQueryParameter['assigned_user_ids'] = assignedUserIds;
            }

            if (sortBy !== undefined) {
                localVarQueryParameter['sort_by'] = sortBy;
            }

            if (sortOrder !== undefined) {
                localVarQueryParameter['sort_order'] = sortOrder;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            if (includeTotal !== undefined) {
                localVarQueryParameter['include_total'] = includeTotal;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Export Counts By Terminal
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportCountsByTerminalExportsTerminalCountsGet: async (bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/exports/terminal_counts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (bookingNumber !== undefined) {
                localVarQueryParameter['booking_number'] = bookingNumber;
            }

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (cargoTypes) {
                localVarQueryParameter['cargo_types'] = cargoTypes;
            }

            if (holds) {
                localVarQueryParameter['holds'] = holds;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }

            if (lifecycleState) {
                localVarQueryParameter['lifecycle_state'] = lifecycleState;
            }

            if (portComplex) {
                localVarQueryParameter['port_complex'] = portComplex;
            }

            if (erdFrom !== undefined) {
                localVarQueryParameter['erd_from'] = (erdFrom as any instanceof Date) ?
                    (erdFrom as any).toISOString().substring(0,10) :
                    erdFrom;
            }

            if (erdTo !== undefined) {
                localVarQueryParameter['erd_to'] = (erdTo as any instanceof Date) ?
                    (erdTo as any).toISOString().substring(0,10) :
                    erdTo;
            }

            if (cutoffFrom !== undefined) {
                localVarQueryParameter['cutoff_from'] = (cutoffFrom as any instanceof Date) ?
                    (cutoffFrom as any).toISOString().substring(0,10) :
                    cutoffFrom;
            }

            if (cutoffTo !== undefined) {
                localVarQueryParameter['cutoff_to'] = (cutoffTo as any instanceof Date) ?
                    (cutoffTo as any).toISOString().substring(0,10) :
                    cutoffTo;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (assignedUserIds) {
                localVarQueryParameter['assigned_user_ids'] = assignedUserIds;
            }

            if (sortBy !== undefined) {
                localVarQueryParameter['sort_by'] = sortBy;
            }

            if (sortOrder !== undefined) {
                localVarQueryParameter['sort_order'] = sortOrder;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            if (includeTotal !== undefined) {
                localVarQueryParameter['include_total'] = includeTotal;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Export
         * @param {string} bookingNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportExportsBookingNumberGet: async (bookingNumber: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bookingNumber' is not null or undefined
            assertParamExists('getExportExportsBookingNumberGet', 'bookingNumber', bookingNumber)
            const localVarPath = `/exports/{booking_number}`
                .replace(`{${"booking_number"}}`, encodeURIComponent(String(bookingNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Hold names for the Holds filter. Not the bookings on the current page.  Not limited to the terminal filter. That filter still picks which bookings show. The menu stays the org\'s full list, same as customers and shipping lines.
         * @summary Get Export Hold Labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportHoldLabelsExportsHoldLabelsGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/exports/hold_labels/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Exports
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportsExportsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportsExportsGet: async (bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportsExportsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/exports/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (bookingNumber !== undefined) {
                localVarQueryParameter['booking_number'] = bookingNumber;
            }

            if (terminal) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (customerIds) {
                localVarQueryParameter['customer_ids'] = customerIds;
            }

            if (shippingLine) {
                localVarQueryParameter['shipping_line'] = shippingLine;
            }

            if (containerType) {
                localVarQueryParameter['container_type'] = containerType;
            }

            if (cargoTypes) {
                localVarQueryParameter['cargo_types'] = cargoTypes;
            }

            if (holds) {
                localVarQueryParameter['holds'] = holds;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }

            if (isUntagged !== undefined) {
                localVarQueryParameter['is_untagged'] = isUntagged;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }

            if (lifecycleState) {
                localVarQueryParameter['lifecycle_state'] = lifecycleState;
            }

            if (portComplex) {
                localVarQueryParameter['port_complex'] = portComplex;
            }

            if (erdFrom !== undefined) {
                localVarQueryParameter['erd_from'] = (erdFrom as any instanceof Date) ?
                    (erdFrom as any).toISOString().substring(0,10) :
                    erdFrom;
            }

            if (erdTo !== undefined) {
                localVarQueryParameter['erd_to'] = (erdTo as any instanceof Date) ?
                    (erdTo as any).toISOString().substring(0,10) :
                    erdTo;
            }

            if (cutoffFrom !== undefined) {
                localVarQueryParameter['cutoff_from'] = (cutoffFrom as any instanceof Date) ?
                    (cutoffFrom as any).toISOString().substring(0,10) :
                    cutoffFrom;
            }

            if (cutoffTo !== undefined) {
                localVarQueryParameter['cutoff_to'] = (cutoffTo as any instanceof Date) ?
                    (cutoffTo as any).toISOString().substring(0,10) :
                    cutoffTo;
            }

            if (excludedCustomerIds) {
                localVarQueryParameter['excluded_customer_ids'] = excludedCustomerIds;
            }

            if (assignedUserIds) {
                localVarQueryParameter['assigned_user_ids'] = assignedUserIds;
            }

            if (sortBy !== undefined) {
                localVarQueryParameter['sort_by'] = sortBy;
            }

            if (sortOrder !== undefined) {
                localVarQueryParameter['sort_order'] = sortOrder;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            if (includeTotal !== undefined) {
                localVarQueryParameter['include_total'] = includeTotal;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update user-authored fields on one or more watched bookings. Returns the booking numbers that changed.
         * @summary Patch Exports
         * @param {PATCHExports} pATCHExports 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchExportsExportsPatch: async (pATCHExports: PATCHExports, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pATCHExports' is not null or undefined
            assertParamExists('patchExportsExportsPatch', 'pATCHExports', pATCHExports)
            const localVarPath = `/exports/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHExports, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stops tracking one or more export bookings. Deletes the watch row, stamping who archived it and why onto the history row the versioning trigger writes, so the audit trail survives. Silently skips booking numbers that aren\'t watched. Booking-number-centric mirror of `POST /containers/unwatch/`.
         * @summary Stop Watching Export Bookings
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        stopWatchingExportBookingsExportsUnwatchPost: async (requestBody: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'requestBody' is not null or undefined
            assertParamExists('stopWatchingExportBookingsExportsUnwatchPost', 'requestBody', requestBody)
            const localVarPath = `/exports/unwatch/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(requestBody, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Track export bookings. Can be called again with bookings that are already tracked: they are not duplicated, and they get the customer from this request. A booking that can\'t be tracked (invalid number, duplicate row, unknown customer) is returned in `errors`; the other rows in the same request are still tracked.
         * @summary Watch Export Bookings
         * @param {POSTExportBookingWatch} pOSTExportBookingWatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchExportBookingsExportsWatchPost: async (pOSTExportBookingWatch: POSTExportBookingWatch, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTExportBookingWatch' is not null or undefined
            assertParamExists('watchExportBookingsExportsWatchPost', 'pOSTExportBookingWatch', pOSTExportBookingWatch)
            const localVarPath = `/exports/watch/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTExportBookingWatch, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Supports upload via .csv or .xlsx file. When the first row is headers, the booking column is the one whose header contains \"booking\", and an optional `customer` column holds the customer name (matched ignoring case). Without a \"booking\" header, bookings are read from the first column. Same result as `POST /exports/watch/`.
         * @summary Watch Export Bookings Via Upload
         * @param {File} file 
         * @param {number} [customerId] Customer for every row whose &#x60;customer&#x60; cell is blank
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchExportBookingsViaUploadExportsWatchViaUploadPost: async (file: File, customerId?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('watchExportBookingsViaUploadExportsWatchViaUploadPost', 'file', file)
            const localVarPath = `/exports/watch/via_upload/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (customerId !== undefined) {
                localVarQueryParameter['customer_id'] = customerId;
            }


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ExportsApi - functional programming interface
 * @export
 */
export const ExportsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ExportsApiAxiosParamCreator(configuration)
    return {
        /**
         * Book multiple container-less empty pickups against one watched export booking.  Bulk by construction: `quantity` fans out into N independent workflows, each with its own request row, so a single bad unit fails only itself (409 if any failed).
         * @summary Book Empty Pickups
         * @param {string} bookingNumber 
         * @param {POSTEmptyPickupBooking} pOSTEmptyPickupBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber: string, pOSTEmptyPickupBooking: POSTEmptyPickupBooking, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkBookEmptyPickupResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber, pOSTEmptyPickupBooking, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Check bookings before adding them: whether each is found, not found, invalid or couldn\'t be checked yet (`pending`), whether the org already tracks it, and its containers on file. Always 200: the check is advisory, and adding must work whatever it says. Creates no watches. A booking anyone looked up in the last 30 minutes answers without a portal call.
         * @summary Check Export Bookings
         * @param {POSTExportBookingCheck} pOSTExportBookingCheck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck: POSTExportBookingCheck, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETExportBookingChecksResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.checkExportBookingsExportsBookingsCheckPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * This org\'s \"My Active Vessels\" for the Schedule board.  Separate from `/vessels/etas/`, which is also pushed to every org over a shared websocket and so can\'t carry per-org data.
         * @summary Get Active Vessel Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETActiveVesselSchedules>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getActiveVesselSchedulesExportsActiveVesselSchedulesGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Export Counts By State
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByStateExportsStateCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExportCountsByStateExportsStateCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByStateExportsStateCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETExportStateCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExportCountsByStateExportsStateCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getExportCountsByStateExportsStateCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Export Counts By Terminal
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETExportTerminalCount>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getExportCountsByTerminalExportsTerminalCountsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Export
         * @param {string} bookingNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExportExportsBookingNumberGet(bookingNumber: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExportExportsBookingNumberGet(bookingNumber, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getExportExportsBookingNumberGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Hold names for the Holds filter. Not the bookings on the current page.  Not limited to the terminal filter. That filter still picks which bookings show. The menu stays the org\'s full list, same as customers and shipping lines.
         * @summary Get Export Hold Labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExportHoldLabelsExportsHoldLabelsGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<string>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExportHoldLabelsExportsHoldLabelsGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getExportHoldLabelsExportsHoldLabelsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Exports
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportsExportsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExportsExportsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportsExportsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETExportsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExportsExportsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.getExportsExportsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update user-authored fields on one or more watched bookings. Returns the booking numbers that changed.
         * @summary Patch Exports
         * @param {PATCHExports} pATCHExports 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async patchExportsExportsPatch(pATCHExports: PATCHExports, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<string>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.patchExportsExportsPatch(pATCHExports, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.patchExportsExportsPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stops tracking one or more export bookings. Deletes the watch row, stamping who archived it and why onto the history row the versioning trigger writes, so the audit trail survives. Silently skips booking numbers that aren\'t watched. Booking-number-centric mirror of `POST /containers/unwatch/`.
         * @summary Stop Watching Export Bookings
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async stopWatchingExportBookingsExportsUnwatchPost(requestBody: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.stopWatchingExportBookingsExportsUnwatchPost(requestBody, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.stopWatchingExportBookingsExportsUnwatchPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Track export bookings. Can be called again with bookings that are already tracked: they are not duplicated, and they get the customer from this request. A booking that can\'t be tracked (invalid number, duplicate row, unknown customer) is returned in `errors`; the other rows in the same request are still tracked.
         * @summary Watch Export Bookings
         * @param {POSTExportBookingWatch} pOSTExportBookingWatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async watchExportBookingsExportsWatchPost(pOSTExportBookingWatch: POSTExportBookingWatch, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTWatchExportsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.watchExportBookingsExportsWatchPost(pOSTExportBookingWatch, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.watchExportBookingsExportsWatchPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Supports upload via .csv or .xlsx file. When the first row is headers, the booking column is the one whose header contains \"booking\", and an optional `customer` column holds the customer name (matched ignoring case). Without a \"booking\" header, bookings are read from the first column. Same result as `POST /exports/watch/`.
         * @summary Watch Export Bookings Via Upload
         * @param {File} file 
         * @param {number} [customerId] Customer for every row whose &#x60;customer&#x60; cell is blank
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async watchExportBookingsViaUploadExportsWatchViaUploadPost(file: File, customerId?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTWatchExportsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.watchExportBookingsViaUploadExportsWatchViaUploadPost(file, customerId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExportsApi.watchExportBookingsViaUploadExportsWatchViaUploadPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ExportsApi - factory interface
 * @export
 */
export const ExportsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ExportsApiFp(configuration)
    return {
        /**
         * Book multiple container-less empty pickups against one watched export booking.  Bulk by construction: `quantity` fans out into N independent workflows, each with its own request row, so a single bad unit fails only itself (409 if any failed).
         * @summary Book Empty Pickups
         * @param {string} bookingNumber 
         * @param {POSTEmptyPickupBooking} pOSTEmptyPickupBooking 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber: string, pOSTEmptyPickupBooking: POSTEmptyPickupBooking, options?: any): AxiosPromise<BulkBookEmptyPickupResponse> {
            return localVarFp.bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber, pOSTEmptyPickupBooking, options).then((request) => request(axios, basePath));
        },
        /**
         * Check bookings before adding them: whether each is found, not found, invalid or couldn\'t be checked yet (`pending`), whether the org already tracks it, and its containers on file. Always 200: the check is advisory, and adding must work whatever it says. Creates no watches. A booking anyone looked up in the last 30 minutes answers without a portal call.
         * @summary Check Export Bookings
         * @param {POSTExportBookingCheck} pOSTExportBookingCheck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck: POSTExportBookingCheck, options?: any): AxiosPromise<GETExportBookingChecksResponse> {
            return localVarFp.checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck, options).then((request) => request(axios, basePath));
        },
        /**
         * This org\'s \"My Active Vessels\" for the Schedule board.  Separate from `/vessels/etas/`, which is also pushed to every org over a shared websocket and so can\'t carry per-org data.
         * @summary Get Active Vessel Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<GETActiveVesselSchedules> {
            return localVarFp.getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Export Counts By State
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByStateExportsStateCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportCountsByStateExportsStateCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByStateExportsStateCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: any): AxiosPromise<Array<GETExportStateCount>> {
            return localVarFp.getExportCountsByStateExportsStateCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Export Counts By Terminal
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: any): AxiosPromise<Array<GETExportTerminalCount>> {
            return localVarFp.getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Export
         * @param {string} bookingNumber 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportExportsBookingNumberGet(bookingNumber: string, options?: any): AxiosPromise<GETExport> {
            return localVarFp.getExportExportsBookingNumberGet(bookingNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Hold names for the Holds filter. Not the bookings on the current page.  Not limited to the terminal filter. That filter still picks which bookings show. The menu stays the org\'s full list, same as customers and shipping lines.
         * @summary Get Export Hold Labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportHoldLabelsExportsHoldLabelsGet(options?: any): AxiosPromise<Array<string>> {
            return localVarFp.getExportHoldLabelsExportsHoldLabelsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Exports
         * @param {string} [bookingNumber] Search by booking number (partial match)
         * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
         * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
         * @param {Array<ShippingLine>} [shippingLine] 
         * @param {Array<ContainerType>} [containerType] 
         * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
         * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
         * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
         * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
         * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
         * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
         * @param {Array<PortComplex>} [portComplex] 
         * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
         * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
         * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
         * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
         * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
         * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
         * @param {ExportSortOptions} [sortBy] 
         * @param {GetExportsExportsGetSortOrderEnum} [sortOrder] 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
         * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExportsExportsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportsExportsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: any): AxiosPromise<GETExportsResponse> {
            return localVarFp.getExportsExportsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(axios, basePath));
        },
        /**
         * Update user-authored fields on one or more watched bookings. Returns the booking numbers that changed.
         * @summary Patch Exports
         * @param {PATCHExports} pATCHExports 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchExportsExportsPatch(pATCHExports: PATCHExports, options?: any): AxiosPromise<Array<string>> {
            return localVarFp.patchExportsExportsPatch(pATCHExports, options).then((request) => request(axios, basePath));
        },
        /**
         * Stops tracking one or more export bookings. Deletes the watch row, stamping who archived it and why onto the history row the versioning trigger writes, so the audit trail survives. Silently skips booking numbers that aren\'t watched. Booking-number-centric mirror of `POST /containers/unwatch/`.
         * @summary Stop Watching Export Bookings
         * @param {Array<string>} requestBody 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        stopWatchingExportBookingsExportsUnwatchPost(requestBody: Array<string>, options?: any): AxiosPromise<any> {
            return localVarFp.stopWatchingExportBookingsExportsUnwatchPost(requestBody, options).then((request) => request(axios, basePath));
        },
        /**
         * Track export bookings. Can be called again with bookings that are already tracked: they are not duplicated, and they get the customer from this request. A booking that can\'t be tracked (invalid number, duplicate row, unknown customer) is returned in `errors`; the other rows in the same request are still tracked.
         * @summary Watch Export Bookings
         * @param {POSTExportBookingWatch} pOSTExportBookingWatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchExportBookingsExportsWatchPost(pOSTExportBookingWatch: POSTExportBookingWatch, options?: any): AxiosPromise<POSTWatchExportsResponse> {
            return localVarFp.watchExportBookingsExportsWatchPost(pOSTExportBookingWatch, options).then((request) => request(axios, basePath));
        },
        /**
         * Supports upload via .csv or .xlsx file. When the first row is headers, the booking column is the one whose header contains \"booking\", and an optional `customer` column holds the customer name (matched ignoring case). Without a \"booking\" header, bookings are read from the first column. Same result as `POST /exports/watch/`.
         * @summary Watch Export Bookings Via Upload
         * @param {File} file 
         * @param {number} [customerId] Customer for every row whose &#x60;customer&#x60; cell is blank
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        watchExportBookingsViaUploadExportsWatchViaUploadPost(file: File, customerId?: number, options?: any): AxiosPromise<POSTWatchExportsResponse> {
            return localVarFp.watchExportBookingsViaUploadExportsWatchViaUploadPost(file, customerId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ExportsApi - object-oriented interface
 * @export
 * @class ExportsApi
 * @extends {BaseAPI}
 */
export class ExportsApi extends BaseAPI {
    /**
     * Book multiple container-less empty pickups against one watched export booking.  Bulk by construction: `quantity` fans out into N independent workflows, each with its own request row, so a single bad unit fails only itself (409 if any failed).
     * @summary Book Empty Pickups
     * @param {string} bookingNumber 
     * @param {POSTEmptyPickupBooking} pOSTEmptyPickupBooking 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber: string, pOSTEmptyPickupBooking: POSTEmptyPickupBooking, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).bookEmptyPickupsExportsBookingNumberBookEmptyPickupPost(bookingNumber, pOSTEmptyPickupBooking, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Check bookings before adding them: whether each is found, not found, invalid or couldn\'t be checked yet (`pending`), whether the org already tracks it, and its containers on file. Always 200: the check is advisory, and adding must work whatever it says. Creates no watches. A booking anyone looked up in the last 30 minutes answers without a portal call.
     * @summary Check Export Bookings
     * @param {POSTExportBookingCheck} pOSTExportBookingCheck 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck: POSTExportBookingCheck, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).checkExportBookingsExportsBookingsCheckPost(pOSTExportBookingCheck, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * This org\'s \"My Active Vessels\" for the Schedule board.  Separate from `/vessels/etas/`, which is also pushed to every org over a shared websocket and so can\'t carry per-org data.
     * @summary Get Active Vessel Schedules
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getActiveVesselSchedulesExportsActiveVesselSchedulesGet(portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Export Counts By State
     * @param {string} [bookingNumber] Search by booking number (partial match)
     * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
     * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
     * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
     * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
     * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
     * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
     * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
     * @param {Array<PortComplex>} [portComplex] 
     * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
     * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
     * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
     * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
     * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
     * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
     * @param {ExportSortOptions} [sortBy] 
     * @param {GetExportCountsByStateExportsStateCountsGetSortOrderEnum} [sortOrder] 
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
     * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getExportCountsByStateExportsStateCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByStateExportsStateCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getExportCountsByStateExportsStateCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Export Counts By Terminal
     * @param {string} [bookingNumber] Search by booking number (partial match)
     * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
     * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
     * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
     * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
     * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
     * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
     * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
     * @param {Array<PortComplex>} [portComplex] 
     * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
     * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
     * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
     * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
     * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
     * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
     * @param {ExportSortOptions} [sortBy] 
     * @param {GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum} [sortOrder] 
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
     * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getExportCountsByTerminalExportsTerminalCountsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Export
     * @param {string} bookingNumber 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getExportExportsBookingNumberGet(bookingNumber: string, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getExportExportsBookingNumberGet(bookingNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Hold names for the Holds filter. Not the bookings on the current page.  Not limited to the terminal filter. That filter still picks which bookings show. The menu stays the org\'s full list, same as customers and shipping lines.
     * @summary Get Export Hold Labels
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getExportHoldLabelsExportsHoldLabelsGet(options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getExportHoldLabelsExportsHoldLabelsGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Exports
     * @param {string} [bookingNumber] Search by booking number (partial match)
     * @param {Array<TerminalName>} [terminal] Limit to bookings with a status at one of these terminals. Container type and cargo type, when set, must be on one equipment line at one of these terminals. Holds are matched at these terminals only.
     * @param {Array<number>} [customerIds] Limit to bookings for one or more customers. Pass &#x60;-1&#x60; to include bookings with no customer. An empty list does not filter.
     * @param {Array<ShippingLine>} [shippingLine] 
     * @param {Array<ContainerType>} [containerType] 
     * @param {Array<ExportCargoType>} [cargoTypes] Limit to bookings with an equipment line of one of these cargo types. &#x60;general&#x60; means a line with no hazmat, reefer, or over-dimensional flag.
     * @param {Array<string>} [holds] Limit to bookings with an active hold whose label matches. The label is the hold reason code, or the description when there is no code.
     * @param {Array<string>} [tags] Limit to bookings with an appointment or container row that has all of these tags
     * @param {boolean} [isUntagged] Limit to bookings with no tags on any appointment or container row.
     * @param {Array<ExportWatchState>} [status] Limit to bookings in these lifecycle states
     * @param {Array<ExportBookingState>} [lifecycleState] Limit to bookings in these lifecycle states. Not the active or archived watch status.
     * @param {Array<PortComplex>} [portComplex] 
     * @param {string} [erdFrom] Limit to bookings whose earliest ERD is on or after this date. A booking with no ERD does not match.
     * @param {string} [erdTo] Limit to bookings whose earliest ERD is on or before this date.
     * @param {string} [cutoffFrom] Limit to bookings whose earliest cutoff falls on or after this Pacific calendar day. A booking with no cutoff does not match.
     * @param {string} [cutoffTo] Limit to bookings whose earliest cutoff falls on or before this Pacific calendar day.
     * @param {Array<number>} [excludedCustomerIds] Hide bookings for these customers. Pass &#x60;-1&#x60; to hide bookings with no customer. An empty list does not filter.
     * @param {Array<number>} [assignedUserIds] Limit to bookings with an appointment in the list window assigned to one of these users. Pass &#x60;-1&#x60; for an appointment with no assignee. A booking with no appointment in that window does not match.
     * @param {ExportSortOptions} [sortBy] 
     * @param {GetExportsExportsGetSortOrderEnum} [sortOrder] 
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {number} [offset] Skip this many bookings before returning results. When set, this replaces &#x60;page&#x60;.
     * @param {boolean} [includeTotal] When true, response &#x60;total&#x60; is the full match count. Default false so plain list loads skip the count. The list sets this true on the first bite of a page only.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public getExportsExportsGet(bookingNumber?: string, terminal?: Array<TerminalName>, customerIds?: Array<number>, shippingLine?: Array<ShippingLine>, containerType?: Array<ContainerType>, cargoTypes?: Array<ExportCargoType>, holds?: Array<string>, tags?: Array<string>, isUntagged?: boolean, status?: Array<ExportWatchState>, lifecycleState?: Array<ExportBookingState>, portComplex?: Array<PortComplex>, erdFrom?: string, erdTo?: string, cutoffFrom?: string, cutoffTo?: string, excludedCustomerIds?: Array<number>, assignedUserIds?: Array<number>, sortBy?: ExportSortOptions, sortOrder?: GetExportsExportsGetSortOrderEnum, page?: number, pageSize?: number, offset?: number, includeTotal?: boolean, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).getExportsExportsGet(bookingNumber, terminal, customerIds, shippingLine, containerType, cargoTypes, holds, tags, isUntagged, status, lifecycleState, portComplex, erdFrom, erdTo, cutoffFrom, cutoffTo, excludedCustomerIds, assignedUserIds, sortBy, sortOrder, page, pageSize, offset, includeTotal, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update user-authored fields on one or more watched bookings. Returns the booking numbers that changed.
     * @summary Patch Exports
     * @param {PATCHExports} pATCHExports 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public patchExportsExportsPatch(pATCHExports: PATCHExports, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).patchExportsExportsPatch(pATCHExports, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stops tracking one or more export bookings. Deletes the watch row, stamping who archived it and why onto the history row the versioning trigger writes, so the audit trail survives. Silently skips booking numbers that aren\'t watched. Booking-number-centric mirror of `POST /containers/unwatch/`.
     * @summary Stop Watching Export Bookings
     * @param {Array<string>} requestBody 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public stopWatchingExportBookingsExportsUnwatchPost(requestBody: Array<string>, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).stopWatchingExportBookingsExportsUnwatchPost(requestBody, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Track export bookings. Can be called again with bookings that are already tracked: they are not duplicated, and they get the customer from this request. A booking that can\'t be tracked (invalid number, duplicate row, unknown customer) is returned in `errors`; the other rows in the same request are still tracked.
     * @summary Watch Export Bookings
     * @param {POSTExportBookingWatch} pOSTExportBookingWatch 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public watchExportBookingsExportsWatchPost(pOSTExportBookingWatch: POSTExportBookingWatch, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).watchExportBookingsExportsWatchPost(pOSTExportBookingWatch, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Supports upload via .csv or .xlsx file. When the first row is headers, the booking column is the one whose header contains \"booking\", and an optional `customer` column holds the customer name (matched ignoring case). Without a \"booking\" header, bookings are read from the first column. Same result as `POST /exports/watch/`.
     * @summary Watch Export Bookings Via Upload
     * @param {File} file 
     * @param {number} [customerId] Customer for every row whose &#x60;customer&#x60; cell is blank
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExportsApi
     */
    public watchExportBookingsViaUploadExportsWatchViaUploadPost(file: File, customerId?: number, options?: RawAxiosRequestConfig) {
        return ExportsApiFp(this.configuration).watchExportBookingsViaUploadExportsWatchViaUploadPost(file, customerId, options).then((request) => request(this.axios, this.basePath));
    }
}

/**
 * @export
 */
export const GetExportCountsByStateExportsStateCountsGetSortOrderEnum = {
    Ascending: 'ascending',
    Descending: 'descending'
} as const;
export type GetExportCountsByStateExportsStateCountsGetSortOrderEnum = typeof GetExportCountsByStateExportsStateCountsGetSortOrderEnum[keyof typeof GetExportCountsByStateExportsStateCountsGetSortOrderEnum];
/**
 * @export
 */
export const GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum = {
    Ascending: 'ascending',
    Descending: 'descending'
} as const;
export type GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum = typeof GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum[keyof typeof GetExportCountsByTerminalExportsTerminalCountsGetSortOrderEnum];
/**
 * @export
 */
export const GetExportsExportsGetSortOrderEnum = {
    Ascending: 'ascending',
    Descending: 'descending'
} as const;
export type GetExportsExportsGetSortOrderEnum = typeof GetExportsExportsGetSortOrderEnum[keyof typeof GetExportsExportsGetSortOrderEnum];


/**
 * GateSchedulesApi - axios parameter creator
 * @export
 */
export const GateSchedulesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Used to get gate schedule entries for a specific terminal and reading
         * @summary Get Entries By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet: async (terminal: TerminalName, readingFirstObserved: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet', 'terminal', terminal)
            // verify required parameter 'readingFirstObserved' is not null or undefined
            assertParamExists('getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet', 'readingFirstObserved', readingFirstObserved)
            const localVarPath = `/gate_schedules/entries/by_terminal/{terminal}/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (readingFirstObserved !== undefined) {
                localVarQueryParameter['reading_first_observed'] = (readingFirstObserved as any instanceof Date) ?
                    (readingFirstObserved as any).toISOString() :
                    readingFirstObserved;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the most up to date
         * @summary Get Latest Gate Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestGateSchedulesGateSchedulesLatestGet: async (portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/gate_schedules/latest/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Load the most recent gate schedule readings
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestReadingsGateSchedulesReadingsLatestGet: async (portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/gate_schedules/readings/latest/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingFileGateSchedulesReadingsFilesPathGet: async (path: string, download?: boolean, filename?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'path' is not null or undefined
            assertParamExists('getReadingFileGateSchedulesReadingsFilesPathGet', 'path', path)
            const localVarPath = `/gate_schedules/readings/files/{path}`
                .replace(`{${"path"}}`, encodeURIComponent(String(path)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (download !== undefined) {
                localVarQueryParameter['download'] = download;
            }

            if (filename !== undefined) {
                localVarQueryParameter['filename'] = filename;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet: async (terminal: TerminalName, observedAfter?: string, observedBefore?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet', 'terminal', terminal)
            const localVarPath = `/gate_schedules/readings/{terminal}/history/`
                .replace(`{${"terminal"}}`, encodeURIComponent(String(terminal)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (observedAfter !== undefined) {
                localVarQueryParameter['observed_after'] = (observedAfter as any instanceof Date) ?
                    (observedAfter as any).toISOString() :
                    observedAfter;
            }

            if (observedBefore !== undefined) {
                localVarQueryParameter['observed_before'] = (observedBefore as any instanceof Date) ?
                    (observedBefore as any).toISOString() :
                    observedBefore;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * GateSchedulesApi - functional programming interface
 * @export
 */
export const GateSchedulesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = GateSchedulesApiAxiosParamCreator(configuration)
    return {
        /**
         * Used to get gate schedule entries for a specific terminal and reading
         * @summary Get Entries By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETCurrentTerminalGateScheduleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal, readingFirstObserved, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateSchedulesApi.getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the most up to date
         * @summary Get Latest Gate Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLatestGateSchedulesGateSchedulesLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETCurrentTerminalGateScheduleResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLatestGateSchedulesGateSchedulesLatestGet(portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateSchedulesApi.getLatestGateSchedulesGateSchedulesLatestGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Load the most recent gate schedule readings
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETGateScheduleReading>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateSchedulesApi.getLatestReadingsGateSchedulesReadingsLatestGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReadingFileGateSchedulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReadingFileGateSchedulesReadingsFilesPathGet(path, download, filename, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateSchedulesApi.getReadingFileGateSchedulesReadingsFilesPathGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETGateScheduleReading>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateSchedulesApi.getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * GateSchedulesApi - factory interface
 * @export
 */
export const GateSchedulesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = GateSchedulesApiFp(configuration)
    return {
        /**
         * Used to get gate schedule entries for a specific terminal and reading
         * @summary Get Entries By Terminal
         * @param {TerminalName} terminal 
         * @param {string} readingFirstObserved 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: any): AxiosPromise<GETCurrentTerminalGateScheduleResponse> {
            return localVarFp.getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal, readingFirstObserved, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the most up to date
         * @summary Get Latest Gate Schedules
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestGateSchedulesGateSchedulesLatestGet(portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETCurrentTerminalGateScheduleResponse>> {
            return localVarFp.getLatestGateSchedulesGateSchedulesLatestGet(portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * Load the most recent gate schedule readings
         * @summary Get Latest Readings
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETGateScheduleReading>> {
            return localVarFp.getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Reading File
         * @param {string} path 
         * @param {boolean} [download] 
         * @param {string} [filename] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingFileGateSchedulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: any): AxiosPromise<any> {
            return localVarFp.getReadingFileGateSchedulesReadingsFilesPathGet(path, download, filename, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Readings History
         * @param {TerminalName} terminal 
         * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
         * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: any): AxiosPromise<Array<GETGateScheduleReading>> {
            return localVarFp.getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * GateSchedulesApi - object-oriented interface
 * @export
 * @class GateSchedulesApi
 * @extends {BaseAPI}
 */
export class GateSchedulesApi extends BaseAPI {
    /**
     * Used to get gate schedule entries for a specific terminal and reading
     * @summary Get Entries By Terminal
     * @param {TerminalName} terminal 
     * @param {string} readingFirstObserved 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof GateSchedulesApi
     */
    public getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal: TerminalName, readingFirstObserved: string, options?: RawAxiosRequestConfig) {
        return GateSchedulesApiFp(this.configuration).getEntriesByTerminalGateSchedulesEntriesByTerminalTerminalGet(terminal, readingFirstObserved, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the most up to date
     * @summary Get Latest Gate Schedules
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof GateSchedulesApi
     */
    public getLatestGateSchedulesGateSchedulesLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return GateSchedulesApiFp(this.configuration).getLatestGateSchedulesGateSchedulesLatestGet(portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Load the most recent gate schedule readings
     * @summary Get Latest Readings
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof GateSchedulesApi
     */
    public getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return GateSchedulesApiFp(this.configuration).getLatestReadingsGateSchedulesReadingsLatestGet(portComplexes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Reading File
     * @param {string} path 
     * @param {boolean} [download] 
     * @param {string} [filename] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof GateSchedulesApi
     */
    public getReadingFileGateSchedulesReadingsFilesPathGet(path: string, download?: boolean, filename?: string, options?: RawAxiosRequestConfig) {
        return GateSchedulesApiFp(this.configuration).getReadingFileGateSchedulesReadingsFilesPathGet(path, download, filename, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Readings History
     * @param {TerminalName} terminal 
     * @param {string} [observedAfter] Only return readings that were observed at least once after or at this time. Defaults to 7 days ago. Can be max 100 days before &#x60;observed_before&#x60;
     * @param {string} [observedBefore] Only return readings that were observed at least once before or atthis time. Defaults to now. Can be max 100 days after &#x60;observed_after&#x60;
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof GateSchedulesApi
     */
    public getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal: TerminalName, observedAfter?: string, observedBefore?: string, options?: RawAxiosRequestConfig) {
        return GateSchedulesApiFp(this.configuration).getReadingsHistoryGateSchedulesReadingsTerminalHistoryGet(terminal, observedAfter, observedBefore, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * GateTransactionsApi - axios parameter creator
 * @export
 */
export const GateTransactionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get Gate Transactions
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGateTransactionsGateTransactionsGet: async (page?: number, pageSize?: number, containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/gate_transactions/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * GateTransactionsApi - functional programming interface
 * @export
 */
export const GateTransactionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = GateTransactionsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Get Gate Transactions
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getGateTransactionsGateTransactionsGet(page?: number, pageSize?: number, containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GateTransactionNoRawData>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getGateTransactionsGateTransactionsGet(page, pageSize, containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GateTransactionsApi.getGateTransactionsGateTransactionsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * GateTransactionsApi - factory interface
 * @export
 */
export const GateTransactionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = GateTransactionsApiFp(configuration)
    return {
        /**
         * 
         * @summary Get Gate Transactions
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGateTransactionsGateTransactionsGet(page?: number, pageSize?: number, containerNumbers?: string, options?: any): AxiosPromise<Array<GateTransactionNoRawData>> {
            return localVarFp.getGateTransactionsGateTransactionsGet(page, pageSize, containerNumbers, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * GateTransactionsApi - object-oriented interface
 * @export
 * @class GateTransactionsApi
 * @extends {BaseAPI}
 */
export class GateTransactionsApi extends BaseAPI {
    /**
     * 
     * @summary Get Gate Transactions
     * @param {number} [page] 
     * @param {number} [pageSize] 
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof GateTransactionsApi
     */
    public getGateTransactionsGateTransactionsGet(page?: number, pageSize?: number, containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return GateTransactionsApiFp(this.configuration).getGateTransactionsGateTransactionsGet(page, pageSize, containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * HiddenApi - axios parameter creator
 * @export
 */
export const HiddenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a list of criteria for network requests that should be sent to the [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post) endpoint.
         * @summary Get Allowed Requests
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllowedRequestsBrowserExtensionAllowedRequestsGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/browser_extension/allowed_requests/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersLineAndTypeContainersLineAndTypeGet: async (containerNumbers?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/containers/line_and_type/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (containerNumbers !== undefined) {
                localVarQueryParameter['container_numbers'] = containerNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHealthCheckHealthCheckGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/health_check/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHealthCheckHealthCheckGet_1: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/health_check/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Allows us to identify users are using the extension even if they are not sending any network traffic to [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post).
         * @summary Ping
         * @param {PingRequest} pingRequest 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        pingBrowserExtensionPingPost: async (pingRequest: PingRequest, userAgent?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pingRequest' is not null or undefined
            assertParamExists('pingBrowserExtensionPingPost', 'pingRequest', pingRequest)
            const localVarPath = `/browser_extension/ping/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (userAgent != null) {
                localVarHeaderParameter['user-agent'] = String(userAgent);
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Post Network Request
         * @param {POSTNetworkRequestBody} pOSTNetworkRequestBody 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postNetworkRequestBrowserExtensionIngestNetworkResponsePost: async (pOSTNetworkRequestBody: POSTNetworkRequestBody, userAgent?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTNetworkRequestBody' is not null or undefined
            assertParamExists('postNetworkRequestBrowserExtensionIngestNetworkResponsePost', 'pOSTNetworkRequestBody', pOSTNetworkRequestBody)
            const localVarPath = `/browser_extension/ingest_network_response/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (userAgent != null) {
                localVarHeaderParameter['user-agent'] = String(userAgent);
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTNetworkRequestBody, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Test Error
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testErrorTestErrorGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/test_error/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Test Error With Repo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testErrorWithRepoTestErrorWithRepoGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/test_error_with_repo/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * HiddenApi - functional programming interface
 * @export
 */
export const HiddenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = HiddenApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a list of criteria for network requests that should be sent to the [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post) endpoint.
         * @summary Get Allowed Requests
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllowedRequestsBrowserExtensionAllowedRequestsGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETNetworkRequestMatcher>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllowedRequestsBrowserExtensionAllowedRequestsGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.getAllowedRequestsBrowserExtensionAllowedRequestsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETContainerLineAndType>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.getContainersLineAndTypeContainersLineAndTypeGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHealthCheckHealthCheckGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHealthCheckHealthCheckGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.getHealthCheckHealthCheckGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHealthCheckHealthCheckGet_1(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHealthCheckHealthCheckGet_1(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.getHealthCheckHealthCheckGet_1']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Allows us to identify users are using the extension even if they are not sending any network traffic to [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post).
         * @summary Ping
         * @param {PingRequest} pingRequest 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async pingBrowserExtensionPingPost(pingRequest: PingRequest, userAgent?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.pingBrowserExtensionPingPost(pingRequest, userAgent, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.pingBrowserExtensionPingPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Post Network Request
         * @param {POSTNetworkRequestBody} pOSTNetworkRequestBody 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody: POSTNetworkRequestBody, userAgent?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTNetworkRequestResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody, userAgent, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.postNetworkRequestBrowserExtensionIngestNetworkResponsePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Test Error
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testErrorTestErrorGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testErrorTestErrorGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.testErrorTestErrorGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Test Error With Repo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testErrorWithRepoTestErrorWithRepoGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testErrorWithRepoTestErrorWithRepoGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HiddenApi.testErrorWithRepoTestErrorWithRepoGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * HiddenApi - factory interface
 * @export
 */
export const HiddenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = HiddenApiFp(configuration)
    return {
        /**
         * Returns a list of criteria for network requests that should be sent to the [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post) endpoint.
         * @summary Get Allowed Requests
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllowedRequestsBrowserExtensionAllowedRequestsGet(options?: any): AxiosPromise<Array<GETNetworkRequestMatcher>> {
            return localVarFp.getAllowedRequestsBrowserExtensionAllowedRequestsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
         * @summary Get Containers Line And Type
         * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: any): AxiosPromise<Array<GETContainerLineAndType>> {
            return localVarFp.getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHealthCheckHealthCheckGet(options?: any): AxiosPromise<any> {
            return localVarFp.getHealthCheckHealthCheckGet(options).then((request) => request(axios, basePath));
        },
        /**
         * For use by Kubernetes to identify when a pod is ready to serve traffic
         * @summary Get Health Check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHealthCheckHealthCheckGet_1(options?: any): AxiosPromise<any> {
            return localVarFp.getHealthCheckHealthCheckGet_1(options).then((request) => request(axios, basePath));
        },
        /**
         * Allows us to identify users are using the extension even if they are not sending any network traffic to [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post).
         * @summary Ping
         * @param {PingRequest} pingRequest 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        pingBrowserExtensionPingPost(pingRequest: PingRequest, userAgent?: string, options?: any): AxiosPromise<any> {
            return localVarFp.pingBrowserExtensionPingPost(pingRequest, userAgent, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Post Network Request
         * @param {POSTNetworkRequestBody} pOSTNetworkRequestBody 
         * @param {string} [userAgent] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody: POSTNetworkRequestBody, userAgent?: string, options?: any): AxiosPromise<POSTNetworkRequestResponse> {
            return localVarFp.postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody, userAgent, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Test Error
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testErrorTestErrorGet(options?: any): AxiosPromise<any> {
            return localVarFp.testErrorTestErrorGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Test Error With Repo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testErrorWithRepoTestErrorWithRepoGet(options?: any): AxiosPromise<any> {
            return localVarFp.testErrorWithRepoTestErrorWithRepoGet(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * HiddenApi - object-oriented interface
 * @export
 * @class HiddenApi
 * @extends {BaseAPI}
 */
export class HiddenApi extends BaseAPI {
    /**
     * Returns a list of criteria for network requests that should be sent to the [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post) endpoint.
     * @summary Get Allowed Requests
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public getAllowedRequestsBrowserExtensionAllowedRequestsGet(options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).getAllowedRequestsBrowserExtensionAllowedRequestsGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Internal helper for the UI, so it is hidden from the public API docs.  Looks up the newest known shipping line for containers, and their container type (preferring import statuses over appointments), searching import statuses and appointments from ALL organizations with no age limit. Meant as a fallback for when `/basic/` has no line or type for a container, for example an empty this org never tracked as an import.  Returns only the container number, line, type and when they were last seen, never anything org-specific. Containers with no known line or type are left out, so an unknown container gives an empty list rather than a 404. The values are suggestions: type is nearly always right but line is less reliable on old data.
     * @summary Get Containers Line And Type
     * @param {string} [containerNumbers] A list of container numbers. Can be comma or space separated
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers?: string, options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).getContainersLineAndTypeContainersLineAndTypeGet(containerNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * For use by Kubernetes to identify when a pod is ready to serve traffic
     * @summary Get Health Check
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public getHealthCheckHealthCheckGet(options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).getHealthCheckHealthCheckGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * For use by Kubernetes to identify when a pod is ready to serve traffic
     * @summary Get Health Check
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public getHealthCheckHealthCheckGet_1(options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).getHealthCheckHealthCheckGet_1(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Allows us to identify users are using the extension even if they are not sending any network traffic to [POST /browser_extension/ingest_network_response/](#operation/post_network_request_browser_extension_ingest_network_response__post).
     * @summary Ping
     * @param {PingRequest} pingRequest 
     * @param {string} [userAgent] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public pingBrowserExtensionPingPost(pingRequest: PingRequest, userAgent?: string, options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).pingBrowserExtensionPingPost(pingRequest, userAgent, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Post Network Request
     * @param {POSTNetworkRequestBody} pOSTNetworkRequestBody 
     * @param {string} [userAgent] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody: POSTNetworkRequestBody, userAgent?: string, options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).postNetworkRequestBrowserExtensionIngestNetworkResponsePost(pOSTNetworkRequestBody, userAgent, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Test Error
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public testErrorTestErrorGet(options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).testErrorTestErrorGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Test Error With Repo
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof HiddenApi
     */
    public testErrorWithRepoTestErrorWithRepoGet(options?: RawAxiosRequestConfig) {
        return HiddenApiFp(this.configuration).testErrorWithRepoTestErrorWithRepoGet(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * NotificationsApi - axios parameter creator
 * @export
 */
export const NotificationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns active global notifications visible to the caller\'s org.  Read `org_id` first. The cached load closes the repo, and the cache is one shared slot, so filtering inside it would hand one org\'s list to every other org for the TTL.
         * @summary Get Active Global Notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveGlobalNotificationsNotificationsGlobalGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/notifications/global/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * NotificationsApi - functional programming interface
 * @export
 */
export const NotificationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = NotificationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns active global notifications visible to the caller\'s org.  Read `org_id` first. The cached load closes the repo, and the cache is one shared slot, so filtering inside it would hand one org\'s list to every other org for the TTL.
         * @summary Get Active Global Notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActiveGlobalNotificationsNotificationsGlobalGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<Notification>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActiveGlobalNotificationsNotificationsGlobalGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationsApi.getActiveGlobalNotificationsNotificationsGlobalGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * NotificationsApi - factory interface
 * @export
 */
export const NotificationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = NotificationsApiFp(configuration)
    return {
        /**
         * Returns active global notifications visible to the caller\'s org.  Read `org_id` first. The cached load closes the repo, and the cache is one shared slot, so filtering inside it would hand one org\'s list to every other org for the TTL.
         * @summary Get Active Global Notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveGlobalNotificationsNotificationsGlobalGet(options?: any): AxiosPromise<Array<Notification>> {
            return localVarFp.getActiveGlobalNotificationsNotificationsGlobalGet(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * NotificationsApi - object-oriented interface
 * @export
 * @class NotificationsApi
 * @extends {BaseAPI}
 */
export class NotificationsApi extends BaseAPI {
    /**
     * Returns active global notifications visible to the caller\'s org.  Read `org_id` first. The cached load closes the repo, and the cache is one shared slot, so filtering inside it would hand one org\'s list to every other org for the TTL.
     * @summary Get Active Global Notifications
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NotificationsApi
     */
    public getActiveGlobalNotificationsNotificationsGlobalGet(options?: RawAxiosRequestConfig) {
        return NotificationsApiFp(this.configuration).getActiveGlobalNotificationsNotificationsGlobalGet(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * OrganizationsApi - axios parameter creator
 * @export
 */
export const OrganizationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Accept Organization Invite
         * @param {POSTAcceptCompanyInvite} pOSTAcceptCompanyInvite 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptOrganizationInviteOrganizationsActivatePost: async (pOSTAcceptCompanyInvite: POSTAcceptCompanyInvite, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAcceptCompanyInvite' is not null or undefined
            assertParamExists('acceptOrganizationInviteOrganizationsActivatePost', 'pOSTAcceptCompanyInvite', pOSTAcceptCompanyInvite)
            const localVarPath = `/organizations/activate/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAcceptCompanyInvite, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a billing session for the organization the user is currently authenticated with. The return_url is optional and is the url to redirect to after the billing session is complete.
         * @summary Create Billing Session
         * @param {string} [returnUrl] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBillingSessionOrganizationsBillingSessionPost: async (returnUrl?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/organizations/billing/session/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (returnUrl !== undefined) {
                localVarQueryParameter['return_url'] = returnUrl;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates preferences for the organization you are currently authenticated with.  Preferences can be fetched via [GET /users/current/info/](#operation/get_current_user_info_users_current_info__get)
         * @summary Update Organization Preferences
         * @param {GETOrgConfig} gETOrgConfig 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationPreferencesOrganizationsPreferencesPut: async (gETOrgConfig: GETOrgConfig, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'gETOrgConfig' is not null or undefined
            assertParamExists('updateOrganizationPreferencesOrganizationsPreferencesPut', 'gETOrgConfig', gETOrgConfig)
            const localVarPath = `/organizations/preferences/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gETOrgConfig, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * OrganizationsApi - functional programming interface
 * @export
 */
export const OrganizationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = OrganizationsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Accept Organization Invite
         * @param {POSTAcceptCompanyInvite} pOSTAcceptCompanyInvite 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite: POSTAcceptCompanyInvite, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTAcceptCompanyInviteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.acceptOrganizationInviteOrganizationsActivatePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a billing session for the organization the user is currently authenticated with. The return_url is optional and is the url to redirect to after the billing session is complete.
         * @summary Create Billing Session
         * @param {string} [returnUrl] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createBillingSessionOrganizationsBillingSessionPost(returnUrl?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTOrganizationBillingSession>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createBillingSessionOrganizationsBillingSessionPost(returnUrl, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.createBillingSessionOrganizationsBillingSessionPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates preferences for the organization you are currently authenticated with.  Preferences can be fetched via [GET /users/current/info/](#operation/get_current_user_info_users_current_info__get)
         * @summary Update Organization Preferences
         * @param {GETOrgConfig} gETOrgConfig 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig: GETOrgConfig, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.updateOrganizationPreferencesOrganizationsPreferencesPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * OrganizationsApi - factory interface
 * @export
 */
export const OrganizationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = OrganizationsApiFp(configuration)
    return {
        /**
         * 
         * @summary Accept Organization Invite
         * @param {POSTAcceptCompanyInvite} pOSTAcceptCompanyInvite 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite: POSTAcceptCompanyInvite, options?: any): AxiosPromise<POSTAcceptCompanyInviteResponse> {
            return localVarFp.acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a billing session for the organization the user is currently authenticated with. The return_url is optional and is the url to redirect to after the billing session is complete.
         * @summary Create Billing Session
         * @param {string} [returnUrl] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBillingSessionOrganizationsBillingSessionPost(returnUrl?: string, options?: any): AxiosPromise<POSTOrganizationBillingSession> {
            return localVarFp.createBillingSessionOrganizationsBillingSessionPost(returnUrl, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates preferences for the organization you are currently authenticated with.  Preferences can be fetched via [GET /users/current/info/](#operation/get_current_user_info_users_current_info__get)
         * @summary Update Organization Preferences
         * @param {GETOrgConfig} gETOrgConfig 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig: GETOrgConfig, options?: any): AxiosPromise<any> {
            return localVarFp.updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * OrganizationsApi - object-oriented interface
 * @export
 * @class OrganizationsApi
 * @extends {BaseAPI}
 */
export class OrganizationsApi extends BaseAPI {
    /**
     * 
     * @summary Accept Organization Invite
     * @param {POSTAcceptCompanyInvite} pOSTAcceptCompanyInvite 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApi
     */
    public acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite: POSTAcceptCompanyInvite, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).acceptOrganizationInviteOrganizationsActivatePost(pOSTAcceptCompanyInvite, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a billing session for the organization the user is currently authenticated with. The return_url is optional and is the url to redirect to after the billing session is complete.
     * @summary Create Billing Session
     * @param {string} [returnUrl] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApi
     */
    public createBillingSessionOrganizationsBillingSessionPost(returnUrl?: string, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).createBillingSessionOrganizationsBillingSessionPost(returnUrl, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates preferences for the organization you are currently authenticated with.  Preferences can be fetched via [GET /users/current/info/](#operation/get_current_user_info_users_current_info__get)
     * @summary Update Organization Preferences
     * @param {GETOrgConfig} gETOrgConfig 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApi
     */
    public updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig: GETOrgConfig, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).updateOrganizationPreferencesOrganizationsPreferencesPut(gETOrgConfig, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PortalMetadataApi - axios parameter creator
 * @export
 */
export const PortalMetadataApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get Portal Metadata
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalMetadataPortalMetadataGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/portal_metadata/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalMetadataApi - functional programming interface
 * @export
 */
export const PortalMetadataApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalMetadataApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Get Portal Metadata
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPortalMetadataPortalMetadataGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETPortalMetadata>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPortalMetadataPortalMetadataGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalMetadataApi.getPortalMetadataPortalMetadataGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalMetadataApi - factory interface
 * @export
 */
export const PortalMetadataApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalMetadataApiFp(configuration)
    return {
        /**
         * 
         * @summary Get Portal Metadata
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalMetadataPortalMetadataGet(options?: any): AxiosPromise<Array<GETPortalMetadata>> {
            return localVarFp.getPortalMetadataPortalMetadataGet(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalMetadataApi - object-oriented interface
 * @export
 * @class PortalMetadataApi
 * @extends {BaseAPI}
 */
export class PortalMetadataApi extends BaseAPI {
    /**
     * 
     * @summary Get Portal Metadata
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortalMetadataApi
     */
    public getPortalMetadataPortalMetadataGet(options?: RawAxiosRequestConfig) {
        return PortalMetadataApiFp(this.configuration).getPortalMetadataPortalMetadataGet(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * TestModeApi - axios parameter creator
 * @export
 */
export const TestModeApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Update mutable fields on an appointment owned by this test-mode org.  Only fields included in the request body are modified; omitted fields retain their current values. The primary use case is advancing the appointment `status` to simulate lifecycle transitions (e.g. SCHEDULED → IN_PROGRESS → COMPLETED).
         * @summary Update Test Mode Appointment
         * @param {number} appointmentId 
         * @param {PATCHTestModeAppointment} pATCHTestModeAppointment 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch: async (appointmentId: number, pATCHTestModeAppointment: PATCHTestModeAppointment, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch', 'appointmentId', appointmentId)
            // verify required parameter 'pATCHTestModeAppointment' is not null or undefined
            assertParamExists('updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch', 'pATCHTestModeAppointment', pATCHTestModeAppointment)
            const localVarPath = `/test_mode/appointments/{appointment_id}/`
                .replace(`{${"appointment_id"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pATCHTestModeAppointment, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TestModeApi - functional programming interface
 * @export
 */
export const TestModeApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TestModeApiAxiosParamCreator(configuration)
    return {
        /**
         * Update mutable fields on an appointment owned by this test-mode org.  Only fields included in the request body are modified; omitted fields retain their current values. The primary use case is advancing the appointment `status` to simulate lifecycle transitions (e.g. SCHEDULED → IN_PROGRESS → COMPLETED).
         * @summary Update Test Mode Appointment
         * @param {number} appointmentId 
         * @param {PATCHTestModeAppointment} pATCHTestModeAppointment 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId: number, pATCHTestModeAppointment: PATCHTestModeAppointment, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETAppointment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId, pATCHTestModeAppointment, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TestModeApi.updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TestModeApi - factory interface
 * @export
 */
export const TestModeApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TestModeApiFp(configuration)
    return {
        /**
         * Update mutable fields on an appointment owned by this test-mode org.  Only fields included in the request body are modified; omitted fields retain their current values. The primary use case is advancing the appointment `status` to simulate lifecycle transitions (e.g. SCHEDULED → IN_PROGRESS → COMPLETED).
         * @summary Update Test Mode Appointment
         * @param {number} appointmentId 
         * @param {PATCHTestModeAppointment} pATCHTestModeAppointment 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId: number, pATCHTestModeAppointment: PATCHTestModeAppointment, options?: any): AxiosPromise<GETAppointment> {
            return localVarFp.updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId, pATCHTestModeAppointment, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * TestModeApi - object-oriented interface
 * @export
 * @class TestModeApi
 * @extends {BaseAPI}
 */
export class TestModeApi extends BaseAPI {
    /**
     * Update mutable fields on an appointment owned by this test-mode org.  Only fields included in the request body are modified; omitted fields retain their current values. The primary use case is advancing the appointment `status` to simulate lifecycle transitions (e.g. SCHEDULED → IN_PROGRESS → COMPLETED).
     * @summary Update Test Mode Appointment
     * @param {number} appointmentId 
     * @param {PATCHTestModeAppointment} pATCHTestModeAppointment 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TestModeApi
     */
    public updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId: number, pATCHTestModeAppointment: PATCHTestModeAppointment, options?: RawAxiosRequestConfig) {
        return TestModeApiFp(this.configuration).updateTestModeAppointmentTestModeAppointmentsAppointmentIdPatch(appointmentId, pATCHTestModeAppointment, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * TrucksApi - axios parameter creator
 * @export
 */
export const TrucksApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create new trucks or update existing ones
         * @summary Create Trucks
         * @param {Array<POSTTruck>} pOSTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTrucksTrucksPost: async (pOSTTruck: Array<POSTTruck>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTTruck' is not null or undefined
            assertParamExists('createTrucksTrucksPost', 'pOSTTruck', pOSTTruck)
            const localVarPath = `/trucks/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTruck, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Allows updating a truck\'s name, archived state, and green/EV status.
         * @summary Edit Truck
         * @param {Array<PUTTruck>} pUTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editTruckTrucksPut: async (pUTTruck: Array<PUTTruck>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pUTTruck' is not null or undefined
            assertParamExists('editTruckTrucksPut', 'pUTTruck', pUTTruck)
            const localVarPath = `/trucks/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pUTTruck, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Trucks
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrucksTrucksGet: async (includeArchived?: boolean, ignoreInvalidPlates?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/trucks/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (includeArchived !== undefined) {
                localVarQueryParameter['include_archived'] = includeArchived;
            }

            if (ignoreInvalidPlates !== undefined) {
                localVarQueryParameter['ignore_invalid_plates'] = ignoreInvalidPlates;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Fetches license plates that are linked to some appointments along with various stats. Trucks with stats that are all zero will not be included in results.  Orders by num_completed descending
         * @summary Get Trucks With Stats
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {TerminalName} [terminal] 
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrucksWithStatsTrucksWithStatsGet: async (after: string, terminal?: TerminalName, includeArchived?: boolean, ignoreInvalidPlates?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'after' is not null or undefined
            assertParamExists('getTrucksWithStatsTrucksWithStatsGet', 'after', after)
            const localVarPath = `/trucks/with_stats/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (includeArchived !== undefined) {
                localVarQueryParameter['include_archived'] = includeArchived;
            }

            if (ignoreInvalidPlates !== undefined) {
                localVarQueryParameter['ignore_invalid_plates'] = ignoreInvalidPlates;
            }

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add or update trucks via spreadsheet upload. Returns the updated trucks that were passed, will not return existing trucks *not passed*.  Spreadsheet must contain columns with headers \'License Plate\' and \'Truck Name\'.
         * @summary Upload Trucks
         * @param {File} file 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadTrucksTrucksUploadPost: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadTrucksTrucksUploadPost', 'file', file)
            const localVarPath = `/trucks/upload/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TrucksApi - functional programming interface
 * @export
 */
export const TrucksApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TrucksApiAxiosParamCreator(configuration)
    return {
        /**
         * Create new trucks or update existing ones
         * @summary Create Trucks
         * @param {Array<POSTTruck>} pOSTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createTrucksTrucksPost(pOSTTruck: Array<POSTTruck>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruck>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createTrucksTrucksPost(pOSTTruck, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TrucksApi.createTrucksTrucksPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Allows updating a truck\'s name, archived state, and green/EV status.
         * @summary Edit Truck
         * @param {Array<PUTTruck>} pUTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async editTruckTrucksPut(pUTTruck: Array<PUTTruck>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruck>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.editTruckTrucksPut(pUTTruck, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TrucksApi.editTruckTrucksPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Trucks
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTrucksTrucksGet(includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruck>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTrucksTrucksGet(includeArchived, ignoreInvalidPlates, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TrucksApi.getTrucksTrucksGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Fetches license plates that are linked to some appointments along with various stats. Trucks with stats that are all zero will not be included in results.  Orders by num_completed descending
         * @summary Get Trucks With Stats
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {TerminalName} [terminal] 
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTrucksWithStatsTrucksWithStatsGet(after: string, terminal?: TerminalName, includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruckWithStats>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTrucksWithStatsTrucksWithStatsGet(after, terminal, includeArchived, ignoreInvalidPlates, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TrucksApi.getTrucksWithStatsTrucksWithStatsGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Add or update trucks via spreadsheet upload. Returns the updated trucks that were passed, will not return existing trucks *not passed*.  Spreadsheet must contain columns with headers \'License Plate\' and \'Truck Name\'.
         * @summary Upload Trucks
         * @param {File} file 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadTrucksTrucksUploadPost(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETTruck>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadTrucksTrucksUploadPost(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TrucksApi.uploadTrucksTrucksUploadPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TrucksApi - factory interface
 * @export
 */
export const TrucksApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TrucksApiFp(configuration)
    return {
        /**
         * Create new trucks or update existing ones
         * @summary Create Trucks
         * @param {Array<POSTTruck>} pOSTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTrucksTrucksPost(pOSTTruck: Array<POSTTruck>, options?: any): AxiosPromise<Array<GETTruck>> {
            return localVarFp.createTrucksTrucksPost(pOSTTruck, options).then((request) => request(axios, basePath));
        },
        /**
         * Allows updating a truck\'s name, archived state, and green/EV status.
         * @summary Edit Truck
         * @param {Array<PUTTruck>} pUTTruck 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        editTruckTrucksPut(pUTTruck: Array<PUTTruck>, options?: any): AxiosPromise<Array<GETTruck>> {
            return localVarFp.editTruckTrucksPut(pUTTruck, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Trucks
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrucksTrucksGet(includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: any): AxiosPromise<Array<GETTruck>> {
            return localVarFp.getTrucksTrucksGet(includeArchived, ignoreInvalidPlates, options).then((request) => request(axios, basePath));
        },
        /**
         * Fetches license plates that are linked to some appointments along with various stats. Trucks with stats that are all zero will not be included in results.  Orders by num_completed descending
         * @summary Get Trucks With Stats
         * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
         * @param {TerminalName} [terminal] 
         * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
         * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrucksWithStatsTrucksWithStatsGet(after: string, terminal?: TerminalName, includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: any): AxiosPromise<Array<GETTruckWithStats>> {
            return localVarFp.getTrucksWithStatsTrucksWithStatsGet(after, terminal, includeArchived, ignoreInvalidPlates, options).then((request) => request(axios, basePath));
        },
        /**
         * Add or update trucks via spreadsheet upload. Returns the updated trucks that were passed, will not return existing trucks *not passed*.  Spreadsheet must contain columns with headers \'License Plate\' and \'Truck Name\'.
         * @summary Upload Trucks
         * @param {File} file 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadTrucksTrucksUploadPost(file: File, options?: any): AxiosPromise<Array<GETTruck>> {
            return localVarFp.uploadTrucksTrucksUploadPost(file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * TrucksApi - object-oriented interface
 * @export
 * @class TrucksApi
 * @extends {BaseAPI}
 */
export class TrucksApi extends BaseAPI {
    /**
     * Create new trucks or update existing ones
     * @summary Create Trucks
     * @param {Array<POSTTruck>} pOSTTruck 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TrucksApi
     */
    public createTrucksTrucksPost(pOSTTruck: Array<POSTTruck>, options?: RawAxiosRequestConfig) {
        return TrucksApiFp(this.configuration).createTrucksTrucksPost(pOSTTruck, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Allows updating a truck\'s name, archived state, and green/EV status.
     * @summary Edit Truck
     * @param {Array<PUTTruck>} pUTTruck 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TrucksApi
     */
    public editTruckTrucksPut(pUTTruck: Array<PUTTruck>, options?: RawAxiosRequestConfig) {
        return TrucksApiFp(this.configuration).editTruckTrucksPut(pUTTruck, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Trucks
     * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
     * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TrucksApi
     */
    public getTrucksTrucksGet(includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: RawAxiosRequestConfig) {
        return TrucksApiFp(this.configuration).getTrucksTrucksGet(includeArchived, ignoreInvalidPlates, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Fetches license plates that are linked to some appointments along with various stats. Trucks with stats that are all zero will not be included in results.  Orders by num_completed descending
     * @summary Get Trucks With Stats
     * @param {string} after Only consider appointments booked after this time when collecting past license plate uses
     * @param {TerminalName} [terminal] 
     * @param {boolean} [includeArchived] Include inactive (archived) trucks in the response. Defaults to False.
     * @param {boolean} [ignoreInvalidPlates] Ignore trucks which do not have between 6 and 7 characters. California commercial license plates are 7 characters and most other states are 6 or 7 characters. When enabled, this parameter filters out trucks with non-compliant plate lengths. Defaults to True.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TrucksApi
     */
    public getTrucksWithStatsTrucksWithStatsGet(after: string, terminal?: TerminalName, includeArchived?: boolean, ignoreInvalidPlates?: boolean, options?: RawAxiosRequestConfig) {
        return TrucksApiFp(this.configuration).getTrucksWithStatsTrucksWithStatsGet(after, terminal, includeArchived, ignoreInvalidPlates, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add or update trucks via spreadsheet upload. Returns the updated trucks that were passed, will not return existing trucks *not passed*.  Spreadsheet must contain columns with headers \'License Plate\' and \'Truck Name\'.
     * @summary Upload Trucks
     * @param {File} file 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TrucksApi
     */
    public uploadTrucksTrucksUploadPost(file: File, options?: RawAxiosRequestConfig) {
        return TrucksApiFp(this.configuration).uploadTrucksTrucksUploadPost(file, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UsersApi - axios parameter creator
 * @export
 */
export const UsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Use `/accept_invites/` instead
         * @summary Accept Invite
         * @param {POSTAcceptInvite} pOSTAcceptInvite 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        acceptInviteUsersAcceptInvitePost: async (pOSTAcceptInvite: POSTAcceptInvite, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAcceptInvite' is not null or undefined
            assertParamExists('acceptInviteUsersAcceptInvitePost', 'pOSTAcceptInvite', pOSTAcceptInvite)
            const localVarPath = `/users/accept_invite/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAcceptInvite, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Activation for existing user who has been invited to one or more organizations
         * @summary Accept Invites
         * @param {POSTAcceptInvites} pOSTAcceptInvites 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptInvitesUsersAcceptInvitesPost: async (pOSTAcceptInvites: POSTAcceptInvites, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTAcceptInvites' is not null or undefined
            assertParamExists('acceptInvitesUsersAcceptInvitesPost', 'pOSTAcceptInvites', pOSTAcceptInvites)
            const localVarPath = `/users/accept_invites/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTAcceptInvites, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Activation for new users
         * @summary Activate User
         * @param {POSTActivateUser} pOSTActivateUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateUserUsersActivatePost: async (pOSTActivateUser: POSTActivateUser, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTActivateUser' is not null or undefined
            assertParamExists('activateUserUsersActivatePost', 'pOSTActivateUser', pOSTActivateUser)
            const localVarPath = `/users/activate/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTActivateUser, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deactivate User
         * @param {number} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateUserUsersDeactivateUserUserIdPatch: async (userId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('deactivateUserUsersDeactivateUserUserIdPatch', 'userId', userId)
            const localVarPath = `/users/deactivate_user/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns information about the current user
         * @summary Get Current User Info
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUserInfoUsersCurrentInfoGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/users/current/info/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get Token For Pusher
         * @param {string} channelName 
         * @param {string} socketId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenForPusherUsersTokenForPusherPost: async (channelName: string, socketId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'channelName' is not null or undefined
            assertParamExists('getTokenForPusherUsersTokenForPusherPost', 'channelName', channelName)
            // verify required parameter 'socketId' is not null or undefined
            assertParamExists('getTokenForPusherUsersTokenForPusherPost', 'socketId', socketId)
            const localVarPath = `/users/token_for_pusher/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new URLSearchParams();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


            if (channelName !== undefined) { 
                localVarFormParams.set('channel_name', channelName as any);
            }
    
            if (socketId !== undefined) { 
                localVarFormParams.set('socket_id', socketId as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'application/x-www-form-urlencoded';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams.toString();

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token
         * @param {BodyGetTokenUsersTokenPost} bodyGetTokenUsersTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenUsersTokenPost: async (bodyGetTokenUsersTokenPost: BodyGetTokenUsersTokenPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyGetTokenUsersTokenPost' is not null or undefined
            assertParamExists('getTokenUsersTokenPost', 'bodyGetTokenUsersTokenPost', bodyGetTokenUsersTokenPost)
            const localVarPath = `/users/token/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyGetTokenUsersTokenPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get a login token using a user name and password.  This is really only used for the Swagger UI (/docs), since we use the /token/ endpoint for the actual login flow and this does not support selecting a specific organization.
         * @summary Get Token Via Form
         * @param {string} username 
         * @param {string} password 
         * @param {string} [grantType] 
         * @param {string} [scope] 
         * @param {string} [clientId] 
         * @param {string} [clientSecret] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaFormUsersTokenViaFormPost: async (username: string, password: string, grantType?: string, scope?: string, clientId?: string, clientSecret?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'username' is not null or undefined
            assertParamExists('getTokenViaFormUsersTokenViaFormPost', 'username', username)
            // verify required parameter 'password' is not null or undefined
            assertParamExists('getTokenViaFormUsersTokenViaFormPost', 'password', password)
            const localVarPath = `/users/token_via_form/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new URLSearchParams();

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


            if (grantType !== undefined) { 
                localVarFormParams.set('grant_type', grantType as any);
            }
    
            if (username !== undefined) { 
                localVarFormParams.set('username', username as any);
            }
    
            if (password !== undefined) { 
                localVarFormParams.set('password', password as any);
            }
    
            if (scope !== undefined) { 
                localVarFormParams.set('scope', scope as any);
            }
    
            if (clientId !== undefined) { 
                localVarFormParams.set('client_id', clientId as any);
            }
    
            if (clientSecret !== undefined) { 
                localVarFormParams.set('client_secret', clientSecret as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'application/x-www-form-urlencoded';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams.toString();

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token Via Port Optimizer Token
         * @param {POSTTokenViaPortOptimizerToken} pOSTTokenViaPortOptimizerToken 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost: async (pOSTTokenViaPortOptimizerToken: POSTTokenViaPortOptimizerToken, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTTokenViaPortOptimizerToken' is not null or undefined
            assertParamExists('getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost', 'pOSTTokenViaPortOptimizerToken', pOSTTokenViaPortOptimizerToken)
            const localVarPath = `/users/token_via_port_optimizer_token/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTTokenViaPortOptimizerToken, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Use a token to get a new token, most likely for a different organization
         * @summary Get Token Via Token
         * @param {BodyGetTokenViaTokenUsersTokenViaTokenPost} bodyGetTokenViaTokenUsersTokenViaTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaTokenUsersTokenViaTokenPost: async (bodyGetTokenViaTokenUsersTokenViaTokenPost: BodyGetTokenViaTokenUsersTokenViaTokenPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyGetTokenViaTokenUsersTokenViaTokenPost' is not null or undefined
            assertParamExists('getTokenViaTokenUsersTokenViaTokenPost', 'bodyGetTokenViaTokenUsersTokenViaTokenPost', bodyGetTokenViaTokenUsersTokenViaTokenPost)
            const localVarPath = `/users/token_via_token/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyGetTokenViaTokenUsersTokenViaTokenPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get User Setup Info
         * @param {BodyGetUserSetupInfoUsersSetupInfoPost} bodyGetUserSetupInfoUsersSetupInfoPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserSetupInfoUsersSetupInfoPost: async (bodyGetUserSetupInfoUsersSetupInfoPost: BodyGetUserSetupInfoUsersSetupInfoPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyGetUserSetupInfoUsersSetupInfoPost' is not null or undefined
            assertParamExists('getUserSetupInfoUsersSetupInfoPost', 'bodyGetUserSetupInfoUsersSetupInfoPost', bodyGetUserSetupInfoUsersSetupInfoPost)
            const localVarPath = `/users/setup_info/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyGetUserSetupInfoUsersSetupInfoPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List users for the current user\'s organization
         * @summary Get Users
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersUsersGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/users/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Invite Users
         * @param {Array<POSTInviteUser>} pOSTInviteUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        inviteUsersUsersInvitePost: async (pOSTInviteUser: Array<POSTInviteUser>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTInviteUser' is not null or undefined
            assertParamExists('inviteUsersUsersInvitePost', 'pOSTInviteUser', pOSTInviteUser)
            const localVarPath = `/users/invite/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTInviteUser, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Resend User Signup Email
         * @param {BodyResendUserSignupEmailUsersResendSignupEmailPost} bodyResendUserSignupEmailUsersResendSignupEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendUserSignupEmailUsersResendSignupEmailPost: async (bodyResendUserSignupEmailUsersResendSignupEmailPost: BodyResendUserSignupEmailUsersResendSignupEmailPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyResendUserSignupEmailUsersResendSignupEmailPost' is not null or undefined
            assertParamExists('resendUserSignupEmailUsersResendSignupEmailPost', 'bodyResendUserSignupEmailUsersResendSignupEmailPost', bodyResendUserSignupEmailUsersResendSignupEmailPost)
            const localVarPath = `/users/resend_signup_email/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyResendUserSignupEmailUsersResendSignupEmailPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Reset Password
         * @param {BodyResetPasswordUsersResetPasswordPost} bodyResetPasswordUsersResetPasswordPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetPasswordUsersResetPasswordPost: async (bodyResetPasswordUsersResetPasswordPost: BodyResetPasswordUsersResetPasswordPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodyResetPasswordUsersResetPasswordPost' is not null or undefined
            assertParamExists('resetPasswordUsersResetPasswordPost', 'bodyResetPasswordUsersResetPasswordPost', bodyResetPasswordUsersResetPasswordPost)
            const localVarPath = `/users/reset_password/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyResetPasswordUsersResetPasswordPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Send Reset Password Email
         * @param {BodySendResetPasswordEmailUsersSendResetPasswordEmailPost} bodySendResetPasswordEmailUsersSendResetPasswordEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendResetPasswordEmailUsersSendResetPasswordEmailPost: async (bodySendResetPasswordEmailUsersSendResetPasswordEmailPost: BodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bodySendResetPasswordEmailUsersSendResetPasswordEmailPost' is not null or undefined
            assertParamExists('sendResetPasswordEmailUsersSendResetPasswordEmailPost', 'bodySendResetPasswordEmailUsersSendResetPasswordEmailPost', bodySendResetPasswordEmailUsersSendResetPasswordEmailPost)
            const localVarPath = `/users/send_reset_password_email/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates user preferences that are editable
         * @summary Update Current User Preferences
         * @param {UserPreferences} userPreferences 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentUserPreferencesUsersCurrentPreferencesPut: async (userPreferences: UserPreferences, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userPreferences' is not null or undefined
            assertParamExists('updateCurrentUserPreferencesUsersCurrentPreferencesPut', 'userPreferences', userPreferences)
            const localVarPath = `/users/current/preferences/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userPreferences, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update user info
         * @summary Update User Info
         * @param {number} userId 
         * @param {BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch} bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserInfoUsersUpdateUserInfoUserIdPatch: async (userId: number, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch: BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('updateUserInfoUsersUpdateUserInfoUserIdPatch', 'userId', userId)
            // verify required parameter 'bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch' is not null or undefined
            assertParamExists('updateUserInfoUsersUpdateUserInfoUserIdPatch', 'bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch', bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch)
            const localVarPath = `/users/update_user_info/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UsersApi - functional programming interface
 * @export
 */
export const UsersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UsersApiAxiosParamCreator(configuration)
    return {
        /**
         * Use `/accept_invites/` instead
         * @summary Accept Invite
         * @param {POSTAcceptInvite} pOSTAcceptInvite 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite: POSTAcceptInvite, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.acceptInviteUsersAcceptInvitePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Activation for existing user who has been invited to one or more organizations
         * @summary Accept Invites
         * @param {POSTAcceptInvites} pOSTAcceptInvites 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites: POSTAcceptInvites, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.acceptInvitesUsersAcceptInvitesPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Activation for new users
         * @summary Activate User
         * @param {POSTActivateUser} pOSTActivateUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activateUserUsersActivatePost(pOSTActivateUser: POSTActivateUser, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activateUserUsersActivatePost(pOSTActivateUser, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.activateUserUsersActivatePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deactivate User
         * @param {number} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateUserUsersDeactivateUserUserIdPatch(userId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateUserUsersDeactivateUserUserIdPatch(userId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.deactivateUserUsersDeactivateUserUserIdPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns information about the current user
         * @summary Get Current User Info
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentUserInfoUsersCurrentInfoGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETUserInfo>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentUserInfoUsersCurrentInfoGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getCurrentUserInfoUsersCurrentInfoGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get Token For Pusher
         * @param {string} channelName 
         * @param {string} socketId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTokenForPusherUsersTokenForPusherPost(channelName: string, socketId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTokenForPusherUsersTokenForPusherPost(channelName, socketId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getTokenForPusherUsersTokenForPusherPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token
         * @param {BodyGetTokenUsersTokenPost} bodyGetTokenUsersTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTokenUsersTokenPost(bodyGetTokenUsersTokenPost: BodyGetTokenUsersTokenPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTokenUsersTokenPost(bodyGetTokenUsersTokenPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getTokenUsersTokenPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get a login token using a user name and password.  This is really only used for the Swagger UI (/docs), since we use the /token/ endpoint for the actual login flow and this does not support selecting a specific organization.
         * @summary Get Token Via Form
         * @param {string} username 
         * @param {string} password 
         * @param {string} [grantType] 
         * @param {string} [scope] 
         * @param {string} [clientId] 
         * @param {string} [clientSecret] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTokenViaFormUsersTokenViaFormPost(username: string, password: string, grantType?: string, scope?: string, clientId?: string, clientSecret?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTokenViaFormUsersTokenViaFormPost(username, password, grantType, scope, clientId, clientSecret, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getTokenViaFormUsersTokenViaFormPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token Via Port Optimizer Token
         * @param {POSTTokenViaPortOptimizerToken} pOSTTokenViaPortOptimizerToken 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken: POSTTokenViaPortOptimizerToken, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Use a token to get a new token, most likely for a different organization
         * @summary Get Token Via Token
         * @param {BodyGetTokenViaTokenUsersTokenViaTokenPost} bodyGetTokenViaTokenUsersTokenViaTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost: BodyGetTokenViaTokenUsersTokenViaTokenPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GETToken>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getTokenViaTokenUsersTokenViaTokenPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get User Setup Info
         * @param {BodyGetUserSetupInfoUsersSetupInfoPost} bodyGetUserSetupInfoUsersSetupInfoPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost: BodyGetUserSetupInfoUsersSetupInfoPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTSetUpUserResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getUserSetupInfoUsersSetupInfoPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List users for the current user\'s organization
         * @summary Get Users
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUsersUsersGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETUser>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUsersUsersGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getUsersUsersGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Invite Users
         * @param {Array<POSTInviteUser>} pOSTInviteUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async inviteUsersUsersInvitePost(pOSTInviteUser: Array<POSTInviteUser>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.inviteUsersUsersInvitePost(pOSTInviteUser, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.inviteUsersUsersInvitePost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Resend User Signup Email
         * @param {BodyResendUserSignupEmailUsersResendSignupEmailPost} bodyResendUserSignupEmailUsersResendSignupEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost: BodyResendUserSignupEmailUsersResendSignupEmailPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<POSTResendSignupEmailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.resendUserSignupEmailUsersResendSignupEmailPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Reset Password
         * @param {BodyResetPasswordUsersResetPasswordPost} bodyResetPasswordUsersResetPasswordPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost: BodyResetPasswordUsersResetPasswordPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.resetPasswordUsersResetPasswordPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Send Reset Password Email
         * @param {BodySendResetPasswordEmailUsersSendResetPasswordEmailPost} bodySendResetPasswordEmailUsersSendResetPasswordEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost: BodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.sendResetPasswordEmailUsersSendResetPasswordEmailPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates user preferences that are editable
         * @summary Update Current User Preferences
         * @param {UserPreferences} userPreferences 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences: UserPreferences, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.updateCurrentUserPreferencesUsersCurrentPreferencesPut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update user info
         * @summary Update User Info
         * @param {number} userId 
         * @param {BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch} bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateUserInfoUsersUpdateUserInfoUserIdPatch(userId: number, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch: BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateUserInfoUsersUpdateUserInfoUserIdPatch(userId, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.updateUserInfoUsersUpdateUserInfoUserIdPatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UsersApi - factory interface
 * @export
 */
export const UsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UsersApiFp(configuration)
    return {
        /**
         * Use `/accept_invites/` instead
         * @summary Accept Invite
         * @param {POSTAcceptInvite} pOSTAcceptInvite 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite: POSTAcceptInvite, options?: any): AxiosPromise<GETToken> {
            return localVarFp.acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite, options).then((request) => request(axios, basePath));
        },
        /**
         * Activation for existing user who has been invited to one or more organizations
         * @summary Accept Invites
         * @param {POSTAcceptInvites} pOSTAcceptInvites 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites: POSTAcceptInvites, options?: any): AxiosPromise<GETToken> {
            return localVarFp.acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites, options).then((request) => request(axios, basePath));
        },
        /**
         * Activation for new users
         * @summary Activate User
         * @param {POSTActivateUser} pOSTActivateUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateUserUsersActivatePost(pOSTActivateUser: POSTActivateUser, options?: any): AxiosPromise<GETToken> {
            return localVarFp.activateUserUsersActivatePost(pOSTActivateUser, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deactivate User
         * @param {number} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateUserUsersDeactivateUserUserIdPatch(userId: number, options?: any): AxiosPromise<Message> {
            return localVarFp.deactivateUserUsersDeactivateUserUserIdPatch(userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns information about the current user
         * @summary Get Current User Info
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUserInfoUsersCurrentInfoGet(options?: any): AxiosPromise<GETUserInfo> {
            return localVarFp.getCurrentUserInfoUsersCurrentInfoGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get Token For Pusher
         * @param {string} channelName 
         * @param {string} socketId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenForPusherUsersTokenForPusherPost(channelName: string, socketId: string, options?: any): AxiosPromise<any> {
            return localVarFp.getTokenForPusherUsersTokenForPusherPost(channelName, socketId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token
         * @param {BodyGetTokenUsersTokenPost} bodyGetTokenUsersTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenUsersTokenPost(bodyGetTokenUsersTokenPost: BodyGetTokenUsersTokenPost, options?: any): AxiosPromise<GETToken> {
            return localVarFp.getTokenUsersTokenPost(bodyGetTokenUsersTokenPost, options).then((request) => request(axios, basePath));
        },
        /**
         * Get a login token using a user name and password.  This is really only used for the Swagger UI (/docs), since we use the /token/ endpoint for the actual login flow and this does not support selecting a specific organization.
         * @summary Get Token Via Form
         * @param {string} username 
         * @param {string} password 
         * @param {string} [grantType] 
         * @param {string} [scope] 
         * @param {string} [clientId] 
         * @param {string} [clientSecret] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaFormUsersTokenViaFormPost(username: string, password: string, grantType?: string, scope?: string, clientId?: string, clientSecret?: string, options?: any): AxiosPromise<GETToken> {
            return localVarFp.getTokenViaFormUsersTokenViaFormPost(username, password, grantType, scope, clientId, clientSecret, options).then((request) => request(axios, basePath));
        },
        /**
         * Get a login token using a user name and password
         * @summary Get Token Via Port Optimizer Token
         * @param {POSTTokenViaPortOptimizerToken} pOSTTokenViaPortOptimizerToken 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken: POSTTokenViaPortOptimizerToken, options?: any): AxiosPromise<any> {
            return localVarFp.getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken, options).then((request) => request(axios, basePath));
        },
        /**
         * Use a token to get a new token, most likely for a different organization
         * @summary Get Token Via Token
         * @param {BodyGetTokenViaTokenUsersTokenViaTokenPost} bodyGetTokenViaTokenUsersTokenViaTokenPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost: BodyGetTokenViaTokenUsersTokenViaTokenPost, options?: any): AxiosPromise<GETToken> {
            return localVarFp.getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get User Setup Info
         * @param {BodyGetUserSetupInfoUsersSetupInfoPost} bodyGetUserSetupInfoUsersSetupInfoPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost: BodyGetUserSetupInfoUsersSetupInfoPost, options?: any): AxiosPromise<POSTSetUpUserResponse> {
            return localVarFp.getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost, options).then((request) => request(axios, basePath));
        },
        /**
         * List users for the current user\'s organization
         * @summary Get Users
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersUsersGet(options?: any): AxiosPromise<Array<GETUser>> {
            return localVarFp.getUsersUsersGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Invite Users
         * @param {Array<POSTInviteUser>} pOSTInviteUser 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        inviteUsersUsersInvitePost(pOSTInviteUser: Array<POSTInviteUser>, options?: any): AxiosPromise<any> {
            return localVarFp.inviteUsersUsersInvitePost(pOSTInviteUser, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Resend User Signup Email
         * @param {BodyResendUserSignupEmailUsersResendSignupEmailPost} bodyResendUserSignupEmailUsersResendSignupEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost: BodyResendUserSignupEmailUsersResendSignupEmailPost, options?: any): AxiosPromise<POSTResendSignupEmailResponse> {
            return localVarFp.resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Reset Password
         * @param {BodyResetPasswordUsersResetPasswordPost} bodyResetPasswordUsersResetPasswordPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost: BodyResetPasswordUsersResetPasswordPost, options?: any): AxiosPromise<Message> {
            return localVarFp.resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Send Reset Password Email
         * @param {BodySendResetPasswordEmailUsersSendResetPasswordEmailPost} bodySendResetPasswordEmailUsersSendResetPasswordEmailPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost: BodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options?: any): AxiosPromise<Message> {
            return localVarFp.sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates user preferences that are editable
         * @summary Update Current User Preferences
         * @param {UserPreferences} userPreferences 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences: UserPreferences, options?: any): AxiosPromise<any> {
            return localVarFp.updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences, options).then((request) => request(axios, basePath));
        },
        /**
         * Update user info
         * @summary Update User Info
         * @param {number} userId 
         * @param {BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch} bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserInfoUsersUpdateUserInfoUserIdPatch(userId: number, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch: BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options?: any): AxiosPromise<Message> {
            return localVarFp.updateUserInfoUsersUpdateUserInfoUserIdPatch(userId, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * UsersApi - object-oriented interface
 * @export
 * @class UsersApi
 * @extends {BaseAPI}
 */
export class UsersApi extends BaseAPI {
    /**
     * Use `/accept_invites/` instead
     * @summary Accept Invite
     * @param {POSTAcceptInvite} pOSTAcceptInvite 
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite: POSTAcceptInvite, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).acceptInviteUsersAcceptInvitePost(pOSTAcceptInvite, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Activation for existing user who has been invited to one or more organizations
     * @summary Accept Invites
     * @param {POSTAcceptInvites} pOSTAcceptInvites 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites: POSTAcceptInvites, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).acceptInvitesUsersAcceptInvitesPost(pOSTAcceptInvites, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Activation for new users
     * @summary Activate User
     * @param {POSTActivateUser} pOSTActivateUser 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public activateUserUsersActivatePost(pOSTActivateUser: POSTActivateUser, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).activateUserUsersActivatePost(pOSTActivateUser, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deactivate User
     * @param {number} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public deactivateUserUsersDeactivateUserUserIdPatch(userId: number, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).deactivateUserUsersDeactivateUserUserIdPatch(userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns information about the current user
     * @summary Get Current User Info
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getCurrentUserInfoUsersCurrentInfoGet(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getCurrentUserInfoUsersCurrentInfoGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get Token For Pusher
     * @param {string} channelName 
     * @param {string} socketId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getTokenForPusherUsersTokenForPusherPost(channelName: string, socketId: string, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getTokenForPusherUsersTokenForPusherPost(channelName, socketId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get a login token using a user name and password
     * @summary Get Token
     * @param {BodyGetTokenUsersTokenPost} bodyGetTokenUsersTokenPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getTokenUsersTokenPost(bodyGetTokenUsersTokenPost: BodyGetTokenUsersTokenPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getTokenUsersTokenPost(bodyGetTokenUsersTokenPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get a login token using a user name and password.  This is really only used for the Swagger UI (/docs), since we use the /token/ endpoint for the actual login flow and this does not support selecting a specific organization.
     * @summary Get Token Via Form
     * @param {string} username 
     * @param {string} password 
     * @param {string} [grantType] 
     * @param {string} [scope] 
     * @param {string} [clientId] 
     * @param {string} [clientSecret] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getTokenViaFormUsersTokenViaFormPost(username: string, password: string, grantType?: string, scope?: string, clientId?: string, clientSecret?: string, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getTokenViaFormUsersTokenViaFormPost(username, password, grantType, scope, clientId, clientSecret, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get a login token using a user name and password
     * @summary Get Token Via Port Optimizer Token
     * @param {POSTTokenViaPortOptimizerToken} pOSTTokenViaPortOptimizerToken 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken: POSTTokenViaPortOptimizerToken, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(pOSTTokenViaPortOptimizerToken, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Use a token to get a new token, most likely for a different organization
     * @summary Get Token Via Token
     * @param {BodyGetTokenViaTokenUsersTokenViaTokenPost} bodyGetTokenViaTokenUsersTokenViaTokenPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost: BodyGetTokenViaTokenUsersTokenViaTokenPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getTokenViaTokenUsersTokenViaTokenPost(bodyGetTokenViaTokenUsersTokenViaTokenPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get User Setup Info
     * @param {BodyGetUserSetupInfoUsersSetupInfoPost} bodyGetUserSetupInfoUsersSetupInfoPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost: BodyGetUserSetupInfoUsersSetupInfoPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getUserSetupInfoUsersSetupInfoPost(bodyGetUserSetupInfoUsersSetupInfoPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List users for the current user\'s organization
     * @summary Get Users
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public getUsersUsersGet(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getUsersUsersGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Invite Users
     * @param {Array<POSTInviteUser>} pOSTInviteUser 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public inviteUsersUsersInvitePost(pOSTInviteUser: Array<POSTInviteUser>, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).inviteUsersUsersInvitePost(pOSTInviteUser, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Resend User Signup Email
     * @param {BodyResendUserSignupEmailUsersResendSignupEmailPost} bodyResendUserSignupEmailUsersResendSignupEmailPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost: BodyResendUserSignupEmailUsersResendSignupEmailPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).resendUserSignupEmailUsersResendSignupEmailPost(bodyResendUserSignupEmailUsersResendSignupEmailPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Reset Password
     * @param {BodyResetPasswordUsersResetPasswordPost} bodyResetPasswordUsersResetPasswordPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost: BodyResetPasswordUsersResetPasswordPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).resetPasswordUsersResetPasswordPost(bodyResetPasswordUsersResetPasswordPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Send Reset Password Email
     * @param {BodySendResetPasswordEmailUsersSendResetPasswordEmailPost} bodySendResetPasswordEmailUsersSendResetPasswordEmailPost 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost: BodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).sendResetPasswordEmailUsersSendResetPasswordEmailPost(bodySendResetPasswordEmailUsersSendResetPasswordEmailPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates user preferences that are editable
     * @summary Update Current User Preferences
     * @param {UserPreferences} userPreferences 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences: UserPreferences, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).updateCurrentUserPreferencesUsersCurrentPreferencesPut(userPreferences, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update user info
     * @summary Update User Info
     * @param {number} userId 
     * @param {BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch} bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public updateUserInfoUsersUpdateUserInfoUserIdPatch(userId: number, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch: BodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).updateUserInfoUsersUpdateUserInfoUserIdPatch(userId, bodyUpdateUserInfoUsersUpdateUserInfoUserIdPatch, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * VesselsApi - axios parameter creator
 * @export
 */
export const VesselsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Loads the ETA/ERD/cutoff change-log for one vessel\'s schedule at a terminal.
         * @summary Get Vessel Schedule History
         * @param {string} vesselName 
         * @param {TerminalName} terminal The terminal this vessel\&#39;s schedule is at.
         * @param {number} [limit] Maximum number of events to return, most recent first.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet: async (vesselName: string, terminal: TerminalName, limit?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'vesselName' is not null or undefined
            assertParamExists('getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet', 'vesselName', vesselName)
            // verify required parameter 'terminal' is not null or undefined
            assertParamExists('getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet', 'terminal', terminal)
            const localVarPath = `/vessels/schedules/{vessel_name}/history/`
                .replace(`{${"vessel_name"}}`, encodeURIComponent(String(vesselName)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (terminal !== undefined) {
                localVarQueryParameter['terminal'] = terminal;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Load vessel etas for the given vessel names and time window.
         * @summary Vessel Etas
         * @param {Array<string>} [names] Vessel names to get etas for. If not provided, all vessels with etas are returned.
         * @param {string} [after] Only return etas that with ETA after this time
         * @param {string} [before] Enables the Exports schedule window, with this inclusive ETA upper bound. Open receiving windows and cutoffs within the last 14 days are included regardless of ETA. Missing receiving dates retain past ETAs for 30 days. Delayed voyages previously within seven days and this organization\&#39;s active booking vessels are also retained. Omitting this parameter preserves the unbounded Imports behavior.
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        vesselEtasVesselsEtasGet: async (names?: Array<string>, after?: string, before?: string, portComplexes?: Array<PortComplex>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/vessels/etas/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (names) {
                localVarQueryParameter['names'] = names;
            }

            if (after !== undefined) {
                localVarQueryParameter['after'] = (after as any instanceof Date) ?
                    (after as any).toISOString() :
                    after;
            }

            if (before !== undefined) {
                localVarQueryParameter['before'] = (before as any instanceof Date) ?
                    (before as any).toISOString() :
                    before;
            }

            if (portComplexes) {
                localVarQueryParameter['port_complexes'] = portComplexes;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * VesselsApi - functional programming interface
 * @export
 */
export const VesselsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = VesselsApiAxiosParamCreator(configuration)
    return {
        /**
         * Loads the ETA/ERD/cutoff change-log for one vessel\'s schedule at a terminal.
         * @summary Get Vessel Schedule History
         * @param {string} vesselName 
         * @param {TerminalName} terminal The terminal this vessel\&#39;s schedule is at.
         * @param {number} [limit] Maximum number of events to return, most recent first.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName: string, terminal: TerminalName, limit?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VesselScheduleHistory>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName, terminal, limit, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VesselsApi.getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Load vessel etas for the given vessel names and time window.
         * @summary Vessel Etas
         * @param {Array<string>} [names] Vessel names to get etas for. If not provided, all vessels with etas are returned.
         * @param {string} [after] Only return etas that with ETA after this time
         * @param {string} [before] Enables the Exports schedule window, with this inclusive ETA upper bound. Open receiving windows and cutoffs within the last 14 days are included regardless of ETA. Missing receiving dates retain past ETAs for 30 days. Delayed voyages previously within seven days and this organization\&#39;s active booking vessels are also retained. Omitting this parameter preserves the unbounded Imports behavior.
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async vesselEtasVesselsEtasGet(names?: Array<string>, after?: string, before?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GETVesselSchedules>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.vesselEtasVesselsEtasGet(names, after, before, portComplexes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VesselsApi.vesselEtasVesselsEtasGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * VesselsApi - factory interface
 * @export
 */
export const VesselsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = VesselsApiFp(configuration)
    return {
        /**
         * Loads the ETA/ERD/cutoff change-log for one vessel\'s schedule at a terminal.
         * @summary Get Vessel Schedule History
         * @param {string} vesselName 
         * @param {TerminalName} terminal The terminal this vessel\&#39;s schedule is at.
         * @param {number} [limit] Maximum number of events to return, most recent first.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName: string, terminal: TerminalName, limit?: number, options?: any): AxiosPromise<VesselScheduleHistory> {
            return localVarFp.getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName, terminal, limit, options).then((request) => request(axios, basePath));
        },
        /**
         * Load vessel etas for the given vessel names and time window.
         * @summary Vessel Etas
         * @param {Array<string>} [names] Vessel names to get etas for. If not provided, all vessels with etas are returned.
         * @param {string} [after] Only return etas that with ETA after this time
         * @param {string} [before] Enables the Exports schedule window, with this inclusive ETA upper bound. Open receiving windows and cutoffs within the last 14 days are included regardless of ETA. Missing receiving dates retain past ETAs for 30 days. Delayed voyages previously within seven days and this organization\&#39;s active booking vessels are also retained. Omitting this parameter preserves the unbounded Imports behavior.
         * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        vesselEtasVesselsEtasGet(names?: Array<string>, after?: string, before?: string, portComplexes?: Array<PortComplex>, options?: any): AxiosPromise<Array<GETVesselSchedules>> {
            return localVarFp.vesselEtasVesselsEtasGet(names, after, before, portComplexes, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * VesselsApi - object-oriented interface
 * @export
 * @class VesselsApi
 * @extends {BaseAPI}
 */
export class VesselsApi extends BaseAPI {
    /**
     * Loads the ETA/ERD/cutoff change-log for one vessel\'s schedule at a terminal.
     * @summary Get Vessel Schedule History
     * @param {string} vesselName 
     * @param {TerminalName} terminal The terminal this vessel\&#39;s schedule is at.
     * @param {number} [limit] Maximum number of events to return, most recent first.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof VesselsApi
     */
    public getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName: string, terminal: TerminalName, limit?: number, options?: RawAxiosRequestConfig) {
        return VesselsApiFp(this.configuration).getVesselScheduleHistoryVesselsSchedulesVesselNameHistoryGet(vesselName, terminal, limit, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Load vessel etas for the given vessel names and time window.
     * @summary Vessel Etas
     * @param {Array<string>} [names] Vessel names to get etas for. If not provided, all vessels with etas are returned.
     * @param {string} [after] Only return etas that with ETA after this time
     * @param {string} [before] Enables the Exports schedule window, with this inclusive ETA upper bound. Open receiving windows and cutoffs within the last 14 days are included regardless of ETA. Missing receiving dates retain past ETAs for 30 days. Delayed voyages previously within seven days and this organization\&#39;s active booking vessels are also retained. Omitting this parameter preserves the unbounded Imports behavior.
     * @param {Array<PortComplex>} [portComplexes] Limit to items at these port complexes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof VesselsApi
     */
    public vesselEtasVesselsEtasGet(names?: Array<string>, after?: string, before?: string, portComplexes?: Array<PortComplex>, options?: RawAxiosRequestConfig) {
        return VesselsApiFp(this.configuration).vesselEtasVesselsEtasGet(names, after, before, portComplexes, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WebhooksApi - axios parameter creator
 * @export
 */
export const WebhooksApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Deletes a webhook
         * @summary Delete Webhook
         * @param {number} webhookId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteWebhookWebhooksWebhookIdDelete: async (webhookId: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'webhookId' is not null or undefined
            assertParamExists('deleteWebhookWebhooksWebhookIdDelete', 'webhookId', webhookId)
            const localVarPath = `/webhooks/{webhook_id}/`
                .replace(`{${"webhook_id"}}`, encodeURIComponent(String(webhookId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Loads registered webhooks
         * @summary Get Registered Webhooks
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRegisteredWebhooksWebhooksGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/webhooks/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Registers a webhook.  You can view format of requests that will be *sent* to your callback url in the [POST /webhooksevent](#operation/webhook_format_webhooksevent_post) endpoint.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Register Webhook
         * @param {POSTWebhook} pOSTWebhook 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        registerWebhookWebhooksPost: async (pOSTWebhook: POSTWebhook, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pOSTWebhook' is not null or undefined
            assertParamExists('registerWebhookWebhooksPost', 'pOSTWebhook', pOSTWebhook)
            const localVarPath = `/webhooks/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pOSTWebhook, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * After registering a webhook with the [POST /webhooks/](#operation/register_webhook_webhooks__post) endpoint, events will be sent to your callback URL in this format for the events that you have subscribed to.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Webhook Format
         * @param {WebhookEvent} webhookEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        webhookFormatWebhookseventPost: async (webhookEvent: WebhookEvent, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'webhookEvent' is not null or undefined
            assertParamExists('webhookFormatWebhookseventPost', 'webhookEvent', webhookEvent)
            const localVarPath = `/webhooksevent`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(webhookEvent, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WebhooksApi - functional programming interface
 * @export
 */
export const WebhooksApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WebhooksApiAxiosParamCreator(configuration)
    return {
        /**
         * Deletes a webhook
         * @summary Delete Webhook
         * @param {number} webhookId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteWebhookWebhooksWebhookIdDelete(webhookId: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteWebhookWebhooksWebhookIdDelete(webhookId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksApi.deleteWebhookWebhooksWebhookIdDelete']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Loads registered webhooks
         * @summary Get Registered Webhooks
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getRegisteredWebhooksWebhooksGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SavedWebhook>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getRegisteredWebhooksWebhooksGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksApi.getRegisteredWebhooksWebhooksGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Registers a webhook.  You can view format of requests that will be *sent* to your callback url in the [POST /webhooksevent](#operation/webhook_format_webhooksevent_post) endpoint.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Register Webhook
         * @param {POSTWebhook} pOSTWebhook 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async registerWebhookWebhooksPost(pOSTWebhook: POSTWebhook, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Message>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.registerWebhookWebhooksPost(pOSTWebhook, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksApi.registerWebhookWebhooksPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * After registering a webhook with the [POST /webhooks/](#operation/register_webhook_webhooks__post) endpoint, events will be sent to your callback URL in this format for the events that you have subscribed to.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Webhook Format
         * @param {WebhookEvent} webhookEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async webhookFormatWebhookseventPost(webhookEvent: WebhookEvent, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<any>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.webhookFormatWebhookseventPost(webhookEvent, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksApi.webhookFormatWebhookseventPost']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WebhooksApi - factory interface
 * @export
 */
export const WebhooksApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WebhooksApiFp(configuration)
    return {
        /**
         * Deletes a webhook
         * @summary Delete Webhook
         * @param {number} webhookId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteWebhookWebhooksWebhookIdDelete(webhookId: number, options?: any): AxiosPromise<Message> {
            return localVarFp.deleteWebhookWebhooksWebhookIdDelete(webhookId, options).then((request) => request(axios, basePath));
        },
        /**
         * Loads registered webhooks
         * @summary Get Registered Webhooks
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRegisteredWebhooksWebhooksGet(options?: any): AxiosPromise<Array<SavedWebhook>> {
            return localVarFp.getRegisteredWebhooksWebhooksGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Registers a webhook.  You can view format of requests that will be *sent* to your callback url in the [POST /webhooksevent](#operation/webhook_format_webhooksevent_post) endpoint.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Register Webhook
         * @param {POSTWebhook} pOSTWebhook 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        registerWebhookWebhooksPost(pOSTWebhook: POSTWebhook, options?: any): AxiosPromise<Message> {
            return localVarFp.registerWebhookWebhooksPost(pOSTWebhook, options).then((request) => request(axios, basePath));
        },
        /**
         * After registering a webhook with the [POST /webhooks/](#operation/register_webhook_webhooks__post) endpoint, events will be sent to your callback URL in this format for the events that you have subscribed to.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
         * @summary Webhook Format
         * @param {WebhookEvent} webhookEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        webhookFormatWebhookseventPost(webhookEvent: WebhookEvent, options?: any): AxiosPromise<any> {
            return localVarFp.webhookFormatWebhookseventPost(webhookEvent, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * WebhooksApi - object-oriented interface
 * @export
 * @class WebhooksApi
 * @extends {BaseAPI}
 */
export class WebhooksApi extends BaseAPI {
    /**
     * Deletes a webhook
     * @summary Delete Webhook
     * @param {number} webhookId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebhooksApi
     */
    public deleteWebhookWebhooksWebhookIdDelete(webhookId: number, options?: RawAxiosRequestConfig) {
        return WebhooksApiFp(this.configuration).deleteWebhookWebhooksWebhookIdDelete(webhookId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Loads registered webhooks
     * @summary Get Registered Webhooks
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebhooksApi
     */
    public getRegisteredWebhooksWebhooksGet(options?: RawAxiosRequestConfig) {
        return WebhooksApiFp(this.configuration).getRegisteredWebhooksWebhooksGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Registers a webhook.  You can view format of requests that will be *sent* to your callback url in the [POST /webhooksevent](#operation/webhook_format_webhooksevent_post) endpoint.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
     * @summary Register Webhook
     * @param {POSTWebhook} pOSTWebhook 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebhooksApi
     */
    public registerWebhookWebhooksPost(pOSTWebhook: POSTWebhook, options?: RawAxiosRequestConfig) {
        return WebhooksApiFp(this.configuration).registerWebhookWebhooksPost(pOSTWebhook, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * After registering a webhook with the [POST /webhooks/](#operation/register_webhook_webhooks__post) endpoint, events will be sent to your callback URL in this format for the events that you have subscribed to.  In the event that a callback stops responding with status code 200, we will retry up to 3 times with an exponential backoff. If the callback still does not respond  with status code 200, we will set `disabled=true` on the webhook registration which will stop sending events to that callback url. You can reenable the webhook to be valid again by calling [POST /webhooks/](#operation/register_webhook_webhooks__post)
     * @summary Webhook Format
     * @param {WebhookEvent} webhookEvent 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebhooksApi
     */
    public webhookFormatWebhookseventPost(webhookEvent: WebhookEvent, options?: RawAxiosRequestConfig) {
        return WebhooksApiFp(this.configuration).webhookFormatWebhookseventPost(webhookEvent, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WebsocketsApi - axios parameter creator
 * @export
 */
export const WebsocketsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Channels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listChannelsWsChannelsListGet: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/ws/channels/list/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Event Types
         * @param {EventName} eventName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEventTypesWsEventsTypesListGet: async (eventName: EventName, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'eventName' is not null or undefined
            assertParamExists('listEventTypesWsEventsTypesListGet', 'eventName', eventName)
            const localVarPath = `/ws/events/types/list/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication OAuth2PasswordBearer required
            // oauth required
            await setOAuthToObject(localVarHeaderParameter, "OAuth2PasswordBearer", [], configuration)

            if (eventName !== undefined) {
                localVarQueryParameter['event_name'] = eventName;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WebsocketsApi - functional programming interface
 * @export
 */
export const WebsocketsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WebsocketsApiAxiosParamCreator(configuration)
    return {
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Channels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listChannelsWsChannelsListGet(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ChannelInfo>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listChannelsWsChannelsListGet(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebsocketsApi.listChannelsWsChannelsListGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Event Types
         * @param {EventName} eventName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listEventTypesWsEventsTypesListGet(eventName: EventName, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ListEventTypesWsEventsTypesListGet200ResponseInner>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listEventTypesWsEventsTypesListGet(eventName, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebsocketsApi.listEventTypesWsEventsTypesListGet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WebsocketsApi - factory interface
 * @export
 */
export const WebsocketsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WebsocketsApiFp(configuration)
    return {
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Channels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listChannelsWsChannelsListGet(options?: any): AxiosPromise<Array<ChannelInfo>> {
            return localVarFp.listChannelsWsChannelsListGet(options).then((request) => request(axios, basePath));
        },
        /**
         * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
         * @summary List Event Types
         * @param {EventName} eventName 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEventTypesWsEventsTypesListGet(eventName: EventName, options?: any): AxiosPromise<Array<ListEventTypesWsEventsTypesListGet200ResponseInner>> {
            return localVarFp.listEventTypesWsEventsTypesListGet(eventName, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * WebsocketsApi - object-oriented interface
 * @export
 * @class WebsocketsApi
 * @extends {BaseAPI}
 */
export class WebsocketsApi extends BaseAPI {
    /**
     * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
     * @summary List Channels
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebsocketsApi
     */
    public listChannelsWsChannelsListGet(options?: RawAxiosRequestConfig) {
        return WebsocketsApiFp(this.configuration).listChannelsWsChannelsListGet(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Just exists to get typing information into the docs.  This version is for our new setup with Soketi, which has a different format
     * @summary List Event Types
     * @param {EventName} eventName 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof WebsocketsApi
     */
    public listEventTypesWsEventsTypesListGet(eventName: EventName, options?: RawAxiosRequestConfig) {
        return WebsocketsApiFp(this.configuration).listEventTypesWsEventsTypesListGet(eventName, options).then((request) => request(this.axios, this.basePath));
    }
}



